import { createWsClient } from './ws-client';
import type { WsClient, WsClientDeps, WsCloseInfo } from './ws-client';
import { logClientWsEvent } from './ws-wire-log';
import { installClientRealtimeTraceControls, logClientRealtimeTrace } from './realtime-trace';
import {
  SERVER_UNAVAILABLE_ELAPSED_MS,
  classifyReconnect,
  isSameOriginGameWsUrl,
  isSessionRestoredElsewhereClose,
  type ControlProbeResult,
  type ReconnectVariant,
} from './reconnect-classifier';
import { createBoardPresentationQueue } from './board-presentation-queue';
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
  type StoredSmashpartySession,
} from './session-storage';
import { installGameSelectionGuard } from './selection-guard';
import { createGsapRegistry } from './renderer/vfx/gsap-registry';
import type { GsapRegistry } from './renderer/vfx/gsap-registry';
import { createRenderer, createDefaultShaderRegistry, applyLightingConfig } from './renderer';
import type { BoardAtmosphereHandle, ComposerMount, LightingRig, RendererHandle } from './renderer';
import { loadMap } from './renderer/maps';
import type { MapHandle, ThemeOverride } from './renderer/maps';
import { applyMapFx } from './renderer/maps/fx';
import { buildBoard } from './renderer/board/build-board';
import type { BoardRenderHandle } from './renderer/types';
import type { BoardNode } from '../shared/board/types';
import { minigameRegistry } from '@shared/minigames/registry';
import type { MinigameClientHandle, MinigameStartArgs } from '@shared/minigames/registry';
import type { MinigameInputReceipt } from '@shared/minigames/registry';
import { createRealtimeClockSync } from '@minigames/realtime-reconciliation';
import { bootPreGameScreens } from './ui/screens/boot';
import type {
  BoardLiveHudPlayer,
  BoardLiveHudState,
  BoardMountPayload,
  ItemDirectionalTargetingSession,
  ItemPlayerTargetingSession,
  ItemRangePreviewSession,
} from './ui/screens/board-payload';
import { deriveActiveBoardTimer } from './ui/screens/board-timer';
import { createTileEffectPopupScreen } from './ui/screens/tile-effect-popup';
import { createTurnBanner, TURN_BANNER_TOTAL_SECONDS } from './ui/components/turn-banner';
import type { DiceRollVisualHandle } from './ui/screens/dice-roll-overlay';
import {
  createLiveTutorialWidget,
  type LiveTutorialWidgetHandle,
  type LiveTutorialStep,
} from './ui/components/live-tutorial-widget';
import { createToastStackOverlay } from './ui/overlays/toast-stack';
import type { ScreenHandle, ScreenId, ScreenRouter } from './ui/screens/types';
import { createErrorReporter } from './error-reporter';
import type { ErrorReporter } from './error-reporter';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { MeshoptDecoder } from 'meshoptimizer';
import {
  createAudioAssetLoader,
  createGltfAssetLoader,
  createTextureAssetLoader,
  loadManifest,
} from './assets';
import { clearStaleRuntimeAssetCaches, runtimeAssetUrl } from './assets/runtime-asset-url';
import type { CharacterPreviewAdapter } from './ui/screens/character-preview-adapter';
import type {
  CharacterCustomizeCameraRequest,
  CharacterCustomizeCameraView,
} from './ui/screens/character-select';
import {
  createCharacterAdapterPreloadHandle,
  type BlobAnimationBundle,
  type CharacterAnimationBundlePolicyOptions,
} from './character-animation-adapter-loader';
import {
  getBoardCharacterHoverOffset,
  selectBoardCharacterLocomotionAlias,
  selectBoardCharacterWalkTurnAlias,
} from './renderer/character/board-locomotion-animation';
import {
  createCharacterStatusEffectManager,
  createCharacterEyeExpressionManager,
  CHARACTER_REACTION_ANIMATION_BUNDLES,
  createLobbyAmbientAnimationScheduler,
  EYE_EXPRESSIONS,
  LOBBY_AMBIENT_ANIMATION_ALIASES,
  playCharacterAnimationAlias,
  selectCharacterAnimationAlias,
  setCharacterAnimationOverride,
} from './renderer/character';
import { computeDiceCatchLayout } from './renderer/character/dice-catch-layout';
import { DICE_CATCH_TUNING } from './renderer/character/dice-catch-tuning';
import type {
  EyeExpression,
  EyeTextureStore,
  CharacterAnimationAlias,
  LobbyAmbientAnimationClip,
} from './renderer/character';
import type { CharacterPreviewRig } from './renderer/character/preview-rig';
import {
  setCharacterRigColorableGhostOpacity,
  setCharacterRigColorableTint,
  getCharacterRigOpacity,
  setCharacterRigEyesVisible,
  setCharacterRigOpacity,
} from './renderer/character/opacity';
import { createLandingFxPool } from './renderer/vfx/tile/landing-fx-pool';
import { createBloodPool } from './renderer/vfx/blood-pool';
import { createAudioCueHooks } from './renderer/vfx/audio-hooks';
import {
  getItemVfxSmokeTexture,
  ITEM_VFX_SMOKE_ALPHA_TEST,
} from './renderer/vfx/items/sprite-textures';
import {
  createThunderFistProjectileObject,
  orientThunderFistProjectile,
} from './renderer/vfx/items/thunder-fist';
import { createPathArrows, PATH_ARROW_STYLE_VERSION } from './renderer/board/path-arrows';
import type { PathArrowsHandle } from './renderer/board/path-arrows';
import { computeSameTileStackOffset } from './renderer/board/same-tile-layout';
import { BoardGraph } from '../shared/board/graph';
import { findWalkPath, nodesWithinGraphRange } from '../shared/board/targeting';
import { spawnProceduralD20Roll } from './renderer/animation/dice-throw';
import { createOrbitTargetFollower } from './renderer/camera-follow';
import type { OrbitTargetFollower } from './renderer/camera-follow';
import {
  applyLobbyCameraState,
  createLobbyCameraTunerOverlay,
  readLobbyCameraQuery,
} from './dev/lobby-cam-tuner';
import type { LobbyCameraTunerOverlayHandle } from './dev/lobby-cam-tuner';
import { createWsDebugOverlay, shouldShowWsDebugOverlay } from './ws-debug-overlay';
import type { WsDebugOverlayHandle } from './ws-debug-overlay';
import { deserializeGameState } from '../shared/protocol';
import type {
  ClientMessage,
  ClientPresentationTraceMessage,
  ServerMessage,
} from '../shared/protocol';
import {
  ALL_EYE_IDS,
  ALL_HEAD_IDS,
  COLOR_ID_TO_HEX,
  type DiceRollEvent,
  type DroppedKeyPile,
  type GameEvent,
  type GameState,
  type ItemDashEvent,
  type ItemHitEvent,
  type CharacterCustomization,
  type MovementStepEvent,
} from '../shared/game/types';
import { createGamepadSource } from './input/gamepad';
import { getInputModality } from './input/modality';
import { createKeyboardSource } from './input/keyboard';
import { applyDeadzoneAndCurve } from './input/deadzone';
import type { Vec2 } from './input/types';
import { createAudioManager } from './audio';
import type { AudioManager, MusicTrackId, SfxId, Vec3 } from './audio';
import {
  createEmoteRuntime,
  EMOTE_ICON_ASSET_IDS,
  EMOTE_IDS,
  type EmoteId,
  type EmoteRuntimeDiagnostic,
  type EmoteRuntimeHandle,
} from './emotes';
import { createAudioSettingsController, isMusicMuted, isSfxMuted } from './audio-settings';
import { setButtonClickSfxHandler, setButtonHoverSfxHandler } from './ui/components/button';
import {
  buildWarmupSubscribeMessage,
  areTutorialsHidden,
  hasSeenGameplayTutorial,
  hasSeenGameplayTutorialLesson,
  hasSeenWarmupTutorial,
  markGameplayTutorialLessonSeen,
  markGameplayTutorialSeen,
  readPreferredCustomization,
  savePreferredCustomization,
} from './portal-entry';
import { pickWarmupArenaDirectionalTarget } from './warmup-arena-movement';
import { itemIconUrl, itemModelUrl } from './ui/screens/item-assets';
import { allItems, getItem } from '../shared/game/items';
import * as THREE from 'three';
import renderScales from '@shared/render-scales';
import {
  getLightDirectionFromSunDirection,
  getSunDirectionFromSkyConfig,
} from './renderer/atmosphere/daylight-sky';
import { getCloudLayerBaseSize, getCloudLayerBoundaryRadius } from './renderer/board/cloud-layer';

export type { WsClient, WsClientDeps } from './ws-client';

const MAX_PLAYERS = 8;
const BOARD_RIG_SCALE = renderScales.character.rigScale;
const BOARD_RIG_FOOT_OFFSET_FALLBACK = renderScales.character.footOffsetFallback;
const BOARD_RIG_FOOT_OFFSET_MIN_HEIGHT_RATIO = renderScales.character.footOffsetMinHeightRatio;
const BOARD_RIG_STACK_CLEARANCE_PADDING = 0.06;
const BOARD_RIG_STACK_CLEARANCE_FALLBACK =
  BOARD_RIG_FOOT_OFFSET_FALLBACK * 2 + BOARD_RIG_STACK_CLEARANCE_PADDING;
const BOARD_FOCUS_Y_OFFSET = renderScales.character.focusYOffset;
const MAX_MAIN_GSAP_DELTA_SECONDS = 0.1;
const PAUSE_GSAP_ALLOW_LABEL_PREFIXES = [
  'pause-menu:',
  'button-states:',
  'panel:',
  'modal:',
  'tab-bar:',
  'toggle:',
  'slider:',
] as const;
const BOARD_MOVE_DURATION_SECONDS = 0.48;
const BOARD_MOVE_LIFT = 0.035;
const DICE_ROLL_VISUAL_DURATION_SECONDS = DICE_CATCH_TUNING.rollVisualDurationSeconds;
const DICE_STRETCH_OVERRIDE_MS = DICE_CATCH_TUNING.stretchOverrideMs;
const DICE_CATCH_FALLBACK_DURATION_SECONDS = DICE_CATCH_TUNING.fallbackDurationSeconds;
const DICE_CATCH_GRAB_AT_RATIO = DICE_CATCH_TUNING.grabAtRatio;
const DICE_CATCH_MIN_START_SECONDS = DICE_CATCH_TUNING.minStartSeconds;
const DICE_CATCH_D20_SCALE = DICE_CATCH_TUNING.d20Scale;
const DICE_CATCH_RESULT_ANCHOR_Y_OFFSET = DICE_CATCH_TUNING.d20Radius * DICE_CATCH_D20_SCALE + 0.5;
const DICE_CATCH_HOLD_AFTER_IMPACT_SECONDS = DICE_CATCH_TUNING.holdAfterImpactSeconds;
const DICE_CATCH_DESCEND_SECONDS = DICE_CATCH_TUNING.descendSeconds;
const SHADOW_STALKER_HOVER_HEIGHT = 0.68;
const SHADOW_STALKER_BOB_HEIGHT = 0.055;
const SHADOW_STALKER_MOVE_DURATION_SECONDS = 0.38;
const SHADOW_STALKER_KILL_SECONDS = 0.76;
const SHADOW_STALKER_MIST_SPRITES = 58;
const PRESENTATION_TRACE_RATE_LIMIT = 10;
const PRESENTATION_TRACE_RATE_WINDOW_MS = 1_000;
const WARMUP_ARENA_WATCH_EVENT = 'smashparty:warmup-watch-active-game';
const WARMUP_ARENA_BACK_EVENT = 'smashparty:warmup-watch-arena';
const DISCORD_CHANNEL_URL = readDiscordChannelUrl();
const GAMEPLAY_ORBIT_MIN_DISTANCE = 2;
const GAMEPLAY_ORBIT_FALLBACK_MAX_DISTANCE = 120;
const GAMEPLAY_ORBIT_CLOUD_BOUNDARY_PADDING = 4;
const ITEM_TARGETING_ORBIT_MAX_DISTANCE = 20;
type WarmupCameraView = 'arena' | 'mainland';
type GameplayCoreStepId = 'roll' | 'movement' | 'objective' | 'players' | 'round';
type GameplayLessonId =
  | GameplayCoreStepId
  | 'actions'
  | 'emotes'
  | 'menu'
  | 'fork'
  | 'gold'
  | 'item'
  | 'hazard'
  | 'heal'
  | 'soul-anchor'
  | 'treasure-chest'
  | 'weapons-cache'
  | 'dropped-keys'
  | 'death-respawn';
type GameplayLesson = LiveTutorialStep & {
  readonly id: GameplayLessonId;
  readonly critical?: boolean;
  readonly passive?: boolean;
};

const GAMEPLAY_TUTORIAL_CORE_LESSON_IDS: readonly GameplayCoreStepId[] = [
  'roll',
  'movement',
  'objective',
  'players',
  'round',
];
function minigameActionAnimationAliasesForStart(
  minigameId: string,
): readonly CharacterAnimationAlias[] {
  if (minigameId === 'crown-capture') return ['punch', 'knockdown'];
  if (minigameId === 'laser-leap') return ['defeat'];
  return [];
}
const WARMUP_SNAPSHOT_RIG_HEIGHT = 0.42;
const WARMUP_DIRECTIONAL_MOVE_COOLDOWN_SECONDS = 0.18;
const WARMUP_DIRECTIONAL_MOVE_PENDING_TIMEOUT_SECONDS = 0.75;
const WARMUP_DIRECTIONAL_INPUT_MIN_MAGNITUDE = 0.25;
const HELD_ITEM_PREVIEW_SCALE = 0.16;
const ITEM_USE_PRESENTATION_SECONDS = 0.72;
const ITEM_USE_HIT_REACTION_SECONDS = 0.32;
const BACKGROUND_PRELOAD_CONCURRENCY = 2;
const THUNDER_FIST_HIT_CAMERA_IN_SECONDS = 0.2;
const THUNDER_FIST_HIT_CAMERA_RETURN_SECONDS = 0.28;
const THUNDER_FIST_HIT_TRAVEL_SECONDS = 0.55;
const THUNDER_FIST_HIT_LIFETIME_SECONDS = 0.78;
const THUNDER_FIST_CHEST_Y_OFFSET = Math.max(BOARD_FOCUS_Y_OFFSET, 0.82);
const THUNDER_FIST_HAND_Y_OFFSET = Math.max(BOARD_FOCUS_Y_OFFSET * 0.78, 0.58);
const EMOTE_ID_TO_CHARACTER_ANIMATION: Record<EmoteId, string> = {
  1: 'emoteGG',
  2: 'emoteOof',
  3: 'emoteTaunt',
  4: 'emoteThanks',
  5: 'emoteCry',
  6: 'emoteCheer',
};
const CHARACTER_TURN_ANIMATION_SECONDS = 0.42;
const PATH_ARROW_FACING_TURN_SECONDS = 0.22;
const PATH_ARROW_FACING_IDLE_TURN_THRESHOLD = 0.28;
// Gentle breathing-style idle values. Keep these top-level so a future tuner panel can expose them.
const CHARACTER_IDLE_BOB_RADIANS_PER_SECOND = 0.8;
const CHARACTER_IDLE_SWAY_RADIANS_PER_SECOND = 0.4;
const LOBBY_ROSTER_IDLE_BOB_AMPLITUDE = 0.02;
const GHOST_ROSTER_IDLE_BOB_AMPLITUDE = 0.0125;
const BOARD_ROSTER_IDLE_BOB_AMPLITUDE = 0.01;
const BOARD_BUTT_WOBBLE_RADIANS_PER_SECOND = Math.PI * 5; // ~2.5 Hz
const BOARD_BUTT_WOBBLE_IDLE_AMPLITUDE = 0.005;
const BOARD_BUTT_WOBBLE_MOVING_AMPLITUDE = 0.01125;
const ACTIVE_MINIGAME_PHASES: ReadonlySet<GameState['phase']> = new Set([
  'minigame_announce',
  'minigame_countdown',
  'minigame_play',
  'minigame_results',
]);

function preloadImageUrl(url: string): Promise<void> {
  if (typeof Image === 'undefined') return Promise.resolve();
  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve();
    };
    const timeout = window.setTimeout(done, 75);
    image.onload = done;
    image.onerror = done;
    image.src = url;
    if ('clock' in window.setTimeout) done();
    if (image.complete) done();
  });
}

async function runLimitedConcurrency<T>(
  values: readonly T[],
  limit: number,
  task: (value: T) => Promise<void>,
): Promise<void> {
  const queue = [...values];
  const workerCount = Math.max(1, Math.min(limit, queue.length));
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (queue.length > 0) {
        const value = queue.shift();
        if (value !== undefined) await task(value);
      }
    }),
  );
}

function headMenuThumbnailUrl(headId: string): string {
  return `/assets/textures/character-head/menu/head_${headId.replaceAll('-', '_')}.webp`;
}

function eyeMenuThumbnailUrl(eyeId: string): string {
  return `/assets/textures/eyes/menu/${eyeId === 'x-eyes' ? 'xeyes' : eyeId}.webp`;
}

const MUTATING_BOARD_ACTION_TYPES = new Set<string>([
  'roll_dice',
  'use_item',
  'skip_item',
  'choose_fork',
  'buy_trophy',
  'skip_trophy',
  'claim_weapons_cache',
  'claim_soul_anchor',
]);
type PendingLocalActionKind =
  | 'roll_dice'
  | 'use_item'
  | 'skip_item'
  | 'choose_fork'
  | 'buy_trophy'
  | 'skip_trophy'
  | 'claim_weapons_cache'
  | 'claim_soul_anchor';
interface PendingLocalActionBase {
  readonly kind: PendingLocalActionKind;
  readonly playerId: string;
  readonly turnKey: string;
  readonly submittedPhase: GameState['phase'] | null;
  readonly submitEventCursor: number;
  readonly submittedAtMs: number;
}
type PendingLocalAction =
  | (PendingLocalActionBase & {
      readonly kind: 'roll_dice';
      readonly nextNodeId?: string;
    })
  | (PendingLocalActionBase & {
      readonly kind: 'choose_fork';
      readonly nextNodeId: string;
    })
  | (PendingLocalActionBase & {
      readonly kind: 'use_item';
      readonly itemId: string;
      readonly targetId?: string;
      readonly targetNodeId?: string;
      readonly direction?: { dx: number; dy: number };
    })
  | (PendingLocalActionBase & {
      readonly kind: 'skip_item';
    })
  | (PendingLocalActionBase & {
      readonly kind: 'buy_trophy' | 'skip_trophy';
      readonly promptNodeId: string | null;
    })
  | (PendingLocalActionBase & {
      readonly kind: 'claim_weapons_cache';
      readonly promptNodeId: string | null;
      readonly itemId: string;
    })
  | (PendingLocalActionBase & {
      readonly kind: 'claim_soul_anchor';
      readonly promptNodeId: string | null;
      readonly choice?: 'keys' | 'hp';
    });
type PromptSuppression =
  | {
      readonly kind: 'treasure_chest' | 'weapons_cache';
      readonly playerId: string;
      readonly nodeId: string;
      readonly turnKey: string;
    }
  | {
      readonly kind: 'soul_anchor';
      readonly playerId: string;
      readonly nodeId: string;
      readonly turnKey: string;
      readonly choice?: 'keys' | 'hp';
    };
const ACTION_REJECTION_TEXTS: Record<PendingLocalActionKind, ReadonlySet<string>> = {
  roll_dice: new Set([
    'invalid_movement_direction',
    'nextNodeId required',
    'not_your_turn',
    'trapped',
    'no_room',
  ]),
  use_item: new Set([
    'no_room',
    'not_your_turn',
    'item_not_owned',
    'trapped',
    'cursed_relic_lockout',
    'unknown_item',
    'invalid_target',
    'use_item_failed',
  ]),
  skip_item: new Set(['no_room', 'not_your_turn']),
  choose_fork: new Set(['no_room', 'no_pending_fork', 'invalid_fork_choice', 'fork_failed']),
  buy_trophy: new Set([
    'no_room',
    'not_your_turn',
    'not_at_chest',
    'cannot_purchase',
    'purchase_failed',
  ]),
  skip_trophy: new Set(['no_room', 'not_your_turn', 'not_at_chest']),
  claim_weapons_cache: new Set([
    'no_room',
    'not_your_turn',
    'not_at_weapons_cache',
    'invalid_cache_item',
    'cache_claim_failed',
  ]),
  claim_soul_anchor: new Set([
    'no_room',
    'not_your_turn',
    'invalid_choice',
    'no_pending_claim',
    'claim_failed',
  ]),
};
type PresentationTraceDetails = Omit<
  ClientPresentationTraceMessage,
  'type' | 'playerId' | 'round' | 'currentPlayerIndex'
>;

export interface MainBootOpts {
  readonly canvas: HTMLCanvasElement;
  readonly host: HTMLElement;
  readonly wsUrl: string;
  readonly presentationTraceEnabled?: boolean;
  readonly wsClientFactory?: (deps: WsClientDeps) => WsClient;
  readonly rendererFactory?: typeof import('@client/renderer').createRenderer;
  readonly gsapRegistryFactory?: () => GsapRegistry;
  readonly bootScreensFactory?: typeof import('@client/ui/screens/boot').bootPreGameScreens;
  readonly errorReporterFactory?: typeof createErrorReporter;
}

export interface MainBootHandle {
  readonly renderer: RendererHandle;
  readonly wsClient: WsClient;
  readonly screenRouter: ScreenRouter;
  readonly errorReporter: ErrorReporter;
  readonly atmosphere: MainAtmosphereHandle;
  readonly devTools: MainDevToolsHandle;
  /** null if body GLB failed to load or we're in test/headless mode */
  readonly characterAdapter: CharacterPreviewAdapter | null;
  dispose(): void;
}

export interface MainDevToolsHandle {
  getLocalPlayerId(): string | null;
  subscribeBoardTileClick(listener: (nodeId: string) => void): () => void;
  getWarmupTapTargets(): MainWarmupTapTarget[];
  getEyeDebugSnapshot(): MainEyeDebugSnapshot;
  getMobileDebugSnapshot(): MainMobileDebugSnapshot;
  setPlayerEyeExpression(playerId: string, expression: EyeExpression): boolean;
}

export interface MainWarmupTapTarget {
  readonly nodeId: string;
  readonly screenX: number;
  readonly screenY: number;
}

export interface MainMobileDebugSnapshot {
  readonly screen: ScreenId | null;
  readonly phase: GameState['phase'] | 'game_start' | 'game_started';
  readonly inWarmupArena: boolean;
  readonly mapLoaded: boolean;
  readonly boardLoaded: boolean;
  readonly boardVisible: boolean;
  readonly characterAdapterLoaded: boolean;
  readonly localPlayerId: string | null;
  readonly localPlayer: {
    readonly id: string;
    readonly nodeId: string;
    readonly isReady: boolean;
  } | null;
  readonly localRig: {
    readonly visible: boolean;
    readonly parented: boolean;
    readonly position: Vec3;
  } | null;
  readonly canvas: {
    readonly width: number;
    readonly height: number;
  };
}

export interface MainEyeDebugSnapshot {
  readonly phase: GameState['phase'] | null;
  readonly players: Array<{
    readonly id: string;
    readonly name: string;
    readonly isBot: boolean;
    readonly head: string;
    readonly eyes: string;
    readonly boardRig: MainEyeRigDebugSnapshot | null;
    readonly rosterRig: MainEyeRigDebugSnapshot | null;
  }>;
}

export interface MainEyeRigDebugSnapshot {
  readonly eyeExpression: string | null;
  readonly eyeGroupName: string | null;
  readonly eyeGroupExpression: string | null;
  readonly eyeGroupVisible: boolean | null;
  readonly eyeMeshCount: number;
  readonly textureUuids: string[];
}

export interface MainAtmosphereHandle {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly lightingRig: LightingRig;
  readonly postChain: ComposerMount;
  getMapTheme(): ThemeOverride | null;
  getBoardAtmosphere(): BoardAtmosphereHandle | null;
  applyTheme(theme: ThemeOverride): void;
  resetToMapTheme(): void;
  subscribe(listener: () => void): () => void;
}

interface MinigameResultsReceiver extends MinigameClientHandle {
  applyRoundResults?(
    roundIndex: number,
    placements: ReadonlyArray<{
      readonly playerId: string;
      readonly rank: number;
      readonly count: number;
      readonly score: number;
    }>,
    phaseId?: string,
  ): void;
  applyServerResults?(
    placements: ReadonlyArray<{
      readonly playerId: string;
      readonly rank: number;
      readonly score: number;
    }>,
    rewards?: ReadonlyArray<{
      readonly playerId: string;
      readonly itemIds: readonly string[];
    }>,
  ): void;
}

interface ClientMinigameStartArgs extends MinigameStartArgs {
  readonly localPlayerId?: string;
  readonly onSubmit: (input: unknown, roundIndex: number) => MinigameInputReceipt | void;
}

export function deriveWsUrl(): string {
  const explicit = new URLSearchParams(window.location.search).get('ws');
  if (
    explicit !== null &&
    explicit !== '' &&
    (import.meta.env?.DEV === true || import.meta.env?.MODE === 'test')
  ) {
    return explicit;
  }
  if (import.meta.env?.VITE_WS_URL) return import.meta.env.VITE_WS_URL as string;
  return deriveWsUrlFromLocation(window.location);
}

export function deriveWsUrlFromLocation(
  location: Pick<Location, 'host' | 'hostname' | 'protocol'>,
): string {
  if (location.protocol === 'https:') {
    return 'wss://' + location.host + '/ws-game';
  }

  // Arena worktree default for local HTTP development.
  return 'ws://' + location.hostname + ':8799';
}

function readDiscordChannelUrl(): string | null {
  const raw = import.meta.env?.VITE_DISCORD_CHANNEL_URL;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

type AudioContextConstructor = typeof AudioContext;

function resolveAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  return (
    (globalThis as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext ?? null
  );
}

const NOTIFICATION_TEXT: Record<string, string> = {
  'Settings are locked in Quick Play': 'Settings are locked in Quick Play',
  already_started: 'Game already started',
  arena_full: 'The warmup arena is full',
  bots_added_at_autostart: 'Bots added to start the match',
  cannot_purchase: 'You cannot purchase a trophy right now',
  cannot_remove_host: 'The host cannot be removed',
  claim_failed: 'Could not claim the soul anchor',
  color_taken: 'That color is already taken',
  'code required': 'Room code is required',
  chosenMinigameId_required: 'Choose a minigame first',
  'chosenMinigameId required': 'Choose a minigame first',
  difficulty_required: 'Bot difficulty is required',
  'difficulty required': 'Bot difficulty is required',
  emote_player_mismatch: 'That emote is for a different player',
  'emoteId must be integer 1-6': 'Choose a valid emote',
  fork_failed: 'Could not choose that path',
  internal_error: 'Something went wrong',
  'invalid customization': 'Choose a valid character look',
  'invalid customization.cape': 'Choose a valid cape',
  'invalid customization.color': 'Choose a valid color',
  'invalid customization.eyes': 'Choose valid eyes',
  'invalid customization.head': 'Choose a valid head',
  invalid_emote: 'Choose a valid emote',
  invalid_arena_tile: 'Choose a warmup arena tile',
  invalid_local_slot: 'That local player slot is invalid',
  invalid_slot: 'That slot is invalid',
  invalid_target: 'Choose a valid arena target',
  item_tile_cooling_down: 'That item tile is recharging',
  'isReady required': 'Ready state is required',
  item_not_owned: 'You do not own that item',
  'itemId required': 'Choose an item first',
  'localSlot required': 'Local player slot is required',
  minigame_not_active: 'The minigame is not active',
  maintenance_unavailable: 'Maintenance status is unavailable',
  'missing type': 'Invalid message',
  'mode required': 'Game mode is required',
  need_min_players: 'Need at least 2 players to start',
  'nextNodeId required': 'Choose a path first',
  no_pending_fork: 'No path choice is pending',
  no_room: 'You are not in a room',
  not_all_ready: 'Waiting for all players to be ready',
  not_host: 'Only the host can do that',
  not_your_turn: 'It is not your turn',
  game_paused: 'Game is paused',
  god_mode_enabled: 'God Mode enabled',
  god_mode_invalid: 'God Mode token is invalid',
  god_mode_required: 'God Mode is required',
  pause_disabled: 'Pause is disabled in Quick Play',
  pause_unavailable: 'Pause is not available here',
  'playerId required': 'Player is required',
  'playerName required': 'Player name is required',
  purchase_failed: 'Could not purchase the trophy',
  room_full: 'That room is full',
  room_not_found: 'Room not found',
  'roomType required': 'Room type is required',
  session_expired: 'Session expired',
  session_restored_elsewhere: 'Session opened in another tab',
  'sessionToken required': 'Session token is required',
  'settings required': 'Settings are required',
  slot_in_use: 'That slot is already in use',
  trapped: 'You are trapped this turn',
  cursed_relic_lockout: 'Cursed Relic blocks item use',
  unknown_item: 'Unknown item',
  unknown_message_type: 'That action is not available here',
  unknown_player: 'Unknown player',
  unknown_session: 'Unknown session',
  'unknown type': 'Unknown message type',
  use_item_failed: 'Could not use that item',
};

function formatNotificationText(
  text: string,
  opts?: { activeGameRooms?: number; estimateMinutes?: number },
): string {
  const normalized = text.trim();
  if (normalized === 'maintenance_update_pending') {
    const activeGameRooms =
      typeof opts?.activeGameRooms === 'number' && Number.isFinite(opts.activeGameRooms)
        ? Math.max(0, Math.trunc(opts.activeGameRooms))
        : 0;
    const estimateMinutes =
      typeof opts?.estimateMinutes === 'number' && Number.isFinite(opts.estimateMinutes)
        ? Math.max(1, Math.trunc(opts.estimateMinutes))
        : 30;
    return (
      'Update queued. New rooms are temporarily disabled. ' +
      `${activeGameRooms} active game(s) still running; updates usually land within about ` +
      `${estimateMinutes} minutes.`
    );
  }
  const mapped = NOTIFICATION_TEXT[normalized];
  if (mapped != null) return mapped;
  const humanized = normalized.replace(/_/g, ' ').replace(/:\s*/g, ': ');
  if (humanized.length === 0) return 'Something went wrong';
  return humanized.charAt(0).toUpperCase() + humanized.slice(1);
}

export async function bootMain(opts: MainBootOpts): Promise<MainBootHandle> {
  installClientRealtimeTraceControls();
  await clearStaleRuntimeAssetCaches().catch((err: unknown) => {
    console.warn('[smashparty] stale asset cache cleanup failed:', err);
  });

  const wsClientFactory = opts.wsClientFactory ?? createWsClient;
  const rendererFactory = opts.rendererFactory ?? createRenderer;
  const gsapRegistryFactory = opts.gsapRegistryFactory ?? createGsapRegistry;
  const bootScreensFactory = opts.bootScreensFactory ?? bootPreGameScreens;
  const errorReporterFactory = opts.errorReporterFactory ?? createErrorReporter;
  const selectionGuard = installGameSelectionGuard([opts.canvas, opts.host]);

  let activeStoredSession: StoredSmashpartySession | null = readStoredSession();
  let restoreAttemptInFlight = activeStoredSession != null;
  let reconnectLastCloseInfo: WsCloseInfo | null = null;
  let reconnectFailureCount = 0;
  let reconnectCycleStartedAt: number | null = null;
  let reconnectLastVariant: ReconnectVariant | null = null;
  let reconnectControlProbeResult: ControlProbeResult | null = null;
  let reconnectControlProbeSeq = 0;
  let reconnectCloseObservedSinceLastState = false;
  let reconnectRefreshTimer: number | null = null;
  let warmupRestoreFallbackTimer: number | null = null;
  let warmupResubscribeOnNextOpen = false;
  const sameOriginGameWs =
    typeof window === 'undefined' ? false : isSameOriginGameWsUrl(opts.wsUrl, window.location);

  function clearReconnectRefreshTimer(): void {
    if (reconnectRefreshTimer == null) return;
    window.clearTimeout(reconnectRefreshTimer);
    reconnectRefreshTimer = null;
  }

  function clearWarmupRestoreFallbackTimer(): void {
    if (warmupRestoreFallbackTimer == null) return;
    window.clearTimeout(warmupRestoreFallbackTimer);
    warmupRestoreFallbackTimer = null;
  }

  function clearReconnectCycle(): void {
    clearReconnectRefreshTimer();
    reconnectLastCloseInfo = null;
    reconnectFailureCount = 0;
    reconnectCycleStartedAt = null;
    reconnectLastVariant = null;
    reconnectControlProbeResult = null;
    reconnectControlProbeSeq++;
    reconnectCloseObservedSinceLastState = false;
    clearWarmupRestoreFallbackTimer();
    warmupResubscribeOnNextOpen = false;
  }

  const wsClient = wsClientFactory({
    url: opts.wsUrl,
    maxReconnectAttempts: Number.MAX_SAFE_INTEGER,
    shouldReconnect: (event) => !isSessionRestoredElsewhereClose(event),
    onClose: (event) => {
      reconnectLastCloseInfo = event;
      reconnectFailureCount += 1;
      reconnectCycleStartedAt ??= Date.now();
      reconnectControlProbeResult = null;
      reconnectCloseObservedSinceLastState = true;
      if (isSessionRestoredElsewhereClose(event)) {
        clearStoredSession();
        activeStoredSession = null;
        restoreAttemptInFlight = false;
        clearReconnectCycle();
      }
    },
  });
  const presentationTraceEnabled = opts.presentationTraceEnabled ?? shouldShowWsDebugOverlay();
  const wsDebugOverlay: WsDebugOverlayHandle | null = presentationTraceEnabled
    ? createWsDebugOverlay(opts.host)
    : null;
  const gsapRegistry = gsapRegistryFactory();
  const renderer = rendererFactory({ canvas: opts.canvas });
  renderer.cameraRig.setMode('spectator');
  renderer.loop.addFrameTickCallback((dt) =>
    gsapRegistry.update(Math.min(Math.max(dt, 0), MAX_MAIN_GSAP_DELTA_SECONDS)),
  );
  renderer.loop.start();

  let audioManager: AudioManager | null = null;
  let audioUnlockStarted = false;
  let audioUnlockListenersRegistered = false;
  let audioDisposed = false;
  let audioUnavailable = false;
  let audioDesiredMusic: MusicTrackId = 'music-menu-theme';
  let prePauseMusic: MusicTrackId | null = null;
  const pendingSfxQueue: Array<{ id: SfxId; opts?: { spatialPosition?: Vec3 } }> = [];
  const audioSettings = createAudioSettingsController({
    getAudioManager: () => audioManager,
    onSnapshotApplied: (next, previous) => {
      if (next.musicMuted) {
        audioManager?.stopMusic({ fadeMs: 150 });
        return;
      }
      if (previous.musicMuted && !next.musicMuted && audioManager != null) {
        playDesiredMusic(500);
      }
    },
  });
  const boardAudioHooks = createAudioCueHooks(() => audioManager);

  function manifestSfxIds(manifest: Awaited<ReturnType<typeof loadManifest>>): SfxId[] {
    return manifest.entries
      .filter((entry) => entry.category === 'sfx' && entry.id.startsWith('sfx-'))
      .map((entry) => entry.id as SfxId);
  }

  function getAudioListenerTransform(): { position: Vec3; forward: Vec3 } | null {
    const camera = renderer.cameraRig?.camera;
    if (camera == null) return null;
    const forward = camera.getWorldDirection(new THREE.Vector3());
    return {
      position: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      },
      forward: {
        x: forward.x,
        y: forward.y,
        z: forward.z,
      },
    };
  }

  function playSfx(id: SfxId, opts?: { spatialPosition?: Vec3 }): void {
    if (isSfxMuted()) {
      pendingSfxQueue.length = 0;
      return;
    }
    if (audioManager != null) {
      audioManager.playSfx(id, opts);
      return;
    }
    if (!audioUnlockStarted || audioUnavailable || audioDisposed) return;
    if (pendingSfxQueue.length >= 16) {
      pendingSfxQueue.shift();
    }
    pendingSfxQueue.push({ id, opts });
  }

  function flushPendingSfxQueue(): void {
    if (audioManager == null || pendingSfxQueue.length === 0) return;
    const queued = pendingSfxQueue.splice(0);
    for (const cue of queued) {
      playSfx(cue.id, cue.opts);
    }
  }

  function playDesiredMusic(fadeMs = 500): void {
    if (isMusicMuted()) return;
    audioManager?.playMusic(audioDesiredMusic, { fadeMs, loop: true });
  }

  function removeAudioUnlockListeners(): void {
    if (!audioUnlockListenersRegistered) return;
    opts.host.removeEventListener('pointerdown', unlockAudioOnce, { capture: true });
    document.removeEventListener('keydown', unlockAudioOnce);
    audioUnlockListenersRegistered = false;
  }

  async function unlockAudioOnce(): Promise<void> {
    if (audioUnlockStarted || audioDisposed) return;
    audioUnlockStarted = true;
    removeAudioUnlockListeners();

    const AudioContextCtor = resolveAudioContextConstructor();
    if (AudioContextCtor == null) {
      audioUnavailable = true;
      pendingSfxQueue.length = 0;
      console.warn('[smashparty] Web Audio is unavailable; audio disabled');
      return;
    }

    const audioContext = new AudioContextCtor();
    try {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const loader = createAudioAssetLoader({ audioContext });
      const manifest = await loadManifest('/manifest.json');
      const manager = createAudioManager({
        audioContext,
        loader,
        resolveUrl: (id) => {
          const entry = manifest.byId.get(id);
          if (entry == null) throw new Error(`manifest missing audio asset: ${id}`);
          return runtimeAssetUrl('/' + entry.path);
        },
        addFrameTickCallback: renderer.loop.addFrameTickCallback,
        getListenerTransform: getAudioListenerTransform,
        preloadSfxIds: manifestSfxIds(manifest),
      });

      await manager.init();
      if (audioDisposed) {
        manager.dispose();
        return;
      }

      audioManager = manager;
      audioSettings.applyCurrent();
      flushPendingSfxQueue();
      playDesiredMusic(500);
    } catch (err) {
      audioUnavailable = true;
      pendingSfxQueue.length = 0;
      console.warn('[smashparty] audio unlock failed:', err);
      if (audioContext.state !== 'closed') {
        void audioContext.close();
      }
    }
  }

  function addAudioUnlockListeners(): void {
    if (audioUnlockListenersRegistered) return;
    opts.host.addEventListener('pointerdown', unlockAudioOnce, { capture: true, once: true });
    document.addEventListener('keydown', unlockAudioOnce, { once: true });
    audioUnlockListenersRegistered = true;
  }

  addAudioUnlockListeners();
  setButtonClickSfxHandler(() => {
    playSfx('sfx-ui-button-click');
  });
  setButtonHoverSfxHandler(() => {
    playSfx('sfx-ui-button-hover');
  });
  const warmupKeyboard = createKeyboardSource(window);
  const warmupGamepad = createGamepadSource();
  const warmupTutorialInput = {
    setActive(active: boolean): void {
      void active;
    },
    setAdvanceHandler(handler: (() => void) | null): void {
      void handler;
    },
  };

  let actionInputLocked = false;
  const actionInputLockReasons = new Set<string>();
  const actionControlLockSubscribers = new Set<(locked: boolean) => void>();
  let pendingLocalAction: PendingLocalAction | null = null;
  let suppressedPrompt: PromptSuppression | null = null;
  let lastObservedBoardEventTimestamp = -1;
  let eyeExpressionEventCursor = -1;
  let eyeExpressionEventCursorSeeded = false;
  let eyeExpressionSnapshotSeedPending = false;
  const ACTION_LOCK_CHEST_SPAWN = 'chest-spawn';
  const ACTION_LOCK_SPECIAL_REVEAL = 'special-reveal';
  const ACTION_LOCK_TRAP_CINEMATIC = 'trap-cinematic';
  const ACTION_LOCK_ORBITAL_CINEMATIC = 'orbital-cinematic';
  const ACTION_LOCK_THUNDER_FIST_CINEMATIC = 'thunder-fist-cinematic';
  const ACTION_LOCK_SHADOW_STALKER_SPAWN = 'shadow-stalker-spawn';
  const ACTION_LOCK_SHADOW_STALKER_MOTION = 'shadow-stalker-motion';
  const ACTION_LOCK_LIGHTNING_DASH = 'lightning-dash';
  const ACTION_LOCK_LOCAL_MUTATION = 'local-mutation';
  function isLockedActionMessage(msg: unknown): boolean {
    if (typeof msg !== 'object' || msg === null || !('type' in msg)) return false;
    const type = (msg as { type?: unknown }).type;
    return typeof type === 'string' && MUTATING_BOARD_ACTION_TYPES.has(type);
  }
  function isActionControlLocked(): boolean {
    return actionInputLocked;
  }
  function subscribeActionControlLock(handler: (locked: boolean) => void): () => void {
    actionControlLockSubscribers.add(handler);
    handler(actionInputLocked);
    return () => {
      actionControlLockSubscribers.delete(handler);
    };
  }
  function setActionInputLock(reason: string, locked: boolean): void {
    if (locked) {
      actionInputLockReasons.add(reason);
    } else {
      actionInputLockReasons.delete(reason);
    }
    const nextLocked = actionInputLockReasons.size > 0;
    if (actionInputLocked === nextLocked) return;
    actionInputLocked = nextLocked;
    if (latestGameState != null) {
      syncPathArrowsForState(latestGameState);
    }
    for (const handler of [...actionControlLockSubscribers]) {
      handler(actionInputLocked);
    }
  }

  function isOwnedPlayerId(playerId: string | null | undefined, state = latestGameState): boolean {
    if (playerId == null || state == null || localPlayerId == null) return false;
    if (ownedPlayerIds.size > 0) return ownedPlayerIds.has(playerId);
    if (playerId === localPlayerId) return true;
    return state.players.get(playerId)?.isLocal === true;
  }

  function getActiveOwnedPlayerId(state = latestGameState): string | null {
    const activeId = state?.turnOrder[state.currentPlayerIndex] ?? null;
    return isOwnedPlayerId(activeId, state) ? activeId : null;
  }

  function resolveBoardActionPlayerId(msg: unknown): string | null {
    if (typeof msg !== 'object' || msg === null || !('type' in msg)) return null;
    const record = msg as { readonly type?: unknown; readonly playerId?: unknown };
    if (typeof record.type !== 'string' || !MUTATING_BOARD_ACTION_TYPES.has(record.type)) {
      return null;
    }
    if (typeof record.playerId === 'string') return record.playerId;
    const state = latestGameState;
    if (state != null) {
      if (
        (record.type === 'buy_trophy' ||
          record.type === 'skip_trophy' ||
          record.type === 'claim_weapons_cache') &&
        isOwnedPlayerId(state.pendingBoardEncounter?.playerId, state)
      ) {
        return state.pendingBoardEncounter?.playerId ?? null;
      }
      if (
        record.type === 'claim_soul_anchor' &&
        isOwnedPlayerId(state.pendingSoulAnchorClaim?.playerId, state)
      ) {
        return state.pendingSoulAnchorClaim?.playerId ?? null;
      }
      const activeOwnedPlayerId = getActiveOwnedPlayerId(state);
      if (activeOwnedPlayerId != null) return activeOwnedPlayerId;
    }
    return localPlayerId;
  }

  function attachBoardActionPlayerId(msg: unknown): unknown {
    if (typeof msg !== 'object' || msg === null || !('type' in msg)) return msg;
    const record = msg as Record<string, unknown>;
    if (typeof record.type !== 'string' || !MUTATING_BOARD_ACTION_TYPES.has(record.type)) {
      return msg;
    }
    if (typeof record.playerId === 'string') return msg;
    const playerId = resolveBoardActionPlayerId(msg);
    return playerId == null ? msg : { ...record, playerId };
  }

  function sendWithActionLock(msg: unknown): void {
    const normalizedMsg = attachBoardActionPlayerId(msg);
    if (actionInputLocked && isLockedActionMessage(normalizedMsg)) {
      console.warn('[smashparty] action input ignored during cinematic');
      return;
    }
    const type =
      typeof normalizedMsg === 'object' && normalizedMsg !== null
        ? (normalizedMsg as { type?: unknown }).type
        : undefined;
    if (type === 'leave') noteLocalLeaveIntent();
    const actorId = resolveBoardActionPlayerId(normalizedMsg);
    if (type === 'roll_dice') {
      if (actorId != null) {
        const key = getPreRollPathArrowKey(latestGameState);
        if (key != null) pendingRollSubmissionKeysByPlayer.set(actorId, key);
      }
      clearPathArrowsAndFacing();
    } else if (type === 'choose_fork') {
      clearPathArrowsAndFacing();
    } else if (type === 'use_item') {
      if (actorId != null) {
        pendingRollSubmissionKeysByPlayer.delete(actorId);
      }
      clearPathArrowsAndFacing();
    } else if (type === 'set_character_customization') {
      const record = normalizedMsg as {
        readonly playerId?: unknown;
        readonly customization?: unknown;
      };
      if (typeof record.playerId === 'string' && record.customization != null) {
        const customization =
          record.customization as import('../shared/game/types').CharacterCustomization;
        rosterRigs.get(record.playerId)?.applyCustomization(customization);
      }
    }
    maybeBeginPendingLocalAction(normalizedMsg);
    wsClient.send(normalizedMsg);
  }

  function maybeBeginPendingLocalAction(msg: unknown): void {
    if (!shouldTrackLocalBoardAction(msg)) return;
    const action = buildPendingLocalAction(msg as ClientMessage);
    if (action == null) return;
    pendingLocalAction = action;
    setActionInputLock(ACTION_LOCK_LOCAL_MUTATION, true);
  }

  function shouldTrackLocalBoardAction(msg: unknown): boolean {
    if (!isLockedActionMessage(msg)) return false;
    if (inWarmupArena) return false;
    if (latestGameState == null || latestGameState.phase === 'lobby') return false;
    if (ACTIVE_MINIGAME_PHASES.has(latestGameState.phase)) return false;
    return resolveBoardActionPlayerId(msg) != null;
  }

  function buildPendingLocalAction(msg: ClientMessage): PendingLocalAction | null {
    if (!('type' in msg) || typeof msg.type !== 'string') return null;
    if (!MUTATING_BOARD_ACTION_TYPES.has(msg.type)) return null;
    const actorId = resolveBoardActionPlayerId(msg);
    if (actorId == null) return null;

    const state = latestGameState;
    const activeId = state?.turnOrder[state.currentPlayerIndex] ?? actorId;
    const base: PendingLocalActionBase = {
      kind: msg.type as PendingLocalActionKind,
      playerId: actorId,
      turnKey: state == null ? `unknown:${actorId}` : turnKeyForState(state, activeId),
      submittedPhase: state?.phase ?? null,
      submitEventCursor: currentEventCursor(),
      submittedAtMs: performance.now(),
    };

    switch (msg.type) {
      case 'roll_dice':
        return { ...base, kind: 'roll_dice', nextNodeId: msg.nextNodeId };
      case 'choose_fork':
        return { ...base, kind: 'choose_fork', nextNodeId: msg.nextNodeId };
      case 'use_item':
        return {
          ...base,
          kind: 'use_item',
          itemId: msg.itemId,
          targetId: msg.targetId,
          targetNodeId: msg.targetNodeId,
          direction: msg.direction,
        };
      case 'skip_item':
        return { ...base, kind: 'skip_item' };
      case 'buy_trophy':
        return {
          ...base,
          kind: 'buy_trophy',
          promptNodeId:
            state?.pendingBoardEncounter?.kind === 'treasure_chest'
              ? state.pendingBoardEncounter.nodeId
              : null,
        };
      case 'skip_trophy':
        return {
          ...base,
          kind: 'skip_trophy',
          promptNodeId:
            state?.pendingBoardEncounter?.kind === 'treasure_chest'
              ? state.pendingBoardEncounter.nodeId
              : null,
        };
      case 'claim_weapons_cache':
        return {
          ...base,
          kind: 'claim_weapons_cache',
          promptNodeId:
            state?.pendingBoardEncounter?.kind === 'weapons_cache'
              ? state.pendingBoardEncounter.nodeId
              : null,
          itemId: msg.itemId,
        };
      case 'claim_soul_anchor':
        return {
          ...base,
          kind: 'claim_soul_anchor',
          promptNodeId: state?.pendingSoulAnchorClaim?.nodeId ?? null,
          choice: msg.choice,
        };
      default:
        return null;
    }
  }

  function currentEventCursor(): number {
    return Math.max(lastObservedBoardEventTimestamp, maxEventTimestamp(latestGameState));
  }

  function maxEventTimestamp(state: GameState | null): number {
    if (state == null || state.eventLog.length === 0) return -1;
    return state.eventLog.reduce((max, event) => Math.max(max, event.timestamp), -1);
  }

  function syncLocalEventCursorFromState(state: GameState): void {
    const stateEventCursor = maxEventTimestamp(state);
    lastObservedBoardEventTimestamp =
      stateEventCursor < lastObservedBoardEventTimestamp
        ? stateEventCursor
        : Math.max(lastObservedBoardEventTimestamp, stateEventCursor);
  }

  function resetEyeExpressionEventCursor(seedNextState: boolean): void {
    eyeExpressionEventCursor = -1;
    eyeExpressionEventCursorSeeded = false;
    eyeExpressionSnapshotSeedPending = seedNextState;
  }

  function observeEyeExpressionLiveEvent(event: GameEvent): void {
    eyeExpressionSnapshotSeedPending = false;
    eyeExpressionEventCursorSeeded = true;
    if (event.timestamp <= eyeExpressionEventCursor) return;
    eyeExpressionEventCursor = Math.max(eyeExpressionEventCursor, event.timestamp);
    characterEyeExpressions.handleEvent(event);
  }

  function replayEyeExpressionEventsFromState(state: GameState): void {
    if (boardHandle == null) {
      if (eyeExpressionSnapshotSeedPending) {
        eyeExpressionEventCursor = maxEventTimestamp(state);
        eyeExpressionEventCursorSeeded = true;
        eyeExpressionSnapshotSeedPending = false;
      }
      return;
    }

    if (eyeExpressionSnapshotSeedPending) {
      eyeExpressionEventCursor = maxEventTimestamp(state);
      eyeExpressionEventCursorSeeded = true;
      eyeExpressionSnapshotSeedPending = false;
      return;
    }

    if (!eyeExpressionEventCursorSeeded) {
      eyeExpressionEventCursorSeeded = true;
    }

    const presentPlayerIds = new Set(state.players.keys());
    const events = state.eventLog
      .filter((event) => event.timestamp > eyeExpressionEventCursor)
      .sort((a, b) => a.timestamp - b.timestamp);
    for (const event of events) {
      eyeExpressionEventCursor = Math.max(eyeExpressionEventCursor, event.timestamp);
      characterEyeExpressions.handleEvent(event);
    }
    characterEyeExpressions.cleanupMissingPlayers(presentPlayerIds);
  }

  function clearPendingLocalAction(): void {
    pendingLocalAction = null;
    setActionInputLock(ACTION_LOCK_LOCAL_MUTATION, false);
  }

  function clearLocalActionLifecycle(): void {
    pendingLocalAction = null;
    suppressedPrompt = null;
    lastObservedBoardEventTimestamp = -1;
    setActionInputLock(ACTION_LOCK_LOCAL_MUTATION, false);
  }

  function handlePendingLocalActionRejection(text: string): void {
    const pending = pendingLocalAction;
    if (pending == null) return;
    if (!isRejectionForPendingAction(pending.kind, text)) return;
    if (pending.kind === 'choose_fork') {
      submittedForkChoiceKey = null;
    }
    if (pending.kind === 'roll_dice') {
      pendingRollSubmissionKeysByPlayer.delete(pending.playerId);
    }
    suppressedPrompt = null;
    clearPendingLocalAction();
    if (latestGameState != null) {
      syncPathArrowsForState(latestGameState);
    }
  }

  function isRejectionForPendingAction(kind: PendingLocalActionKind, text: string): boolean {
    return ACTION_REJECTION_TEXTS[kind].has(text) || text.startsWith('wrong_phase:');
  }

  // ── Window resize handling ───────────────────────────────────────────────────
  function effectiveViewportSize(): { width: number; height: number } {
    const viewport = window.visualViewport;
    const width = Math.max(1, Math.round(viewport?.width ?? window.innerWidth));
    const height = Math.max(1, Math.round(viewport?.height ?? window.innerHeight));
    return { width, height };
  }

  function syncViewportCssVars(width: number, height: number): void {
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty('--app-viewport-width', `${width}px`);
    rootStyle.setProperty('--app-viewport-height', `${height}px`);
  }

  function applyViewport(): void {
    const { width: w, height: h } = effectiveViewportSize();
    syncViewportCssVars(w, h);
    renderer.scene.onResize(w, h);
    renderer.cameraRig?.onResize(w, h);
  }
  applyViewport();
  window.addEventListener('resize', applyViewport);
  window.visualViewport?.addEventListener('resize', applyViewport);
  window.visualViewport?.addEventListener('scroll', applyViewport);

  let currentPhase: GameState['phase'] | 'game_start' | 'game_started' = 'lobby';
  // Track room type for the global ESC → pause-menu binding. Quick-play rooms must
  // never reach pause-menu (the factory throws), so we suppress ESC there.
  let currentRoomType: 'create_game' | 'quick_play' = 'create_game';
  let currentHostId: string | null = null;
  const ownedPlayerIds = new Set<string>();
  const lobbyCameraQuery = readLobbyCameraQuery();
  const lobbyCameraTuneEnabled = lobbyCameraQuery.tune;
  let lobbyCameraTunerOverlay: LobbyCameraTunerOverlayHandle | null = null;
  let screenRouterForOrbit: ScreenRouter | null = null;
  let cameraUserDirty = false;
  let lobbyCameraApplied = false;

  // ── OrbitControls (drag to spin, scroll to zoom) ─────────────────────────────
  let orbitControls: OrbitControls | null = null;
  let orbitTargetFollower: OrbitTargetFollower | null = null;
  let orbitControlsDomGateCleanup: (() => void) | null = null;
  let activeOrbitControlProfile: 'lobby' | 'tune' | 'gameplay' | null = null;
  const orbitCam = renderer.cameraRig?.camera;
  if (orbitCam != null) {
    orbitControls = new OrbitControls(orbitCam, opts.canvas);
    applyOrbitControlProfile('lobby');
    orbitControls.enabled = false;
    orbitControls.target.set(0, 2, 0);
    orbitControls.update();
    orbitControlsDomGateCleanup = installOrbitControlsDomGate();
    orbitControls.addEventListener('start', handleOrbitControlStart);
    orbitTargetFollower = createOrbitTargetFollower({
      controls: orbitControls,
      domElement: opts.canvas,
      isUserInteractionTarget: (target) => !shouldSuppressOrbitControlsForTarget(target),
    });
    renderer.loop.addFrameTickCallback((dt) => {
      if (orbitControls == null) return;
      const profile = desiredOrbitControlProfile();
      if (profile != null) {
        applyOrbitControlProfile(profile);
        refreshOrbitControlDynamicBounds(profile);
      }
      orbitControls.enabled = shouldEnableOrbitControls();
      if (orbitControls.enabled) {
        orbitTargetFollower?.update(dt);
        clampItemTargetingCameraDistance();
        orbitControls.update();
      }
      lobbyCameraTunerOverlay?.update();
    });
  }

  function desiredOrbitControlProfile(): 'lobby' | 'tune' | 'gameplay' | null {
    if (currentPhase !== 'lobby') {
      return 'gameplay';
    }
    if (lobbyCameraTuneEnabled) return isLobbyScreen(screenRouterForOrbit?.current) ? 'tune' : null;
    return isLobbyScreen(screenRouterForOrbit?.current) ? 'lobby' : null;
  }

  function shouldEnableOrbitControls(): boolean {
    return desiredOrbitControlProfile() != null && !actionInputLocked;
  }

  function handleOrbitControlStart(): void {
    cameraUserDirty = true;
    orbitTargetFollower?.notifyUserInteractionStart();
  }

  function isLobbyScreen(screenId: ScreenId | null | undefined): boolean {
    return (
      screenId === 'lobby-host' || screenId === 'lobby-joiner' || screenId === 'lobby-quick-play'
    );
  }

  function isWarmupOverlayScreen(screenId: ScreenId | null | undefined): boolean {
    return (
      screenId === 'warmup-arena-overlay' ||
      screenId === 'how-to-play' ||
      screenId === 'inventory' ||
      screenId === 'item-targeting' ||
      screenId === 'pause-menu' ||
      screenId === 'settings'
    );
  }

  function applyOrbitControlProfile(profile: 'lobby' | 'tune' | 'gameplay'): void {
    if (orbitControls == null || activeOrbitControlProfile === profile) return;
    activeOrbitControlProfile = profile;
    orbitControls.enableDamping = true;
    orbitControls.dampingFactor = 0.08;
    orbitControls.enablePan = true;
    orbitControls.enableZoom = true;
    orbitControls.autoRotate = false;

    if (profile === 'tune') {
      orbitControls.minDistance = 0;
      orbitControls.maxDistance = Infinity;
      orbitControls.minPolarAngle = 0;
      orbitControls.maxPolarAngle = Math.PI;
      orbitControls.panSpeed = 1;
      orbitControls.zoomSpeed = 1;
      return;
    }

    if (profile === 'lobby') {
      orbitControls.minDistance = 2;
      orbitControls.maxDistance = 80;
      orbitControls.minPolarAngle = Math.PI / 6;
      orbitControls.maxPolarAngle = Math.PI * 0.42;
      orbitControls.panSpeed = 0.6;
      orbitControls.zoomSpeed = 0.7;
      return;
    }

    orbitControls.dampingFactor = 0.05;
    orbitControls.minDistance = GAMEPLAY_ORBIT_MIN_DISTANCE;
    orbitControls.maxDistance = computeGameplayOrbitMaxDistance();
    orbitControls.minPolarAngle = Math.PI / 12;
    orbitControls.maxPolarAngle = Math.PI * 0.48;
    orbitControls.panSpeed = 1;
    orbitControls.zoomSpeed = 1;
  }

  function refreshOrbitControlDynamicBounds(profile: 'lobby' | 'tune' | 'gameplay'): void {
    if (orbitControls == null || profile !== 'gameplay') return;
    orbitControls.maxDistance = computeGameplayOrbitMaxDistance();
  }

  function computeGameplayOrbitMaxDistance(): number {
    if (orbitControls == null) return GAMEPLAY_ORBIT_FALLBACK_MAX_DISTANCE;
    const cloudMesh = boardHandle?.atmosphere.cloudLayer.mesh;
    if (cloudMesh == null || cloudMesh.name !== 'snow-globe-cloud-layer') {
      return GAMEPLAY_ORBIT_FALLBACK_MAX_DISTANCE;
    }

    const boundaryRadius = getCloudLayerBoundaryRadius(cloudMesh);
    const targetDistanceFromCloudCenter = Math.hypot(
      orbitControls.target.x - cloudMesh.position.x,
      orbitControls.target.z - cloudMesh.position.z,
    );
    const boundedMaxDistance =
      boundaryRadius - targetDistanceFromCloudCenter - GAMEPLAY_ORBIT_CLOUD_BOUNDARY_PADDING;

    if (!Number.isFinite(boundedMaxDistance)) return GAMEPLAY_ORBIT_FALLBACK_MAX_DISTANCE;
    return THREE.MathUtils.clamp(
      boundedMaxDistance,
      GAMEPLAY_ORBIT_MIN_DISTANCE,
      GAMEPLAY_ORBIT_FALLBACK_MAX_DISTANCE,
    );
  }

  function clampItemTargetingCameraDistance(): void {
    if (!isItemPlayerTargetingActive()) return;
    if (orbitControls == null || orbitCam == null) return;
    const maxDistance = Math.min(ITEM_TARGETING_ORBIT_MAX_DISTANCE, orbitControls.maxDistance);
    const offset = orbitCam.position.clone().sub(orbitControls.target);
    const distance = offset.length();
    if (!Number.isFinite(distance) || distance <= maxDistance || distance <= 0.001) return;
    orbitCam.position.copy(orbitControls.target).add(offset.multiplyScalar(maxDistance / distance));
  }

  function installOrbitControlsDomGate(): () => void {
    const activeForwardedPointers = new Set<number>();

    const suppressOrbitControlsForNextTick = (): void => {
      if (orbitControls == null) return;
      orbitControls.enabled = false;
      window.setTimeout(() => {
        if (orbitControls != null) {
          orbitControls.enabled = shouldEnableOrbitControls();
        }
      }, 0);
    };

    const forwardToCanvas = (event: Event): void => {
      const forwarded = cloneOrbitControlsEvent(event);
      opts.canvas.dispatchEvent(forwarded);
      if (forwarded.defaultPrevented && event.cancelable) {
        event.preventDefault();
      }
    };

    const handleHostOrbitEvent = (event: Event): void => {
      if (orbitControls == null) return;

      if (isPointerEvent(event) && event.type !== 'pointerdown') {
        if (!activeForwardedPointers.has(event.pointerId)) return;
        forwardToCanvas(event);
        if (event.type === 'pointerup' || event.type === 'pointercancel') {
          activeForwardedPointers.delete(event.pointerId);
        }
        return;
      }

      if (shouldSuppressOrbitControlsForTarget(event.target)) {
        suppressOrbitControlsForNextTick();
        return;
      }

      if (event.target === opts.canvas) return;

      if (isPointerEvent(event) && event.type === 'pointerdown') {
        activeForwardedPointers.add(event.pointerId);
      }
      forwardToCanvas(event);
    };

    const orbitEventTypes = [
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointercancel',
      'wheel',
      'contextmenu',
    ] as const;
    for (const type of orbitEventTypes) {
      opts.host.addEventListener(type, handleHostOrbitEvent, { capture: true });
    }
    return () => {
      for (const type of orbitEventTypes) {
        opts.host.removeEventListener(type, handleHostOrbitEvent, { capture: true });
      }
      activeForwardedPointers.clear();
    };
  }

  function shouldSuppressOrbitControlsForTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    if (target === opts.host || target === opts.canvas) return false;
    return (
      target.closest(
        [
          '[data-lobby-cam-tuner]',
          '.lobby-responsive__inline-select',
          'button',
          'a',
          'input',
          'select',
          'textarea',
          '[role="button"]',
          '[role="slider"]',
          '[role="switch"]',
          '[contenteditable="true"]',
        ].join(','),
      ) != null
    );
  }

  function cloneOrbitControlsEvent(event: Event): Event {
    const eventOptions: EventInit = {
      bubbles: true,
      cancelable: event.cancelable,
      composed: event.composed,
    };

    if (isPointerEvent(event)) {
      return new PointerEvent(event.type, {
        ...mouseEventInit(event),
        pointerId: event.pointerId,
        width: event.width,
        height: event.height,
        pressure: event.pressure,
        tangentialPressure: event.tangentialPressure,
        tiltX: event.tiltX,
        tiltY: event.tiltY,
        twist: event.twist,
        pointerType: event.pointerType,
        isPrimary: event.isPrimary,
      });
    }

    if (event instanceof WheelEvent) {
      return new WheelEvent(event.type, {
        ...mouseEventInit(event),
        deltaX: event.deltaX,
        deltaY: event.deltaY,
        deltaZ: event.deltaZ,
        deltaMode: event.deltaMode,
      });
    }

    if (event instanceof MouseEvent) {
      return new MouseEvent(event.type, mouseEventInit(event));
    }

    return new Event(event.type, eventOptions);
  }

  function isPointerEvent(event: Event): event is PointerEvent {
    return typeof PointerEvent !== 'undefined' && event instanceof PointerEvent;
  }

  function mouseEventInit(event: MouseEvent): MouseEventInit {
    return {
      bubbles: true,
      cancelable: event.cancelable,
      composed: event.composed,
      view: window,
      detail: event.detail,
      screenX: event.screenX,
      screenY: event.screenY,
      clientX: event.clientX,
      clientY: event.clientY,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      button: event.button,
      buttons: event.buttons,
      relatedTarget: event.relatedTarget,
    };
  }

  // ── Shared asset loaders (character preload + decoration loading) ───────────
  // Hoisted outside the character-preload try/catch so gltfLoader is also
  // accessible to loadMap() for Skyforge decoration GLBs.
  // If construction fails (e.g. mocked WebGLRenderer in tests) we fall back to
  // null everywhere and both character adapter and decorations silently skip.
  let gltfLoader: ReturnType<typeof createGltfAssetLoader> | null = null;
  let textureLoader: ReturnType<typeof createTextureAssetLoader> | null = null;
  try {
    const webglRenderer = renderer.scene.renderer;
    const ktx2Loader = new KTX2Loader()
      .setTranscoderPath('/jsm/libs/basis/')
      .detectSupport(webglRenderer);
    const dracoLoader = new DRACOLoader().setDecoderPath('/jsm/libs/draco/gltf/');
    gltfLoader = createGltfAssetLoader({
      dracoDecoderPath: '/jsm/libs/draco/gltf/',
      ktx2TranscoderPath: '/jsm/libs/basis/',
      renderer: webglRenderer,
      ktx2Loader,
      dracoLoader,
      meshoptDecoder: MeshoptDecoder,
    });
    textureLoader = createTextureAssetLoader({ ktx2Loader });
  } catch (err) {
    console.warn('[smashparty] failed to construct GLTF/texture loaders:', err);
  }

  const emoteIconUrls: Partial<Record<EmoteId, string>> = {};
  const emoteBubbleTextures = new Map<EmoteId, THREE.Texture>();

  function positiveCanvasDimension(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
      ? Math.round(value)
      : null;
  }

  function getCanvasSourceSize(
    source: CanvasImageSource,
  ): { width: number; height: number } | null {
    const sizedSource = source as {
      width?: unknown;
      height?: unknown;
      naturalWidth?: unknown;
      naturalHeight?: unknown;
      videoWidth?: unknown;
      videoHeight?: unknown;
    };
    const width =
      positiveCanvasDimension(sizedSource.naturalWidth) ??
      positiveCanvasDimension(sizedSource.videoWidth) ??
      positiveCanvasDimension(sizedSource.width);
    const height =
      positiveCanvasDimension(sizedSource.naturalHeight) ??
      positiveCanvasDimension(sizedSource.videoHeight) ??
      positiveCanvasDimension(sizedSource.height);
    return width != null && height != null ? { width, height } : null;
  }

  function createCircularMaskedTexture(sourceTexture: THREE.Texture): THREE.Texture | null {
    const source = sourceTexture.image as CanvasImageSource | undefined;
    if (source == null || typeof document === 'undefined') return null;
    const sourceSize = getCanvasSourceSize(source);
    if (sourceSize == null) return null;
    const canvas = document.createElement('canvas');
    canvas.width = sourceSize.width;
    canvas.height = sourceSize.height;
    const ctx = canvas.getContext('2d');
    if (ctx == null) return null;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      Math.min(canvas.width, canvas.height) / 2,
      0,
      Math.PI * 2,
    );
    ctx.clip();
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    ctx.restore();
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = sourceTexture.colorSpace;
    texture.needsUpdate = true;
    return texture;
  }

  if (textureLoader != null) {
    void loadManifest('/manifest.json')
      .then((manifest) =>
        Promise.all(
          EMOTE_IDS.map(async (emoteId) => {
            const assetId = EMOTE_ICON_ASSET_IDS[emoteId];
            const entry = manifest.byId.get(assetId);
            if (entry == null) {
              console.warn(`[smashparty] manifest missing emote icon: ${assetId}`);
              return;
            }
            const url = runtimeAssetUrl('/' + entry.path);
            emoteIconUrls[emoteId] = url;
            try {
              const loadedTexture = await textureLoader!.load(url);
              const maskedTexture = createCircularMaskedTexture(loadedTexture);
              if (maskedTexture != null) {
                emoteBubbleTextures.set(emoteId, maskedTexture);
              } else {
                emoteBubbleTextures.set(emoteId, loadedTexture);
                return;
              }
              loadedTexture.dispose();
            } catch (err) {
              console.warn(`[smashparty] emote icon load failed: ${assetId}`, err);
            }
          }),
        ),
      )
      .catch((err) => {
        console.warn('[smashparty] emote icon preload failed:', err);
      });
  }

  // ── Character asset preload ─────────────────────────────────────────────────
  // Build shared decoder loaders + preload all character assets before
  // bootScreensFactory so Tasks 3+ can consume the adapter immediately.
  // Entire preload is wrapped in try/catch: any failure (including test
  // environments where renderer.scene.renderer may be a mock) → null adapter.
  let characterAdapter: CharacterPreviewAdapter | null = null;
  let characterEyeTextureStore: EyeTextureStore | null = null;
  let areCharacterCosmeticsLoaded: (() => boolean) | null = null;
  let ensureCharacterCosmeticsLoaded: (() => Promise<void>) | null = null;
  let loadBlobAnimationBundle:
    | ((bundle: BlobAnimationBundle) => Promise<THREE.AnimationClip[]>)
    | null = null;
  let addBlobAnimationBundleToAdapter: ((bundle: BlobAnimationBundle) => Promise<void>) | null =
    null;
  let ensureCharacterAnimationBundles:
    | ((
        bundles: readonly BlobAnimationBundle[],
        options: CharacterAnimationBundlePolicyOptions,
      ) => Promise<void>)
    | null = null;
  let reactionAnimationBundlePromise: Promise<void> | null = null;
  const deferredWarmupCustomization = readPreferredCustomization();
  let deferredWarmupCustomizationApplied = deferredWarmupCustomization == null;
  let customizationBackgroundStarted = false;
  let customizationBackgroundPromise: Promise<void> | null = null;
  if (gltfLoader != null && textureLoader != null) {
    try {
      const manifest = await loadManifest('/manifest.json');
      const preloadHandle = await createCharacterAdapterPreloadHandle({
        manifest,
        gltfLoader,
        textureLoader,
        assetUrl: runtimeAssetUrl,
        initialCustomization: null,
        preloadAllCosmetics: false,
        warn: (...parts: unknown[]) => console.warn(...parts),
      });
      characterAdapter = preloadHandle.adapter;
      characterEyeTextureStore = preloadHandle.eyeTextureStore;
      areCharacterCosmeticsLoaded = preloadHandle.areAllCosmeticsLoaded;
      ensureCharacterCosmeticsLoaded = preloadHandle.ensureAllCosmeticsLoaded;
      loadBlobAnimationBundle = preloadHandle.loadAnimationBundle;
      addBlobAnimationBundleToAdapter = preloadHandle.addAnimationBundleToAdapter;
      ensureCharacterAnimationBundles = preloadHandle.ensureAnimationBundles;
      await Promise.all(
        EYE_EXPRESSIONS.map((expression) => characterEyeTextureStore!.resolve('dots', expression)),
      );

      // eslint-disable-next-line no-console
      console.log(
        `[smashparty] character assets preloaded: heads=${preloadHandle.assetCounts.heads}/8, eyes=${preloadHandle.assetCounts.eyes}/8, capes=${preloadHandle.assetCounts.capes}/8, locomotionClips=${preloadHandle.assetCounts.locomotionClips}`,
      );
    } catch (e) {
      console.warn('[smashparty] character asset preload failed — adapter will be null:', e);
      characterEyeTextureStore?.dispose();
      characterEyeTextureStore = null;
      characterAdapter = null;
      areCharacterCosmeticsLoaded = null;
      ensureCharacterCosmeticsLoaded = null;
    }
  }

  const characterSelectThumbnailUrls = [
    ...ALL_HEAD_IDS.map((headId) => headMenuThumbnailUrl(headId)),
    ...ALL_EYE_IDS.map((eyeId) => eyeMenuThumbnailUrl(eyeId)),
  ];

  function applyDeferredWarmupCustomization(): void {
    if (deferredWarmupCustomizationApplied || deferredWarmupCustomization == null) return;
    if (!inWarmupArena || localPlayerId == null) return;
    deferredWarmupCustomizationApplied = true;
    wsClient.send(
      buildWarmupSubscribeMessage({
        customization: deferredWarmupCustomization,
        isReady: hasSeenWarmupTutorial(),
      }),
    );
  }

  function startCustomizationBackgroundPreload(reason: string): Promise<void> {
    void reason;
    if (customizationBackgroundStarted && customizationBackgroundPromise != null) {
      return customizationBackgroundPromise;
    }
    customizationBackgroundStarted = true;
    customizationBackgroundPromise = Promise.all([
      ensureCharacterCosmeticsLoaded?.() ?? Promise.resolve(),
      runLimitedConcurrency(characterSelectThumbnailUrls, BACKGROUND_PRELOAD_CONCURRENCY, (url) =>
        preloadImageUrl(url),
      ),
    ])
      .then(() => {
        applyDeferredWarmupCustomization();
        startPostCustomizationBackgroundLoad('customization_preload_complete');
      })
      .catch((error: unknown) => {
        console.warn('[smashparty] character customization preload failed:', error);
      })
      .then(() => undefined);
    return customizationBackgroundPromise;
  }

  function ensureCharacterReactionAnimationsLoaded(): Promise<void> {
    if (reactionAnimationBundlePromise != null) return reactionAnimationBundlePromise;
    if (loadBlobAnimationBundle == null || addBlobAnimationBundleToAdapter == null) {
      return Promise.resolve();
    }
    reactionAnimationBundlePromise = Promise.all(
      CHARACTER_REACTION_ANIMATION_BUNDLES.map((bundle) => addBlobAnimationBundleToAdapter(bundle)),
    ).then(() => undefined);
    return reactionAnimationBundlePromise;
  }

  // ── Board shell GLB preload ─────────────────────────────────────────────────
  // Critical for first render: if chunk GLBs are absent, islands intentionally
  // fall back to procedural geometry.
  let bridgeGltfScene: THREE.Group | null = null;
  let chunkLargeGltfScene: THREE.Group | null = null;
  let chunkSmallGltfScene: THREE.Group | null = null;
  let droppedKeyGltfScene: THREE.Group | null = null;
  let boardShellAssetsPromise: Promise<void> | null = null;
  function ensureBoardShellAssetsLoaded(): Promise<void> {
    if (gltfLoader == null) return Promise.resolve();
    if (boardShellAssetsPromise != null) return boardShellAssetsPromise;
    boardShellAssetsPromise = Promise.all([
      gltfLoader.load('/models/bridge.glb').catch((e) => {
        console.warn('[smashparty] bridge.glb failed:', e);
        return null;
      }),
      gltfLoader.load('/models/deco-floating-chunk-large.glb').catch((e) => {
        console.warn('[smashparty] chunk-large.glb failed:', e);
        return null;
      }),
      gltfLoader.load('/models/deco-floating-chunk-small.glb').catch((e) => {
        console.warn('[smashparty] chunk-small.glb failed:', e);
        return null;
      }),
      gltfLoader.load('/models/dropped-key.glb').catch((e) => {
        console.warn('[smashparty] dropped-key.glb failed:', e);
        return null;
      }),
    ])
      .then(([bridge, chunkLarge, chunkSmall, droppedKey]) => {
        bridgeGltfScene = bridge;
        chunkLargeGltfScene = chunkLarge;
        chunkSmallGltfScene = chunkSmall;
        droppedKeyGltfScene = droppedKey;
      })
      .catch((e) => {
        console.warn('[smashparty] board shell GLB preload failed:', e);
      });
    return boardShellAssetsPromise;
  }

  // ── Tile-icon texture preload ─────────────────────────────────────────────────
  // Loaded from /textures/tile-icons/<type>.webp via Vite publicDir.
  // Graceful: failed loads skip the decal for that tile type (pad still renders).
  const TILE_ICON_TYPES = [
    'hazard',
    'soul-anchor',
    'gold',
    'start',
    'graveyard',
    'event',
    'heal',
    'item',
  ] as const;
  type TileIconType = (typeof TILE_ICON_TYPES)[number];
  const tileIconTextures: Partial<Record<TileIconType, THREE.Texture>> = {};
  const tileIconTexturesReady =
    textureLoader == null
      ? Promise.resolve()
      : Promise.all(
          TILE_ICON_TYPES.map(async (type) => {
            try {
              tileIconTextures[type] = await textureLoader!.load(
                `/textures/tile-icons/${type}.webp`,
              );
            } catch (e) {
              console.warn(`[smashparty] tile-icon ${type} failed to load:`, e);
            }
          }),
        ).then(() => undefined);

  const itemIconUrls = allItems().map((item) => itemIconUrl(item.id));
  await Promise.all([
    tileIconTexturesReady,
    runLimitedConcurrency(itemIconUrls, BACKGROUND_PRELOAD_CONCURRENCY, (url) =>
      preloadImageUrl(url),
    ),
  ]);

  // ── Screens ─────────────────────────────────────────────────────────────────
  const screenRouter = bootScreensFactory({
    host: opts.host,
    gsapRegistry,
    send: sendWithActionLock,
    subscribe: wsClient.subscribe,
    characterAdapter,
    areCharacterCosmeticsLoaded: areCharacterCosmeticsLoaded ?? undefined,
    ensureCharacterCosmeticsLoaded:
      ensureCharacterCosmeticsLoaded == null
        ? undefined
        : () => startCustomizationBackgroundPreload('screen_request'),
    playSfx,
    previewItemInHand,
    clearItemInHandPreview: clearHeldItemPreview,
    isActionControlLocked,
    subscribeActionControlLock,
    startItemPlayerTargeting,
    startItemDirectionalTargeting,
    startItemRangePreview,
    getPlayerNotificationAnchor,
    audioSettings,
    warmupTutorialInput,
    onCustomizeCamera: focusLobbyCustomizationCamera,
  });
  screenRouterForOrbit = screenRouter;

  const toastOverlay = createToastStackOverlay({
    host: opts.host,
    gsapRegistry,
    maxVisible: 3,
    dismissMs: 3000,
  });

  const originalGlobalUiHostStyle = {
    position: opts.host.style.position,
    zIndex: opts.host.style.zIndex,
  };
  const globalUiHostComputedStyle = window.getComputedStyle(opts.host);
  const globalUiHostZIndex = Number.parseInt(globalUiHostComputedStyle.zIndex, 10);
  if (
    globalUiHostComputedStyle.position === '' ||
    globalUiHostComputedStyle.position === 'static'
  ) {
    opts.host.style.position = 'relative';
  }
  if (!Number.isFinite(globalUiHostZIndex) || globalUiHostZIndex < 1000) {
    opts.host.style.zIndex = '1000';
  }

  function restoreGlobalUiHostLayer(): void {
    opts.host.style.position = originalGlobalUiHostStyle.position;
    opts.host.style.zIndex = originalGlobalUiHostStyle.zIndex;
  }

  function globalSystemButtonCss(rightOffset: string): string {
    return [
      'position: fixed',
      'top: max(14px, env(safe-area-inset-top))',
      `right: ${rightOffset}`,
      'z-index: 900',
      'width: 44px',
      'height: 44px',
      'display: grid',
      'place-items: center',
      'border-radius: 50%',
      'border: 1px solid rgba(255,255,255,0.22)',
      'background: rgba(20,24,34,0.82)',
      'box-shadow: 0 6px 18px rgba(0,0,0,0.30)',
      'color: #FFFFFF',
      'font-size: 20px',
      'line-height: 1',
      'cursor: pointer',
      'pointer-events: auto',
      'touch-action: manipulation',
    ].join('; ');
  }

  const settingsButton = document.createElement('button');
  settingsButton.type = 'button';
  settingsButton.setAttribute('aria-label', 'Settings');
  settingsButton.dataset.globalSettings = 'true';
  settingsButton.setAttribute('data-tutorial-target', 'settings');
  settingsButton.textContent = '⚙';
  settingsButton.style.cssText = globalSystemButtonCss('max(14px, env(safe-area-inset-right))');

  const discordButton = document.createElement('button');
  discordButton.type = 'button';
  discordButton.setAttribute('aria-label', 'Discord');
  discordButton.title = 'Discord';
  discordButton.dataset.globalDiscord = 'true';
  discordButton.style.cssText = globalSystemButtonCss(
    'calc(max(14px, env(safe-area-inset-right)) + 52px)',
  );
  discordButton.style.display = 'none';
  discordButton.innerHTML = [
    '<svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" focusable="false">',
    '<path fill="currentColor" d="M19.2 5.1A16.1 16.1 0 0 0 15.3 3.9l-.5 1a14.7 14.7 0 0 0-5.6 0l-.5-1A16.1 16.1 0 0 0 4.8 5.1C2.4 8.7 1.8 12.2 2.1 15.7a16 16 0 0 0 4.8 2.4l1-1.6c-.6-.2-1.1-.5-1.6-.8l.4-.3a11.5 11.5 0 0 0 10.6 0l.4.3c-.5.3-1 .6-1.6.8l1 1.6a16 16 0 0 0 4.8-2.4c.4-4-1-7.4-2.7-10.6ZM8.7 13.5c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8Zm6.6 0c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8Z"/>',
    '</svg>',
  ].join('');

  const pauseButton = document.createElement('button');
  pauseButton.type = 'button';
  pauseButton.setAttribute('aria-label', 'Pause');
  pauseButton.dataset.globalPause = 'true';
  pauseButton.setAttribute('data-tutorial-target', 'pause');
  pauseButton.textContent = 'Ⅱ';
  pauseButton.style.cssText = globalSystemButtonCss(
    'calc(max(14px, env(safe-area-inset-right)) + 52px)',
  );
  pauseButton.style.display = 'none';

  function closeSystemOverlays(): void {
    screenRouter.closeOverlay?.('settings');
    screenRouter.closeOverlay?.('how-to-play');
    screenRouter.closeOverlay?.('confirm-dialog');
    screenRouter.closeOverlay?.('pause-menu');
  }

  function refreshDiscordButton(): void {
    discordButton.style.display =
      DISCORD_CHANNEL_URL != null && screenRouter.current === 'warmup-arena-overlay'
        ? 'grid'
        : 'none';
  }

  function refreshGlobalSystemButtons(): void {
    pauseButton.style.display = canServerPause() ? 'grid' : 'none';
    refreshDiscordButton();
  }

  function handleSettingsButtonClick(): void {
    openSystemMenu();
  }

  function handlePauseButtonClick(): void {
    requestPauseGame();
  }

  function handleDiscordButtonClick(): void {
    if (DISCORD_CHANNEL_URL == null) return;
    const opened = window.open(DISCORD_CHANNEL_URL, '_blank', 'noopener,noreferrer');
    if (opened == null) {
      window.location.assign(DISCORD_CHANNEL_URL);
    }
  }

  function preserveFunctionProperties<T extends (...args: never[]) => unknown>(
    target: T,
    source: T,
  ): T {
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    return target;
  }

  const originalScreenNavigateFn = screenRouter.navigate;
  const originalScreenNavigate = originalScreenNavigateFn.bind(screenRouter);
  screenRouter.navigate = preserveFunctionProperties(
    ((id, payload) => {
      originalScreenNavigate(id, payload);
      refreshDiscordButton();
    }) as ScreenRouter['navigate'],
    originalScreenNavigateFn,
  );
  const originalScreenClearFn = screenRouter.clear;
  const originalScreenClear = originalScreenClearFn?.bind(screenRouter);
  if (originalScreenClear != null) {
    screenRouter.clear = preserveFunctionProperties(
      (() => {
        originalScreenClear();
        refreshDiscordButton();
      }) as NonNullable<ScreenRouter['clear']>,
      originalScreenClearFn!,
    );
  }

  settingsButton.addEventListener('click', handleSettingsButtonClick);
  pauseButton.addEventListener('click', handlePauseButtonClick);
  discordButton.addEventListener('click', handleDiscordButtonClick);
  opts.host.appendChild(pauseButton);
  opts.host.appendChild(discordButton);
  opts.host.appendChild(settingsButton);

  type RestoredSessionMessage = Extract<ServerMessage, { type: 'session_restored' }>;

  function readNavigatorOnline(): boolean | null {
    if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') return null;
    return navigator.onLine;
  }

  function currentReconnectVariant(): ReconnectVariant | null {
    return classifyReconnect({
      lastCloseInfo: reconnectLastCloseInfo,
      failureCount: reconnectFailureCount,
      elapsedMs: reconnectCycleStartedAt == null ? 0 : Date.now() - reconnectCycleStartedAt,
      online: readNavigatorOnline(),
      controlProbeResult: reconnectControlProbeResult,
      sameOriginWs: sameOriginGameWs,
    });
  }

  function scheduleReconnectRefresh(variant: ReconnectVariant): void {
    clearReconnectRefreshTimer();
    if (variant !== 'reconnecting' || !sameOriginGameWs || reconnectCycleStartedAt == null) return;
    const elapsedMs = Date.now() - reconnectCycleStartedAt;
    const delayMs = Math.max(0, SERVER_UNAVAILABLE_ELAPSED_MS - elapsedMs + 10);
    reconnectRefreshTimer = window.setTimeout(() => {
      reconnectRefreshTimer = null;
      refreshReconnectingScreen();
    }, delayMs);
  }

  function showReconnectingScreen(): void {
    const variant = currentReconnectVariant();
    if (variant == null) return;
    reconnectLastVariant = variant;
    screenRouter.navigate('reconnecting', { variant });
    scheduleReconnectRefresh(variant);
  }

  function refreshReconnectingScreen(): void {
    const variant = currentReconnectVariant();
    if (variant == null) return;
    if (!(screenRouter.current === 'reconnecting' && reconnectLastVariant === variant)) {
      reconnectLastVariant = variant;
      screenRouter.navigate('reconnecting', { variant });
    }
    scheduleReconnectRefresh(variant);
  }

  function probeReconnectControlFile(): void {
    if (
      !sameOriginGameWs ||
      typeof fetch !== 'function' ||
      typeof AbortController === 'undefined'
    ) {
      return;
    }
    const seq = ++reconnectControlProbeSeq;
    const controller = new AbortController();
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 1_500);
    fetch(`/healthz.txt?cacheBust=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error('not ok'))))
      .then((text) => {
        if (seq !== reconnectControlProbeSeq) return;
        reconnectControlProbeResult = text.trim() === 'smashparty-control-ok' ? 'ok' : 'mismatched';
        refreshReconnectingScreen();
      })
      .catch(() => {
        if (seq !== reconnectControlProbeSeq) return;
        reconnectControlProbeResult = timedOut ? 'timeout' : 'failed';
        refreshReconnectingScreen();
      })
      .finally(() => window.clearTimeout(timeout));
  }

  function setActiveStoredSession(
    token: string,
    roomCode: string,
    metadata: Pick<StoredSmashpartySession, 'mode' | 'phase'> = {},
  ): void {
    writeStoredSession({ token, roomCode, ...metadata });
    activeStoredSession = {
      token,
      roomCode,
      ...metadata,
      savedAt: new Date().toISOString(),
    };
    clearReconnectCycle();
  }

  function clearActiveStoredSession(): void {
    clearStoredSession();
    activeStoredSession = null;
    restoreAttemptInFlight = false;
    clearReconnectCycle();
  }

  function noteLocalLeaveIntent(): void {
    clearActiveStoredSession();
  }

  function routeReconnectingScreenToWarmup(reason: string): void {
    if (screenRouter.current !== 'reconnecting') return;
    logClientWsEvent({
      direction: 'in',
      stage: 'reconnect_warmup_recovery',
      detail: reason,
      payload: {
        type: 'client_reconnect_lifecycle',
        reason,
        phase: latestGameState?.phase ?? currentPhase,
        activeMinigameInstanceId,
      },
    });
    screenRouter.navigate('warmup-arena-overlay', { portal: null });
  }

  function recoverStaleStoredSessionToWarmup(
    reason: string,
    opts: { resubscribeOnNextOpen?: boolean } = {},
  ): void {
    clearStoredSession();
    activeStoredSession = null;
    restoreAttemptInFlight = false;
    clearReconnectCycle();
    warmupResubscribeOnNextOpen = opts.resubscribeOnNextOpen === true;
    routeReconnectingScreenToWarmup(reason);
  }

  function clearReconnectStateAfterWarmupRecovery(reason: string): void {
    restoreAttemptInFlight = false;
    clearReconnectCycle();
    routeReconnectingScreenToWarmup(reason);
  }

  function routeRestoredLobby(restored: RestoredSessionMessage): void {
    if (restored.mode === 'warmup-arena') {
      screenRouter.navigate('warmup-arena-overlay', { portal: null });
      return;
    }

    const payload = {
      mode: restored.mode,
      roomType: restored.roomType,
      code: restored.roomCode,
      playerName: restored.playerName,
      send: sendWithActionLock,
      subscribe: (handler: (msg: ServerMessage) => void) =>
        wsClient.subscribe((msg) => handler(msg as ServerMessage)),
      characterAdapter,
      initialOwnedPlayerIds: restored.ownedPlayerIds,
      skipInitialRequest: true,
      restoredSession: {
        playerId: restored.playerId,
        hostId: restored.hostId,
        roomCode: restored.roomCode,
      },
    };

    if (restored.roomType === 'quick_play') {
      screenRouter.navigate('lobby-quick-play', payload);
    } else if (restored.playerId === restored.hostId) {
      screenRouter.navigate('lobby-host', payload);
    } else {
      screenRouter.navigate('lobby-joiner', payload);
    }
  }

  function sendRestoreSession(session: StoredSmashpartySession): void {
    restoreAttemptInFlight = true;
    if (session.mode === 'warmup-arena') {
      clearWarmupRestoreFallbackTimer();
      warmupRestoreFallbackTimer = window.setTimeout(() => {
        warmupRestoreFallbackTimer = null;
        if (activeStoredSession?.token !== session.token) return;
        recoverStaleStoredSessionToWarmup('warmup_restore_timeout', {
          resubscribeOnNextOpen: true,
        });
        wsClient.reconnectNow('warmup_restore_timeout');
      }, 8_000);
    }
    logClientWsEvent({
      direction: 'out',
      stage: 'restore_session_request',
      payload: { type: 'restore_session', token: session.token, roomCode: session.roomCode },
    });
    wsClient.send({ type: 'restore_session', token: session.token });
  }

  function dismissReconnectingScreenAfterRestore(reason: string): void {
    if (screenRouter.current !== 'reconnecting') return;
    logClientWsEvent({
      direction: 'in',
      stage: 'restore_session_screen_clear',
      detail: reason,
      payload: {
        type: 'client_reconnect_lifecycle',
        reason,
        phase: latestGameState?.phase ?? currentPhase,
        activeMinigameInstanceId,
      },
    });
    screenRouter.clear?.();
  }

  function markReconnectSocketOpened(): void {
    reconnectLastCloseInfo = null;
    reconnectFailureCount = 0;
    reconnectCycleStartedAt = null;
    reconnectLastVariant = null;
    reconnectControlProbeResult = null;
    reconnectControlProbeSeq++;
    reconnectCloseObservedSinceLastState = false;
  }

  function markReconnectSocketClosed(): void {
    if (!reconnectCloseObservedSinceLastState) {
      reconnectLastCloseInfo = null;
      reconnectFailureCount += 1;
      reconnectCycleStartedAt ??= Date.now();
      reconnectControlProbeResult = null;
    }
    reconnectCloseObservedSinceLastState = false;
  }

  function handlePageLifecycleHidden(): void {
    if (disposed) return;
    if (wsClient.state === 'closed') return;
    logClientWsEvent({
      direction: 'error',
      stage: 'page_lifecycle_hidden',
      detail: document.visibilityState,
    });
    wsClient.disconnectTemporarily('page_lifecycle_hidden');
  }

  function resumeSocketAfterPageLifecycle(reason: string): void {
    if (disposed) return;
    if (activeStoredSession != null) {
      showReconnectingScreen();
      restoreAttemptInFlight = false;
      wsClient.reconnectNow(reason);
      return;
    }
    if (wsClient.state === 'closed' || screenRouter.current === 'reconnecting') {
      wsClient.reconnectNow(reason);
    }
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      handlePageLifecycleHidden();
      return;
    }
    if (document.visibilityState === 'visible') {
      resumeSocketAfterPageLifecycle('visibility_visible');
    }
  }

  function handlePageShow(): void {
    resumeSocketAfterPageLifecycle('pageshow');
  }

  function handleOnline(): void {
    resumeSocketAfterPageLifecycle('online');
  }

  window.addEventListener('pagehide', handlePageLifecycleHidden);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('online', handleOnline);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  const restoreStateUnsub = wsClient.onState((state) => {
    if (state === 'connecting' && activeStoredSession != null && !restoreAttemptInFlight) {
      showReconnectingScreen();
      restoreAttemptInFlight = true;
    }
    if (state === 'open' && activeStoredSession != null) {
      markReconnectSocketOpened();
      sendRestoreSession(activeStoredSession);
    }
    if (state === 'open' && activeStoredSession == null) {
      if (warmupResubscribeOnNextOpen) {
        warmupResubscribeOnNextOpen = false;
        clearReconnectCycle();
        wsClient.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
        return;
      }
      clearReconnectCycle();
      if (screenRouter.current === 'reconnecting') {
        wsClient.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
        routeReconnectingScreenToWarmup('socket_open_without_stored_session');
      }
    }
    if (state === 'closed' && !isSessionRestoredElsewhereClose(reconnectLastCloseInfo)) {
      markReconnectSocketClosed();
      const shouldShow =
        activeStoredSession != null ||
        currentReconnectVariant() === 'server-unavailable' ||
        currentReconnectVariant() === 'connection-interrupted';
      if (shouldShow) {
        refreshReconnectingScreen();
        probeReconnectControlFile();
      }
    }
  });

  if (activeStoredSession != null) {
    showReconnectingScreen();
  }

  const notificationUnsub = wsClient.subscribe((rawMsg) => {
    const msg = rawMsg as { type?: string; level?: string; text?: string };
    if (msg.type !== 'notification' || typeof msg.text !== 'string') return;
    if (msg.text === 'session_expired' || msg.text === 'session_restored_elsewhere') {
      recoverStaleStoredSessionToWarmup(msg.text);
      wsClient.send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
    }
    if (
      localPlayerId != null &&
      pendingRollSubmissionKeysByPlayer.has(localPlayerId) &&
      (msg.text === 'invalid_movement_direction' ||
        msg.text === 'nextNodeId required' ||
        msg.text.startsWith('wrong_phase:') ||
        msg.text === 'not_your_turn')
    ) {
      pendingRollSubmissionKeysByPlayer.delete(localPlayerId);
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
    }
    if (handlePauseRequestNotification(msg.text)) return;
    handlePendingLocalActionRejection(msg.text);
    boardPresentationQueue.push(rawMsg as ServerMessage);
    const notificationMeta: { activeGameRooms?: number; estimateMinutes?: number } = {};
    const activeGameRooms = (msg as { activeGameRooms?: unknown }).activeGameRooms;
    if (typeof activeGameRooms === 'number') notificationMeta.activeGameRooms = activeGameRooms;
    const estimateMinutes = (msg as { estimateMinutes?: unknown }).estimateMinutes;
    if (typeof estimateMinutes === 'number') notificationMeta.estimateMinutes = estimateMinutes;
    toastOverlay.push({
      type: msg.level === 'error' ? 'kill' : msg.level === 'warn' ? 'item' : 'positive',
      message: formatNotificationText(msg.text, notificationMeta),
    });
  });

  const errorReporter = errorReporterFactory({
    onFatal: (_info) => {
      screenRouter.navigate('error-modal', { variant: 'connection-failed' });
    },
  });

  // ── Lobby roster ────────────────────────────────────────────────────────────
  // Parent group for all in-lobby character rigs. Added to the environment group
  // so it renders alongside map geometry. Driven entirely from state_sync.
  // groups may be absent in test/headless mode — guard defensively.
  const lobbyRosterParent = new THREE.Group();
  lobbyRosterParent.name = 'lobby-roster';
  const hasSceneGroups = renderer.scene.groups != null;
  if (hasSceneGroups) {
    renderer.scene.groups.environment.add(lobbyRosterParent);
  }
  const rosterRigs = new Map<string, CharacterPreviewRig>();
  const rosterIdleBaseY = new Map<string, number>();
  const rosterPhaseOffsets = new Map<string, number>();
  const rosterRigAnimationOverrides = new Map<string, { alias: string; untilMs: number }>();
  const lobbyAmbientAnimationScheduler = createLobbyAmbientAnimationScheduler();
  const ghostRigs = new Map<string, CharacterPreviewRig>();
  const ghostIdleBaseY = new Map<string, number>();
  const ghostPhaseOffsets = new Map<string, number>();
  const ghostFadeHandles = new Map<string, ReturnType<GsapRegistry['create']>>();
  const ghostRigAnimationOverrides = new Map<string, { alias: string; untilMs: number }>();
  let lobbyCameraFocus: THREE.Vector3 | null = null;
  let lobbyCameraDistance = 12;
  let customizationCameraHandle: ReturnType<GsapRegistry['create']> | null = null;
  let activeLobbyCustomizationPlayerId: string | null = null;
  let activeLobbyCustomizationCameraTarget: {
    readonly playerId: string;
    readonly view: CharacterCustomizeCameraView;
    readonly rigPositionKey: string;
  } | null = null;
  const lobbyCameraAngle = Math.PI * 0.16;
  const lobbyCameraPitch = 0.56;
  const LOBBY_CAMERA_FOV = 42;
  const LOBBY_CAMERA_FILL_RATIO = 0.55;

  // ── Board roster ─────────────────────────────────────────────────────────────
  // Mirrors the lobby roster pattern but positions rigs at tile world positions
  // after game start. Hidden during lobby phase.
  const boardRosterParent = new THREE.Group();
  boardRosterParent.name = 'board-roster';
  boardRosterParent.visible = false;
  if (hasSceneGroups) {
    renderer.scene.groups.board.add(boardRosterParent);
  }
  const boardRigs = new Map<string, CharacterPreviewRig>();
  const boardRigCustomizationSignatures = new Map<string, string>();
  const boardRigBaseY = new Map<string, number>();
  const boardRigPhases = new Map<string, number>();
  const boardRigCapeNone = new Map<string, boolean>();
  const boardRigNodeIds = new Map<string, string>();
  const boardRigVisualOrder = new Map<string, number>();
  const boardRigHopOffsets = new Map<string, number>();
  const boardRigFacingYawOffsets = new Map<string, number>();
  const boardRigAnimationOverrides = new Map<string, { alias: string; untilMs: number }>();
  const boardRigActionYawOffsets = new Map<string, number>();
  const boardRigActionPitchOffsets = new Map<string, number>();
  const boardRigActionRollOffsets = new Map<string, number>();
  const boardRigActionScale = new Map<string, number>();
  const boardRigActionPositionOffsets = new Map<string, THREE.Vector2>();
  const boardRigAppliedActionPositionOffsets = new Map<string, THREE.Vector2>();
  const characterStatusEffects = createCharacterStatusEffectManager({
    registry: gsapRegistry,
    getRig: (playerId) => boardRigs.get(playerId) ?? null,
  });
  const characterEyeExpressions = createCharacterEyeExpressionManager({
    getRig: (playerId) => boardRigs.get(playerId) ?? rosterRigs.get(playerId) ?? null,
  });
  let emoteRuntime: EmoteRuntimeHandle | null = null;
  const boardNpcParent = new THREE.Group();
  boardNpcParent.name = 'board-npcs';
  boardNpcParent.visible = false;
  if (hasSceneGroups) {
    renderer.scene.groups.board.add(boardNpcParent);
  }
  const boardNpcModels = new Map<
    string,
    {
      root: THREE.Group;
      nodeId: string;
      animationHandle: ReturnType<GsapRegistry['create']> | null;
      mistMaterials: THREE.SpriteMaterial[];
      baseY: number;
      bobPhase: number;
      color: number;
      moving: boolean;
      despawning: boolean;
    }
  >();
  const pendingShadowStalkerSpawnReveals: Array<{
    npcId: string;
    nodeId: string;
    summonerId?: string;
  }> = [];
  const pendingShadowStalkerMoves: Array<{
    npcId: string;
    fromNodeId: string;
    toNodeId: string;
    stepIndex: number;
    totalSteps: number;
  }> = [];
  const pendingShadowStalkerKills: Array<{
    npcId: string;
    playerId: string;
    atNodeId: string;
  }> = [];
  const pendingShadowStalkerKilledDespawns = new Set<string>();
  let shadowStalkerSpawnRevealRunning = false;
  let shadowStalkerMotionRunning = false;
  let shadowStalkerKillRunning = false;
  let shadowStalkerTemplatePromise: Promise<THREE.Group | null> | null = null;
  const itemUsePresentationHandles = new Map<string, ReturnType<GsapRegistry['create']>>();
  const itemImpactPresentationHandles = new Map<string, ReturnType<GsapRegistry['create']>>();
  const boardMoveHandles = new Map<string, ReturnType<GsapRegistry['create']>>();
  const boardRigFacingPreviewHandles = new Map<string, ReturnType<GsapRegistry['create']>>();
  const heldItemObjects = new Map<string, THREE.Object3D>();
  const heldItemObjectModes = new Map<string, 'preview' | 'use'>();
  const heldItemUseTokens = new Map<string, number>();
  const itemModelSceneCache = new Map<string, Promise<THREE.Group | null>>();
  const resolvedItemModelScenes = new Map<string, THREE.Group | null>();
  const liveItemVfxSubscribers = new Map<string, (event: GameEvent) => void>();
  let heldItemPreviewToken = 0;
  let liveItemVfxSubscriberSeq = 0;
  let boardPathArrowFacingPreviewKey: string | null = null;
  renderer.loop.addFrameTickCallback(() => {
    const t = performance.now() * 0.001;
    for (const record of boardNpcModels.values()) {
      if (record.despawning || record.moving) continue;
      record.root.position.y =
        record.baseY + Math.sin(t * 2.15 + record.bobPhase) * SHADOW_STALKER_BOB_HEIGHT;
    }
  });
  let activeHeldItemPreview: { playerId: string; itemId: string; token: number } | null = null;
  const arenaSnapshotParent = new THREE.Group();
  arenaSnapshotParent.name = 'arena-main-game-snapshot';
  arenaSnapshotParent.visible = false;
  if (hasSceneGroups) {
    renderer.scene.groups.board.add(arenaSnapshotParent);
  }
  const arenaSnapshotMeshes = new Map<string, THREE.Mesh>();
  let latestArenaSnapshotPlayers: Extract<
    ServerMessage,
    { type: 'arena_main_game_snapshot' }
  >['players'] = [];
  const heldMovementPlayers = new Set<string>();
  const animatingMovementPlayers = new Set<string>();
  const queuedMovementSteps = new Map<string, MovementStepEvent[]>();
  type SpecialRelocationRevealKind = 'treasure_chest' | 'weapons_cache';
  interface PendingSpecialRelocationReveal {
    readonly kind: SpecialRelocationRevealKind;
    readonly nodeId: string;
  }
  interface PendingTurnBannerRequest {
    readonly playerId: string;
    readonly turnKey: string;
    readonly requireCurrent: boolean;
    readonly sendIntroComplete: boolean;
    resolveLeadIn(): void;
  }
  interface PendingBoardPromptReady {
    readonly key: string;
    readonly msg: Extract<ClientMessage, { type: 'board_prompt_ready' }>;
  }
  interface PendingBoardPresentationMessage {
    readonly msg: ServerMessage;
    readonly forceBoardRemount: boolean;
    readonly stateSyncState?: GameState;
    readonly deferredStateSync?: boolean;
    readonly coalescibleStateSync?: boolean;
    readonly liveHudDeltaApplied?: boolean;
  }
  const pendingSpecialRelocationReveals: PendingSpecialRelocationReveal[] = [];
  const pendingLightningDashPresentations: ItemDashEvent[] = [];
  const movementProgressByPlayer = new Map<string, { expected: number; observed: number }>();
  const pendingForkOptionsByPlayer = new Map<
    string,
    { atNodeId: string; options: readonly string[] }
  >();
  const pendingRollSubmissionKeysByPlayer = new Map<string, string>();
  const pendingBoardPresentationMessages: PendingBoardPresentationMessage[] = [];
  const liveHudStateSubscribers = new Set<(state: BoardLiveHudState) => void>();
  let latestLiveHudState: BoardLiveHudState | null = null;
  const pendingForcedRelocationMessages: PendingBoardPresentationMessage[] = [];
  let deferredBoardRouteState: { state: GameState; forceBoardRemount: boolean } | null = null;
  let drainingBoardPresentationMessages = false;
  let awaitingForcedRelocationStateSync = false;
  let lastAnnouncedTurnKey: string | null = null;
  let notificationsActive = false;
  let boardEffectActivityCount = 0;
  let pendingBoardEffectMinigameStateSync: {
    msg: Extract<ServerMessage, { type: 'state_sync' }>;
    state: GameState;
  } | null = null;
  let pendingTurnBanner: PendingTurnBannerRequest | null = null;
  let pendingBoardPromptReady: PendingBoardPromptReady | null = null;
  let lastSentBoardPromptReadyKey: string | null = null;
  const turnBannerLeadIns = new Map<string, Promise<void>>();
  const botTurnIntroKeyByPlayer = new Map<string, string>();
  let boardPresentationPaused = false;
  let localPresentationPauseActive = false;
  let localPauseRequestCoversPresentation = false;
  let serverPauseActive = false;
  let suppressedPauseNotificationText: string | null = null;
  let presentationTraceTimestamps: number[] = [];
  let dicePresentationChain: Promise<void> = Promise.resolve();
  let tileEffectOverlayHandle: ScreenHandle | null = null;
  let trapCinematicRunning = false;
  let trapCinematicOverlay: HTMLElement | null = null;
  let orbitalCinematicRunning = false;
  let orbitalCinematicOverlay: HTMLElement | null = null;
  let thunderFistCinematicRunning = false;
  let localPlayerId: string | null = null;
  let canonicalGameState: GameState | null = null;
  let canonicalGameStateRoomId: string | null = null;
  let canonicalGameStateRevision: number | null = null;
  let latestGameState: GameState | null = null;
  let inWarmupArena = false;
  let gameplayTutorialHandle: LiveTutorialWidgetHandle | null = null;
  let gameplayTutorialVisibleLesson: GameplayLesson | null = null;
  let gameplayTutorialMinimized = false;
  let gameplayTutorialCoreCompletedThisMatch = false;
  const gameplayTutorialCompletedCoreLessons = new Set<GameplayCoreStepId>();
  const gameplayTutorialSeenLessons = new Set<GameplayLessonId>();
  const gameplayTutorialLessonQueue: GameplayLesson[] = [];
  let gameplayTutorialLastLanding: {
    playerId: string;
    nodeId: string;
    tileType: string;
    timestamp: number;
  } | null = null;
  let gameplayTutorialLandingLessonQueued = false;
  let suppressRoomMessagesUntilEntry = false;
  let warmupArenaCameraFocused = false;
  let warmupCameraView: WarmupCameraView = 'arena';
  let activeMinigame: MinigameResultsReceiver | null = null;
  let disposed = false;
  let navOverlayGroup: THREE.Group | null = null;
  let pathArrows: PathArrowsHandle | null = null;
  const turnBannerHandle = createTurnBanner({ gsapRegistry, mount: opts.host });
  let activeMinigameInstanceId: string | null = null;
  let activeMinigameInputSeq = 0;
  let activeMinigameInputPhaseId: string | null = null;
  let activeMinigameReadyPhaseId: string | null = null;
  let activeMinigameResultsReceived = false;
  const realtimeClock = createRealtimeClockSync();
  let pendingMinigameMountInstanceId: string | null = null;
  let pendingMinigameStartMessage: Extract<ServerMessage, { type: 'minigame_start' }> | null = null;
  let pendingMinigameSelectionMessage: Extract<
    ServerMessage,
    { type: 'minigame_selection' }
  > | null = null;
  let activeMinigameSelectionInstanceId: string | null = null;
  let activeMinigameSelectionWatchdog: number | null = null;
  const pendingMinigameMessages = new Map<string, ServerMessage[]>();

  function setOwnedPlayerIds(
    ids: readonly string[] | undefined,
    fallbackPlayerId?: string | null,
  ): void {
    ownedPlayerIds.clear();
    if (ids != null && ids.length > 0) {
      for (const playerId of ids) ownedPlayerIds.add(playerId);
      return;
    }
    if (fallbackPlayerId != null) {
      ownedPlayerIds.add(fallbackPlayerId);
    }
  }

  function disposeActiveMinigame(): void {
    pendingMinigameStartMessage = null;
    pendingMinigameMountInstanceId = null;
    cancelMinigameSelection();
    activeMinigame?.dispose();
    activeMinigame = null;
    activeMinigameInstanceId = null;
    activeMinigameInputSeq = 0;
    activeMinigameInputPhaseId = null;
    activeMinigameReadyPhaseId = null;
    activeMinigameResultsReceived = false;
  }

  function disposeCompletedMinigameForSelection(): void {
    if (!activeMinigameResultsReceived) return;
    activeMinigame?.dispose();
    activeMinigame = null;
    activeMinigameInstanceId = null;
    activeMinigameInputSeq = 0;
    activeMinigameInputPhaseId = null;
    activeMinigameReadyPhaseId = null;
    activeMinigameResultsReceived = false;
  }

  function canSubmitActiveMinigameInput(instanceId: string): boolean {
    if (disposed) return false;
    if (activeMinigameInstanceId !== instanceId) return false;
    if (activeMinigameResultsReceived) return false;
    if (latestGameState != null && latestGameState.phase !== 'minigame_play') return false;
    return true;
  }

  function seedActiveMinigameInputSeqFromSnapshot(
    message: Extract<ServerMessage, { type: 'minigame_snapshot' }>,
  ): void {
    if (message.instanceId !== activeMinigameInstanceId) return;
    activeMinigameReadyPhaseId = message.flow?.phaseId ?? message.phaseId ?? null;
    activeMinigameInputPhaseId = message.flow?.inputPhaseId ?? null;
    const lastProcessedInputSeq = message.lastProcessedInputSeq;
    if (lastProcessedInputSeq == null) return;
    const playerIds = new Set(ownedPlayerIds);
    if (localPlayerId != null) playerIds.add(localPlayerId);
    if (playerIds.size === 0) return;

    let nextBaseSeq = activeMinigameInputSeq;
    for (const playerId of playerIds) {
      const seq = lastProcessedInputSeq[playerId];
      if (typeof seq !== 'number' || !Number.isFinite(seq)) continue;
      nextBaseSeq = Math.max(nextBaseSeq, Math.max(0, Math.trunc(seq)));
    }
    activeMinigameInputSeq = nextBaseSeq;
  }

  function seedActiveMinigameInputSeqFromBufferedSnapshots(instanceId: string): void {
    const pending = pendingMinigameMessages.get(instanceId);
    if (pending == null) return;
    for (const message of pending) {
      if (message.type === 'minigame_snapshot') {
        seedActiveMinigameInputSeqFromSnapshot(message);
      }
    }
  }

  function applyMinigameMessage(message: ServerMessage): boolean {
    if (activeMinigame == null) return false;
    if (
      (message.type === 'minigame_round_results' ||
        message.type === 'minigame_results' ||
        message.type === 'minigame_event' ||
        message.type === 'minigame_snapshot' ||
        message.type === 'minigame_ready_state') &&
      message.instanceId !== activeMinigameInstanceId
    ) {
      return false;
    }
    if (
      activeMinigameResultsReceived &&
      (message.type === 'minigame_round_results' ||
        message.type === 'minigame_event' ||
        message.type === 'minigame_snapshot' ||
        message.type === 'minigame_ready_state')
    ) {
      return true;
    }
    if (message.type === 'minigame_ready_state') {
      activeMinigameReadyPhaseId = message.phaseId ?? activeMinigameReadyPhaseId;
      activeMinigame.applyReadyState?.({
        instanceId: message.instanceId,
        minigameId: message.minigameId,
        ...(message.phaseId === undefined ? {} : { phaseId: message.phaseId }),
        readyPlayerIds: message.readyPlayerIds,
        requiredPlayerIds: message.requiredPlayerIds,
        serverNowMs: message.serverNowMs,
        ...(message.playStartsAtMs === undefined ? {} : { playStartsAtMs: message.playStartsAtMs }),
      });
      return true;
    }
    if (message.type === 'minigame_snapshot') {
      const receivedAtMs = Date.now();
      const receivedPerfAtMs = performance.now();
      const clockState = realtimeClock.observe({
        clientReceivedAtMs: receivedPerfAtMs,
        serverNowMs: message.serverNowMs,
      });
      logClientRealtimeTrace({
        stage: 'client_snapshot_received',
        minigameId: message.minigameId,
        instanceId: message.instanceId,
        ...(message.roundIndex === undefined ? {} : { roundIndex: message.roundIndex }),
        sequence: message.sequence,
        ...(message.serverTick === undefined ? {} : { serverTick: message.serverTick }),
        serverNowMs: message.serverNowMs,
        snapshotAgeMs: receivedAtMs - message.serverNowMs,
        payload: {
          phase: message.phase,
          phaseId: message.phaseId,
          lastProcessedInputSeq: message.lastProcessedInputSeq,
          clockOffsetMs: clockState.offsetMs,
          rttEstimateMs: clockState.rttEstimateMs,
          renderDelayMs: clockState.renderDelayMs,
        },
      });
      seedActiveMinigameInputSeqFromSnapshot(message);
      activeMinigame.applyServerState?.({
        instanceId: message.instanceId,
        minigameId: message.minigameId,
        serverNowMs: message.serverNowMs,
        phase: message.phase,
        ...(message.roundIndex === undefined ? {} : { roundIndex: message.roundIndex }),
        sequence: message.sequence,
        ...(message.serverTick === undefined ? {} : { serverTick: message.serverTick }),
        ...(message.phaseId === undefined ? {} : { phaseId: message.phaseId }),
        ...(message.flow === undefined ? {} : { flow: message.flow }),
        ...(message.lastProcessedInputSeq === undefined
          ? {}
          : { lastProcessedInputSeq: message.lastProcessedInputSeq }),
        payload: message.payload,
      });
      return true;
    }
    if (message.type === 'minigame_event') {
      activeMinigame.applyLiveEvent?.({
        instanceId: message.instanceId,
        minigameId: message.minigameId,
        serverNowMs: message.serverNowMs,
        sequence: message.sequence,
        ...(message.roundIndex === undefined ? {} : { roundIndex: message.roundIndex }),
        ...(message.phaseId === undefined ? {} : { phaseId: message.phaseId }),
        event: message.event,
      });
      return true;
    }
    if (message.type === 'minigame_round_results') {
      activeMinigame.applyRoundResults?.(message.roundIndex, message.placements, message.phaseId);
      return true;
    }
    if (message.type === 'minigame_results') {
      activeMinigameResultsReceived = true;
      activeMinigame.applyServerResults?.(message.placements, message.rewards);
      return true;
    }
    return false;
  }

  function bufferOrApplyMinigameMessage(message: ServerMessage): void {
    if (applyMinigameMessage(message)) return;
    if (
      message.type !== 'minigame_round_results' &&
      message.type !== 'minigame_results' &&
      message.type !== 'minigame_event' &&
      message.type !== 'minigame_snapshot' &&
      message.type !== 'minigame_ready_state'
    ) {
      return;
    }
    const pending = pendingMinigameMessages.get(message.instanceId) ?? [];
    pending.push(message);
    pendingMinigameMessages.set(message.instanceId, pending);
  }

  function flushPendingMinigameMessages(instanceId: string): void {
    const pending = pendingMinigameMessages.get(instanceId);
    if (pending == null) return;
    pendingMinigameMessages.delete(instanceId);
    for (const message of pending) {
      applyMinigameMessage(message);
    }
  }

  function ensureTileEffectOverlay(payloadBase: BoardMountPayload): void {
    if (tileEffectOverlayHandle != null) return;
    tileEffectOverlayHandle = createTileEffectPopupScreen({
      gsapRegistry,
      host: opts.host,
      router: screenRouter,
      payload: payloadBase,
    });
  }

  function disposeTileEffectOverlay(): void {
    tileEffectOverlayHandle?.dispose();
    tileEffectOverlayHandle = null;
  }

  async function mountActiveMinigame(
    message: Extract<ServerMessage, { type: 'minigame_start' }>,
  ): Promise<void> {
    cancelMinigameSelection(message.instanceId);
    pendingMinigameMountInstanceId = message.instanceId;
    let minigame = minigameRegistry.get(message.minigameId);
    if (minigame === undefined) {
      await import('@minigames/index');
      minigame = minigameRegistry.get(message.minigameId);
    }
    if (pendingMinigameMountInstanceId !== message.instanceId) {
      pendingMinigameMessages.delete(message.instanceId);
      return;
    }
    pendingMinigameMountInstanceId = null;
    const messageRoomId = typeof message.roomId === 'string' ? message.roomId : null;
    const messageRevision = typeof message.roomRevision === 'number' ? message.roomRevision : null;
    const canonicalCanVeto =
      canonicalGameState != null &&
      !ACTIVE_MINIGAME_PHASES.has(canonicalGameState.phase) &&
      canonicalGameStateRevision != null &&
      messageRevision != null &&
      (messageRoomId == null ||
        canonicalGameStateRoomId == null ||
        messageRoomId === canonicalGameStateRoomId) &&
      canonicalGameStateRevision >= messageRevision;
    if (canonicalCanVeto) {
      pendingMinigameMessages.delete(message.instanceId);
      return;
    }
    disposeActiveMinigame();
    if (minigame === undefined) return;
    const actionAnimationAliases = minigameActionAnimationAliasesForStart(message.minigameId);
    if (actionAnimationAliases.length > 0 && ensureCharacterAnimationBundles != null) {
      try {
        await ensureCharacterAnimationBundles(['actions'], {
          policy: 'required',
          requiredAliases: actionAnimationAliases,
          reason: message.minigameId,
        });
      } catch (error: unknown) {
        console.warn(
          `[smashparty] ${message.minigameId} starting with degraded character action animations:`,
          error,
        );
      }
      if (pendingMinigameMountInstanceId !== null || activeMinigameInstanceId !== null) return;
    }
    activeMinigameInstanceId = message.instanceId;
    activeMinigameResultsReceived = false;
    seedActiveMinigameInputSeqFromBufferedSnapshots(message.instanceId);
    activeMinigame = minigame.startClient({
      players: message.players,
      seed: message.seed,
      characterAdapter: characterAdapter ?? undefined,
      localPlayerId: localPlayerId ?? undefined,
      onReady: () => {
        if (disposed || activeMinigameInstanceId !== message.instanceId) return;
        if (activeMinigameResultsReceived) return;
        sendWithActionLock({
          type: 'minigame_ready',
          minigameId: message.minigameId,
          instanceId: message.instanceId,
          ...(activeMinigameReadyPhaseId === null ? {} : { phaseId: activeMinigameReadyPhaseId }),
        });
      },
      onSubmit: (input: unknown, roundIndex: number) => {
        if (!canSubmitActiveMinigameInput(message.instanceId)) return undefined;
        activeMinigameInputSeq += 1;
        const inputSeq = activeMinigameInputSeq;
        const clientCommandMs = performance.now();
        const clockState = realtimeClock.snapshot();
        const phaseId = activeMinigameInputPhaseId ?? `play:${roundIndex}`;
        const payload =
          typeof input === 'object' && input !== null
            ? { ...input, roundIndex, phaseId }
            : { type: 'count', count: input, roundIndex, phaseId };
        sendWithActionLock({
          type: 'minigame_input',
          minigameId: message.minigameId,
          instanceId: message.instanceId,
          inputPhaseId: phaseId,
          inputSeq,
          clientTick: inputSeq,
          clientCommandMs,
          estimatedServerNowMs: realtimeClock.estimateServerNow(clientCommandMs),
          clockOffsetMs: clockState.offsetMs,
          rttEstimateMs: clockState.rttEstimateMs,
          renderDelayMs: clockState.renderDelayMs,
          payload,
        });
        logClientRealtimeTrace({
          stage: 'client_input_sent',
          minigameId: message.minigameId,
          instanceId: message.instanceId,
          playerId: localPlayerId ?? undefined,
          roundIndex,
          inputSeq,
          clientTick: inputSeq,
          payload: {
            input: payload,
            clientCommandMs,
            estimatedServerNowMs: realtimeClock.estimateServerNow(clientCommandMs),
            clockOffsetMs: clockState.offsetMs,
            rttEstimateMs: clockState.rttEstimateMs,
            renderDelayMs: clockState.renderDelayMs,
          },
        });
        return { inputSeq, clientTick: inputSeq };
      },
    } as ClientMinigameStartArgs) as MinigameResultsReceiver;
    flushPendingMinigameMessages(message.instanceId);
  }

  function shouldDelayMinigameTransitionForBoardPresentation(): boolean {
    if (boardPresentationPaused) return true;
    if (isPresentationBlocked()) return true;
    if (boardEffectActivityCount > 0) return true;
    if (pendingBoardPresentationMessages.length > 0) return true;
    if (pendingBoardEffectMinigameStateSync != null) return true;
    return false;
  }

  function shouldDelayMinigameMountForBoardPresentation(): boolean {
    if (shouldDelayMinigameTransitionForBoardPresentation()) return true;
    if (activeMinigameSelectionInstanceId != null) return true;
    if (pendingMinigameSelectionMessage != null) return true;
    return false;
  }

  function queueMinigameStart(message: Extract<ServerMessage, { type: 'minigame_start' }>): void {
    pendingMinigameStartMessage = message;
    maybeMountPendingMinigameStart();
  }

  function descriptorForSelection(entry: { readonly id: string; readonly displayName: string }): {
    id: string;
    name: string;
    description: string;
    iconUrl?: string;
  } {
    return {
      id: entry.id,
      name: entry.displayName,
      description: 'Get ready.',
      ...(entry.id.startsWith('_')
        ? {}
        : { iconUrl: `/assets/textures/minigame-icons/${entry.id}.webp` }),
    };
  }

  function queueMinigameSelection(
    message: Extract<ServerMessage, { type: 'minigame_selection' }>,
  ): void {
    if (activeMinigameInstanceId === message.instanceId) return;
    pendingMinigameSelectionMessage = message;
    maybeShowPendingMinigameSelection();
  }

  function cancelMinigameSelection(instanceId?: string): void {
    if (
      instanceId !== undefined &&
      pendingMinigameSelectionMessage?.instanceId !== instanceId &&
      activeMinigameSelectionInstanceId !== instanceId
    ) {
      return;
    }
    pendingMinigameSelectionMessage = null;
    activeMinigameSelectionInstanceId = null;
    if (activeMinigameSelectionWatchdog !== null) {
      window.clearTimeout(activeMinigameSelectionWatchdog);
      activeMinigameSelectionWatchdog = null;
    }
    screenRouter.closeOverlay?.('minigame-announce');
  }

  function maybeShowPendingMinigameSelection(): void {
    const message = pendingMinigameSelectionMessage;
    if (message == null) return;
    if (activeMinigameSelectionInstanceId === message.instanceId) return;
    if (activeMinigameInstanceId === message.instanceId) {
      pendingMinigameSelectionMessage = null;
      return;
    }
    if (shouldDelayMinigameTransitionForBoardPresentation()) return;
    const state = latestGameState;
    if (state == null || !ACTIVE_MINIGAME_PHASES.has(state.phase)) return;
    const payloadBase = makeBoardPayload(state);
    if (payloadBase == null) return;

    const messageRoomId = typeof message.roomId === 'string' ? message.roomId : null;
    const messageRevision = typeof message.roomRevision === 'number' ? message.roomRevision : null;
    const canonicalCanVeto =
      canonicalGameState != null &&
      !ACTIVE_MINIGAME_PHASES.has(canonicalGameState.phase) &&
      canonicalGameStateRevision != null &&
      messageRevision != null &&
      (messageRoomId == null ||
        canonicalGameStateRoomId == null ||
        messageRoomId === canonicalGameStateRoomId) &&
      canonicalGameStateRevision >= messageRevision;
    if (canonicalCanVeto) {
      pendingMinigameSelectionMessage = null;
      return;
    }

    disposeCompletedMinigameForSelection();
    pendingMinigameSelectionMessage = null;
    activeMinigameSelectionInstanceId = message.instanceId;
    if (activeMinigameSelectionWatchdog !== null) {
      window.clearTimeout(activeMinigameSelectionWatchdog);
      activeMinigameSelectionWatchdog = null;
    }
    const pool =
      message.selectionPool.length > 0
        ? message.selectionPool
        : [{ id: message.minigameId, displayName: message.displayName }];
    const selectedIndex =
      message.selectedIndex >= 0 && message.selectedIndex < pool.length ? message.selectedIndex : 0;
    const completeSelection = (): void => {
      if (disposed) return;
      if (activeMinigameSelectionInstanceId !== message.instanceId) return;
      if (activeMinigameInstanceId === message.instanceId) return;
      if (latestGameState != null && !ACTIVE_MINIGAME_PHASES.has(latestGameState.phase)) return;
      activeMinigameSelectionInstanceId = null;
      if (activeMinigameSelectionWatchdog !== null) {
        window.clearTimeout(activeMinigameSelectionWatchdog);
        activeMinigameSelectionWatchdog = null;
      }
      screenRouter.closeOverlay?.('minigame-announce');
      console.warn('[smashparty] minigame selection completed', {
        instanceId: message.instanceId,
        minigameId: message.minigameId,
      });
      sendWithActionLock({
        type: 'minigame_selection_complete',
        instanceId: message.instanceId,
        minigameId: message.minigameId,
      });
      maybeMountPendingMinigameStart();
    };

    const payload = {
      ...payloadBase,
      minigameList: pool.map(descriptorForSelection),
      selectedIndex,
      onComplete: completeSelection,
    };
    if (screenRouter.showOverlay != null) {
      screenRouter.showOverlay('minigame-announce', payload);
    } else {
      screenRouter.navigate('minigame-announce', payload);
    }
    activeMinigameSelectionWatchdog = window.setTimeout(() => {
      if (disposed || activeMinigameSelectionInstanceId !== message.instanceId) return;
      if (pendingMinigameStartMessage?.instanceId !== message.instanceId) return;
      console.warn('[smashparty] minigame selection watchdog forcing mount', {
        instanceId: message.instanceId,
        minigameId: message.minigameId,
      });
      activeMinigameSelectionInstanceId = null;
      activeMinigameSelectionWatchdog = null;
      screenRouter.closeOverlay?.('minigame-announce');
      maybeMountPendingMinigameStart();
    }, 4_350);
    if (disposed || activeMinigameSelectionInstanceId !== message.instanceId) return;
    sendWithActionLock({
      type: 'minigame_selection_ready',
      instanceId: message.instanceId,
      minigameId: message.minigameId,
    });
  }

  function maybeMountPendingMinigameStart(): void {
    const message = pendingMinigameStartMessage;
    if (message == null) return;
    if (
      activeMinigameSelectionInstanceId != null &&
      activeMinigameSelectionInstanceId !== message.instanceId
    ) {
      console.warn('[smashparty] clearing stale minigame selection before mount', {
        activeSelectionInstanceId: activeMinigameSelectionInstanceId,
        startInstanceId: message.instanceId,
        minigameId: message.minigameId,
      });
      cancelMinigameSelection();
    }
    if (shouldDelayMinigameMountForBoardPresentation()) return;
    pendingMinigameStartMessage = null;
    inWarmupArena = false;
    warmupCameraView = 'arena';
    arenaSnapshotParent.visible = false;
    closeSystemOverlays();
    screenRouter.closeOverlay?.('inventory');
    window.dispatchEvent(new CustomEvent('smashparty:warmup-suppress-hud'));
    playSfx('sfx-minigame-start-bell');
    void mountActiveMinigame(message);
  }

  // Direct burst pool for chest/cache spawn landings. It reuses the existing
  // landing FX primitive without routing through the event-log manager.
  const spawnLandingFxPool = createLandingFxPool({
    scene: renderer.scene.scene,
    registry: gsapRegistry,
  });
  const spawnLandingFxEmitters = new Set<THREE.Object3D>();
  renderer.loop.addFrameTickCallback((dt) => {
    const stillActive = spawnLandingFxPool.update(dt);
    for (const emitter of [...spawnLandingFxEmitters]) {
      if (!stillActive.has(emitter) || spawnLandingFxPool.isFinished(emitter)) {
        spawnLandingFxPool.release(emitter);
        spawnLandingFxEmitters.delete(emitter);
      }
    }
  });

  const bloodPool = createBloodPool(renderer.scene.groups.effects);
  const bloodPoolTickUnsub = renderer.loop.addFrameTickCallback((dt) => {
    bloodPool.update(dt);
  });

  function spawnLandingBurst(position: THREE.Vector3, tileType: 'gold' | 'item'): void {
    playSfx('sfx-board-tile-land-good', {
      spatialPosition: { x: position.x, y: position.y, z: position.z },
    });
    const emitter = spawnLandingFxPool.acquire({
      position: { x: position.x, y: position.y + 0.05, z: position.z },
      tileType,
    });
    if (emitter != null) {
      spawnLandingFxEmitters.add(emitter);
    }
  }

  function playShadowStalkerPortal(
    position: THREE.Vector3,
    color: number,
    onReveal: () => void,
  ): Promise<void> {
    playSfx('sfx-items-shadow-stalker-summon', {
      spatialPosition: { x: position.x, y: position.y, z: position.z },
    });

    const group = new THREE.Group();
    group.name = 'shadow-stalker-portal';
    group.position.copy(position);
    group.position.y += 0.06;
    group.lookAt(renderer.cameraRig.camera.position);
    group.scale.set(0.08, 0.08, 0.08);

    const playerColor = new THREE.Color(color);
    const rimColor = playerColor.clone().lerp(new THREE.Color(0xffffff), 0.28);
    const hotColor = playerColor.clone().lerp(new THREE.Color(0xfff4cf), 0.52);
    const innerColor = playerColor.clone().lerp(new THREE.Color(0x030009), 0.82);

    const innerMaterial = new THREE.MeshBasicMaterial({
      color: innerColor,
      transparent: true,
      opacity: 0,
      blending: THREE.NormalBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const inner = new THREE.Mesh(new THREE.CircleGeometry(0.74, 48), innerMaterial);
    inner.scale.set(0.68, 1.18, 1);
    inner.position.z = -0.012;
    group.add(inner);

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: rimColor,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.74, 0.035, 12, 72), ringMaterial);
    ring.scale.set(0.68, 1.18, 1);
    group.add(ring);

    const outerMaterial = new THREE.MeshBasicMaterial({
      color: hotColor,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const outerRing = new THREE.Mesh(new THREE.TorusGeometry(0.79, 0.018, 8, 72), outerMaterial);
    outerRing.scale.set(0.68, 1.18, 1);
    group.add(outerRing);

    const smokeTexture = getItemVfxSmokeTexture();
    const smokeMaterials: THREE.SpriteMaterial[] = [];
    const smokeSprites: THREE.Sprite[] = [];
    for (let index = 0; index < 34; index += 1) {
      const t = index / 34;
      const angle = t * Math.PI * 2;
      const edgeJitter = index % 3 === 0 ? 0.12 : index % 3 === 1 ? -0.05 : 0.03;
      const radius = 0.78 + edgeJitter;
      const material = new THREE.SpriteMaterial({
        map: smokeTexture,
        color: index % 2 === 0 ? rimColor : playerColor,
        transparent: true,
        alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
        opacity: 0,
        blending: THREE.NormalBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.position.set(
        Math.cos(angle) * radius * 0.52,
        Math.sin(angle) * radius * 0.92,
        0.025 + (index % 5) * 0.002,
      );
      const scale = 0.28 + (index % 5) * 0.045;
      sprite.scale.set(scale, scale, 1);
      group.add(sprite);
      smokeMaterials.push(material);
      smokeSprites.push(sprite);
    }

    const sparkMaterials: THREE.MeshBasicMaterial[] = [];
    const sparkMeshes: THREE.Mesh[] = [];
    for (let index = 0; index < 28; index += 1) {
      const material = new THREE.MeshBasicMaterial({
        color: index % 2 === 0 ? hotColor : rimColor,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const spark = new THREE.Mesh(
        new THREE.SphereGeometry(0.018 + (index % 3) * 0.004, 6, 4),
        material,
      );
      const angle = (index / 28) * Math.PI * 2;
      spark.position.set(Math.cos(angle) * 0.52, Math.sin(angle) * 0.92, 0.035);
      group.add(spark);
      sparkMaterials.push(material);
      sparkMeshes.push(spark);
    }

    renderer.scene.groups.effects.add(group);
    const handle = gsapRegistry.create('shadow-stalker-portal');
    const pulse = { value: 0 };

    return new Promise((resolve) => {
      handle.timeline.to(
        group.scale,
        { x: 1, y: 1, z: 1, duration: 0.28, ease: 'back.out(1.7)' },
        0,
      );
      handle.timeline.to(ringMaterial, { opacity: 0.95, duration: 0.22, ease: 'power1.out' }, 0);
      handle.timeline.to(
        outerMaterial,
        { opacity: 0.62, duration: 0.22, ease: 'power1.out' },
        0.05,
      );
      handle.timeline.to(innerMaterial, { opacity: 0.42, duration: 0.3, ease: 'power1.out' }, 0.06);
      handle.timeline.to(
        smokeMaterials,
        { opacity: 0.5, duration: 0.28, ease: 'power1.out' },
        0.08,
      );
      handle.timeline.to(
        sparkMaterials,
        { opacity: 0.9, duration: 0.18, ease: 'power1.out' },
        0.12,
      );

      handle.timeline.to(
        pulse,
        {
          value: 1,
          duration: 0.72,
          ease: 'none',
          onUpdate: () => {
            const wobble = Math.sin(pulse.value * Math.PI * 6);
            ring.scale.set(0.68 + wobble * 0.018, 1.18 + wobble * 0.025, 1);
            outerRing.scale.set(0.69 - wobble * 0.012, 1.2 - wobble * 0.014, 1);
            smokeSprites.forEach((sprite, index) => {
              const angle = (index / smokeSprites.length) * Math.PI * 2 + pulse.value * 0.9;
              const radius = 0.79 + Math.sin(pulse.value * Math.PI * 2 + index) * 0.04;
              sprite.position.x = Math.cos(angle) * radius * 0.52;
              sprite.position.y = Math.sin(angle) * radius * 0.92;
            });
            sparkMeshes.forEach((spark, index) => {
              const angle = (index / sparkMeshes.length) * Math.PI * 2;
              const drift = pulse.value * (0.18 + (index % 4) * 0.025);
              spark.position.x = Math.cos(angle) * (0.52 + drift);
              spark.position.y = Math.sin(angle) * (0.92 + drift * 1.3);
            });
          },
        },
        0.18,
      );

      handle.timeline.call(onReveal, undefined, 0.22);
      handle.timeline.to(innerMaterial, { opacity: 0.14, duration: 0.34, ease: 'power1.in' }, 0.68);
      handle.timeline.to(sparkMaterials, { opacity: 0, duration: 0.28, ease: 'power1.in' }, 0.76);
      handle.timeline.to(smokeMaterials, { opacity: 0, duration: 0.34, ease: 'power1.in' }, 0.82);
      handle.timeline.to(ringMaterial, { opacity: 0, duration: 0.28, ease: 'power2.in' }, 0.86);
      handle.timeline.to(outerMaterial, { opacity: 0, duration: 0.28, ease: 'power2.in' }, 0.86);
      handle.timeline.to(
        group.scale,
        { x: 0.08, y: 1.08, z: 0.08, duration: 0.34, ease: 'power2.in' },
        0.86,
      );

      handle.timeline.call(
        () => {
          renderer.scene.groups.effects.remove(group);
          inner.geometry.dispose();
          innerMaterial.dispose();
          ring.geometry.dispose();
          ringMaterial.dispose();
          outerRing.geometry.dispose();
          outerMaterial.dispose();
          for (const material of smokeMaterials) material.dispose();
          for (const spark of sparkMeshes) spark.geometry.dispose();
          for (const material of sparkMaterials) material.dispose();
          handle.dispose();
          resolve();
        },
        undefined,
        1.22,
      );
    });
  }

  // ── Idle bob + sway animation for both rosters ──────────────────────────────
  function getAvailableLobbyAmbientAnimationClips(
    rig: CharacterPreviewRig,
  ): LobbyAmbientAnimationClip[] {
    const actions = rig.actions;
    if (actions == null) return [];
    return LOBBY_AMBIENT_ANIMATION_ALIASES.flatMap((alias) => {
      const action = actions.get(alias) ?? actions.get(alias.toLowerCase());
      if (action == null) return [];
      const durationMs = action.getClip().duration * 1_000;
      if (!Number.isFinite(durationMs) || durationMs <= 0) return [];
      return [{ alias, durationMs }];
    });
  }

  renderer.loop.addFrameTickCallback((dt) => {
    const t = performance.now() * 0.001;
    const nowMs = performance.now();
    // Lobby roster
    for (const [id, rig] of rosterRigs) {
      const ambientAlias = lobbyAmbientAnimationScheduler.getAnimationAlias(
        id,
        nowMs,
        getAvailableLobbyAmbientAnimationClips(rig),
      );
      const animationAlias = selectCharacterAnimationAlias({
        overrides: rosterRigAnimationOverrides,
        id,
        fallbackAlias: ambientAlias,
        nowMs,
      });
      playCharacterAnimationAlias(rig, animationAlias, 'idle');
      rig.tick?.(dt);
      const baseY = rosterIdleBaseY.get(id) ?? 2.5;
      const phase = rosterPhaseOffsets.get(id) ?? 0;
      rig.root.position.y =
        baseY +
        Math.sin(t * CHARACTER_IDLE_BOB_RADIANS_PER_SECOND + phase) *
          LOBBY_ROSTER_IDLE_BOB_AMPLITUDE;
      rig.root.rotation.y = Math.sin(t * CHARACTER_IDLE_SWAY_RADIANS_PER_SECOND + phase) * 0.15;
    }
    for (const [id, rig] of ghostRigs) {
      const animationAlias = selectCharacterAnimationAlias({
        overrides: ghostRigAnimationOverrides,
        id,
        fallbackAlias: 'idle',
        nowMs,
      });
      playCharacterAnimationAlias(rig, animationAlias, 'idle');
      rig.tick?.(dt);
      const baseY = ghostIdleBaseY.get(id) ?? 2.5;
      const phase = ghostPhaseOffsets.get(id) ?? 0;
      rig.root.position.y =
        baseY +
        Math.sin(t * CHARACTER_IDLE_BOB_RADIANS_PER_SECOND + phase) *
          GHOST_ROSTER_IDLE_BOB_AMPLITUDE;
      rig.root.rotation.y = Math.sin(t * CHARACTER_IDLE_SWAY_RADIANS_PER_SECOND + phase) * 0.08;
    }
    if (boardPresentationPaused) return;
    // Board roster idle bob + optional butt-wobble
    for (const [id, rig] of boardRigs) {
      const baseY = boardRigBaseY.get(id);
      if (baseY == null) continue;
      const phase = boardRigPhases.get(id) ?? 0;
      const isMoving = boardMoveHandles.has(id) || animatingMovementPlayers.has(id);
      const locomotionAlias = selectBoardCharacterLocomotionAlias({
        capeNone: boardRigCapeNone.get(id),
        moving: isMoving,
      });
      const animationAlias = selectCharacterAnimationAlias({
        overrides: boardRigAnimationOverrides,
        id,
        fallbackAlias: locomotionAlias,
        nowMs,
      });
      playCharacterAnimationAlias(rig, animationAlias, locomotionAlias);
      rig.tick?.(dt);
      const idleBob =
        Math.sin(t * CHARACTER_IDLE_BOB_RADIANS_PER_SECOND + phase) *
        BOARD_ROSTER_IDLE_BOB_AMPLITUDE;
      const buttWobble =
        boardRigCapeNone.get(id) === true
          ? Math.sin(t * BOARD_BUTT_WOBBLE_RADIANS_PER_SECOND + phase) *
            (isMoving ? BOARD_BUTT_WOBBLE_MOVING_AMPLITUDE : BOARD_BUTT_WOBBLE_IDLE_AMPLITUDE)
          : 0;
      const capeHover = getBoardCharacterHoverOffset({
        capeNone: boardRigCapeNone.get(id),
        timeSeconds: t,
        phase,
      });
      applyBoardRigActionPositionOffset(id, rig);
      rig.root.position.y =
        baseY + (boardRigHopOffsets.get(id) ?? 0) + idleBob + buttWobble + capeHover;
      rig.root.rotation.x = boardRigActionPitchOffsets.get(id) ?? 0;
      rig.root.rotation.y =
        Math.sin(t * CHARACTER_IDLE_SWAY_RADIANS_PER_SECOND + phase) * 0.05 +
        (boardRigFacingYawOffsets.get(id) ?? 0) +
        (boardRigActionYawOffsets.get(id) ?? 0);
      rig.root.rotation.z = boardRigActionRollOffsets.get(id) ?? 0;
      rig.root.scale.setScalar(BOARD_RIG_SCALE * (boardRigActionScale.get(id) ?? 1));
    }
  });

  function setBoardRigActionPositionOffset(playerId: string, x: number, z: number): void {
    const offset = boardRigActionPositionOffsets.get(playerId);
    if (offset != null) {
      offset.set(x, z);
      return;
    }
    boardRigActionPositionOffsets.set(playerId, new THREE.Vector2(x, z));
  }

  function applyBoardRigActionPositionOffset(id: string, rig: CharacterPreviewRig): void {
    const target = boardRigActionPositionOffsets.get(id);
    const applied = boardRigAppliedActionPositionOffsets.get(id);
    const targetX = target?.x ?? 0;
    const targetZ = target?.y ?? 0;
    const appliedX = applied?.x ?? 0;
    const appliedZ = applied?.y ?? 0;
    const deltaX = targetX - appliedX;
    const deltaZ = targetZ - appliedZ;
    if (Math.abs(deltaX) > 0.000001 || Math.abs(deltaZ) > 0.000001) {
      rig.root.position.x += deltaX;
      rig.root.position.z += deltaZ;
    }
    if (target == null || (Math.abs(targetX) <= 0.000001 && Math.abs(targetZ) <= 0.000001)) {
      boardRigAppliedActionPositionOffsets.delete(id);
      return;
    }
    if (applied != null) {
      applied.set(targetX, targetZ);
    } else {
      boardRigAppliedActionPositionOffsets.set(id, new THREE.Vector2(targetX, targetZ));
    }
  }

  function clearBoardRigActionPositionOffset(id: string): void {
    const rig = boardRigs.get(id);
    const applied = boardRigAppliedActionPositionOffsets.get(id);
    if (rig != null && applied != null) {
      rig.root.position.x -= applied.x;
      rig.root.position.z -= applied.y;
    }
    boardRigActionPositionOffsets.delete(id);
    boardRigAppliedActionPositionOffsets.delete(id);
  }

  function clearBoardRigActionPositionOffsets(): void {
    for (const id of new Set([
      ...boardRigActionPositionOffsets.keys(),
      ...boardRigAppliedActionPositionOffsets.keys(),
    ])) {
      clearBoardRigActionPositionOffset(id);
    }
  }

  function setupLobbyCamera(): void {
    // cameraRig.camera may be absent in test/headless mode
    const cam = renderer.cameraRig?.camera;
    if (cam == null || mapHandle == null) return;
    if (cameraUserDirty && lobbyCameraApplied) return;
    const startingNode = getLobbyStartingNode();
    if (startingNode == null) return;
    const startIslandNodes = getStartingIslandNodes(startingNode.id);
    const framingNodes = startIslandNodes.length > 0 ? startIslandNodes : [startingNode];
    const focus = getBoardNodeWorldPosition(startingNode);
    focus.y = (boardHandle?.getSurfaceYForNode(startingNode.id) ?? focus.y) + 1.1;

    const gap = measureLobbyViewportGap();
    const diameter = Math.max(computeXZDiameter(framingNodes, 1.2), 3.25);
    lobbyCameraDistance = computeLobbyCameraDistance(diameter, gap.width / Math.max(gap.height, 1));
    lobbyCameraFocus = focus;
    cam.fov = LOBBY_CAMERA_FOV;
    cam.updateProjectionMatrix();
    renderer.cameraRig.setMode('spectator');
    applyLobbyCamera();
    lobbyCameraApplied = true;
    ensureLobbyCameraTunerOverlay();
  }

  function applyLobbyCamera(): void {
    const cam = renderer.cameraRig?.camera;
    if (cam == null || lobbyCameraFocus == null) return;
    if (lobbyCameraQuery.override != null) {
      applyLobbyCameraState(cam, orbitControls, lobbyCameraQuery.override);
      return;
    }
    if (mapHandle?.camera?.lobby != null) {
      applyLobbyCameraState(cam, orbitControls, mapHandle.camera.lobby);
      return;
    }
    const horizontal = Math.cos(lobbyCameraPitch) * lobbyCameraDistance;
    const position = new THREE.Vector3(
      lobbyCameraFocus.x + Math.sin(lobbyCameraAngle) * horizontal,
      lobbyCameraFocus.y + Math.sin(lobbyCameraPitch) * lobbyCameraDistance,
      lobbyCameraFocus.z + Math.cos(lobbyCameraAngle) * horizontal,
    );
    const lookAt = computeLobbyLookAtForGap(position, lobbyCameraFocus);
    cam.position.copy(position);
    cam.lookAt(lookAt);
    cam.updateProjectionMatrix();
    if (orbitControls != null) {
      orbitControls.target.copy(lookAt);
      orbitControls.update();
    }
  }

  function ensureLobbyCameraTunerOverlay(): void {
    const cam = renderer.cameraRig?.camera;
    if (!lobbyCameraTuneEnabled || cam == null || orbitControls == null) return;
    if (lobbyCameraTunerOverlay != null) return;
    lobbyCameraTunerOverlay = createLobbyCameraTunerOverlay({
      camera: cam,
      controls: orbitControls,
      mount: opts.host,
      baseCamera: mapHandle?.camera ?? null,
    });
  }

  function computeLobbyCameraDistance(diameter: number, safeAspect: number): number {
    const verticalTan = Math.tan(THREE.MathUtils.degToRad(LOBBY_CAMERA_FOV) / 2);
    const horizontalTan = verticalTan * Math.max(safeAspect, 0.35);
    const verticalDistance = diameter / (2 * verticalTan * LOBBY_CAMERA_FILL_RATIO);
    const horizontalDistance = diameter / (2 * horizontalTan * LOBBY_CAMERA_FILL_RATIO);
    return THREE.MathUtils.clamp(Math.max(verticalDistance, horizontalDistance), 6.5, 12);
  }

  function measureLobbyViewportGap(): { width: number; height: number; centerX: number } {
    const viewportWidth = Math.max(window.innerWidth, 1);
    const viewportHeight = Math.max(window.innerHeight, 1);
    const touchRoot = document.querySelector('.screen-lobby-host--touch-customize');
    if (touchRoot instanceof HTMLElement) {
      const drawer = document.querySelector(
        '.screen-lobby-host--touch-customize .lobby-responsive__inline-select',
      );
      const drawerTop =
        drawer instanceof HTMLElement ? drawer.getBoundingClientRect().top : viewportHeight;
      return {
        width: viewportWidth,
        height: Math.max(drawerTop, viewportHeight * 0.4),
        centerX: viewportWidth / 2,
      };
    }
    const leftPanel = document.querySelector('.lobby-responsive__panel-wrapper');
    const rightRail = document.querySelector('.lobby-responsive__inline-select');
    const leftEdge = leftPanel instanceof HTMLElement ? leftPanel.getBoundingClientRect().right : 0;
    const rightEdge =
      rightRail instanceof HTMLElement ? rightRail.getBoundingClientRect().left : viewportWidth;
    const gapWidth = rightEdge - leftEdge;
    if (gapWidth > viewportWidth * 0.25) {
      return {
        width: gapWidth,
        height: viewportHeight,
        centerX: leftEdge + gapWidth / 2,
      };
    }
    return {
      width: viewportWidth,
      height: viewportHeight,
      centerX: viewportWidth / 2,
    };
  }

  function computeLobbyLookAtForGap(
    cameraPosition: THREE.Vector3,
    focus: THREE.Vector3,
  ): THREE.Vector3 {
    const gap = measureLobbyViewportGap();
    const viewportWidth = Math.max(window.innerWidth, 1);
    const offsetNdc = (gap.centerX - viewportWidth / 2) / (viewportWidth / 2);
    if (Math.abs(offsetNdc) < 0.001) return focus;

    const viewDir = focus.clone().sub(cameraPosition).normalize();
    const cameraRight = new THREE.Vector3().crossVectors(viewDir, new THREE.Vector3(0, 1, 0));
    if (cameraRight.lengthSq() < 0.0001) return focus;
    cameraRight.normalize();

    const horizontalHalfWidth =
      Math.tan(THREE.MathUtils.degToRad(LOBBY_CAMERA_FOV) / 2) *
      Math.max(renderer.cameraRig.camera.aspect, 0.1) *
      lobbyCameraDistance;
    return focus.clone().sub(cameraRight.multiplyScalar(offsetNdc * horizontalHalfWidth));
  }

  function focusLobbyCustomizationCamera(request: CharacterCustomizeCameraRequest): void {
    if (inWarmupArena) {
      clearLobbyCustomizationCameraState();
      return;
    }
    if (request == null) {
      restoreLobbyCustomizationCamera();
      return;
    }
    const { playerId, view } = request;
    const camera = renderer.cameraRig?.camera;
    const rig = rosterRigs.get(playerId) ?? null;
    if (camera == null || rig == null) return;
    const rigPositionKey = customizationRigPositionKey(rig);
    if (
      activeLobbyCustomizationCameraTarget?.playerId === playerId &&
      activeLobbyCustomizationCameraTarget.view === view &&
      activeLobbyCustomizationCameraTarget.rigPositionKey === rigPositionKey
    ) {
      return;
    }
    activeLobbyCustomizationPlayerId = playerId;
    activeLobbyCustomizationCameraTarget = { playerId, view, rigPositionKey };

    const focus = computeCustomizationCameraLookAt(rig, view);
    const position = computeFallbackCustomizationCameraPosition(rig, focus, view);
    const lookAt = computeCustomizationLookAtForPreviewRect(position, focus);
    const fov = computeCustomizationCameraFov(rig, position, focus);

    cameraUserDirty = true;
    warmupCameraView = 'arena';
    if (inWarmupArena) warmupArenaCameraFocused = true;
    renderer.cameraRig.setMode('spectator');
    tweenCustomizationCameraTo(position, lookAt, fov);
  }

  function computeCustomizationLookAtForPreviewRect(
    cameraPosition: THREE.Vector3,
    focus: THREE.Vector3,
  ): THREE.Vector3 {
    const touchRoot = document.querySelector('.screen-lobby-host--touch-customize');
    if (!(touchRoot instanceof HTMLElement)) return focus;

    const visible = measureLobbyViewportGap();
    const viewportWidth = Math.max(window.innerWidth, 1);
    const viewportHeight = Math.max(window.innerHeight, 1);
    const offsetX = (visible.centerX - viewportWidth / 2) / (viewportWidth / 2);
    const offsetY = (visible.height / 2 - viewportHeight / 2) / (viewportHeight / 2);
    if (Math.abs(offsetX) < 0.001 && Math.abs(offsetY) < 0.001) return focus;

    const viewDir = focus.clone().sub(cameraPosition).normalize();
    const cameraRight = new THREE.Vector3().crossVectors(viewDir, new THREE.Vector3(0, 1, 0));
    if (cameraRight.lengthSq() < 0.0001) return focus;
    cameraRight.normalize();
    const cameraUp = new THREE.Vector3().crossVectors(cameraRight, viewDir).normalize();
    const distance = Math.max(cameraPosition.distanceTo(focus), 0.1);
    const verticalHalfHeight = Math.tan(THREE.MathUtils.degToRad(LOBBY_CAMERA_FOV) / 2) * distance;
    const horizontalHalfWidth =
      verticalHalfHeight * Math.max(renderer.cameraRig.camera.aspect, 0.1);

    return focus
      .clone()
      .sub(cameraRight.multiplyScalar(offsetX * horizontalHalfWidth))
      .add(cameraUp.multiplyScalar(offsetY * verticalHalfHeight));
  }

  function clearLobbyCustomizationCameraState(): void {
    customizationCameraHandle?.dispose();
    customizationCameraHandle = null;
    activeLobbyCustomizationPlayerId = null;
    activeLobbyCustomizationCameraTarget = null;
  }

  function customizationRigPositionKey(rig: CharacterPreviewRig): string {
    rig.root.updateWorldMatrix(true, false);
    const position = rig.root.getWorldPosition(new THREE.Vector3());
    return `${position.x.toFixed(4)},${position.y.toFixed(4)},${position.z.toFixed(4)}`;
  }

  function restoreLobbyCustomizationCamera(): void {
    clearLobbyCustomizationCameraState();
    cameraUserDirty = false;
    lobbyCameraApplied = false;
    setupLobbyCamera();
  }

  function computeCustomizationCameraLookAt(
    rig: CharacterPreviewRig,
    view: CharacterCustomizeCameraView,
  ): THREE.Vector3 {
    const box = new THREE.Box3().setFromObject(rig.root);
    if (box.isEmpty()) {
      const fallback = new THREE.Vector3();
      rig.root.getWorldPosition(fallback);
      fallback.y += view === 'front' ? 1.1 : 0.75;
      return fallback;
    }

    const center = box.getCenter(new THREE.Vector3());
    const height = Math.max(box.max.y - box.min.y, 0.5);
    center.y = box.min.y + height * (view === 'front' ? 0.72 : 0.5);
    return center;
  }

  function computeFallbackCustomizationCameraPosition(
    rig: CharacterPreviewRig,
    lookAt: THREE.Vector3,
    view: CharacterCustomizeCameraView,
  ): THREE.Vector3 {
    const rootQuaternion = rig.root.getWorldQuaternion(new THREE.Quaternion());
    const facing = new THREE.Vector3(0, 0, view === 'front' ? 1 : -1)
      .applyQuaternion(rootQuaternion)
      .normalize();
    const up = new THREE.Vector3(0, 1, 0);
    const box = new THREE.Box3().setFromObject(rig.root);
    const size = box.isEmpty() ? new THREE.Vector3(1, 2, 1) : box.getSize(new THREE.Vector3());
    const diameter = Math.max(size.x, size.y, size.z, 1);
    const distance = diameter * (view === 'front' ? 1.55 : 1.75);
    const height = view === 'front' ? 0.08 : 0.26;
    return lookAt.clone().addScaledVector(facing, distance).addScaledVector(up, height);
  }

  function computeCustomizationCameraFov(
    rig: CharacterPreviewRig,
    position: THREE.Vector3,
    lookAt: THREE.Vector3,
  ): number {
    const box = new THREE.Box3().setFromObject(rig.root);
    if (box.isEmpty()) return 34;
    const size = box.getSize(new THREE.Vector3());
    const visible = measureLobbyViewportGap();
    const aspect = Math.max(visible.width / Math.max(visible.height, 1), 0.35);
    const distance = Math.max(position.distanceTo(lookAt), 0.1);
    const verticalFov = THREE.MathUtils.radToDeg(
      2 * Math.atan((Math.max(size.y, 1.1) * 0.62) / distance),
    );
    const horizontalFov = THREE.MathUtils.radToDeg(
      2 * Math.atan((Math.max(size.x, size.z, 0.8) * 0.62) / (distance * aspect)),
    );
    return THREE.MathUtils.clamp(Math.max(verticalFov, horizontalFov), 26, 42);
  }

  function tweenCustomizationCameraTo(
    position: THREE.Vector3,
    lookAt: THREE.Vector3,
    fov: number,
  ): void {
    const camera = renderer.cameraRig.camera;
    customizationCameraHandle?.dispose();

    const startLookAt = camera.position
      .clone()
      .add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(4));
    const proxy = {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z,
      lx: startLookAt.x,
      ly: startLookAt.y,
      lz: startLookAt.z,
      fov: camera.fov,
    };
    const handle = gsapRegistry.create('customization-camera');
    customizationCameraHandle = handle;

    const applyPose = (): void => {
      camera.position.set(proxy.x, proxy.y, proxy.z);
      camera.lookAt(proxy.lx, proxy.ly, proxy.lz);
      camera.fov = proxy.fov;
      camera.updateProjectionMatrix();
      if (orbitControls != null) {
        orbitControls.target.set(proxy.lx, proxy.ly, proxy.lz);
        orbitControls.update();
      }
      lobbyCameraTunerOverlay?.update();
    };

    handle.timeline.to(
      proxy,
      {
        x: position.x,
        y: position.y,
        z: position.z,
        lx: lookAt.x,
        ly: lookAt.y,
        lz: lookAt.z,
        fov,
        duration: 0.35,
        ease: 'power2.out',
        onUpdate: applyPose,
      },
      0,
    );
    handle.timeline.call(
      () => {
        if (customizationCameraHandle !== handle) return;
        proxy.x = position.x;
        proxy.y = position.y;
        proxy.z = position.z;
        proxy.lx = lookAt.x;
        proxy.ly = lookAt.y;
        proxy.lz = lookAt.z;
        proxy.fov = fov;
        applyPose();
        customizationCameraHandle = null;
        handle.dispose();
      },
      undefined,
      0.35,
    );
  }

  function setOrbitTarget(target: THREE.Vector3): void {
    if (orbitControls == null) return;
    orbitControls.target.copy(target);
    orbitControls.update();
  }

  function setOrbitTargetToBoardCentroid(nodes: readonly BoardNode[]): void {
    const centroid = computeBoardCentroidFocus(nodes);
    if (centroid == null) return;
    setOrbitTarget(centroid);
  }

  function computeBoardCentroidFocus(nodes: readonly BoardNode[]): THREE.Vector3 | null {
    const playableNodes = nodes.filter((node) => node.type !== 'pathing');
    const sourceNodes = playableNodes.length > 0 ? playableNodes : nodes;
    if (sourceNodes.length === 0) return null;
    const centroid = sourceNodes.reduce(
      (acc, node) => acc.add(getBoardNodeWorldPosition(node)),
      new THREE.Vector3(),
    );
    centroid.multiplyScalar(1 / sourceNodes.length);
    centroid.y += 2;
    return centroid;
  }

  function getWarmupArenaIslandId(): string {
    return mapHandle?.schema.spectatorArenaIslandId ?? 'island-14';
  }

  function nodesForIsland(islandId: string): BoardNode[] {
    if (mapHandle == null) return [];
    return (mapHandle.schema.nodes as readonly BoardNode[]).filter(
      (node) => node.type !== 'pathing' && node.id.startsWith(`${islandId}-`),
    );
  }

  function centroidForNodes(nodes: readonly BoardNode[], yOffset: number): THREE.Vector3 | null {
    if (nodes.length === 0) return null;
    const centroid = nodes.reduce(
      (acc, node) => acc.add(getBoardNodeWorldPosition(node)),
      new THREE.Vector3(),
    );
    centroid.multiplyScalar(1 / nodes.length);
    centroid.y += yOffset;
    return centroid;
  }

  function focusWarmupArenaCamera(options: { force?: boolean } = {}): void {
    const force = options.force === true;
    if (force) {
      warmupCameraView = 'arena';
      cameraUserDirty = false;
    }
    if (
      mapHandle == null ||
      (!force && (warmupArenaCameraFocused || cameraUserDirty || warmupCameraView === 'mainland'))
    ) {
      return;
    }
    const focus = centroidForNodes(nodesForIsland(getWarmupArenaIslandId()), 1.35);
    const camera = renderer.cameraRig?.camera;
    if (focus == null || camera == null) return;
    warmupCameraView = 'arena';
    warmupArenaCameraFocused = true;
    renderer.cameraRig.setMode('spectator');
    const position = focus.clone().add(new THREE.Vector3(0, 4.8, 6.2));
    camera.position.copy(position);
    camera.lookAt(focus);
    camera.updateProjectionMatrix();
    if (force) {
      orbitTargetFollower?.snapTo(focus);
    } else {
      setOrbitTarget(focus);
    }
  }

  function focusWarmupMainlandCamera(): void {
    warmupCameraView = 'mainland';
    if (mapHandle == null) return;
    const arenaIslandId = getWarmupArenaIslandId();
    const snapshotNodeIds = new Set(latestArenaSnapshotPlayers.map((player) => player.nodeId));
    const snapshotNodes = (mapHandle.schema.nodes as readonly BoardNode[]).filter((node) =>
      snapshotNodeIds.has(node.id),
    );
    const mainlandNodes =
      snapshotNodes.length > 0
        ? snapshotNodes
        : (mapHandle.schema.nodes as readonly BoardNode[]).filter(
            (node) =>
              node.type !== 'pathing' &&
              !node.id.startsWith(`${arenaIslandId}-`) &&
              node.id.startsWith('island-'),
          );
    const focus = centroidForNodes(mainlandNodes, 1.6);
    if (focus == null) return;
    warmupArenaCameraFocused = false;
    orbitTargetFollower?.follow(focus);
  }

  function handleWarmupArenaBackCamera(): void {
    focusWarmupArenaCamera({ force: true });
  }

  function getSpawnNodes(): BoardNode[] {
    if (mapHandle == null) return [];
    const nodes = mapHandle.schema.nodes as readonly BoardNode[];
    const byId = new Map(nodes.map((node) => [node.id, node]));
    return mapHandle.schema.spawnNodeIds
      .map((id) => byId.get(id))
      .filter((node): node is BoardNode => node != null)
      .slice(0, MAX_PLAYERS);
  }

  function getLobbyStartingNode(): BoardNode | null {
    if (mapHandle == null) return null;
    const nodes = mapHandle.schema.nodes as readonly BoardNode[];
    const byId = new Map(nodes.map((node) => [node.id, node]));
    const directNodeId =
      localPlayerId != null ? latestGameState?.players.get(localPlayerId)?.nodeId : null;
    if (directNodeId != null && directNodeId !== '') {
      return byId.get(directNodeId) ?? null;
    }
    if (localPlayerId != null && latestGameState != null) {
      const playerIndex = latestGameState.turnOrder.indexOf(localPlayerId);
      const spawnId =
        playerIndex >= 0
          ? mapHandle.schema.spawnNodeIds[playerIndex % mapHandle.schema.spawnNodeIds.length]
          : null;
      if (spawnId != null) return byId.get(spawnId) ?? null;
    }
    const firstSpawnId = mapHandle.schema.spawnNodeIds[0];
    return firstSpawnId != null ? (byId.get(firstSpawnId) ?? null) : null;
  }

  function getStartingIslandNodes(firstSpawnNodeId: string): BoardNode[] {
    if (mapHandle == null) return [];
    const islandId = /^(island-\d+)-/.exec(firstSpawnNodeId)?.[1];
    if (islandId == null) return [];
    return (mapHandle.schema.nodes as readonly BoardNode[]).filter((node) =>
      node.id.startsWith(`${islandId}-`),
    );
  }

  function computeXZDiameter(nodes: readonly BoardNode[], padding: number): number {
    if (nodes.length === 0) return 4.5 + padding;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const node of nodes) {
      const position = getBoardNodeWorldPosition(node);
      minX = Math.min(minX, position.x);
      maxX = Math.max(maxX, position.x);
      minZ = Math.min(minZ, position.z);
      maxZ = Math.max(maxZ, position.z);
    }
    return Math.max(maxX - minX, maxZ - minZ) + padding;
  }

  function rebuildLobbyRoster(
    players: ReadonlyArray<{
      id: string;
      nodeId: string;
      customization: import('../shared/game/types').CharacterCustomization;
    }>,
  ): void {
    if (characterAdapter == null || mapHandle == null || boardHandle == null) return;
    const spawnNodes = getSpawnNodes();
    if (spawnNodes.length === 0) return;
    const presentIds = new Set(players.map((p) => p.id));
    // Remove rigs for players who left
    for (const [id, rig] of rosterRigs) {
      if (!presentIds.has(id)) {
        if (activeLobbyCustomizationPlayerId === id) {
          restoreLobbyCustomizationCamera();
        }
        removeHeldItemObject(id);
        emoteRuntime?.clearPlayer(id);
        characterEyeExpressions.cleanupPlayer(id);
        lobbyRosterParent.remove(rig.root);
        rig.dispose();
        rosterRigs.delete(id);
        rosterIdleBaseY.delete(id);
        rosterPhaseOffsets.delete(id);
        rosterRigAnimationOverrides.delete(id);
        lobbyAmbientAnimationScheduler.clearPlayer(id);
      }
    }
    const occupiedNodeIds = new Set<string>();

    // Build / update rigs at assigned spawn tiles.
    players.forEach((p, i) => {
      let rig = rosterRigs.get(p.id);
      const isNew = rig == null;
      if (!rig) {
        rig = characterAdapter!.buildRig(p.customization);
        lobbyRosterParent.add(rig.root);
        rosterRigs.set(p.id, rig);
      } else {
        emoteRuntime?.clearPlayer(p.id);
        rig.applyCustomization(p.customization);
      }
      if (activeHeldItemPreview?.playerId === p.id && !heldItemObjects.has(p.id)) {
        void attachHeldItemObject(p.id, activeHeldItemPreview.itemId, activeHeldItemPreview.token);
      }
      const node =
        findLobbyNodeForPlayer(p.nodeId, i, spawnNodes) ?? spawnNodes[i % spawnNodes.length]!;
      occupiedNodeIds.add(node.id);
      const placement = computeBoardRigBasePlacement(rig, node);
      if (isNew || getCharacterRigOpacity(rig) < 0.999) {
        setCharacterRigOpacity(rig, 1);
      }
      characterEyeExpressions.refresh(p.id);
      rig.root.position.set(placement.x, placement.y, placement.z);
      rig.root.rotation.y = 0; // facing camera
      rig.root.scale.setScalar(BOARD_RIG_SCALE);
      // Record idle animation anchors for new rigs
      if (isNew) {
        rosterIdleBaseY.set(p.id, placement.y);
        rosterPhaseOffsets.set(p.id, i * 0.7);
      } else {
        rosterIdleBaseY.set(p.id, placement.y);
      }
    });

    syncGhostRigs(spawnNodes, occupiedNodeIds);
  }

  function findLobbyNodeForPlayer(
    nodeId: string,
    index: number,
    spawnNodes: readonly BoardNode[],
  ): BoardNode | null {
    if (mapHandle == null || spawnNodes.length === 0) return null;
    const byId = new Map(
      (mapHandle.schema.nodes as readonly BoardNode[]).map((node) => [node.id, node]),
    );
    if (nodeId !== '') {
      const assigned = byId.get(nodeId);
      if (assigned != null) return assigned;
    }
    return spawnNodes[index % spawnNodes.length] ?? null;
  }

  const EMPTY_SLOT_GHOST_OPACITY = 0.22;
  const EMPTY_SLOT_GHOST_BODY_OPACITY = 0.025;
  const EMPTY_SLOT_GHOST_BODY_TINT = 0xffffff;
  const EMPTY_SLOT_GHOST_OPACITY_EPSILON = 0.005;

  function syncGhostRigs(
    spawnNodes: readonly BoardNode[],
    occupiedNodeIds: ReadonlySet<string>,
  ): void {
    const targetNodeIds = new Set(spawnNodes.map((node) => node.id));
    for (const nodeId of targetNodeIds) {
      if (occupiedNodeIds.has(nodeId)) {
        fadeOutGhost(nodeId);
      }
    }
    for (const [nodeId, rig] of ghostRigs) {
      if (!targetNodeIds.has(nodeId)) {
        lobbyRosterParent.remove(rig.root);
        rig.dispose();
        ghostRigs.delete(nodeId);
        ghostIdleBaseY.delete(nodeId);
        ghostPhaseOffsets.delete(nodeId);
        ghostRigAnimationOverrides.delete(nodeId);
        ghostFadeHandles.get(nodeId)?.dispose();
        ghostFadeHandles.delete(nodeId);
      }
    }
    spawnNodes.forEach((node, index) => {
      if (occupiedNodeIds.has(node.id)) return;
      let rig = ghostRigs.get(node.id);
      const isNew = rig == null;
      if (!rig) {
        rig = buildGhostRig();
        setGhostRigVisualOpacity(rig, 0);
        setCharacterRigEyesVisible(rig, false);
        lobbyRosterParent.add(rig.root);
        ghostRigs.set(node.id, rig);
      }
      const placement = computeBoardRigBasePlacement(rig, node);
      applyGhostRigStaticVisuals(rig);
      rig.root.position.set(placement.x, placement.y, placement.z);
      rig.root.rotation.y = 0;
      rig.root.scale.setScalar(BOARD_RIG_SCALE);
      ghostIdleBaseY.set(node.id, placement.y);
      ghostPhaseOffsets.set(node.id, (playersIndexSeed(node.id, index) + 4) * 0.5);
      if (
        isNew ||
        Math.abs(getGhostRigVisualOpacity(rig) - EMPTY_SLOT_GHOST_OPACITY) >
          EMPTY_SLOT_GHOST_OPACITY_EPSILON
      ) {
        fadeGhostTo(node.id, rig, EMPTY_SLOT_GHOST_OPACITY, null);
      }
    });
  }

  function applyGhostRigStaticVisuals(rig: CharacterPreviewRig): void {
    setCharacterRigColorableTint(rig, EMPTY_SLOT_GHOST_BODY_TINT);
    setGhostRigEyesHidden(rig);
  }

  function setGhostRigEyesHidden(rig: CharacterPreviewRig): void {
    setCharacterRigEyesVisible(rig, false);
    queueMicrotask(() => setCharacterRigEyesVisible(rig, false));
  }

  function applyGhostRigVisualStyle(rig: CharacterPreviewRig): void {
    applyGhostRigStaticVisuals(rig);
    setGhostRigVisualOpacity(rig, EMPTY_SLOT_GHOST_OPACITY);
  }

  function setGhostRigVisualOpacity(rig: CharacterPreviewRig, opacity: number): void {
    rig.root.userData.emptySlotGhostOpacity = opacity;
    setCharacterRigOpacity(rig, opacity);
    setCharacterRigColorableGhostOpacity(rig, ghostBodyOpacityFor(opacity));
  }

  function getGhostRigVisualOpacity(rig: CharacterPreviewRig): number {
    const value = rig.root.userData.emptySlotGhostOpacity;
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  function playersIndexSeed(nodeId: string, fallback: number): number {
    const match = /(\d+)$/.exec(nodeId);
    return match ? Number(match[1]) : fallback;
  }

  function buildGhostRig(): CharacterPreviewRig {
    const rig = characterAdapter!.buildRig({
      color: 'white',
      head: 'horse',
      eyes: 'dots',
      cape: 'none',
    });
    applyGhostRigVisualStyle(rig);
    return rig;
  }

  function fadeGhostTo(
    nodeId: string,
    rig: CharacterPreviewRig,
    targetOpacity: number,
    onComplete: (() => void) | null,
  ): void {
    ghostFadeHandles.get(nodeId)?.dispose();
    const proxy = { opacity: getGhostRigVisualOpacity(rig) };
    const handle = gsapRegistry.create(`lobby-ghost:${nodeId}:fade`);
    ghostFadeHandles.set(nodeId, handle);
    handle.timeline.to(
      proxy,
      {
        opacity: targetOpacity,
        duration: 0.25,
        ease: 'sine.out',
        onUpdate: () => {
          setGhostRigVisualOpacity(rig, proxy.opacity);
          applyGhostRigStaticVisuals(rig);
        },
        onComplete: () => {
          if (targetOpacity <= EMPTY_SLOT_GHOST_OPACITY_EPSILON) {
            setGhostRigVisualOpacity(rig, 0);
            setGhostRigEyesHidden(rig);
          } else {
            applyGhostRigVisualStyle(rig);
          }
          if (ghostFadeHandles.get(nodeId) === handle) {
            ghostFadeHandles.delete(nodeId);
          }
          handle.dispose();
          onComplete?.();
        },
      },
      0,
    );
  }

  function ghostBodyOpacityFor(rigOpacity: number): number {
    if (EMPTY_SLOT_GHOST_OPACITY <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, rigOpacity / EMPTY_SLOT_GHOST_OPACITY));
    return EMPTY_SLOT_GHOST_BODY_OPACITY * ratio;
  }

  function fadeOutGhost(nodeId: string): void {
    const rig = ghostRigs.get(nodeId);
    if (rig == null) return;
    fadeGhostTo(nodeId, rig, 0, () => {
      if (ghostRigs.get(nodeId) !== rig) return;
      lobbyRosterParent.remove(rig.root);
      rig.dispose();
      ghostRigs.delete(nodeId);
      ghostIdleBaseY.delete(nodeId);
      ghostPhaseOffsets.delete(nodeId);
      ghostRigAnimationOverrides.delete(nodeId);
    });
  }

  function customizationSignature(customization: CharacterCustomization): string {
    return [customization.color, customization.head, customization.eyes, customization.cape].join(
      '|',
    );
  }

  function rebuildBoardRoster(
    players: ReadonlyArray<{
      id: string;
      nodeId: string;
      customization: CharacterCustomization;
    }>,
  ): void {
    if (characterAdapter == null || mapHandle == null) return;
    const nodes = mapHandle.schema.nodes as readonly BoardNode[];
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const presentIds = new Set(players.map((p) => p.id));
    const resolveBoardRosterNode = (nodeId: string, index: number): BoardNode | null => {
      let node = nodeById.get(nodeId);
      if (node != null) return node;
      const fallbackIds = mapHandle!.schema.spawnNodeIds;
      if (fallbackIds != null && fallbackIds.length > 0) {
        const fallbackId = fallbackIds[index % fallbackIds.length];
        node = fallbackId == null ? undefined : nodeById.get(fallbackId);
      }
      return node ?? null;
    };

    // Remove rigs for players who left
    for (const [id, rig] of boardRigs) {
      if (!presentIds.has(id)) {
        removeHeldItemObject(id);
        emoteRuntime?.clearPlayer(id);
        characterStatusEffects.cleanupPlayer(id);
        characterEyeExpressions.cleanupPlayer(id);
        boardRosterParent.remove(rig.root);
        rig.dispose();
        boardMoveHandles.get(id)?.dispose();
        boardMoveHandles.delete(id);
        clearBoardRigFacingPreview(id);
        boardRigs.delete(id);
        boardRigCustomizationSignatures.delete(id);
        boardRigBaseY.delete(id);
        boardRigPhases.delete(id);
        boardRigCapeNone.delete(id);
        boardRigNodeIds.delete(id);
        boardRigVisualOrder.delete(id);
        boardRigHopOffsets.delete(id);
        boardRigFacingYawOffsets.delete(id);
        boardRigAnimationOverrides.delete(id);
        boardRigActionYawOffsets.delete(id);
        boardRigActionPitchOffsets.delete(id);
        boardRigActionRollOffsets.delete(id);
        boardRigActionScale.delete(id);
        clearBoardRigActionPositionOffset(id);
        itemUsePresentationHandles.get(id)?.dispose();
        itemUsePresentationHandles.delete(id);
        itemImpactPresentationHandles.get(id)?.dispose();
        itemImpactPresentationHandles.delete(id);
        heldItemObjectModes.delete(id);
        heldItemUseTokens.delete(id);
        heldMovementPlayers.delete(id);
        animatingMovementPlayers.delete(id);
        queuedMovementSteps.delete(id);
        movementProgressByPlayer.delete(id);
        pendingForkOptionsByPlayer.delete(id);
        pendingRollSubmissionKeysByPlayer.delete(id);
      }
    }

    players.forEach((p, i) => {
      boardRigVisualOrder.set(p.id, i);
    });

    const resolvedNodeIdsByPlayer = new Map<string, string>();
    players.forEach((p, i) => {
      const isNew = !boardRigs.has(p.id);
      const queuedSteps = queuedMovementSteps.get(p.id) ?? [];
      const movementBusy =
        !isNew &&
        (heldMovementPlayers.has(p.id) ||
          animatingMovementPlayers.has(p.id) ||
          queuedSteps.length > 0);
      const queuedStartNodeId = isNew && queuedSteps.length > 0 ? queuedSteps[0]!.fromNodeId : null;
      const preferredNodeId = movementBusy
        ? (boardRigNodeIds.get(p.id) ?? p.nodeId)
        : (queuedStartNodeId ?? p.nodeId);
      const node = resolveBoardRosterNode(preferredNodeId, i);
      if (node != null) resolvedNodeIdsByPlayer.set(p.id, node.id);
    });

    // Build / position / customize
    players.forEach((p, i) => {
      let rig = boardRigs.get(p.id);
      const isNew = rig == null;
      const nextCustomizationSignature = customizationSignature(p.customization);
      if (!rig) {
        rig = characterAdapter!.buildRig(p.customization);
        rig.root.scale.setScalar(BOARD_RIG_SCALE);
        boardRosterParent.add(rig.root);
        boardRigs.set(p.id, rig);
        boardRigCustomizationSignatures.set(p.id, nextCustomizationSignature);
        boardRigPhases.set(p.id, i * 0.7);
      } else {
        const previousCustomizationSignature = boardRigCustomizationSignatures.get(p.id);
        if (previousCustomizationSignature !== nextCustomizationSignature) {
          emoteRuntime?.clearPlayer(p.id);
          rig.applyCustomization(p.customization);
          boardRigCustomizationSignatures.set(p.id, nextCustomizationSignature);
          characterStatusEffects.refresh(p.id);
          characterEyeExpressions.refresh(p.id);
        }
      }
      if (activeHeldItemPreview?.playerId === p.id && !heldItemObjects.has(p.id)) {
        void attachHeldItemObject(p.id, activeHeldItemPreview.itemId, activeHeldItemPreview.token);
      }
      if (isNew || getCharacterRigOpacity(rig) < 0.999) {
        setCharacterRigOpacity(rig, 1);
      }
      characterEyeExpressions.refresh(p.id);
      boardRigCapeNone.set(p.id, p.customization.cape === 'none');

      const queuedSteps = queuedMovementSteps.get(p.id) ?? [];
      const movementBusy =
        !isNew &&
        (heldMovementPlayers.has(p.id) ||
          animatingMovementPlayers.has(p.id) ||
          queuedSteps.length > 0);
      if (movementBusy) {
        if (
          queuedSteps.length > 0 &&
          !heldMovementPlayers.has(p.id) &&
          !animatingMovementPlayers.has(p.id)
        ) {
          void processMovementQueue(p.id);
        }
        return;
      }

      // Look up tile position by nodeId; fallback to spawnNodeIds round-robin if empty
      const resolvedNodeId = resolvedNodeIdsByPlayer.get(p.id);
      const node = resolvedNodeId == null ? null : nodeById.get(resolvedNodeId);
      if (node == null) {
        console.warn(
          `[smashparty] board rig: no node found for ${p.id} (nodeId="${p.nodeId}"). Rig left at origin.`,
        );
        return;
      }

      const placement = computeBoardRigPlacement(p.id, rig, node, resolvedNodeIdsByPlayer);
      const previousNodeId = boardRigNodeIds.get(p.id);
      const shouldTween = !isNew && previousNodeId != null && previousNodeId !== node.id;
      boardRigNodeIds.set(p.id, node.id);
      if (shouldTween) {
        void tweenBoardRigTo(p.id, rig, placement);
      } else {
        boardMoveHandles.get(p.id)?.dispose();
        boardMoveHandles.delete(p.id);
        clearBoardRigFacingPreview(p.id);
        boardRigHopOffsets.delete(p.id);
        boardRigBaseY.set(p.id, placement.y);
        if (!boardRigFacingYawOffsets.has(p.id)) boardRigFacingYawOffsets.set(p.id, 0);
        rig.root.position.set(placement.x, placement.y, placement.z);
      }
      rig.root.rotation.y = 0;
      if (
        queuedSteps.length > 0 &&
        !heldMovementPlayers.has(p.id) &&
        !animatingMovementPlayers.has(p.id)
      ) {
        void processMovementQueue(p.id);
      }
    });
  }

  function loadShadowStalkerTemplate(): Promise<THREE.Group | null> {
    if (shadowStalkerTemplatePromise != null) return shadowStalkerTemplatePromise;
    if (gltfLoader == null) return Promise.resolve(null);
    shadowStalkerTemplatePromise = gltfLoader
      .load(runtimeAssetUrl('/assets/models/npc-shadow-stalker.glb'))
      .then((group) => group)
      .catch((err: unknown) => {
        console.warn('[smashparty] failed to load Shadow Stalker model:', err);
        return null;
      });
    return shadowStalkerTemplatePromise;
  }

  function buildShadowStalkerFallback(): THREE.Group {
    const group = new THREE.Group();
    const material = new THREE.MeshStandardMaterial({
      color: 0x2a2038,
      emissive: 0x180a28,
      roughness: 0.6,
      transparent: true,
      opacity: 0.82,
    });
    const body = new THREE.Mesh(new THREE.ConeGeometry(0.42, 1.15, 7), material);
    body.position.y = 0.65;
    group.add(body);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.26, 12, 8), material.clone());
    head.position.y = 1.28;
    group.add(head);
    return group;
  }

  function cloneShadowStalkerModel(template: THREE.Group | null): THREE.Group {
    const root = template?.clone(true) ?? buildShadowStalkerFallback();
    root.name = 'npc-shadow-stalker';
    root.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        const material = child.material;
        if (Array.isArray(material)) child.material = material.map((mat) => mat.clone());
        else child.material = material.clone();
      }
    });
    root.scale.setScalar(0.01);
    return root;
  }

  function colorForShadowStalkerSummoner(summonerId: string | null | undefined): number {
    const player = summonerId == null ? null : latestGameState?.players.get(summonerId);
    return player?.color ?? 0x6b35c8;
  }

  function shadowStalkerPlacementForNode(nodeId: string): THREE.Vector3 | null {
    const node = findMapNode(nodeId);
    if (node == null) return null;
    const position = getBoardNodeWorldPosition(node);
    const surfaceY =
      boardHandle?.getTilePadTopYForNode(node.id) ?? boardHandle?.getSurfaceYForNode(node.id);
    position.y = (surfaceY ?? position.y) + SHADOW_STALKER_HOVER_HEIGHT;
    return position;
  }

  function disposeShadowStalkerRecord(record: {
    root: THREE.Group;
    animationHandle: ReturnType<GsapRegistry['create']> | null;
    mistMaterials: THREE.SpriteMaterial[];
  }): void {
    record.animationHandle?.dispose();
    boardNpcParent.remove(record.root);
    disposeSkillshotGroupLike(record.root);
    for (const material of record.mistMaterials) material.dispose();
  }

  function buildShadowStalkerMist(color: number): {
    group: THREE.Group;
    materials: THREE.SpriteMaterial[];
  } {
    const group = new THREE.Group();
    group.name = 'shadow-stalker-mist';
    const smokeTexture = getItemVfxSmokeTexture();
    const materials: THREE.SpriteMaterial[] = [];
    const tint = new THREE.Color(color).lerp(new THREE.Color(0x11071d), 0.38);
    const noise = (seed: number) => {
      const value = Math.sin(seed * 12.9898) * 43758.5453;
      return value - Math.floor(value);
    };

    for (let index = 0; index < SHADOW_STALKER_MIST_SPRITES; index += 1) {
      const angle = noise(index * 3 + 1) * Math.PI * 2;
      const radius = 0.14 + noise(index * 5 + 2) * 0.82;
      const y = -0.44 + noise(index * 7 + 3) * 0.66;
      const scale = 0.44 + noise(index * 11 + 4) * 0.62;
      const material = new THREE.SpriteMaterial({
        map: smokeTexture,
        color: tint,
        transparent: true,
        alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
        opacity: 0.34 + noise(index * 13 + 5) * 0.28,
        blending: THREE.NormalBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.name = `shadow-stalker-mist:${index}`;
      sprite.position.set(Math.cos(angle) * radius, y, Math.sin(angle) * radius * 0.72);
      sprite.scale.set(scale * 1.35, scale, 1);
      sprite.userData.baseY = y;
      group.add(sprite);
      materials.push(material);
    }

    return { group, materials };
  }

  function positionShadowStalkerModel(
    record: {
      root: THREE.Group;
      baseY: number;
      nodeId?: string;
    },
    nodeId: string,
  ): boolean {
    const position = shadowStalkerPlacementForNode(nodeId);
    if (position == null) return false;
    record.baseY = position.y;
    record.root.position.set(position.x, position.y, position.z);
    record.nodeId = nodeId;
    return true;
  }

  function yawForShadowStalkerDirection(from: THREE.Vector3, to: THREE.Vector3): number | null {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    if (Math.hypot(dx, dz) < 0.001) return null;
    return Math.atan2(dx, dz);
  }

  function spawnShadowStalkerTrail(from: THREE.Vector3, to: THREE.Vector3, color: number): void {
    const group = new THREE.Group();
    group.name = 'shadow-stalker-move-trail';
    const smokeTexture = getItemVfxSmokeTexture();
    const tint = new THREE.Color(color).lerp(new THREE.Color(0x12091f), 0.35);
    const materials: THREE.SpriteMaterial[] = [];
    const sprites: THREE.Sprite[] = [];
    const handle = gsapRegistry.create('shadow-stalker-move-trail');

    for (let index = 0; index < 12; index += 1) {
      const t = index / 11;
      const material = new THREE.SpriteMaterial({
        map: smokeTexture,
        color: tint,
        transparent: true,
        alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
        opacity: 0.24 + (index % 3) * 0.04,
        blending: THREE.NormalBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      const side = index % 2 === 0 ? -1 : 1;
      sprite.position.set(
        THREE.MathUtils.lerp(from.x, to.x, t) + side * 0.045,
        THREE.MathUtils.lerp(from.y, to.y, t) - 0.18 + Math.sin(t * Math.PI) * 0.12,
        THREE.MathUtils.lerp(from.z, to.z, t) - side * 0.035,
      );
      const scale = 0.36 + (index % 4) * 0.06;
      sprite.scale.set(scale * 1.35, scale, 1);
      group.add(sprite);
      materials.push(material);
      sprites.push(sprite);
    }

    renderer.scene.groups.effects.add(group);
    handle.timeline.to(
      sprites.map((sprite) => sprite.scale),
      { x: 0.08, y: 0.08, duration: 0.46, ease: 'power1.in' },
      0,
    );
    handle.timeline.to(materials, { opacity: 0, duration: 0.44, ease: 'power1.in' }, 0.04);
    handle.timeline.call(
      () => {
        renderer.scene.groups.effects.remove(group);
        for (const material of materials) material.dispose();
        handle.dispose();
      },
      undefined,
      0.5,
    );
  }

  function animateShadowStalkerSpawn(record: {
    root: THREE.Group;
    animationHandle: ReturnType<GsapRegistry['create']> | null;
    baseY: number;
    mistMaterials: THREE.SpriteMaterial[];
  }): void {
    record.animationHandle?.dispose();
    const handle = gsapRegistry.create('shadow-stalker-model-spawn');
    record.animationHandle = handle;
    record.root.visible = true;
    record.root.position.y = record.baseY - 0.42;
    record.root.scale.setScalar(0.18);
    for (const material of record.mistMaterials) material.opacity = 0;
    handle.timeline.to(
      record.root.scale,
      {
        x: 0.82,
        y: 0.82,
        z: 0.82,
        duration: 0.72,
        ease: 'back.out(1.8)',
      },
      0,
    );
    handle.timeline.to(
      record.root.position,
      {
        y: record.baseY,
        duration: 0.72,
        ease: 'power3.out',
      },
      0,
    );
    handle.timeline.to(
      record.mistMaterials,
      {
        opacity: 0.46,
        duration: 0.55,
        ease: 'power1.out',
      },
      0.12,
    );
    handle.timeline.call(
      () => {
        handle.dispose();
        if (record.animationHandle === handle) record.animationHandle = null;
      },
      undefined,
      0.76,
    );
  }

  function moveShadowStalkerModel(
    record: {
      root: THREE.Group;
      animationHandle: ReturnType<GsapRegistry['create']> | null;
      baseY: number;
      nodeId: string;
      color: number;
      mistMaterials: THREE.SpriteMaterial[];
      moving: boolean;
    },
    nodeId: string,
  ): Promise<void> {
    const position = shadowStalkerPlacementForNode(nodeId);
    if (position == null) return Promise.resolve();
    record.animationHandle?.dispose();
    record.moving = true;
    const handle = gsapRegistry.create('shadow-stalker-model-move');
    record.animationHandle = handle;
    const start = record.root.position.clone();
    const yaw = yawForShadowStalkerDirection(start, position);
    if (yaw != null) {
      handle.timeline.to(record.root.rotation, { y: yaw, duration: 0.16, ease: 'power1.out' }, 0);
    }
    spawnShadowStalkerTrail(start, position, record.color);
    const proxy = { t: 0 };
    return new Promise((resolve) => {
      handle.timeline.to(
        proxy,
        {
          t: 1,
          duration: SHADOW_STALKER_MOVE_DURATION_SECONDS,
          ease: 'power2.inOut',
          onUpdate: () => {
            const t = proxy.t;
            record.root.position.set(
              THREE.MathUtils.lerp(start.x, position.x, t),
              THREE.MathUtils.lerp(start.y, position.y, t) + Math.sin(t * Math.PI) * 0.24,
              THREE.MathUtils.lerp(start.z, position.z, t),
            );
          },
        },
        0,
      );
      handle.timeline.to(
        record.mistMaterials,
        { opacity: 0.62, duration: 0.16, yoyo: true, repeat: 1, ease: 'sine.inOut' },
        0,
      );
      handle.timeline.call(
        () => {
          record.baseY = position.y;
          record.root.position.set(position.x, position.y, position.z);
          record.nodeId = nodeId;
          record.moving = false;
          handle.dispose();
          if (record.animationHandle === handle) record.animationHandle = null;
          resolve();
        },
        undefined,
        SHADOW_STALKER_MOVE_DURATION_SECONDS,
      );
    });
  }

  function showShadowStalkerModel(record: {
    root: THREE.Group;
    baseY: number;
    mistMaterials: THREE.SpriteMaterial[];
  }): void {
    if (record.root.visible) return;
    record.root.visible = true;
    record.root.scale.setScalar(0.82);
    record.root.position.y = record.baseY;
    for (const material of record.mistMaterials)
      material.opacity = Math.max(material.opacity, 0.42);
  }

  async function ensureShadowStalkerModelAt(
    npcId: string,
    nodeId: string,
    animateOnCreate = true,
    summonerIdOverride?: string,
  ): Promise<void> {
    let record = boardNpcModels.get(npcId);
    if (record != null) {
      if (record.nodeId !== nodeId) {
        if (animateOnCreate) {
          await moveShadowStalkerModel(record, nodeId);
        } else {
          positionShadowStalkerModel(record, nodeId);
        }
      }
      return;
    }

    const template = await loadShadowStalkerTemplate();
    const npc = latestGameState?.npcs.find((candidate) => candidate.id === npcId);
    const color = colorForShadowStalkerSummoner(npc?.summonerId ?? summonerIdOverride);
    const root = cloneShadowStalkerModel(template);
    const mist = buildShadowStalkerMist(color);
    root.add(mist.group);
    record = {
      root,
      nodeId,
      animationHandle: null,
      mistMaterials: mist.materials,
      baseY: 0,
      bobPhase: Math.random() * Math.PI * 2,
      color,
      moving: false,
      despawning: false,
    };
    if (!positionShadowStalkerModel(record, nodeId)) {
      disposeShadowStalkerRecord(record);
      return;
    }
    boardNpcModels.set(npcId, record);
    boardNpcParent.add(root);
    if (animateOnCreate) {
      animateShadowStalkerSpawn(record);
    } else {
      root.visible = false;
    }
  }

  async function playShadowStalkerMoveStep(move: {
    npcId: string;
    fromNodeId: string;
    toNodeId: string;
  }): Promise<void> {
    await ensureShadowStalkerModelAt(move.npcId, move.fromNodeId, false);
    const record = boardNpcModels.get(move.npcId);
    if (record == null || record.despawning) return;
    showShadowStalkerModel(record);
    if (record.nodeId !== move.fromNodeId) {
      positionShadowStalkerModel(record, move.fromNodeId);
    }
    await moveShadowStalkerModel(record, move.toNodeId);
  }

  function playerWorldPositionForShadowStalkerKill(playerId: string): THREE.Vector3 | null {
    const playerPosition = itemVfxPositionForPlayer(playerId);
    if (playerPosition != null) {
      return new THREE.Vector3(playerPosition.x, playerPosition.y, playerPosition.z);
    }
    const nodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    return nodeId == null ? null : shadowStalkerPlacementForNode(nodeId);
  }

  function spawnShadowStalkerKillMist(position: THREE.Vector3, color: number): void {
    const group = new THREE.Group();
    group.name = 'shadow-stalker-kill-mist';
    const smokeTexture = getItemVfxSmokeTexture();
    const tint = new THREE.Color(color).lerp(new THREE.Color(0x05010a), 0.28);
    const materials: THREE.SpriteMaterial[] = [];
    const sprites: THREE.Sprite[] = [];
    const handle = gsapRegistry.create('shadow-stalker-kill-mist');

    for (let index = 0; index < 24; index += 1) {
      const angle = (index / 24) * Math.PI * 2;
      const radius = 0.22 + (index % 5) * 0.055;
      const material = new THREE.SpriteMaterial({
        map: smokeTexture,
        color: tint,
        transparent: true,
        alphaTest: ITEM_VFX_SMOKE_ALPHA_TEST,
        opacity: 0,
        blending: THREE.NormalBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.position.set(
        position.x + Math.cos(angle) * radius,
        position.y - 0.1 + (index % 6) * 0.075,
        position.z + Math.sin(angle) * radius,
      );
      const scale = 0.42 + (index % 4) * 0.08;
      sprite.scale.set(scale * 1.4, scale, 1);
      group.add(sprite);
      materials.push(material);
      sprites.push(sprite);
    }

    renderer.scene.groups.effects.add(group);
    handle.timeline.to(materials, { opacity: 0.7, duration: 0.16, ease: 'power1.out' }, 0);
    handle.timeline.to(
      sprites.map((sprite) => sprite.scale),
      { x: 0.12, y: 0.12, duration: 0.46, ease: 'power2.in' },
      0.16,
    );
    handle.timeline.to(materials, { opacity: 0, duration: 0.42, ease: 'power2.in' }, 0.2);
    handle.timeline.call(
      () => {
        renderer.scene.groups.effects.remove(group);
        for (const material of materials) material.dispose();
        handle.dispose();
      },
      undefined,
      0.66,
    );
  }

  async function playShadowStalkerKillPresentation(kill: {
    npcId: string;
    playerId: string;
    atNodeId: string;
  }): Promise<void> {
    await ensureShadowStalkerModelAt(kill.npcId, kill.atNodeId, false);
    const record = boardNpcModels.get(kill.npcId);
    if (record == null || record.despawning) return;
    showShadowStalkerModel(record);
    positionShadowStalkerModel(record, kill.atNodeId);

    const victimPosition = playerWorldPositionForShadowStalkerKill(kill.playerId);
    const stalkerStart = record.root.position.clone();
    const target = victimPosition ?? stalkerStart.clone();
    const lungeTarget = target.clone();
    lungeTarget.y = Math.max(lungeTarget.y, record.baseY) + 0.05;
    const yaw = yawForShadowStalkerDirection(stalkerStart, lungeTarget);

    shadowStalkerKillRunning = true;

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;

    try {
      if (orbitControls != null) orbitControls.enabled = false;
      renderer.cameraRig.setMode('spectator');
      const focus = stalkerStart.clone().lerp(target, 0.5);
      focus.y += 0.55;
      await tweenCameraTo(
        focus.clone().add(new THREE.Vector3(0, 2.2, 3.15)),
        focus,
        34,
        0.34,
        'shadow-stalker-kill:camera-in',
        originalTarget,
      );

      record.animationHandle?.dispose();
      const handle = gsapRegistry.create('shadow-stalker-kill-lunge');
      record.animationHandle = handle;
      spawnShadowStalkerKillMist(target, record.color);
      if (yaw != null) {
        handle.timeline.to(record.root.rotation, { y: yaw, duration: 0.12, ease: 'power1.out' }, 0);
      }
      handle.timeline.to(
        record.root.position,
        {
          x: lungeTarget.x,
          y: lungeTarget.y + 0.18,
          z: lungeTarget.z,
          duration: SHADOW_STALKER_KILL_SECONDS * 0.42,
          ease: 'power3.in',
        },
        0.06,
      );
      handle.timeline.to(
        record.root.scale,
        { x: 1.06, y: 1.06, z: 1.06, duration: 0.18, ease: 'power2.out' },
        0.08,
      );
      handle.timeline.to(
        record.root.scale,
        { x: 0.74, y: 0.74, z: 0.74, duration: 0.28, ease: 'power2.in' },
        0.38,
      );
      handle.timeline.to(record.mistMaterials, { opacity: 0.76, duration: 0.18 }, 0.08);
      await new Promise<void>((resolve) => {
        handle.timeline.call(
          () => {
            handle.dispose();
            if (record.animationHandle === handle) record.animationHandle = null;
            resolve();
          },
          undefined,
          SHADOW_STALKER_KILL_SECONDS,
        );
      });

      if (pendingShadowStalkerKilledDespawns.delete(kill.npcId)) {
        animateShadowStalkerDespawn(kill.npcId);
      }
      await waitWithGsap(0.18, 'shadow-stalker-kill:hold');
      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        0.34,
        'shadow-stalker-kill:camera-out',
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
    } finally {
      shadowStalkerKillRunning = false;
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
    }
  }

  async function ensureShadowStalkerModel(
    npcId: string,
    nodeId: string,
    animateOnCreate = true,
    summonerIdOverride?: string,
  ): Promise<void> {
    await ensureShadowStalkerModelAt(npcId, nodeId, animateOnCreate, summonerIdOverride);
  }

  function animateShadowStalkerDespawn(npcId: string): void {
    const record = boardNpcModels.get(npcId);
    if (record == null || record.despawning) return;
    record.despawning = true;
    record.animationHandle?.dispose();
    const handle = gsapRegistry.create('shadow-stalker-model-despawn');
    record.animationHandle = handle;
    handle.timeline.to(
      record.root.scale,
      { x: 0.25, y: 0.25, z: 0.25, duration: 0.42, ease: 'power2.in' },
      0,
    );
    handle.timeline.to(record.root.position, { y: record.baseY - 0.46, duration: 0.42 }, 0);
    handle.timeline.to(record.mistMaterials, { opacity: 0, duration: 0.42, ease: 'power1.in' }, 0);
    handle.timeline.call(
      () => {
        boardNpcModels.delete(npcId);
        disposeShadowStalkerRecord(record);
        handle.dispose();
      },
      undefined,
      0.44,
    );
  }

  function syncBoardNpcs(state: GameState): void {
    boardNpcParent.visible = boardRosterParent.visible;
    const activeIds = new Set(state.npcs.map((npc) => npc.id));
    for (const npcId of boardNpcModels.keys()) {
      if (activeIds.has(npcId)) continue;
      animateShadowStalkerDespawn(npcId);
    }
    for (const npc of state.npcs) {
      if (npc.kind !== 'shadow_stalker') continue;
      void ensureShadowStalkerModel(npc.id, npc.nodeId);
    }
  }

  function disposeSkillshotGroupLike(root: THREE.Object3D): void {
    root.traverse((child) => {
      if (child.userData.preserveSharedResources === true) return;
      const renderable = child as THREE.Object3D & {
        geometry?: THREE.BufferGeometry;
        material?: THREE.Material | THREE.Material[];
      };
      if (renderable.geometry == null && renderable.material == null) return;
      renderable.geometry?.dispose();
      const material = renderable.material;
      if (material == null) {
        return;
      } else if (Array.isArray(material)) {
        for (const mat of material) mat.dispose();
      } else {
        material.dispose();
      }
    });
  }

  function computeRigFootOffset(root: THREE.Object3D): number {
    root.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(root);
    if (box.isEmpty()) return BOARD_RIG_FOOT_OFFSET_FALLBACK;
    const size = box.getSize(new THREE.Vector3());
    const footOffset = root.position.y - box.min.y;
    return footOffset > 0
      ? footOffset
      : Math.max(size.y * BOARD_RIG_FOOT_OFFSET_MIN_HEIGHT_RATIO, BOARD_RIG_FOOT_OFFSET_FALLBACK);
  }

  function computeBoardRigStackClearance(rig: CharacterPreviewRig): number {
    const originalScale = rig.root.scale.clone();
    const box = new THREE.Box3();
    const renderMeshes =
      rig.renderMeshes != null && rig.renderMeshes.length > 0 ? rig.renderMeshes : [rig.bodyMesh];

    try {
      rig.root.scale.setScalar(BOARD_RIG_SCALE);
      rig.root.updateWorldMatrix(true, true);
      for (const mesh of renderMeshes) box.expandByObject(mesh);
      const headRoot = rig._headRoot ?? rig._headMesh ?? null;
      if (headRoot != null) box.expandByObject(headRoot);
    } finally {
      rig.root.scale.copy(originalScale);
      rig.root.updateWorldMatrix(true, true);
    }

    if (box.isEmpty()) return BOARD_RIG_STACK_CLEARANCE_FALLBACK;
    const height = box.getSize(new THREE.Vector3()).y;
    return Number.isFinite(height) && height > 0
      ? height + BOARD_RIG_STACK_CLEARANCE_PADDING
      : BOARD_RIG_STACK_CLEARANCE_FALLBACK;
  }

  function boardRigStackClearanceForPlayer(playerId: string): number | null {
    const rig = boardRigs.get(playerId);
    return rig == null ? null : computeBoardRigStackClearance(rig);
  }

  function computeBoardRigBasePlacement(
    rig: CharacterPreviewRig,
    node: { id: string; position: { x: number; z: number } },
  ): THREE.Vector3 {
    const padTopY =
      boardHandle?.getTilePadTopYForNode(node.id) ?? boardHandle?.getSurfaceYForNode(node.id) ?? 0;
    const position = getBoardNodeWorldPosition(node);
    return new THREE.Vector3(position.x, padTopY + computeRigFootOffset(rig.root), position.z);
  }

  function orderedBoardRigOccupantsForNode(
    nodeId: string,
    nodeIdsByPlayer: ReadonlyMap<string, string> = boardRigNodeIds,
  ): string[] {
    return [...nodeIdsByPlayer.entries()]
      .filter(([, occupantNodeId]) => occupantNodeId === nodeId)
      .map(([playerId]) => playerId)
      .sort((a, b) => {
        const orderA = boardRigVisualOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
        const orderB = boardRigVisualOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
        return orderA === orderB ? a.localeCompare(b) : orderA - orderB;
      });
  }

  function computeBoardRigPlacement(
    playerId: string,
    rig: CharacterPreviewRig,
    node: { id: string; position: { x: number; z: number } },
    nodeIdsByPlayer: ReadonlyMap<string, string> = boardRigNodeIds,
  ): THREE.Vector3 {
    const placement = computeBoardRigBasePlacement(rig, node);
    const occupantIds = orderedBoardRigOccupantsForNode(node.id, nodeIdsByPlayer);
    const offset = computeSameTileStackOffset(occupantIds, playerId, {
      stackClearanceForPlayer: boardRigStackClearanceForPlayer,
    });
    placement.x += offset.x;
    placement.y += offset.y;
    placement.z += offset.z;
    return placement;
  }

  function disposeArenaSnapshotMesh(mesh: THREE.Mesh): void {
    arenaSnapshotParent.remove(mesh);
    mesh.geometry.dispose();
    const material = mesh.material;
    if (Array.isArray(material)) {
      material.forEach((mat) => mat.dispose());
    } else {
      material.dispose();
    }
  }

  function updateArenaMainGameSnapshot(
    players: Extract<ServerMessage, { type: 'arena_main_game_snapshot' }>['players'],
  ): void {
    latestArenaSnapshotPlayers = players;
    arenaSnapshotParent.visible = inWarmupArena && players.length > 0;
    if (mapHandle == null || boardHandle == null) return;

    const arenaIslandId = getWarmupArenaIslandId();
    const presentIds = new Set(players.map((player) => player.playerId));
    for (const [playerId, mesh] of [...arenaSnapshotMeshes.entries()]) {
      if (!presentIds.has(playerId)) {
        disposeArenaSnapshotMesh(mesh);
        arenaSnapshotMeshes.delete(playerId);
      }
    }

    for (const player of players) {
      if (player.nodeId.startsWith(`${arenaIslandId}-`)) continue;
      const node = findMapNode(player.nodeId);
      if (node == null) continue;
      let mesh = arenaSnapshotMeshes.get(player.playerId);
      if (mesh == null) {
        mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(0.13, 0.16, WARMUP_SNAPSHOT_RIG_HEIGHT, 10),
          new THREE.MeshStandardMaterial({
            color: player.color,
            roughness: 0.72,
            metalness: 0.05,
          }),
        );
        mesh.name = `arena-snapshot:${player.playerId}`;
        arenaSnapshotParent.add(mesh);
        arenaSnapshotMeshes.set(player.playerId, mesh);
      } else if (mesh.material instanceof THREE.MeshStandardMaterial) {
        mesh.material.color.set(player.color);
      }
      const position = getBoardNodeWorldPosition(node);
      const topY = boardHandle.getTilePadTopYForNode(node.id) ?? position.y;
      mesh.position.set(position.x, topY + WARMUP_SNAPSHOT_RIG_HEIGHT / 2, position.z);
    }
  }

  function tweenBoardRigTo(
    playerId: string,
    rig: CharacterPreviewRig,
    target: THREE.Vector3,
    options: { playSounds?: boolean; durationSeconds?: number } = {},
  ): Promise<void> {
    boardMoveHandles.get(playerId)?.dispose();
    clearBoardRigFacingPreview(playerId);
    const playSounds = options.playSounds ?? true;
    const durationSeconds = options.durationSeconds ?? BOARD_MOVE_DURATION_SECONDS;
    if (playSounds) playSfx('sfx-movement-step-soft');
    const startPosition = rig.root.position.clone();
    const targetYaw =
      Math.hypot(target.x - startPosition.x, target.z - startPosition.z) > 0.001
        ? yawFromTo(startPosition, target)
        : (boardRigFacingYawOffsets.get(playerId) ?? rig.root.rotation.y);
    const startYaw = boardRigFacingYawOffsets.get(playerId) ?? rig.root.rotation.y;
    const turnDelta = shortestAngleDelta(startYaw, targetYaw);
    const turnAlias = selectBoardCharacterWalkTurnAlias({
      capeNone: boardRigCapeNone.get(playerId),
      turnDelta,
    });
    if (turnAlias != null && Math.abs(turnDelta) > 0.35) {
      boardRigAnimationOverrides.set(playerId, {
        alias: turnAlias,
        untilMs: performance.now() + CHARACTER_TURN_ANIMATION_SECONDS * 1000,
      });
    }
    const proxy = {
      x: rig.root.position.x,
      z: rig.root.position.z,
      baseY: boardRigBaseY.get(playerId) ?? rig.root.position.y,
      hop: 0,
      yaw: startYaw,
    };
    return new Promise((resolve) => {
      const handle = gsapRegistry.create(`board-rig:${playerId}:move`);
      boardMoveHandles.set(playerId, handle);
      const apply = (): void => {
        rig.root.position.x = proxy.x;
        rig.root.position.z = proxy.z;
        boardRigBaseY.set(playerId, proxy.baseY);
        boardRigHopOffsets.set(playerId, proxy.hop);
        boardRigFacingYawOffsets.set(playerId, proxy.yaw);
      };

      handle.timeline.to(
        proxy,
        {
          x: target.x,
          z: target.z,
          baseY: target.y,
          duration: durationSeconds,
          ease: 'sine.inOut',
          onUpdate: apply,
        },
        0,
      );
      handle.timeline
        .to(proxy, { hop: BOARD_MOVE_LIFT, duration: 0.18, ease: 'sine.out', onUpdate: apply }, 0)
        .to(proxy, { hop: 0, duration: 0.3, ease: 'sine.in', onUpdate: apply }, 0.18);
      handle.timeline.to(
        proxy,
        { yaw: targetYaw, duration: 0.18, ease: 'power1.out', onUpdate: apply },
        0,
      );
      handle.timeline.call(
        () => {
          if (boardMoveHandles.get(playerId) === handle) {
            boardMoveHandles.delete(playerId);
            boardRigHopOffsets.delete(playerId);
            boardRigBaseY.set(playerId, target.y);
            boardRigFacingYawOffsets.set(playerId, targetYaw);
            rig.root.position.set(target.x, target.y, target.z);
          }
          if (playSounds) playSfx('sfx-movement-land');
          handle.dispose();
          resolve();
        },
        undefined,
        durationSeconds,
      );
    });
  }

  function placementForBoardRigNode(
    playerId: string,
    rig: CharacterPreviewRig,
    nodeId: string,
  ): THREE.Vector3 | null {
    let node = findMapNode(nodeId);
    if (node == null) {
      const fallback = boardHandle?.getWorldPositionForNode(nodeId);
      if (fallback != null) {
        node = {
          id: nodeId,
          type: 'gold',
          position: { x: fallback.x, y: fallback.y, z: fallback.z },
          connections: [],
          supportsGoal: false,
        } as BoardNode;
      }
    }
    if (node == null) return null;
    const nodeIdsByPlayer = new Map(boardRigNodeIds);
    nodeIdsByPlayer.set(playerId, node.id);
    boardRigNodeIds.set(playerId, node.id);
    return computeBoardRigPlacement(playerId, rig, node, nodeIdsByPlayer);
  }

  function settleBoardRigStackForNode(
    nodeId: string | null | undefined,
    excludePlayerId: string,
  ): void {
    if (nodeId == null || nodeId === '') return;
    const node = findMapNode(nodeId);
    if (node == null) return;
    for (const playerId of orderedBoardRigOccupantsForNode(node.id)) {
      if (playerId === excludePlayerId) continue;
      if (heldMovementPlayers.has(playerId) || animatingMovementPlayers.has(playerId)) continue;
      const rig = boardRigs.get(playerId);
      if (rig == null) continue;
      const placement = computeBoardRigPlacement(playerId, rig, node);
      void tweenBoardRigTo(playerId, rig, placement, {
        playSounds: false,
        durationSeconds: 0.22,
      });
    }
  }

  function tweenBoardRigAlongDashPath(
    playerId: string,
    rig: CharacterPreviewRig,
    targets: readonly THREE.Vector3[],
  ): Promise<void> {
    if (targets.length === 0) return waitWithGsap(0.18, `lightning-dash:${playerId}:empty`);

    boardMoveHandles.get(playerId)?.dispose();
    clearBoardRigFacingPreview(playerId);
    playSfx('sfx-movement-step-soft');
    const start = rig.root.position.clone();
    const duration = Math.min(0.95, Math.max(0.28, targets.length * 0.075));
    const proxy = { t: 0, hop: 0 };

    return new Promise((resolve) => {
      const handle = gsapRegistry.create(`lightning-dash:${playerId}:move`);
      boardMoveHandles.set(playerId, handle);
      const apply = (): void => {
        const scaled = proxy.t * targets.length;
        const targetIndex = Math.min(Math.floor(scaled), targets.length - 1);
        const localT = Math.min(1, Math.max(0, scaled - targetIndex));
        const from = targetIndex === 0 ? start : targets[targetIndex - 1]!;
        const to = targets[targetIndex]!;
        const position = from.clone().lerp(to, localT);
        rig.root.position.x = position.x;
        rig.root.position.z = position.z;
        boardRigBaseY.set(playerId, position.y);
        boardRigHopOffsets.set(playerId, proxy.hop);
      };

      handle.timeline.to(
        proxy,
        {
          t: 1,
          duration,
          ease: 'power2.inOut',
          onUpdate: apply,
        },
        0,
      );
      handle.timeline
        .to(proxy, { hop: BOARD_MOVE_LIFT * 2.1, duration: duration * 0.35, onUpdate: apply }, 0)
        .to(
          proxy,
          { hop: 0, duration: duration * 0.65, ease: 'sine.in', onUpdate: apply },
          duration * 0.35,
        );
      handle.timeline.call(
        () => {
          const finalTarget = targets[targets.length - 1]!;
          if (boardMoveHandles.get(playerId) === handle) {
            boardMoveHandles.delete(playerId);
            boardRigHopOffsets.delete(playerId);
            boardRigBaseY.set(playerId, finalTarget.y);
            rig.root.position.set(finalTarget.x, finalTarget.y, finalTarget.z);
          }
          playSfx('sfx-movement-land');
          handle.dispose();
          resolve();
        },
        undefined,
        duration,
      );
    });
  }

  // Map loading: fires on lobby entry (room_created / quick_play_joined /
  // player_joined / state_sync in lobby phase) AND on game_started.
  // Idempotent — first-trigger wins.
  let mapHandle: MapHandle | null = null;
  let boardHandle: BoardRenderHandle | null = null;
  let mapLoading = false;
  let mapDecorationsLoaded = false;
  let mapDecorationsRequested = false;
  let mapDecorationPreloadPromise: Promise<void> | null = null;
  let latestTreasureChestNodeId: string | null = null;
  let latestWeaponsCacheNodeId: string | null = null;
  let hiddenSpecialTileNodeIds = new Set<string>();
  let lastStateSyncPhase: GameState['phase'] | null = null;
  let sawGameStartedEvent = false;
  let pendingRestoredBoardRoute = false;
  let hasRunSpawnSequence = false;
  let spawnSequenceRunning = false;
  let specialRelocationRevealRunning = false;
  let lightningDashPresentationRunning = false;
  let pendingSpawnSequence = false;
  const shaderRegistry = createDefaultShaderRegistry();
  renderer.loop.addFrameTickCallback((dt) => {
    boardHandle?.update(dt);
  });
  const atmosphereListeners = new Set<() => void>();

  function notifyAtmosphereListeners(): void {
    for (const listener of atmosphereListeners) listener();
  }

  function asShaderMaterial(
    material: THREE.Material | THREE.Material[],
  ): THREE.ShaderMaterial | null {
    return material instanceof THREE.ShaderMaterial ? material : null;
  }

  function setNumberUniform(
    uniforms: Record<string, THREE.IUniform<unknown>>,
    name: string,
    value: number | undefined,
  ): void {
    if (value == null || uniforms[name] == null) return;
    uniforms[name].value = value;
  }

  function getCloudBaseSize(mesh: THREE.Mesh): number {
    return getCloudLayerBaseSize(mesh);
  }

  function applyThemeToBoardAtmosphere(
    atmosphere: BoardAtmosphereHandle,
    theme: ThemeOverride,
    sunDirection: THREE.Vector3,
  ): void {
    const skyMaterial = asShaderMaterial(atmosphere.dome.material);
    const skyUniforms = skyMaterial?.uniforms as
      | Record<string, THREE.IUniform<unknown>>
      | undefined;
    if (skyUniforms != null) {
      setNumberUniform(skyUniforms, 'turbidity', theme.sky.turbidity);
      setNumberUniform(skyUniforms, 'rayleigh', theme.sky.rayleigh);
      setNumberUniform(skyUniforms, 'mieCoefficient', theme.sky.mieCoefficient);
      setNumberUniform(skyUniforms, 'mieDirectionalG', theme.sky.mieDirectionalG);
      if (skyUniforms['sunPosition']?.value instanceof THREE.Vector3) {
        skyUniforms['sunPosition'].value.copy(sunDirection);
      }
    }

    const cloudMaterial = asShaderMaterial(atmosphere.cloudLayer.mesh.material);
    const cloudUniforms = cloudMaterial?.uniforms as
      | Record<string, THREE.IUniform<unknown>>
      | undefined;
    if (cloudUniforms != null) {
      if (
        theme.clouds.color != null &&
        cloudUniforms['uCloudColor']?.value instanceof THREE.Color
      ) {
        cloudUniforms['uCloudColor'].value.set(theme.clouds.color);
      }
      setNumberUniform(cloudUniforms, 'uOpacity', theme.clouds.opacity);
      setNumberUniform(cloudUniforms, 'uDensity', theme.clouds.density);
      setNumberUniform(cloudUniforms, 'uNoiseScale', theme.clouds.noiseScale);
      if (theme.clouds.drift != null && cloudUniforms['uDrift']?.value instanceof THREE.Vector2) {
        cloudUniforms['uDrift'].value.set(theme.clouds.drift.x, theme.clouds.drift.y);
      }
    }
    if (theme.clouds.height != null) {
      atmosphere.cloudLayer.mesh.position.y = theme.clouds.height;
    }
    if (theme.clouds.size != null) {
      atmosphere.cloudLayer.mesh.scale.setScalar(
        theme.clouds.size / getCloudBaseSize(atmosphere.cloudLayer.mesh),
      );
    }

    atmosphere.fog.color.set(theme.fog.color);
    atmosphere.fog.density = theme.fog.density;
    renderer.scene.scene.fog = atmosphere.fog;
  }

  function applyLiveAtmosphereTheme(theme: ThemeOverride): void {
    const sunDirection = getSunDirectionFromSkyConfig(theme.sky);
    applyLightingConfig(renderer.lighting, {
      ...theme.lightingRig,
      keyDirection:
        theme.lightingRig.keyDirection ?? getLightDirectionFromSunDirection(sunDirection),
    });

    const atmosphere = boardHandle?.atmosphere;
    if (atmosphere != null) {
      applyThemeToBoardAtmosphere(atmosphere, theme, sunDirection);
    }
    notifyAtmosphereListeners();
  }

  const atmosphereHandle: MainAtmosphereHandle = {
    renderer: renderer.scene.renderer,
    scene: renderer.scene.scene,
    lightingRig: renderer.lighting,
    postChain: renderer.composer,
    getMapTheme(): ThemeOverride | null {
      return mapHandle?.theme ?? null;
    },
    getBoardAtmosphere(): BoardAtmosphereHandle | null {
      return boardHandle?.atmosphere ?? null;
    },
    applyTheme(theme: ThemeOverride): void {
      applyLiveAtmosphereTheme(theme);
    },
    resetToMapTheme(): void {
      if (mapHandle == null) return;
      applyLiveAtmosphereTheme(mapHandle.theme);
    },
    subscribe(listener: () => void): () => void {
      atmosphereListeners.add(listener);
      return () => {
        atmosphereListeners.delete(listener);
      };
    },
  };

  function isRealNodeId(nodeId: string | null | undefined): nodeId is string {
    return nodeId != null && nodeId !== '';
  }

  function ensureNavigationOverlays(): void {
    if (!hasSceneGroups) return;
    if (navOverlayGroup == null) {
      navOverlayGroup = new THREE.Group();
      navOverlayGroup.name = 'nav-overlays';
      renderer.scene.groups.board.add(navOverlayGroup);
    }
    if (pathArrows != null && pathArrows.styleVersion !== PATH_ARROW_STYLE_VERSION) {
      pathArrows.dispose();
      pathArrows = null;
    }
    if (pathArrows == null) {
      pathArrows = createPathArrows({
        parent: navOverlayGroup,
        gsapRegistry,
        getWorldPositionForNode: (nodeId) => boardHandle?.getWorldPositionForNode(nodeId) ?? null,
        getTilePadTopYForNode: (nodeId) => boardHandle?.getTilePadTopYForNode(nodeId) ?? null,
      });
    }
  }

  function disposeNavigationOverlays(): void {
    pathArrows?.dispose();
    pathArrows = null;
    clearBoardRigFacingPreview();
    if (navOverlayGroup != null) {
      renderer.scene.groups.board.remove(navOverlayGroup);
      navOverlayGroup.clear();
      navOverlayGroup = null;
    }
  }

  function clearPathArrowsAndFacing(): void {
    pathArrows?.clear();
    clearBoardRigFacingPreview();
  }

  function findMapNode(nodeId: string): BoardNode | null {
    if (mapHandle == null) return null;
    return (
      (mapHandle.schema.nodes as readonly BoardNode[]).find((node) => node.id === nodeId) ?? null
    );
  }

  const boardClickRaycaster = new THREE.Raycaster();
  const boardClickNdc = new THREE.Vector2();
  const devBoardTileClickListeners = new Set<(nodeId: string) => void>();

  function eyeRigDebugSnapshot(
    rig: CharacterPreviewRig | null | undefined,
  ): MainEyeRigDebugSnapshot | null {
    if (rig == null) return null;
    const eyeGroup = rig._eyeGroup ?? null;
    const textureUuids: string[] = [];
    if (eyeGroup != null) {
      eyeGroup.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) {
          if (!(material instanceof THREE.MeshBasicMaterial)) continue;
          if (material.map != null) textureUuids.push(material.map.uuid);
        }
      });
    }
    const expression = eyeGroup?.userData.eyeExpression;
    return {
      eyeExpression: rig._eyeExpression ?? null,
      eyeGroupName: eyeGroup?.name ?? null,
      eyeGroupExpression: typeof expression === 'string' ? expression : null,
      eyeGroupVisible: eyeGroup?.visible ?? null,
      eyeMeshCount: eyeGroup?.children.length ?? 0,
      textureUuids,
    };
  }

  function getEyeDebugSnapshot(): MainEyeDebugSnapshot {
    const state = latestGameState;
    if (state == null) return { phase: null, players: [] };
    const playerIds = [
      ...state.turnOrder,
      ...[...state.players.keys()].filter((playerId) => !state.turnOrder.includes(playerId)),
    ];
    return {
      phase: state.phase,
      players: playerIds.flatMap((playerId) => {
        const player = state.players.get(playerId);
        if (player == null) return [];
        return [
          {
            id: player.id,
            name: player.name,
            isBot: player.isBot,
            head: player.customization.head,
            eyes: player.customization.eyes,
            boardRig: eyeRigDebugSnapshot(boardRigs.get(playerId)),
            rosterRig: eyeRigDebugSnapshot(rosterRigs.get(playerId)),
          },
        ];
      }),
    };
  }

  function getMobileDebugSnapshot(): MainMobileDebugSnapshot {
    const playerId = localPlayerId;
    const player = playerId == null ? null : (latestGameState?.players.get(playerId) ?? null);
    const rig = playerId == null ? null : (boardRigs.get(playerId) ?? null);
    return {
      screen: screenRouter.current,
      phase: currentPhase,
      inWarmupArena,
      mapLoaded: mapHandle != null,
      boardLoaded: boardHandle != null,
      boardVisible: renderer.scene.groups.board.visible,
      characterAdapterLoaded: characterAdapter != null,
      localPlayerId: playerId,
      localPlayer:
        player == null
          ? null
          : {
              id: player.id,
              nodeId: player.nodeId,
              isReady: player.isReady === true,
            },
      localRig:
        rig == null
          ? null
          : {
              visible: rig.root.visible,
              parented: rig.root.parent != null,
              position: {
                x: rig.root.position.x,
                y: rig.root.position.y,
                z: rig.root.position.z,
              },
            },
      canvas: {
        width: opts.canvas.clientWidth,
        height: opts.canvas.clientHeight,
      },
    };
  }

  function getWarmupTapTargets(): MainWarmupTapTarget[] {
    const board = boardHandle;
    if (!inWarmupArena || mapHandle == null || board == null) return [];
    const camera = renderer.cameraRig?.camera;
    if (camera == null) return [];
    const rect = opts.canvas.getBoundingClientRect();
    const width = rect.width > 0 ? rect.width : opts.canvas.clientWidth || window.innerWidth || 1;
    const height =
      rect.height > 0 ? rect.height : opts.canvas.clientHeight || window.innerHeight || 1;
    const currentNodeId =
      localPlayerId == null ? null : (latestGameState?.players.get(localPlayerId)?.nodeId ?? null);
    camera.updateMatrixWorld();
    return nodesForIsland(getWarmupArenaIslandId())
      .filter((node) => node.id !== currentNodeId)
      .flatMap((node) => {
        const world = board.getWorldPositionForNode(node.id) ?? getBoardNodeWorldPosition(node);
        const projected = world.clone();
        projected.project(camera);
        if (
          !Number.isFinite(projected.x) ||
          !Number.isFinite(projected.y) ||
          !Number.isFinite(projected.z) ||
          projected.z < -1 ||
          projected.z > 1
        ) {
          return [];
        }
        const screenX = rect.left + ((projected.x + 1) / 2) * width;
        const screenY = rect.top + ((1 - projected.y) / 2) * height;
        if (
          screenX < 0 ||
          screenY < 0 ||
          screenX > window.innerWidth ||
          screenY > window.innerHeight
        ) {
          return [];
        }
        return [{ nodeId: node.id, screenX, screenY }];
      });
  }

  function pickWarmupArenaNodeFromPointer(event: PointerEvent): string | null {
    if (!inWarmupArena) return null;
    const maxDistancePx = event.pointerType === 'touch' ? 56 : 34;
    let best: { nodeId: string; distanceSq: number } | null = null;
    for (const target of getWarmupTapTargets()) {
      const dx = target.screenX - event.clientX;
      const dy = target.screenY - event.clientY;
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq > maxDistancePx * maxDistancePx) continue;
      if (best == null || distanceSq < best.distanceSq) {
        best = { nodeId: target.nodeId, distanceSq };
      }
    }
    return best?.nodeId ?? null;
  }

  const devToolsHandle: MainDevToolsHandle = {
    getLocalPlayerId(): string | null {
      return localPlayerId;
    },
    subscribeBoardTileClick(listener: (nodeId: string) => void): () => void {
      devBoardTileClickListeners.add(listener);
      return () => {
        devBoardTileClickListeners.delete(listener);
      };
    },
    getWarmupTapTargets,
    getEyeDebugSnapshot,
    getMobileDebugSnapshot,
    setPlayerEyeExpression(playerId: string, expression: EyeExpression): boolean {
      const rig = boardRigs.get(playerId) ?? rosterRigs.get(playerId) ?? null;
      if (rig == null) return false;
      rig.setEyeExpression(expression);
      return true;
    },
  };
  let boardPointerDown: {
    x: number;
    y: number;
    pointerId: number;
  } | null = null;
  let warmupMoveCooldownSeconds = 0;
  let warmupPendingMove: {
    nodeId: string;
    elapsedSeconds: number;
  } | null = null;
  let movementDirectionStickCooldownSeconds = 0;
  let submittedForkChoiceKey: string | null = null;
  let activeItemPlayerTargeting: ItemPlayerTargetingSession | null = null;
  let activeItemDirectionalTargeting: ItemDirectionalTargetingSession | null = null;
  let activeItemRangePreviewDispose: (() => void) | null = null;

  function tileInfoForObject(object: THREE.Object3D): ReturnType<BoardRenderHandle['getTileInfo']> {
    let current: THREE.Object3D | null = object;
    while (current != null && current !== renderer.scene.groups.board) {
      const info = boardHandle?.getTileInfo(current);
      if (info != null) return info;
      current = current.parent;
    }
    return undefined;
  }

  function pickBoardTileFromPointer(event: PointerEvent): string | null {
    if (boardHandle == null) return null;
    const camera = renderer.cameraRig?.camera;
    if (camera == null) return null;
    const rect = opts.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    boardClickNdc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -(((event.clientY - rect.top) / rect.height) * 2 - 1),
    );
    boardClickRaycaster.setFromCamera(boardClickNdc, camera);
    const hits = boardClickRaycaster.intersectObjects(renderer.scene.groups.board.children, true);
    for (const hit of hits) {
      const info = tileInfoForObject(hit.object);
      if (info != null) return info.nodeId;
    }
    return null;
  }

  function startItemPlayerTargeting(session: ItemPlayerTargetingSession): () => void {
    activeItemPlayerTargeting = session;
    if (session.selectedTargetId != null) {
      session.onSelectTarget(session.selectedTargetId);
      focusItemTargetCameraOnPlayer(session.selectedTargetId);
    } else if (session.targetPlayerIds.length > 0) {
      session.selectedTargetId = session.targetPlayerIds[0]!;
      session.onSelectTarget(session.selectedTargetId);
      focusItemTargetCameraOnPlayer(session.selectedTargetId);
    }
    return () => {
      if (activeItemPlayerTargeting === session) {
        activeItemPlayerTargeting = null;
      }
    };
  }

  function isItemPlayerTargetingActive(): boolean {
    return (
      activeItemPlayerTargeting != null &&
      screenRouter.current === 'item-targeting' &&
      !actionInputLocked
    );
  }

  function startItemDirectionalTargeting(session: ItemDirectionalTargetingSession): () => void {
    activeItemDirectionalTargeting = session;
    session.onAimDirection(normalizeItemAimDirection(session.selectedDirection));
    if (session.selectionMode === 'path-arrow' && latestGameState != null) {
      syncPathArrowsForState(latestGameState);
      const nextNodeId = pathArrows?.getSelectedNextNodeId() ?? null;
      if (nextNodeId != null) {
        aimItemDirectionToNode(nextNodeId);
      }
    }
    return () => {
      if (activeItemDirectionalTargeting === session) {
        activeItemDirectionalTargeting = null;
        if (latestGameState != null) syncPathArrowsForState(latestGameState);
      }
    };
  }

  function startItemRangePreview(session: ItemRangePreviewSession): () => void {
    activeItemRangePreviewDispose?.();
    activeItemRangePreviewDispose = null;

    if (boardHandle == null || mapHandle == null || latestGameState == null) {
      return () => {};
    }
    const centerPlayer = latestGameState.players.get(session.centerPlayerId);
    if (centerPlayer == null) return () => {};
    const centerNode = findMapNode(centerPlayer.nodeId);
    if (centerNode == null) return () => {};

    const group = new THREE.Group();
    group.name = `item-range-preview:${session.itemId}`;

    const ringGeometry = new THREE.RingGeometry(0.42, 0.64, 48);
    const fillGeometry = new THREE.CircleGeometry(0.46, 48);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xa56bff,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const fillMaterial = new THREE.MeshBasicMaterial({
      color: 0x8b5cff,
      transparent: true,
      opacity: 0.34,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    let highlightedNodeIds: Set<string>;
    try {
      highlightedNodeIds = nodesWithinGraphRange(
        new BoardGraph([...(mapHandle.schema.nodes as readonly BoardNode[])]),
        centerNode.id,
        session.radius,
      );
    } catch {
      highlightedNodeIds = new Set([centerNode.id]);
    }

    let highlightedCount = 0;
    for (const node of mapHandle.schema.nodes as readonly BoardNode[]) {
      if (!highlightedNodeIds.has(node.id)) continue;
      const world = boardHandle.getWorldPositionForNode(node.id);
      if (world == null) continue;
      const topY = boardHandle.getTilePadTopYForNode(node.id) ?? world.y;

      const tile = new THREE.Group();
      tile.name = `item-range-preview-tile:${node.id}`;
      tile.position.set(world.x, topY + 0.055, world.z);

      const fill = new THREE.Mesh(fillGeometry, fillMaterial);
      fill.rotation.x = -Math.PI / 2;
      tile.add(fill);

      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = -Math.PI / 2;
      tile.add(ring);

      group.add(tile);
      highlightedCount += 1;
    }

    if (highlightedCount === 0) {
      ringGeometry.dispose();
      fillGeometry.dispose();
      ringMaterial.dispose();
      fillMaterial.dispose();
      return () => {};
    }

    renderer.scene.groups.effects.add(group);

    const proxy = { opacity: 1, scale: 1 };
    const apply = (): void => {
      ringMaterial.opacity = proxy.opacity;
      fillMaterial.opacity = proxy.opacity * 0.38;
      for (const child of group.children) {
        child.scale.setScalar(proxy.scale);
      }
    };
    const handle = gsapRegistry.create(`item-range-preview:${session.itemId}`);
    handle.timeline.to(
      proxy,
      {
        opacity: 0.68,
        scale: 1.08,
        duration: 0.7,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
        onUpdate: apply,
      },
      0,
    );

    const dispose = (): void => {
      handle.dispose();
      renderer.scene.groups.effects.remove(group);
      ringGeometry.dispose();
      fillGeometry.dispose();
      ringMaterial.dispose();
      fillMaterial.dispose();
      if (activeItemRangePreviewDispose === dispose) {
        activeItemRangePreviewDispose = null;
      }
    };
    activeItemRangePreviewDispose = dispose;
    return dispose;
  }

  function isItemDirectionalTargetingActive(): boolean {
    return (
      activeItemDirectionalTargeting != null &&
      screenRouter.current === 'item-targeting' &&
      !actionInputLocked
    );
  }

  function isPathArrowItemDirectionTargetingActive(): boolean {
    return (
      isItemDirectionalTargetingActive() &&
      activeItemDirectionalTargeting?.selectionMode === 'path-arrow'
    );
  }

  function normalizeItemAimDirection(direction: { dx: number; dy: number }): {
    dx: number;
    dy: number;
  } {
    const magnitude = Math.hypot(direction.dx, direction.dy);
    if (!Number.isFinite(magnitude) || magnitude < 0.001) return { dx: 1, dy: 0 };
    return { dx: direction.dx / magnitude, dy: direction.dy / magnitude };
  }

  function aimItemDirection(
    direction: { dx: number; dy: number },
    targetNodeId: string | null = null,
  ): void {
    const target = activeItemDirectionalTargeting;
    if (target == null) return;
    const normalized = normalizeItemAimDirection(direction);
    target.selectedDirection = normalized;
    target.selectedTargetNodeId = targetNodeId;
    target.onAimDirection(normalized, targetNodeId);
  }

  function confirmSelectedItemDirection(): void {
    const target = activeItemDirectionalTargeting;
    if (target == null) return;
    const normalized = normalizeItemAimDirection(target.selectedDirection);
    target.selectedDirection = normalized;
    target.onConfirmDirection(normalized, target.selectedTargetNodeId ?? null);
  }

  function aimItemDirectionFromKey(direction: 'left' | 'right' | 'up' | 'down'): void {
    switch (direction) {
      case 'left':
        aimItemDirection({ dx: -1, dy: 0 });
        break;
      case 'right':
        aimItemDirection({ dx: 1, dy: 0 });
        break;
      case 'up':
        aimItemDirection({ dx: 0, dy: -1 });
        break;
      case 'down':
        aimItemDirection({ dx: 0, dy: 1 });
        break;
    }
  }

  function itemDirectionForNode(targetNodeId: string): { dx: number; dy: number } | null {
    if (latestGameState == null || localPlayerId == null) return null;
    const caster = latestGameState.players.get(localPlayerId);
    if (caster == null || caster.nodeId === targetNodeId) return null;
    const casterNode = findMapNode(caster.nodeId);
    const targetNode = findMapNode(targetNodeId);
    if (casterNode == null || targetNode == null) return null;
    return normalizeItemAimDirection({
      dx: targetNode.position.x - casterNode.position.x,
      dy:
        (targetNode.position.z ?? targetNode.position.y ?? 0) -
        (casterNode.position.z ?? casterNode.position.y ?? 0),
    });
  }

  function aimItemDirectionToNode(targetNodeId: string): boolean {
    const direction = itemDirectionForNode(targetNodeId);
    if (direction == null) return false;
    aimItemDirection(direction, targetNodeId);
    return true;
  }

  function objectBelongsToRoot(object: THREE.Object3D, root: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object;
    while (current != null) {
      if (current === root) return true;
      current = current.parent;
    }
    return false;
  }

  function playerIdForPickedObject(
    object: THREE.Object3D,
    candidatePlayerIds: readonly string[],
  ): string | null {
    for (const playerId of candidatePlayerIds) {
      const rig = boardRigs.get(playerId);
      if (rig != null && objectBelongsToRoot(object, rig.root)) return playerId;
    }
    return null;
  }

  function pickBoardPlayerFromPointer(
    event: PointerEvent,
    candidatePlayerIds?: readonly string[],
  ): string | null {
    const targetPlayerIds = candidatePlayerIds ?? activeItemPlayerTargeting?.targetPlayerIds;
    if (targetPlayerIds == null || targetPlayerIds.length === 0) return null;
    const camera = renderer.cameraRig?.camera;
    if (camera == null) return null;
    const targetRoots: THREE.Object3D[] = [];
    for (const playerId of targetPlayerIds) {
      const root = boardRigs.get(playerId)?.root;
      if (root != null) targetRoots.push(root);
    }
    if (targetRoots.length === 0) return null;
    const rect = opts.canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    boardClickNdc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -(((event.clientY - rect.top) / rect.height) * 2 - 1),
    );
    boardClickRaycaster.setFromCamera(boardClickNdc, camera);
    const hits = boardClickRaycaster.intersectObjects(targetRoots, true);
    for (const hit of hits) {
      const playerId = playerIdForPickedObject(hit.object, targetPlayerIds);
      if (playerId != null) return playerId;
    }
    return null;
  }

  function cycleItemPlayerTarget(direction: -1 | 1): void {
    const target = activeItemPlayerTargeting;
    if (target == null || target.targetPlayerIds.length === 0) return;
    const currentIndex =
      target.selectedTargetId == null
        ? -1
        : target.targetPlayerIds.indexOf(target.selectedTargetId);
    const nextIndex =
      currentIndex < 0
        ? 0
        : (currentIndex + direction + target.targetPlayerIds.length) %
          target.targetPlayerIds.length;
    target.selectedTargetId = target.targetPlayerIds[nextIndex]!;
    target.onSelectTarget(target.selectedTargetId);
    focusItemTargetCameraOnPlayer(target.selectedTargetId);
  }

  function confirmSelectedItemPlayerTarget(): void {
    const target = activeItemPlayerTargeting;
    if (target?.selectedTargetId == null) return;
    target.onConfirmTarget(target.selectedTargetId);
  }

  function selectItemPlayerTargetFromPointer(event: PointerEvent): boolean {
    const target = activeItemPlayerTargeting;
    if (target == null) return false;
    if (!isItemPlayerTargetingActive()) return false;
    const playerId = pickBoardPlayerFromPointer(event);
    if (playerId == null) return false;
    target.selectedTargetId = playerId;
    target.onSelectTarget(playerId);
    focusItemTargetCameraOnPlayer(playerId);
    target.onConfirmTarget(playerId);
    return true;
  }

  function selectItemDirectionFromPointer(event: PointerEvent): boolean {
    if (!isItemDirectionalTargetingActive()) return false;
    if (isPathArrowItemDirectionTargetingActive()) return false;
    const nodeId = pickBoardTileFromPointer(event) ?? pickWarmupArenaNodeFromPointer(event);
    if (nodeId == null) return false;
    const direction = itemDirectionForNode(nodeId);
    if (direction == null) return false;
    aimItemDirection(direction);
    confirmSelectedItemDirection();
    return true;
  }

  function thunderFistPathTargetIds(): string[] {
    if (
      !isPathArrowItemDirectionTargetingActive() ||
      activeItemDirectionalTargeting?.itemId !== 'thunder_fist' ||
      latestGameState == null ||
      localPlayerId == null
    ) {
      return [];
    }
    return latestGameState.turnOrder.filter((playerId) => {
      if (playerId === localPlayerId) return false;
      const player = latestGameState?.players.get(playerId);
      return player != null && !player.isDead;
    });
  }

  function firstWalkStepTowardPlayer(playerId: string): string | null {
    if (latestGameState == null || localPlayerId == null || mapHandle == null) return null;
    const caster = latestGameState.players.get(localPlayerId);
    const target = latestGameState.players.get(playerId);
    if (caster == null || target == null || target.isDead) return null;
    try {
      const graph = new BoardGraph([...(mapHandle.schema.nodes as readonly BoardNode[])]);
      const path = findWalkPath(graph, caster.nodeId, target.nodeId).path;
      return path.length >= 2 ? (path[1] ?? null) : null;
    } catch {
      return null;
    }
  }

  function selectThunderFistPathTargetFromPointer(event: PointerEvent): boolean {
    const targetPlayerIds = thunderFistPathTargetIds();
    if (targetPlayerIds.length === 0) return false;
    const playerId = pickBoardPlayerFromPointer(event, targetPlayerIds);
    if (playerId == null) return false;
    const nextNodeId = firstWalkStepTowardPlayer(playerId);
    if (nextNodeId == null) return false;
    pathArrows?.selectNextNodeId(nextNodeId);
    return aimItemDirectionToNode(nextNodeId);
  }

  function getPendingForkForLocalPlayer(): { atNodeId: string; options: readonly string[] } | null {
    if (latestGameState == null) return null;
    if (latestGameState.phase !== 'fork_choice') return null;
    const actorId = getActiveOwnedPlayerId(latestGameState);
    if (actorId == null) return null;
    const player = latestGameState.players.get(actorId);
    if (player == null) return null;
    const pending = pendingForkOptionsByPlayer.get(actorId);
    if (pending == null || pending.atNodeId !== player.nodeId) return null;
    return pending;
  }

  function isPreRollPathPhase(phase: GameState['phase']): boolean {
    return phase === 'item_phase' || phase === 'rolling';
  }

  function isFrozenForCurrentRound(state: GameState, playerId: string): boolean {
    const player = state.players.get(playerId);
    return (
      player?.statusEffects.frozenUntil != null && player.statusEffects.frozenUntil >= state.round
    );
  }

  function getPreRollPathArrowKey(state: GameState | null): string | null {
    if (state == null || !isPreRollPathPhase(state.phase)) return null;
    const actorId = getActiveOwnedPlayerId(state);
    if (actorId == null) return null;
    const player = state.players.get(actorId);
    if (player == null) return null;
    return `${actorId}:${state.round}:${state.currentPlayerIndex}:${player.nodeId}`;
  }

  function isRollSubmissionPendingForState(state: GameState): boolean {
    const actorId = getActiveOwnedPlayerId(state);
    if (actorId == null) return false;
    const pendingKey = pendingRollSubmissionKeysByPlayer.get(actorId);
    return pendingKey != null && getPreRollPathArrowKey(state) === pendingKey;
  }

  function isPathArrowSelectionActive(): boolean {
    if (latestGameState == null) return false;
    if (actionInputLocked) return false;
    const actorId = getActiveOwnedPlayerId(latestGameState);
    if (actorId == null) return false;
    if (isPathArrowItemDirectionTargetingActive()) return isPreRollPathPhase(latestGameState.phase);
    if (latestGameState.phase === 'fork_choice') return getPendingForkForLocalPlayer() != null;
    if (!isPreRollPathPhase(latestGameState.phase)) return false;
    if (isFrozenForCurrentRound(latestGameState, actorId)) return false;
    if (isRollSubmissionPendingForState(latestGameState)) return false;
    return screenRouter.current === 'board-hud';
  }

  function getSelectedMovementNextNodeIdForRoll(): string | null {
    if (latestGameState == null) return null;
    if (!isPreRollPathPhase(latestGameState.phase)) return null;
    const actorId = getActiveOwnedPlayerId(latestGameState);
    if (actorId == null) return null;
    const nextNodeId = pathArrows?.getSelectedNextNodeId() ?? null;
    if (nextNodeId == null) return null;
    const player = latestGameState.players.get(actorId);
    const currentNode = player == null ? null : findMapNode(player.nodeId);
    const isValidDirection =
      currentNode?.connections.some((connection) => connection.targetId === nextNodeId) ?? false;
    if (isValidDirection) return nextNodeId;

    clearPathArrowsAndFacing();
    syncPathArrowsForState(latestGameState);
    return null;
  }

  function clearBoardRigFacingPreview(playerId?: string): void {
    if (playerId == null) {
      for (const handle of boardRigFacingPreviewHandles.values()) handle.dispose();
      boardRigFacingPreviewHandles.clear();
      boardPathArrowFacingPreviewKey = null;
      return;
    }
    boardRigFacingPreviewHandles.get(playerId)?.dispose();
    boardRigFacingPreviewHandles.delete(playerId);
    if (boardPathArrowFacingPreviewKey?.startsWith(`${playerId}:`) === true) {
      boardPathArrowFacingPreviewKey = null;
    }
  }

  function setIdleTurnPreviewOverride(playerId: string, turnDelta: number, nowMs: number): void {
    if (Math.abs(turnDelta) < PATH_ARROW_FACING_IDLE_TURN_THRESHOLD) return;
    const activeOverride = boardRigAnimationOverrides.get(playerId);
    if (
      activeOverride != null &&
      activeOverride.untilMs > nowMs &&
      activeOverride.alias !== 'idleTurnLeft' &&
      activeOverride.alias !== 'idleTurnRight'
    ) {
      return;
    }
    boardRigAnimationOverrides.set(playerId, {
      alias: turnDelta > 0 ? 'idleTurnRight' : 'idleTurnLeft',
      untilMs: nowMs + CHARACTER_TURN_ANIMATION_SECONDS * 1000,
    });
  }

  function faceActiveBoardRigTowardSelectedPathArrow(nextNodeId: string | null): void {
    const state = latestGameState;
    if (state == null || nextNodeId == null || isPathArrowItemDirectionTargetingActive()) return;
    if (!isPreRollPathPhase(state.phase) && state.phase !== 'fork_choice') return;
    const playerId = getActiveOwnedPlayerId(state);
    if (playerId == null) return;
    const player = state.players.get(playerId);
    const rig = boardRigs.get(playerId);
    if (player == null || rig == null || boardMoveHandles.has(playerId)) return;
    const target = boardHandle?.getWorldPositionForNode(nextNodeId) ?? null;
    if (target == null) return;
    const currentNodeId = player.nodeId;
    const scopeId = state.phase === 'fork_choice' ? (state.pendingForkChoice?.id ?? '') : '';
    const previewKey = `${playerId}:${state.phase}:${state.round}:${state.currentPlayerIndex}:${currentNodeId}:${nextNodeId}:${scopeId}`;
    if (previewKey === boardPathArrowFacingPreviewKey) return;

    const currentPosition = rig.root.position;
    if (Math.hypot(target.x - currentPosition.x, target.z - currentPosition.z) < 0.001) return;

    const startYaw = boardRigFacingYawOffsets.get(playerId) ?? rig.root.rotation.y;
    const targetYaw = yawFromTo(currentPosition, target);
    const turnDelta = shortestAngleDelta(startYaw, targetYaw);
    const finalYaw = startYaw + turnDelta;
    const nowMs = performance.now();
    clearBoardRigFacingPreview(playerId);
    boardPathArrowFacingPreviewKey = previewKey;
    setIdleTurnPreviewOverride(playerId, turnDelta, nowMs);

    const proxy = { yaw: startYaw };
    const handle = gsapRegistry.create(`board-rig:${playerId}:path-arrow-facing`);
    boardRigFacingPreviewHandles.set(playerId, handle);
    const apply = (): void => {
      boardRigFacingYawOffsets.set(playerId, proxy.yaw);
    };
    handle.timeline.to(
      proxy,
      {
        yaw: finalYaw,
        duration: PATH_ARROW_FACING_TURN_SECONDS,
        ease: 'power1.out',
        onUpdate: apply,
      },
      0,
    );
    handle.timeline.call(
      () => {
        if (boardRigFacingPreviewHandles.get(playerId) === handle) {
          boardRigFacingPreviewHandles.delete(playerId);
          boardRigFacingYawOffsets.set(playerId, finalYaw);
        }
        handle.dispose();
      },
      undefined,
      PATH_ARROW_FACING_TURN_SECONDS,
    );
  }

  function selectMovementPathArrow(nextNodeId: string): void {
    pathArrows?.selectNextNodeId(nextNodeId);
    faceActiveBoardRigTowardSelectedPathArrow(pathArrows?.getSelectedNextNodeId() ?? null);
  }

  function cycleMovementPathArrow(direction: -1 | 1): string | null {
    const nextNodeId = pathArrows?.cycleSelection(direction) ?? null;
    faceActiveBoardRigTowardSelectedPathArrow(nextNodeId);
    return nextNodeId;
  }

  function forkChoiceSubmissionKey(nextNodeId: string): string | null {
    const pending = getPendingForkForLocalPlayer();
    const actorId = getActiveOwnedPlayerId(latestGameState);
    if (pending == null || actorId == null) return null;
    return `${actorId}:${pending.atNodeId}:${nextNodeId}`;
  }

  function submitSelectedForkChoice(): void {
    if (latestGameState?.phase !== 'fork_choice') return;
    const nextNodeId = pathArrows?.getSelectedNextNodeId() ?? null;
    if (nextNodeId == null) return;
    const key = forkChoiceSubmissionKey(nextNodeId);
    if (key == null || submittedForkChoiceKey === key) return;
    submittedForkChoiceKey = key;
    sendWithActionLock({ type: 'choose_fork', nextNodeId });
  }

  function confirmSelectedPathArrow(): void {
    if (latestGameState?.phase === 'fork_choice') {
      submitSelectedForkChoice();
      return;
    }
    beginSelectedMovementRoll();
  }

  function selectPathArrowFromPointer(event: PointerEvent): boolean {
    if (!isPathArrowSelectionActive()) return false;
    const camera = renderer.cameraRig?.camera;
    if (camera == null || pathArrows == null) return false;
    const nextNodeId = pathArrows.pickNextNodeId(event.clientX, event.clientY, camera, opts.canvas);
    if (nextNodeId == null) return false;
    if (isPathArrowItemDirectionTargetingActive()) {
      pathArrows.selectNextNodeId(nextNodeId);
      aimItemDirectionToNode(nextNodeId);
      return true;
    }
    selectMovementPathArrow(nextNodeId);
    if (latestGameState?.phase === 'fork_choice') {
      submitSelectedForkChoice();
    }
    return true;
  }

  function beginSelectedMovementRoll(): void {
    const state = latestGameState;
    if (!isPathArrowSelectionActive() || state == null || !isPreRollPathPhase(state.phase)) return;
    const currentPid = state.turnOrder[state.currentPlayerIndex];
    if (currentPid == null) return;
    const payload = makeBoardPayload(state);
    if (payload == null) return;
    screenRouter.navigate('dice-roll', {
      ...payload,
      state,
      rollerPlayerId: currentPid,
      autoRoll: true,
      shouldDeferBoardHudReturn: isSystemOverlayOpen,
    });
  }

  function sendWarmupArenaMove(nodeId: string): void {
    if (!inWarmupArena) return;
    if (warmupPendingMove?.nodeId === nodeId) return;
    warmupPendingMove = { nodeId, elapsedSeconds: 0 };
    wsClient.send({ type: 'walk_to_arena_tile', nodeId });
  }

  function handleBoardPointerDown(event: PointerEvent): void {
    if (event.button !== 0 && event.pointerType !== 'touch') return;
    if (
      !isItemPlayerTargetingActive() &&
      !isItemDirectionalTargetingActive() &&
      !isPathArrowSelectionActive() &&
      !inWarmupArena &&
      devBoardTileClickListeners.size === 0
    ) {
      return;
    }
    boardPointerDown = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
    };
  }

  function handleBoardPointerUp(event: PointerEvent): void {
    if (boardPointerDown == null) return;
    if (boardPointerDown.pointerId !== event.pointerId) return;
    const dx = event.clientX - boardPointerDown.x;
    const dy = event.clientY - boardPointerDown.y;
    boardPointerDown = null;
    if (Math.sqrt(dx * dx + dy * dy) > 8) return;
    if (selectItemPlayerTargetFromPointer(event)) return;
    if (selectThunderFistPathTargetFromPointer(event)) return;
    if (selectItemDirectionFromPointer(event)) return;
    if (selectPathArrowFromPointer(event)) return;
    const nodeId = pickBoardTileFromPointer(event);
    if (nodeId == null) return;
    for (const listener of devBoardTileClickListeners) listener(nodeId);
    if (inWarmupArena) sendWarmupArenaMove(nodeId);
  }

  function isTextEntryTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return (
      target.closest(
        ['input', 'select', 'textarea', '[contenteditable="true"]', '[role="textbox"]'].join(','),
      ) != null
    );
  }

  function readDpadMovement(
    snapshot: {
      readonly buttons: readonly { readonly pressed: boolean }[];
    } | null,
  ): Vec2 {
    if (snapshot == null) return { x: 0, y: 0 };
    return {
      x:
        (snapshot.buttons[15]?.pressed === true ? 1 : 0) -
        (snapshot.buttons[14]?.pressed === true ? 1 : 0),
      y:
        (snapshot.buttons[12]?.pressed === true ? 1 : 0) -
        (snapshot.buttons[13]?.pressed === true ? 1 : 0),
    };
  }

  function combineMovement(primary: Vec2, fallback: Vec2): Vec2 {
    return {
      x: Math.abs(primary.x) > 0.001 ? primary.x : fallback.x,
      y: Math.abs(primary.y) > 0.001 ? primary.y : fallback.y,
    };
  }

  function movementMagnitude(movement: Vec2): number {
    return Math.hypot(movement.x, movement.y);
  }

  function readWarmupDirectionalInput(): Vec2 {
    warmupGamepad.poll();
    for (let idx = 0; idx < 4; idx += 1) {
      const snapshot = warmupGamepad.getSnapshot(idx);
      if (snapshot == null || !snapshot.connected) continue;
      const stick = applyDeadzoneAndCurve(warmupGamepad.getMovement(idx));
      const dpad = readDpadMovement(snapshot);
      const movement = combineMovement(stick, dpad);
      if (movementMagnitude(movement) >= WARMUP_DIRECTIONAL_INPUT_MIN_MAGNITUDE) {
        return movement;
      }
    }

    const p1 = warmupKeyboard.getMovement('p1');
    const p2 = warmupKeyboard.getMovement('p2');
    const keyboard = combineMovement(p1, p2);
    return movementMagnitude(keyboard) >= WARMUP_DIRECTIONAL_INPUT_MIN_MAGNITUDE
      ? keyboard
      : { x: 0, y: 0 };
  }

  function getLocalWarmupNodeId(): string | null {
    if (localPlayerId == null || latestGameState == null) return null;
    return latestGameState.players.get(localPlayerId)?.nodeId ?? null;
  }

  function tickWarmupArenaDirectionalInput(dt: number): void {
    warmupMoveCooldownSeconds = Math.max(0, warmupMoveCooldownSeconds - dt);

    if (warmupPendingMove != null) {
      warmupPendingMove.elapsedSeconds += dt;
      if (getLocalWarmupNodeId() === warmupPendingMove.nodeId) {
        warmupPendingMove = null;
      } else if (
        warmupPendingMove.elapsedSeconds >= WARMUP_DIRECTIONAL_MOVE_PENDING_TIMEOUT_SECONDS
      ) {
        warmupPendingMove = null;
      }
    }

    if (!inWarmupArena || screenRouter.current !== 'warmup-arena-overlay') return;
    if (mapHandle == null || boardHandle == null || warmupMoveCooldownSeconds > 0) return;
    if (isTextEntryTarget(document.activeElement)) return;

    const currentNodeId = getLocalWarmupNodeId();
    const camera = renderer.cameraRig?.camera;
    if (currentNodeId == null || camera == null) return;

    const input = readWarmupDirectionalInput();
    if (movementMagnitude(input) < WARMUP_DIRECTIONAL_INPUT_MIN_MAGNITUDE) return;

    const cameraForward = camera.getWorldDirection(new THREE.Vector3());
    const cameraRight = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
    const targetNodeId = pickWarmupArenaDirectionalTarget({
      nodes: mapHandle.schema.nodes as readonly BoardNode[],
      currentNodeId,
      arenaIslandId: getWarmupArenaIslandId(),
      input,
      cameraForward,
      cameraRight,
    });
    if (targetNodeId == null || targetNodeId === currentNodeId) return;

    sendWarmupArenaMove(targetNodeId);
    warmupMoveCooldownSeconds = WARMUP_DIRECTIONAL_MOVE_COOLDOWN_SECONDS;
  }

  function handleMovementDirectionKeydown(event: KeyboardEvent): void {
    if (
      !isPathArrowSelectionActive() &&
      !isItemPlayerTargetingActive() &&
      !isItemDirectionalTargetingActive()
    )
      return;
    if (isTextEntryTarget(event.target) || event.defaultPrevented) return;
    if (event.code === 'ArrowLeft' || event.code === 'KeyA') {
      event.preventDefault();
      if (isPathArrowItemDirectionTargetingActive()) {
        const nextNodeId = pathArrows?.cycleSelection(-1) ?? null;
        if (nextNodeId != null) aimItemDirectionToNode(nextNodeId);
      } else if (isItemDirectionalTargetingActive()) {
        aimItemDirectionFromKey('left');
      } else if (isItemPlayerTargetingActive()) {
        cycleItemPlayerTarget(-1);
      } else {
        cycleMovementPathArrow(-1);
      }
    } else if (event.code === 'ArrowRight' || event.code === 'KeyD') {
      event.preventDefault();
      if (isPathArrowItemDirectionTargetingActive()) {
        const nextNodeId = pathArrows?.cycleSelection(1) ?? null;
        if (nextNodeId != null) aimItemDirectionToNode(nextNodeId);
      } else if (isItemDirectionalTargetingActive()) {
        aimItemDirectionFromKey('right');
      } else if (isItemPlayerTargetingActive()) {
        cycleItemPlayerTarget(1);
      } else {
        cycleMovementPathArrow(1);
      }
    } else if (event.code === 'ArrowUp' || event.code === 'KeyW') {
      if (isPathArrowItemDirectionTargetingActive()) return;
      if (!isItemDirectionalTargetingActive()) return;
      event.preventDefault();
      aimItemDirectionFromKey('up');
    } else if (event.code === 'ArrowDown' || event.code === 'KeyS') {
      if (isPathArrowItemDirectionTargetingActive()) return;
      if (!isItemDirectionalTargetingActive()) return;
      event.preventDefault();
      aimItemDirectionFromKey('down');
    } else if (event.code === 'Enter' || event.code === 'Space') {
      if (document.activeElement instanceof HTMLButtonElement) return;
      event.preventDefault();
      if (isItemDirectionalTargetingActive()) {
        confirmSelectedItemDirection();
      } else if (isItemPlayerTargetingActive()) {
        confirmSelectedItemPlayerTarget();
      } else {
        confirmSelectedPathArrow();
      }
    }
  }

  function tickMovementDirectionGamepadInput(dt: number): void {
    movementDirectionStickCooldownSeconds = Math.max(0, movementDirectionStickCooldownSeconds - dt);
    if (
      !isPathArrowSelectionActive() &&
      !isItemPlayerTargetingActive() &&
      !isItemDirectionalTargetingActive()
    )
      return;
    warmupGamepad.poll();
    for (let idx = 0; idx < 4; idx += 1) {
      const snapshot = warmupGamepad.getSnapshot(idx);
      if (snapshot == null || !snapshot.connected) continue;
      if (movementDirectionStickCooldownSeconds <= 0 && snapshot.buttons[14]?.pressed === true) {
        if (isPathArrowItemDirectionTargetingActive()) {
          const nextNodeId = pathArrows?.cycleSelection(-1) ?? null;
          if (nextNodeId != null) aimItemDirectionToNode(nextNodeId);
        } else if (isItemDirectionalTargetingActive()) {
          aimItemDirectionFromKey('left');
        } else if (isItemPlayerTargetingActive()) {
          cycleItemPlayerTarget(-1);
        } else {
          cycleMovementPathArrow(-1);
        }
        movementDirectionStickCooldownSeconds = 0.28;
        break;
      }
      if (movementDirectionStickCooldownSeconds <= 0 && snapshot.buttons[15]?.pressed === true) {
        if (isPathArrowItemDirectionTargetingActive()) {
          const nextNodeId = pathArrows?.cycleSelection(1) ?? null;
          if (nextNodeId != null) aimItemDirectionToNode(nextNodeId);
        } else if (isItemDirectionalTargetingActive()) {
          aimItemDirectionFromKey('right');
        } else if (isItemPlayerTargetingActive()) {
          cycleItemPlayerTarget(1);
        } else {
          cycleMovementPathArrow(1);
        }
        movementDirectionStickCooldownSeconds = 0.28;
        break;
      }
      if (warmupGamepad.wasJustPressed(idx, 'action')) {
        if (isItemDirectionalTargetingActive()) {
          confirmSelectedItemDirection();
        } else if (isItemPlayerTargetingActive()) {
          confirmSelectedItemPlayerTarget();
        } else {
          confirmSelectedPathArrow();
        }
        break;
      }
      if (
        warmupGamepad.wasJustPressed(idx, 'item') &&
        (isItemPlayerTargetingActive() || isItemDirectionalTargetingActive())
      ) {
        activeItemPlayerTargeting?.onCancel?.();
        activeItemDirectionalTargeting?.onCancel?.();
        break;
      }

      const stick = applyDeadzoneAndCurve(warmupGamepad.getMovement(idx));
      if (
        isItemDirectionalTargetingActive() &&
        !isPathArrowItemDirectionTargetingActive() &&
        movementMagnitude(stick) >= 0.65
      ) {
        aimItemDirection({ dx: stick.x, dy: stick.y });
        if (movementDirectionStickCooldownSeconds <= 0) {
          movementDirectionStickCooldownSeconds = 0.12;
        }
        break;
      }
      if (movementDirectionStickCooldownSeconds <= 0 && Math.abs(stick.x) >= 0.65) {
        if (isPathArrowItemDirectionTargetingActive()) {
          const nextNodeId = pathArrows?.cycleSelection(stick.x < 0 ? -1 : 1) ?? null;
          if (nextNodeId != null) aimItemDirectionToNode(nextNodeId);
        } else if (isItemPlayerTargetingActive()) {
          cycleItemPlayerTarget(stick.x < 0 ? -1 : 1);
        } else {
          cycleMovementPathArrow(stick.x < 0 ? -1 : 1);
        }
        movementDirectionStickCooldownSeconds = 0.28;
        break;
      }
    }
  }

  opts.canvas.addEventListener('pointerdown', handleBoardPointerDown);
  opts.canvas.addEventListener('pointerup', handleBoardPointerUp);
  document.addEventListener('keydown', handleMovementDirectionKeydown);
  let wasEmoteWheelOpen = false;
  const warmupDirectionalInputUnsub = renderer.loop.addFrameTickCallback((dt) => {
    tickMovementDirectionGamepadInput(dt);
    tickWarmupArenaDirectionalInput(dt);
    emoteRuntime?.tick();
    const emoteOpen = emoteRuntime?.isOpen() === true;
    if (emoteOpen && !wasEmoteWheelOpen && gameplayTutorialHandle?.minimized === false) {
      gameplayTutorialHandle.minimize();
    }
    wasEmoteWheelOpen = emoteOpen;
  });
  window.addEventListener(WARMUP_ARENA_WATCH_EVENT, focusWarmupMainlandCamera);
  window.addEventListener(WARMUP_ARENA_BACK_EVENT, handleWarmupArenaBackCamera);

  function getBoardNodeWorldPosition(node: {
    id: string;
    position: { x: number; y?: number; z: number };
  }): THREE.Vector3 {
    const resolved = boardHandle?.getWorldPositionForNode(node.id);
    if (resolved != null) return resolved.clone();
    return new THREE.Vector3(node.position.x, node.position.y ?? 0, node.position.z);
  }

  function focusForNode(nodeId: string): THREE.Vector3 | null {
    const node = findMapNode(nodeId);
    if (node == null) return null;
    const position = getBoardNodeWorldPosition(node);
    position.y += BOARD_FOCUS_Y_OFFSET;
    return position;
  }

  function focusItemTargetCameraOnPlayer(playerId: string): void {
    const rigPosition = findPlayerRigPosition(playerId);
    if (rigPosition != null) {
      rigPosition.y += BOARD_FOCUS_Y_OFFSET;
      orbitTargetFollower?.follow(rigPosition);
      return;
    }

    const nodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    if (nodeId == null) return;
    const focus = focusForNode(nodeId);
    if (focus == null) return;
    orbitTargetFollower?.follow(focus);
  }

  function boardCentroidFocus(): THREE.Vector3 | null {
    if (mapHandle == null) return null;
    return computeBoardCentroidFocus(mapHandle.schema.nodes as readonly BoardNode[]);
  }

  let lastOrbitFollowKey: string | null = null;

  function syncOrbitTargetToActivePlayer(state: GameState): void {
    if (state.phase === 'lobby' || state.phase === 'game_over') {
      lastOrbitFollowKey = null;
      return;
    }
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    const activeNodeId = activeId == null ? null : state.players.get(activeId)?.nodeId;
    if (activeId == null || activeNodeId == null) {
      lastOrbitFollowKey = null;
      return;
    }
    const followKey = `${activeId}:${activeNodeId}`;
    if (followKey === lastOrbitFollowKey) return;
    const focus = focusForNode(activeNodeId);
    if (focus == null) return;
    lastOrbitFollowKey = followKey;
    orbitTargetFollower?.follow(focus);
  }

  function isBoardPhaseScreen(screenId: ScreenId | null): boolean {
    return (
      screenId === 'board-hud' ||
      screenId === 'turn-banner' ||
      screenId === 'dice-roll' ||
      screenId === 'tile-effect-popup' ||
      screenId === 'item-targeting' ||
      screenId === 'soul-anchor' ||
      screenId === 'treasure-chest' ||
      screenId === 'weapons-cache' ||
      screenId === 'inventory' ||
      screenId === 'map-overview' ||
      screenId === 'scoreboard' ||
      screenId === 'round-summary'
    );
  }

  function isEncounterModalScreen(screenId: ScreenId | null): boolean {
    return screenId === 'treasure-chest' || screenId === 'weapons-cache';
  }

  function hasMatchingLocalBoardEncounterForScreen(
    state: GameState,
    screenId: ScreenId | null,
  ): boolean {
    const pending = state.pendingBoardEncounter;
    if (pending == null || !isOwnedPlayerId(pending.playerId, state)) return false;
    if (screenId === 'treasure-chest') return pending.kind === 'treasure_chest';
    if (screenId === 'weapons-cache') return pending.kind === 'weapons_cache';
    return false;
  }

  function reconcileSuppressedPromptWithState(state: GameState): void {
    if (suppressedPrompt == null) return;
    if (!doesStateStillContainSuppressedPrompt(state)) {
      suppressedPrompt = null;
    }
  }

  function doesStateStillContainSuppressedPrompt(state: GameState): boolean {
    const suppression = suppressedPrompt;
    if (suppression == null) return false;
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    if (turnKeyForState(state, activeId) !== suppression.turnKey) return false;

    if (suppression.kind === 'soul_anchor') {
      const prompt = state.pendingSoulAnchorClaim;
      return (
        prompt != null &&
        prompt.playerId === suppression.playerId &&
        prompt.nodeId === suppression.nodeId
      );
    }

    const prompt = state.pendingBoardEncounter;
    return (
      prompt != null &&
      prompt.kind === suppression.kind &&
      prompt.playerId === suppression.playerId &&
      prompt.nodeId === suppression.nodeId
    );
  }

  function shouldSuppressLocalPromptRoute(state: GameState): boolean {
    if (suppressedPrompt == null) return false;
    return doesStateStillContainSuppressedPrompt(state);
  }

  function queuedMovementStepCount(): number {
    let count = 0;
    for (const queue of queuedMovementSteps.values()) count += queue.length;
    return count;
  }

  function serverMessageTraceDetails(msg: ServerMessage): Partial<PresentationTraceDetails> {
    if (msg.type !== 'event') return { serverMessageType: msg.type };
    return {
      serverMessageType: 'event',
      serverEventType: msg.event.type,
      eventTimestamp: msg.event.timestamp,
      nodeId:
        'nodeId' in msg.event && typeof msg.event.nodeId === 'string'
          ? msg.event.nodeId
          : undefined,
    };
  }

  function tracePresentation(details: PresentationTraceDetails): void {
    if (!presentationTraceEnabled) return;
    if (disposed || localPlayerId == null || latestGameState == null) return;
    if (!allowPresentationTrace()) return;
    const activePlayerId =
      latestGameState.turnOrder[latestGameState.currentPlayerIndex] ?? undefined;
    wsClient.send({
      type: 'client_presentation_trace',
      playerId: localPlayerId,
      round: latestGameState.round,
      currentPlayerIndex: latestGameState.currentPlayerIndex,
      phase: latestGameState.phase,
      activePlayerId,
      screen: screenRouter.current ?? undefined,
      ...details,
      counts: {
        pendingBoardMessages: pendingBoardPresentationMessages.length,
        deferredStateSyncs: pendingBoardStateSyncMessageCount(),
        pendingRelocationReveals: pendingSpecialRelocationReveals.length,
        heldMovementPlayers: heldMovementPlayers.size,
        animatingMovementPlayers: animatingMovementPlayers.size,
        queuedMovementSteps: queuedMovementStepCount(),
        notificationsActive: notificationsActive ? 1 : 0,
        boardEffectActivityCount,
        movementBusy: isMovementCinematicBusy() ? 1 : 0,
        canonicalFutureState:
          canonicalGameState != null && canonicalGameState !== latestGameState ? 1 : 0,
        ...(details.counts ?? {}),
      },
    });
  }

  function allowPresentationTrace(now = performance.now()): boolean {
    const cutoff = now - PRESENTATION_TRACE_RATE_WINDOW_MS;
    presentationTraceTimestamps = presentationTraceTimestamps.filter((t) => t > cutoff);
    if (presentationTraceTimestamps.length >= PRESENTATION_TRACE_RATE_LIMIT) return false;
    presentationTraceTimestamps.push(now);
    return true;
  }

  function makeBoardPayload(state: GameState): BoardMountPayload | null {
    if (localPlayerId == null || !state.players.has(localPlayerId)) return null;
    return {
      state,
      localPlayerId,
      send: sendWithActionLock,
      subscribe: subscribeBoardPresentationMessages,
      subscribeLiveHudState,
      isActionControlLocked,
      subscribeActionControlLock,
      playSfx,
      previewItemInHand,
      clearItemInHandPreview: clearHeldItemPreview,
      getSelectedMovementNextNodeId: getSelectedMovementNextNodeIdForRoll,
      startItemPlayerTargeting,
      startItemDirectionalTargeting,
      startItemRangePreview,
      getPlayerNotificationAnchor,
      isPlayerMovementPresentationContinuing,
      onNotificationActivityChange: handleNotificationActivityChange,
      onBoardEffectActivityChange: handleBoardEffectActivityChange,
      shouldDeferBoardHudReturn: isSystemOverlayOpen,
      tracePresentation,
    };
  }

  function isMovementCinematicBusy(): boolean {
    if (orbitalCinematicRunning) return true;
    if (trapCinematicRunning) return true;
    if (thunderFistCinematicRunning) return true;
    if (lightningDashPresentationRunning || pendingLightningDashPresentations.length > 0) {
      return true;
    }
    if (shadowStalkerSpawnRevealRunning || pendingShadowStalkerSpawnReveals.length > 0) return true;
    if (specialRelocationRevealRunning) return true;
    if (heldMovementPlayers.size > 0 || animatingMovementPlayers.size > 0) return true;
    for (const queue of queuedMovementSteps.values()) {
      if (queue.length > 0) return true;
    }
    return false;
  }

  function isTurnBannerBlockedForPlayer(playerId: string): boolean {
    if (orbitalCinematicRunning) return true;
    if (trapCinematicRunning) return true;
    if (thunderFistCinematicRunning) return true;
    if (lightningDashPresentationRunning || pendingLightningDashPresentations.length > 0) {
      return true;
    }
    if (shadowStalkerSpawnRevealRunning || pendingShadowStalkerSpawnReveals.length > 0) return true;
    if (specialRelocationRevealRunning || pendingSpecialRelocationReveals.length > 0) return true;
    if (animatingMovementPlayers.size > 0) return true;
    for (const heldPlayerId of heldMovementPlayers) {
      if (heldPlayerId !== playerId) return true;
    }
    for (const [queuedPlayerId, queue] of queuedMovementSteps) {
      if (queuedPlayerId !== playerId && queue.length > 0) return true;
    }
    return false;
  }

  function isPresentationBlocked(): boolean {
    return (
      boardPresentationPaused ||
      isMovementCinematicBusy() ||
      notificationsActive ||
      shadowStalkerSpawnRevealRunning ||
      shadowStalkerMotionRunning ||
      shadowStalkerKillRunning ||
      pendingShadowStalkerMoves.length > 0 ||
      pendingShadowStalkerKills.length > 0
    );
  }

  function shouldStateSyncWaitForPendingEvent(event: GameEvent): boolean {
    switch (event.type) {
      case 'movement_step':
      case 'landing':
      case 'board_object_collected':
      case 'player_received_item':
      case 'economy_change':
      case 'damage':
      case 'heal':
      case 'death':
      case 'respawn':
      case 'soul_anchor_claim':
      case 'soul_anchor_trigger':
      case 'item_blocked_stoneskin':
      case 'item_dot_applied':
      case 'item_dot_expired':
      case 'phase_change':
      case 'turn_end':
        return true;
      default:
        return false;
    }
  }

  function hasPendingStateSyncBlockingPresentationEvents(): boolean {
    return pendingBoardPresentationMessages.some((pending) => {
      return pending.msg.type === 'event' && shouldStateSyncWaitForPendingEvent(pending.msg.event);
    });
  }

  function hasPendingBoardStateSyncMessages(): boolean {
    return pendingBoardPresentationMessages.some((pending) => pending.msg.type === 'state_sync');
  }

  function pendingBoardStateSyncMessageCount(): number {
    return pendingBoardPresentationMessages.reduce(
      (count, pending) => count + (pending.msg.type === 'state_sync' ? 1 : 0),
      0,
    );
  }

  function hasPendingUnresolvedForcedRelocationEvent(): boolean {
    return pendingBoardPresentationMessages.some(
      (pending) => pending.msg.type === 'event' && pending.msg.event.type === 'forced_relocation',
    );
  }

  function getBoardStateSyncDeferralReason(): string | null {
    if (isPresentationBlocked()) return 'presentation_blocked';
    if (hasPendingStateSyncBlockingPresentationEvents()) return 'pending_presentation_events';
    if (hasPendingBoardStateSyncMessages()) return 'pending_state_sync';
    return null;
  }

  const boardPresentationQueue = createBoardPresentationQueue({
    isBlocked: isPresentationBlocked,
  });

  function subscribeBoardPresentationMessages(handler: (msg: ServerMessage) => void): () => void {
    return boardPresentationQueue.subscribe(handler);
  }

  function subscribeLiveHudState(handler: (state: BoardLiveHudState) => void): () => void {
    liveHudStateSubscribers.add(handler);
    if (latestLiveHudState != null) {
      handler(latestLiveHudState);
    } else if (latestGameState != null) {
      publishLiveHudState(toLiveHudState(latestGameState));
    }
    return () => {
      liveHudStateSubscribers.delete(handler);
    };
  }

  function toLiveHudState(state: GameState): BoardLiveHudState {
    const activeTimer = deriveActiveBoardTimer(state);
    return {
      round: state.round,
      maxRounds: state.maxRounds,
      phase: state.phase,
      activePlayerId: state.turnOrder[state.currentPlayerIndex] ?? null,
      turnActionDeadlineAtMs: state.turnActionDeadlineAtMs ?? null,
      forkChoiceDeadlineAtMs: state.forkChoiceDeadlineAtMs ?? null,
      activeTimerContext: activeTimer?.context ?? null,
      activeTimerDeadlineAtMs: activeTimer?.deadlineAtMs ?? null,
      players: state.turnOrder
        .map((id) => {
          const player = state.players.get(id);
          if (player == null) return null;
          return {
            id,
            hp: player.hp,
            keys: player.keys,
            trophies: player.trophies,
            itemCount: player.items.length,
            isDead: player.isDead,
          };
        })
        .filter((player): player is BoardLiveHudState['players'][number] => player != null),
    };
  }

  function publishLiveHudState(snapshot: BoardLiveHudState): void {
    latestLiveHudState = snapshot;
    if (liveHudStateSubscribers.size === 0) return;
    for (const handler of liveHudStateSubscribers) {
      handler(snapshot);
    }
  }

  function publishLiveHudStateFromGameState(state: GameState): void {
    publishLiveHudState(toLiveHudState(state));
  }

  function updateCanonicalGameState(
    state: GameState,
    meta?: { roomId?: string | null; roomRevision?: number | null },
  ): void {
    canonicalGameState = state;
    canonicalGameStateRoomId = meta?.roomId ?? null;
    canonicalGameStateRevision = meta?.roomRevision ?? null;
  }

  function applyLiveHudEventDelta(event: GameEvent): boolean {
    if (latestLiveHudState == null) {
      if (latestGameState == null) return false;
      latestLiveHudState = toLiveHudState(latestGameState);
    }

    switch (event.type) {
      case 'damage':
        patchLiveHudPlayer(event.playerId, { hp: event.newHp });
        break;
      case 'heal':
        patchLiveHudPlayer(event.playerId, { hp: event.newHp });
        break;
      case 'death':
        patchLiveHudPlayer(event.playerId, { isDead: true });
        break;
      case 'respawn':
        patchLiveHudPlayer(event.playerId, { hp: 30, isDead: false });
        break;
      case 'economy_change':
        if (event.resource === 'keys') {
          patchLiveHudPlayer(event.playerId, { keys: event.newValue });
        } else {
          patchLiveHudPlayer(event.playerId, { trophies: event.newValue });
        }
        break;
      case 'player_received_item':
        adjustLiveHudItemCount(event.playerId, 1);
        break;
      case 'item_cast':
        adjustLiveHudItemCount(event.playerId, -1);
        break;
      case 'item_steal_item':
        adjustLiveHudItemCount(event.victimId, -1);
        break;
      default:
        break;
    }
    return true;
  }

  function patchLiveHudPlayer(playerId: string, patch: Partial<BoardLiveHudPlayer>): void {
    if (latestLiveHudState == null) return;
    const players = latestLiveHudState.players.map((player) =>
      player.id === playerId ? { ...player, ...patch } : player,
    );
    publishLiveHudState({ ...latestLiveHudState, players });
  }

  function adjustLiveHudItemCount(playerId: string, delta: number): void {
    if (latestLiveHudState == null) return;
    const current = latestLiveHudState.players.find((player) => player.id === playerId);
    if (current == null) return;
    patchLiveHudPlayer(playerId, { itemCount: Math.max(0, current.itemCount + delta) });
  }

  const liveItemVfxEventLog = {
    subscribe(cb: (event: GameEvent) => void): string {
      const id = `live-item-vfx:${++liveItemVfxSubscriberSeq}`;
      liveItemVfxSubscribers.set(id, cb);
      return id;
    },
    unsubscribe(id: string): void {
      liveItemVfxSubscribers.delete(id);
    },
  };

  function emitLiveItemVfxEvent(event: GameEvent): void {
    for (const cb of liveItemVfxSubscribers.values()) {
      cb(event);
    }
  }

  function flushBoardPresentationMessages(): void {
    if (boardPresentationPaused) return;
    boardPresentationQueue.flush();
    drainBoardPresentationMessages();
    flushPendingTurnBanner();
    flushPendingBoardPromptReady();
    maybeShowPendingMinigameSelection();
    maybeMountPendingMinigameStart();
  }

  function flushDroppedKeyPickupMessages(playerId: string): void {
    boardPresentationQueue.flushDroppedKeyPickups(playerId);
  }

  function flushPassThroughCollectionMessages(playerId: string, nodeId: string): void {
    const selected = extractPassThroughCollectionMessages(playerId, nodeId);
    for (const pending of selected) {
      handleSequencedBoardPresentationMessage(pending);
    }
    boardPresentationQueue.flushPassThroughCollections(playerId, nodeId);
  }

  function extractPassThroughCollectionMessages(
    _playerId: string,
    nodeId: string,
  ): PendingBoardPresentationMessage[] {
    const retained: PendingBoardPresentationMessage[] = [];
    const selected: PendingBoardPresentationMessage[] = [];
    const pendingDroppedKeyEconomyPlayers: string[] = [];

    for (const pending of pendingBoardPresentationMessages) {
      const msg = pending.msg;
      if (
        msg.type === 'event' &&
        msg.event.type === 'board_object_collected' &&
        msg.event.nodeId === nodeId &&
        msg.event.objectKind === 'dropped_keys' &&
        msg.event.quantity > 0
      ) {
        pendingDroppedKeyEconomyPlayers.push(msg.event.playerId);
        selected.push(pending);
        continue;
      }

      if (
        msg.type === 'event' &&
        msg.event.type === 'economy_change' &&
        msg.event.resource === 'keys' &&
        msg.event.reason === 'dropped_keys_pickup' &&
        msg.event.newValue > msg.event.oldValue
      ) {
        const matchingIndex = pendingDroppedKeyEconomyPlayers.indexOf(msg.event.playerId);
        if (matchingIndex >= 0) {
          pendingDroppedKeyEconomyPlayers.splice(matchingIndex, 1);
          selected.push(pending);
          continue;
        }
      }

      retained.push(pending);
    }

    pendingBoardPresentationMessages.length = 0;
    pendingBoardPresentationMessages.push(...retained);
    return selected;
  }

  function clearBoardPresentationSequencer(): void {
    pendingBoardPresentationMessages.length = 0;
    pendingForcedRelocationMessages.length = 0;
    deferredBoardRouteState = null;
    pendingBoardEffectMinigameStateSync = null;
    awaitingForcedRelocationStateSync = false;
    drainingBoardPresentationMessages = false;
    boardEffectActivityCount = 0;
    boardPresentationPaused = false;
    localPresentationPauseActive = false;
    localPauseRequestCoversPresentation = false;
    serverPauseActive = false;
    suppressedPauseNotificationText = null;
    setBoardPresentationGsapPaused(false);
    boardPresentationQueue.clear();
    pendingLightningDashPresentations.length = 0;
    lightningDashPresentationRunning = false;
    setActionInputLock(ACTION_LOCK_LIGHTNING_DASH, false);
    thunderFistCinematicRunning = false;
    setActionInputLock(ACTION_LOCK_THUNDER_FIST_CINEMATIC, false);
    clearLocalActionLifecycle();
  }

  function resetBoardPresentationAfterReconnect(): void {
    clearBoardPresentationSequencer();
    for (const handle of boardMoveHandles.values()) handle.dispose();
    boardMoveHandles.clear();
    clearBoardRigFacingPreview();
    for (const handle of itemUsePresentationHandles.values()) handle.dispose();
    itemUsePresentationHandles.clear();
    for (const handle of itemImpactPresentationHandles.values()) handle.dispose();
    itemImpactPresentationHandles.clear();
    for (const playerId of [...boardRigs.keys()]) {
      characterStatusEffects.cleanupPlayer(playerId);
      characterEyeExpressions.cleanupPlayer(playerId);
    }
    boardRigCustomizationSignatures.clear();
    heldItemUseTokens.clear();
    for (const playerId of [...heldItemObjects.keys()]) removeHeldItemObject(playerId);
    boardRigActionYawOffsets.clear();
    boardRigActionPitchOffsets.clear();
    boardRigActionRollOffsets.clear();
    boardRigActionScale.clear();
    clearBoardRigActionPositionOffsets();
    heldMovementPlayers.clear();
    animatingMovementPlayers.clear();
    queuedMovementSteps.clear();
    pendingSpecialRelocationReveals.length = 0;
    specialRelocationRevealRunning = false;
    pendingLightningDashPresentations.length = 0;
    lightningDashPresentationRunning = false;
    pendingShadowStalkerSpawnReveals.length = 0;
    pendingShadowStalkerMoves.length = 0;
    pendingShadowStalkerKills.length = 0;
    pendingShadowStalkerKilledDespawns.clear();
    shadowStalkerSpawnRevealRunning = false;
    shadowStalkerMotionRunning = false;
    shadowStalkerKillRunning = false;
    setActionInputLock(ACTION_LOCK_SHADOW_STALKER_SPAWN, false);
    setActionInputLock(ACTION_LOCK_SHADOW_STALKER_MOTION, false);
    trapCinematicRunning = false;
    trapCinematicOverlay?.remove();
    trapCinematicOverlay = null;
    setActionInputLock(ACTION_LOCK_TRAP_CINEMATIC, false);
    orbitalCinematicRunning = false;
    orbitalCinematicOverlay?.remove();
    orbitalCinematicOverlay = null;
    setActionInputLock(ACTION_LOCK_ORBITAL_CINEMATIC, false);
    thunderFistCinematicRunning = false;
    setActionInputLock(ACTION_LOCK_THUNDER_FIST_CINEMATIC, false);
    setActionInputLock(ACTION_LOCK_LIGHTNING_DASH, false);
    movementProgressByPlayer.clear();
    pendingRollSubmissionKeysByPlayer.clear();
    pendingTurnBanner = null;
    pendingBoardPromptReady = null;
    lastSentBoardPromptReadyKey = null;
    notificationsActive = false;
    boardEffectActivityCount = 0;
    pendingBoardEffectMinigameStateSync = null;
    boardPresentationPaused = false;
    localPresentationPauseActive = false;
    localPauseRequestCoversPresentation = false;
    serverPauseActive = false;
    suppressedPauseNotificationText = null;
    setBoardPresentationGsapPaused(false);
    dicePresentationChain = Promise.resolve();
    presentationTraceTimestamps = [];
  }

  function enqueueDeferredBoardStateSync(
    msg: Extract<ServerMessage, { type: 'state_sync' }>,
    state: GameState,
    opts: { forceBoardRemount?: boolean; relocationGate?: boolean } = {},
  ): void {
    publishLiveHudStateFromGameState(state);
    const coalescibleStateSync = opts.relocationGate !== true;
    const tail = pendingBoardPresentationMessages.at(-1);
    if (
      coalescibleStateSync &&
      tail?.msg.type === 'state_sync' &&
      tail.coalescibleStateSync === true
    ) {
      pendingBoardPresentationMessages[pendingBoardPresentationMessages.length - 1] = {
        msg,
        stateSyncState: state,
        forceBoardRemount: tail.forceBoardRemount || opts.forceBoardRemount === true,
        deferredStateSync: true,
        coalescibleStateSync: true,
      };
      drainBoardPresentationMessages();
      return;
    }

    pendingBoardPresentationMessages.push({
      msg,
      stateSyncState: state,
      forceBoardRemount: opts.forceBoardRemount === true,
      deferredStateSync: true,
      coalescibleStateSync,
    });
    drainBoardPresentationMessages();
  }

  function enqueueBoardPresentationMessage(msg: ServerMessage): void {
    if (isPresentationBlocked()) {
      tracePresentation({
        stage: 'queue_defer',
        reason: 'presentation_blocked',
        ...serverMessageTraceDetails(msg),
      });
    }
    const hasQueuedStateSyncAhead =
      msg.type === 'event' ? hasPendingBoardStateSyncMessages() : false;
    const liveHudDeltaApplied =
      msg.type === 'event' ? applyLiveHudEventDelta(msg.event) && !hasQueuedStateSyncAhead : false;
    pendingBoardPresentationMessages.push({
      msg,
      forceBoardRemount: msg.type === 'state_sync' && isPresentationBlocked(),
      coalescibleStateSync: false,
      liveHudDeltaApplied,
    });
    drainBoardPresentationMessages();
  }

  function drainBoardPresentationMessages(): void {
    if (disposed || drainingBoardPresentationMessages || isPresentationBlocked()) return;
    drainingBoardPresentationMessages = true;
    try {
      while (pendingBoardPresentationMessages.length > 0 && !isPresentationBlocked()) {
        const pending = pendingBoardPresentationMessages.shift()!;
        tracePresentation({
          stage: 'queue_release',
          ...serverMessageTraceDetails(pending.msg),
        });
        handleSequencedBoardPresentationMessage(pending);
        boardPresentationQueue.flush();
      }
    } finally {
      drainingBoardPresentationMessages = false;
    }
    maybeShowPendingMinigameSelection();
    maybeMountPendingMinigameStart();
  }

  function isPlayerMovementPresentationContinuing(playerId: string): boolean {
    const progress = movementProgressByPlayer.get(playerId);
    if (progress != null && progress.observed < progress.expected) return true;
    return (queuedMovementSteps.get(playerId)?.length ?? 0) > 0;
  }

  function handleSequencedBoardPresentationMessage(pending: PendingBoardPresentationMessage): void {
    const msg = pending.msg;
    if (msg.type === 'event') {
      if (msg.event.type === 'forced_relocation') {
        handleSequencedBoardEvent(msg.event, {
          applyLiveHudDelta: pending.liveHudDeltaApplied !== true,
        });
        awaitingForcedRelocationStateSync = true;
        boardPresentationQueue.push(msg);
        return;
      }
      if (
        awaitingForcedRelocationStateSync &&
        shouldDeferUntilForcedRelocationStateSync(msg.event)
      ) {
        pendingForcedRelocationMessages.push(pending);
        return;
      }
      handleSequencedBoardEvent(msg.event, {
        applyLiveHudDelta: pending.liveHudDeltaApplied !== true,
      });
      boardPresentationQueue.push(msg);
      return;
    }

    if (msg.type === 'state_sync') {
      const state = pending.stateSyncState ?? deserializeGameState(msg.state);
      if (pending.deferredStateSync === true) {
        tracePresentation({
          stage: 'state_sync_applied',
          phase: state.phase,
          activePlayerId: state.turnOrder[state.currentPlayerIndex] ?? undefined,
          serverMessageType: 'state_sync',
          reason: 'deferred',
        });
      }
      handleStateSyncState(state, { forceBoardRemount: pending.forceBoardRemount });
      boardPresentationQueue.push(msg);
      flushForcedRelocationMessages();
    }
  }

  function shouldDeferUntilForcedRelocationStateSync(event: GameEvent): boolean {
    switch (event.type) {
      case 'landing':
      case 'board_object_collected':
      case 'player_received_item':
      case 'economy_change':
      case 'damage':
      case 'heal':
      case 'death':
      case 'respawn':
      case 'soul_anchor_claim':
      case 'soul_anchor_trigger':
      case 'item_blocked_stoneskin':
        return true;
      default:
        return false;
    }
  }

  function flushForcedRelocationMessages(): void {
    if (!awaitingForcedRelocationStateSync && pendingForcedRelocationMessages.length === 0) {
      return;
    }
    awaitingForcedRelocationStateSync = false;
    while (pendingForcedRelocationMessages.length > 0) {
      const pending = pendingForcedRelocationMessages.shift()!;
      const msg = pending.msg;
      if (msg.type === 'event') {
        handleSequencedBoardEvent(msg.event, {
          applyLiveHudDelta: pending.liveHudDeltaApplied !== true,
        });
      }
      boardPresentationQueue.push(msg);
    }
  }

  function resolvePendingLocalActionFromEvent(event: GameEvent): void {
    const pending = pendingLocalAction;
    if (pending == null) return;
    if (event.timestamp <= pending.submitEventCursor) return;

    switch (pending.kind) {
      case 'use_item':
        if (
          event.type === 'item_cast' &&
          event.casterId === pending.playerId &&
          event.itemId === pending.itemId
        ) {
          resolveAcceptedLocalAction(pending, 'item_cast', { suppressPrompt: false });
        }
        return;
      case 'buy_trophy':
        if (
          event.type === 'economy_change' &&
          event.playerId === pending.playerId &&
          event.reason === 'trophy_purchase'
        ) {
          resolveAcceptedLocalAction(pending, 'trophy_purchase', { suppressPrompt: true });
        }
        return;
      case 'skip_trophy':
        if (event.type === 'phase_change' && event.fromPhase === 'chest_phase') {
          resolveAcceptedLocalAction(pending, 'phase_change', { suppressPrompt: true });
        }
        return;
      case 'claim_weapons_cache':
        if (
          event.type === 'player_received_item' &&
          event.playerId === pending.playerId &&
          event.source === 'weapons_cache' &&
          event.itemId === pending.itemId
        ) {
          resolveAcceptedLocalAction(pending, 'weapons_cache_claim', { suppressPrompt: true });
        }
        return;
      case 'claim_soul_anchor':
        if (
          event.type === 'soul_anchor_claim' &&
          event.playerId === pending.playerId &&
          pending.promptNodeId != null &&
          event.nodeId === pending.promptNodeId &&
          (pending.choice == null || event.mode == null || event.mode === pending.choice)
        ) {
          resolveAcceptedLocalAction(pending, 'soul_anchor_claim', { suppressPrompt: true });
        }
        return;
      case 'roll_dice':
        if (
          (event.type === 'dice_roll' || event.type === 'movement_step') &&
          event.playerId === pending.playerId
        ) {
          resolveAcceptedLocalAction(pending, event.type, { suppressPrompt: false });
        } else if (
          event.type === 'phase_change' &&
          pending.submittedPhase != null &&
          event.fromPhase === pending.submittedPhase
        ) {
          resolveAcceptedLocalAction(pending, 'phase_change', { suppressPrompt: false });
        }
        return;
      case 'choose_fork':
        if (
          event.type === 'fork_choice' &&
          event.playerId === pending.playerId &&
          event.chosenNodeId === pending.nextNodeId
        ) {
          resolveAcceptedLocalAction(pending, 'fork_choice', { suppressPrompt: false });
        }
        return;
      case 'skip_item':
        if (
          event.type === 'phase_change' &&
          (event.fromPhase === 'item_phase' || event.fromPhase === pending.submittedPhase)
        ) {
          resolveAcceptedLocalAction(pending, 'phase_change', { suppressPrompt: false });
        }
        return;
      default:
        return;
    }
  }

  function resolvePendingLocalActionFromStateSync(state: GameState): void {
    const pending = pendingLocalAction;
    if (pending == null) return;
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    const stateTurnKey = turnKeyForState(state, activeId);
    const turnChanged = stateTurnKey !== pending.turnKey;

    switch (pending.kind) {
      case 'roll_dice':
      case 'skip_item':
      case 'choose_fork':
      case 'use_item':
        if (
          turnChanged ||
          (pending.submittedPhase != null && state.phase !== pending.submittedPhase)
        ) {
          resolveAcceptedLocalAction(pending, 'state_sync', { suppressPrompt: false });
        }
        return;
      case 'buy_trophy':
      case 'skip_trophy':
        if (turnChanged || !stateHasMatchingEncounterPrompt(state, pending, 'treasure_chest')) {
          resolveAcceptedLocalAction(pending, 'state_sync', { suppressPrompt: false });
        }
        return;
      case 'claim_weapons_cache':
        if (turnChanged || !stateHasMatchingEncounterPrompt(state, pending, 'weapons_cache')) {
          resolveAcceptedLocalAction(pending, 'state_sync', { suppressPrompt: false });
        }
        return;
      case 'claim_soul_anchor':
        if (turnChanged || !stateHasMatchingSoulAnchorPrompt(state, pending)) {
          resolveAcceptedLocalAction(pending, 'state_sync', { suppressPrompt: false });
        }
        return;
      default:
        return;
    }
  }

  function stateHasMatchingEncounterPrompt(
    state: GameState,
    pending: Extract<
      PendingLocalAction,
      { kind: 'buy_trophy' | 'skip_trophy' | 'claim_weapons_cache' }
    >,
    kind: 'treasure_chest' | 'weapons_cache',
  ): boolean {
    const prompt = state.pendingBoardEncounter;
    return (
      prompt != null &&
      prompt.kind === kind &&
      prompt.playerId === pending.playerId &&
      pending.promptNodeId != null &&
      prompt.nodeId === pending.promptNodeId
    );
  }

  function stateHasMatchingSoulAnchorPrompt(
    state: GameState,
    pending: Extract<PendingLocalAction, { kind: 'claim_soul_anchor' }>,
  ): boolean {
    const prompt = state.pendingSoulAnchorClaim;
    return (
      prompt != null &&
      prompt.playerId === pending.playerId &&
      pending.promptNodeId != null &&
      prompt.nodeId === pending.promptNodeId
    );
  }

  function resolveAcceptedLocalAction(
    pending: PendingLocalAction,
    reason: string,
    opts: { suppressPrompt: boolean },
  ): void {
    if (pendingLocalAction !== pending) return;
    if (opts.suppressPrompt) {
      suppressPromptForAcceptedAction(pending);
    }
    clearPendingLocalAction();
    dismissAcceptedActionUi(pending, reason);
  }

  function suppressPromptForAcceptedAction(pending: PendingLocalAction): void {
    if (
      (pending.kind === 'buy_trophy' || pending.kind === 'skip_trophy') &&
      pending.promptNodeId != null
    ) {
      suppressedPrompt = {
        kind: 'treasure_chest',
        playerId: pending.playerId,
        nodeId: pending.promptNodeId,
        turnKey: pending.turnKey,
      };
      return;
    }
    if (pending.kind === 'claim_weapons_cache' && pending.promptNodeId != null) {
      suppressedPrompt = {
        kind: 'weapons_cache',
        playerId: pending.playerId,
        nodeId: pending.promptNodeId,
        turnKey: pending.turnKey,
      };
      return;
    }
    if (pending.kind === 'claim_soul_anchor' && pending.promptNodeId != null) {
      suppressedPrompt = {
        kind: 'soul_anchor',
        playerId: pending.playerId,
        nodeId: pending.promptNodeId,
        turnKey: pending.turnKey,
        choice: pending.choice,
      };
    }
  }

  function dismissAcceptedActionUi(pending: PendingLocalAction, reason: string): void {
    const state = latestGameState;
    if (state == null) return;
    const payloadBase = makeBoardPayload(state);
    if (payloadBase == null) return;

    if (pending.kind === 'use_item') {
      if (screenRouter.current === 'item-targeting') {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (pending.kind === 'buy_trophy' || pending.kind === 'skip_trophy') {
      if (screenRouter.current === 'treasure-chest') {
        tracePresentation({
          stage: 'modal_close',
          screen: 'treasure-chest',
          encounterKind: 'treasure_chest',
          nodeId: pending.promptNodeId ?? undefined,
          reason,
        });
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (pending.kind === 'claim_weapons_cache') {
      if (screenRouter.current === 'weapons-cache') {
        tracePresentation({
          stage: 'modal_close',
          screen: 'weapons-cache',
          encounterKind: 'weapons_cache',
          nodeId: pending.promptNodeId ?? undefined,
          reason,
        });
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (pending.kind === 'claim_soul_anchor' && screenRouter.current === 'soul-anchor') {
      tracePresentation({
        stage: 'modal_close',
        screen: 'soul-anchor',
        nodeId: pending.promptNodeId ?? undefined,
        reason,
      });
      screenRouter.navigate('board-hud', payloadBase);
    }
  }

  function playerAudioPosition(playerId: string): Vec3 | null {
    const rigPosition = findPlayerRigPosition(playerId);
    if (rigPosition != null) {
      return { x: rigPosition.x, y: rigPosition.y, z: rigPosition.z };
    }
    const nodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    const nodePosition = nodeId != null ? focusForNode(nodeId) : null;
    return nodePosition == null
      ? null
      : { x: nodePosition.x, y: nodePosition.y, z: nodePosition.z };
  }

  function nodeAudioPosition(nodeId: string): Vec3 | null {
    const position = focusForNode(nodeId);
    return position == null ? null : { x: position.x, y: position.y, z: position.z };
  }

  function playSfxForPlayer(id: SfxId, playerId: string | null | undefined): void {
    const position = playerId == null ? null : playerAudioPosition(playerId);
    if (position != null) {
      playSfx(id, { spatialPosition: position });
    } else {
      playSfx(id);
    }
  }

  function playSfxForNode(id: SfxId, nodeId: string | null | undefined): void {
    const position = nodeId == null ? null : nodeAudioPosition(nodeId);
    if (position != null) {
      playSfx(id, { spatialPosition: position });
    } else {
      playSfx(id);
    }
  }

  function itemImpactSfxId(itemId: string): SfxId {
    switch (itemId) {
      case 'thunder_fist':
        return 'sfx-items-thunder-fist-hit';
      case 'frost_blaster':
        return 'sfx-items-frost-blaster-impact';
      case 'meteor_strike':
        return 'sfx-items-meteor-strike-impact';
      case 'orbital_cannon':
        return 'sfx-items-orbital-cannon-discharge';
      case 'soul_duel':
        return 'sfx-items-soul-duel-victory';
      default:
        return 'sfx-combat-hit-heavy';
    }
  }

  function handleBoardEventSfx(event: GameEvent): void {
    switch (event.type) {
      case 'landing':
        if (event.tileType === 'hazard') {
          playSfxForNode('sfx-board-tile-land-bad', event.nodeId);
        } else if (event.tileType === 'heal') {
          playSfxForNode('sfx-misc-ding-positive', event.nodeId);
        } else if (
          event.tileType === 'gold' ||
          event.tileType === 'item' ||
          event.tileType === 'event' ||
          event.tileType === 'soul_anchor'
        ) {
          playSfxForNode('sfx-board-tile-land-good', event.nodeId);
        }
        return;
      case 'board_object_collected':
        if (event.objectKind === 'dropped_keys' && event.quantity > 0) {
          playSfxForNode('sfx-economy-key-pickup', event.nodeId);
        }
        return;
      case 'economy_change':
        if (event.reason === 'trophy_purchase') {
          playSfxForPlayer('sfx-economy-purchase', event.playerId);
        } else if (
          event.resource === 'keys' &&
          event.newValue > event.oldValue &&
          event.reason !== 'dropped_keys_pickup'
        ) {
          playSfxForPlayer('sfx-economy-coin-pickup', event.playerId);
        } else if (event.resource === 'trophies' && event.newValue > event.oldValue) {
          playSfxForPlayer('sfx-fanfare-victory', event.playerId);
        }
        return;
      case 'player_received_item':
        if (event.source === 'weapons_cache') {
          playSfxForPlayer('sfx-board-treasure-open', event.playerId);
        } else if (event.source === 'mystery_box') {
          playSfxForPlayer('sfx-items-mystery-box-chime', event.playerId);
        } else if (event.source === 'item_theft') {
          playSfxForPlayer('sfx-items-gravity-well-pull', event.playerId);
        } else {
          playSfxForPlayer('sfx-misc-ding-positive', event.playerId);
        }
        return;
      case 'damage':
        if (event.source === 'hazard_tile' || event.source === 'soul_anchor') {
          playSfxForPlayer('sfx-board-tile-land-bad', event.playerId);
        } else if (!event.source.startsWith('item_')) {
          playSfxForPlayer(
            event.amount >= 10 ? 'sfx-combat-hit-heavy' : 'sfx-combat-hit-light',
            event.playerId,
          );
        }
        return;
      case 'heal':
        playSfxForPlayer(
          event.source === 'item_phoenix_potion'
            ? 'sfx-items-phoenix-potion-revive'
            : 'sfx-misc-ding-positive',
          event.playerId,
        );
        return;
      case 'death':
        playSfxForPlayer('sfx-sting-defeat', event.playerId);
        return;
      case 'respawn':
        playSfxForPlayer('sfx-items-phoenix-potion-revive', event.playerId);
        return;
      case 'soul_anchor_claim':
      case 'soul_anchor_trigger':
        playSfxForNode('sfx-board-portal-enter', event.nodeId);
        return;
      case 'item_hit':
        playSfxForPlayer(itemImpactSfxId(event.itemId), event.targetId);
        return;
      case 'item_missed':
        playSfxForPlayer('sfx-transitions-whoosh-out', event.casterId);
        return;
      case 'item_blocked_stoneskin':
        playSfxForPlayer('sfx-combat-block', event.blockerId);
        return;
      case 'item_swap_positions':
        playSfxForPlayer('sfx-items-dimension-rift-arrive', event.targetId);
        return;
      case 'forced_relocation':
        playSfxForPlayer('sfx-transitions-whoosh-in', event.actorId);
        return;
      case 'status_effect_applied':
        if (event.effectType === 'frozen') {
          playSfxForPlayer('sfx-items-frost-blaster-impact', event.playerId);
        } else if (event.effectType === 'stoneSkin') {
          playSfxForPlayer('sfx-items-stone-skin-activate', event.playerId);
        } else if (event.effectType === 'toxicCloud') {
          playSfxForPlayer('sfx-items-toxic-cloud-disperse', event.playerId);
        }
        return;
      case 'npc_spawned':
        playSfxForNode('sfx-stalker-spawn-shriek', event.nodeId);
        return;
      case 'npc_killed_player':
        playSfxForNode('sfx-items-shadow-stalker-kill', event.atNodeId);
        return;
      case 'npc_despawned':
        playSfx('sfx-stalker-banish-puff');
        return;
      case 'item_pull':
      case 'item_steal_keys':
      case 'item_steal_item':
        playSfxForPlayer('sfx-items-gravity-well-pull', event.victimId);
        return;
      case 'item_dash_hit':
        playSfxForPlayer('sfx-combat-hit-light', event.victimId);
        return;
      case 'item_beam_fired':
        playSfxForPlayer('sfx-items-orbital-cannon-discharge', event.targetId);
        return;
      case 'item_dot_applied':
        playSfxForPlayer('sfx-items-cursed-relic-pulse', event.victimId);
        return;
      case 'item_dot_tick':
        playSfxForPlayer('sfx-items-cursed-relic-tick', event.victimId);
        return;
      case 'mystery_box_outcome':
        playSfxForPlayer('sfx-items-mystery-box-chime', event.casterId);
        return;
      case 'mystery_box_teleport':
        playSfxForPlayer('sfx-items-dimension-rift-arrive', event.casterId);
        return;
      case 'mystery_box_choice_resolved':
        playSfxForPlayer('sfx-misc-ding-positive', event.casterId);
        return;
      case 'mystery_box_trapped':
        playSfxForPlayer('sfx-misc-buzzer-negative', event.casterId);
        return;
      case 'soul_duel_triggered':
        playSfxForPlayer('sfx-items-soul-duel-portal', event.challengerId);
        return;
      case 'soul_duel_resolved':
        playSfxForPlayer('sfx-items-soul-duel-victory', event.winnerId);
        return;
      case 'award_granted':
        playSfxForPlayer('sfx-confetti-pop', event.playerId);
        return;
      default:
        return;
    }
  }

  function handleSequencedBoardEvent(
    event: GameEvent,
    opts: { applyLiveHudDelta?: boolean } = {},
  ): void {
    resolvePendingLocalActionFromEvent(event);
    lastObservedBoardEventTimestamp = Math.max(lastObservedBoardEventTimestamp, event.timestamp);
    if (opts.applyLiveHudDelta !== false) {
      applyLiveHudEventDelta(event);
    }
    handleCharacterStatusEffectEvent(event);
    observeEyeExpressionLiveEvent(event);
    emitLiveItemVfxEvent(event);
    handleItemUsePresentationEvent(event);
    handleGravityWellTransferPresentationEvent(event);
    handleBoardObjectCollectedEvent(event);
    handleBoardEventSfx(event);
    observeGameplayTutorialEvent(event);

    if (event.type === 'item_cast' && event.playerId === localPlayerId) {
      pendingRollSubmissionKeysByPlayer.delete(event.playerId);
      clearPathArrowsAndFacing();
    }

    if (event.type === 'item_dash' && event.itemId === 'lightning_dash') {
      enqueueLightningDashPresentation(event);
    }

    if (event.type === 'npc_spawned' && event.npcKind === 'shadow_stalker') {
      enqueueShadowStalkerSpawnReveal(event.npcId, event.nodeId, event.summonerId);
    }

    if (event.type === 'npc_moved') {
      enqueueShadowStalkerMove({
        npcId: event.npcId,
        fromNodeId: event.fromNodeId,
        toNodeId: event.toNodeId,
        stepIndex: event.stepIndex,
        totalSteps: event.totalSteps,
      });
    }

    if (event.type === 'npc_killed_player' && event.npcKind === 'shadow_stalker') {
      enqueueShadowStalkerKill({
        npcId: event.npcId,
        playerId: event.playerId,
        atNodeId: event.atNodeId,
      });
    }

    if (event.type === 'npc_despawned') {
      if (event.reason === 'killed_player') {
        const killPresentationPending =
          shadowStalkerMotionRunning ||
          shadowStalkerKillRunning ||
          pendingShadowStalkerKills.some((pending) => pending.npcId === event.npcId);
        if (killPresentationPending) {
          pendingShadowStalkerKilledDespawns.add(event.npcId);
          void processShadowStalkerMotionQueue();
        } else {
          animateShadowStalkerDespawn(event.npcId);
        }
      } else {
        animateShadowStalkerDespawn(event.npcId);
      }
    }

    if (event.type === 'item_beam_fired' && event.itemId === 'orbital_cannon') {
      void runOrbitalCannonCinematic(event.targetId);
    }

    if (event.type === 'mystery_box_trapped') {
      void runMysteryBoxTrapCinematic(event.casterId);
    }

    if (event.type === 'turn_start') {
      const player = latestGameState?.players.get(event.playerId) ?? null;
      if (player?.isBot === true) {
        const turnKey = `bot-turn:${event.round}:${event.playerId}:${event.timestamp}`;
        botTurnIntroKeyByPlayer.set(event.playerId, turnKey);
        void requestTurnBannerForPlayer(event.playerId, turnKey, { requireCurrent: false });
      }
    } else if (event.type === 'dice_roll') {
      pendingForkOptionsByPlayer.delete(event.playerId);
      const player = latestGameState?.players.get(event.playerId) ?? null;
      const leadIn =
        player?.isBot === true
          ? requestTurnBannerForPlayer(
              event.playerId,
              botTurnIntroKeyByPlayer.get(event.playerId) ??
                `bot-turn:fallback:${event.playerId}:${event.timestamp}`,
              { requireCurrent: false },
            )
          : undefined;
      enqueueDiceRollPresentation(event, leadIn);
    } else if (event.type === 'movement_step') {
      const progress = movementProgressByPlayer.get(event.playerId);
      if (progress != null) {
        progress.observed = Math.max(progress.observed, event.stepIndex + 1);
      }
      enqueueMovementStep(event);
    } else if (event.type === 'landing') {
      const progress = movementProgressByPlayer.get(event.playerId);
      if (progress != null) {
        if (progress.observed < progress.expected) {
          console.warn(
            `[smashparty] movement ended after ${progress.observed}/${progress.expected} steps for ${event.playerId} at ${event.nodeId}; the board path may have no available neighbors.`,
          );
        }
        movementProgressByPlayer.delete(event.playerId);
      }
    } else if (event.type === 'turn_end') {
      botTurnIntroKeyByPlayer.delete(event.playerId);
    } else if (event.type === 'damage') {
      const fallbackNodeId = latestGameState?.players.get(event.playerId)?.nodeId ?? null;
      const position =
        findPlayerRigPosition(event.playerId) ??
        (fallbackNodeId != null ? focusForNode(fallbackNodeId) : null);
      if (position != null) {
        bloodPool.spawn(position, Math.max(1, event.amount / 6));
      }
    } else if (event.type === 'fork_choice') {
      pendingForkOptionsByPlayer.delete(event.playerId);
      if (event.playerId === localPlayerId) submittedForkChoiceKey = null;
    }
  }

  function handleBoardObjectCollectedEvent(event: GameEvent): void {
    if (event.type !== 'board_object_collected' || event.objectKind !== 'dropped_keys') return;

    const remaining = event.remainingDroppedKeys.filter((pile) => pile.amount > 0);
    if (latestGameState != null) {
      if (remaining.length > 0) {
        latestGameState.droppedKeys.set(event.nodeId, remaining.map(cloneDroppedKeyPile));
      } else {
        latestGameState.droppedKeys.delete(event.nodeId);
      }
    }

    if (boardHandle == null) return;
    boardHandle.syncDroppedKeysAtNode(
      event.nodeId,
      remaining.length > 0 ? remaining : undefined,
      latestGameState == null ? new Map() : playerColorsForState(latestGameState),
    );
  }

  function cloneDroppedKeyPile(pile: DroppedKeyPile): DroppedKeyPile {
    return { ownerPlayerId: pile.ownerPlayerId, amount: pile.amount };
  }

  function handleCharacterStatusEffectEvent(event: GameEvent): void {
    if (event.type === 'status_effect_applied') {
      if (event.effectType === 'frozen') {
        characterStatusEffects.show(event.playerId, 'ice');
      } else if (event.effectType === 'stoneSkin') {
        characterStatusEffects.show(event.playerId, 'stone');
      }
      return;
    }

    if (event.type === 'status_effect_expired') {
      if (event.effectType === 'frozen') {
        characterStatusEffects.hide(event.playerId, 'ice');
      } else if (event.effectType === 'stoneSkin') {
        characterStatusEffects.hide(event.playerId, 'stone');
      }
      return;
    }

    if (event.type === 'item_dot_applied' && event.itemId === 'cursed_relic') {
      characterStatusEffects.show(event.victimId, 'curse');
      return;
    }

    if (event.type === 'item_dot_expired' && event.itemId === 'cursed_relic') {
      characterStatusEffects.hide(event.victimId, 'curse');
    }
  }

  function findPlayerRigPosition(playerId: string): THREE.Vector3 | null {
    const rig = boardRigs.get(playerId) ?? rosterRigs.get(playerId);
    if (rig == null) return null;
    return rig.root.getWorldPosition(new THREE.Vector3());
  }

  function makeGravityWellFallbackKey(): THREE.Object3D {
    const group = new THREE.Group();
    const material = new THREE.MeshStandardMaterial({
      color: 0xffd24a,
      emissive: 0x6a4a00,
      emissiveIntensity: 0.35,
      metalness: 0.6,
      roughness: 0.35,
    });
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.26, 10), material);
    shaft.rotation.z = Math.PI / 2;
    group.add(shaft);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.016, 8, 18), material);
    ring.position.x = -0.14;
    group.add(ring);
    return group;
  }

  function makeGravityWellFallbackItem(): THREE.Object3D {
    return new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.18, 1),
      new THREE.MeshStandardMaterial({
        color: 0x9966ff,
        emissive: 0x3c167a,
        emissiveIntensity: 0.5,
        roughness: 0.42,
      }),
    );
  }

  function cloneGravityWellTransferModel(
    modelScene: THREE.Group | null,
    fallback: () => THREE.Object3D,
    targetSize: number,
  ): THREE.Object3D {
    const root = modelScene != null ? modelScene.clone(true) : fallback();
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root);
    if (!box.isEmpty()) {
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxAxis = Math.max(size.x, size.y, size.z, 0.0001);
      root.position.sub(center);
      root.scale.multiplyScalar(targetSize / maxAxis);
    }
    root.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    return root;
  }

  function spawnGravityWellTransferObject(
    object: THREE.Object3D,
    fromPlayerId: string,
    toPlayerId: string,
    label: string,
  ): void {
    const from = itemVfxPositionForPlayer(fromPlayerId);
    const to = itemVfxPositionForPlayer(toPlayerId);
    if (from == null || to == null) return;

    const wrapper = new THREE.Group();
    wrapper.name = label;
    wrapper.position.set(from.x, from.y + 0.16, from.z);
    wrapper.add(object);
    renderer.scene.groups.effects.add(wrapper);

    const mid = new THREE.Vector3(
      (from.x + to.x) * 0.5,
      Math.max(from.y, to.y) + 1.25,
      (from.z + to.z) * 0.5,
    );
    const end = new THREE.Vector3(to.x, to.y + 0.42, to.z);
    const proxy = { t: 0, spin: 0, scale: 0.75 };
    const start = wrapper.position.clone();
    const apply = (): void => {
      const a = start.clone().lerp(mid, proxy.t);
      const b = mid.clone().lerp(end, proxy.t);
      wrapper.position.copy(a.lerp(b, proxy.t));
      wrapper.rotation.y = proxy.spin;
      wrapper.rotation.x = proxy.spin * 0.28;
      wrapper.scale.setScalar(proxy.scale);
    };
    apply();

    const handle = gsapRegistry.create(label);
    const tl = handle.timeline;
    tl.to(
      proxy,
      {
        t: 1,
        spin: Math.PI * 4.2,
        scale: 1,
        duration: 1.25,
        ease: 'power2.inOut',
        onUpdate: apply,
      },
      0,
    );
    tl.to(
      proxy,
      {
        scale: 1.18,
        duration: 0.24,
        yoyo: true,
        repeat: 1,
        ease: 'power2.out',
        onUpdate: apply,
      },
      1.25,
    );
    tl.call(
      () => {
        renderer.scene.groups.effects.remove(wrapper);
        handle.dispose();
      },
      undefined,
      1.78,
    );
  }

  function handleGravityWellTransferPresentationEvent(event: GameEvent): void {
    if (event.type === 'item_steal_keys' && event.itemId === 'gravity_well' && event.amount > 0) {
      const object = cloneGravityWellTransferModel(
        droppedKeyGltfScene,
        makeGravityWellFallbackKey,
        0.38,
      );
      spawnGravityWellTransferObject(
        object,
        event.victimId,
        event.casterId,
        `gravity-well-key-transfer:${event.timestamp}`,
      );
      return;
    }

    if (event.type === 'item_steal_item' && event.itemId === 'gravity_well') {
      void loadItemModelScene(event.stolenItemId).then((modelScene) => {
        const object = cloneGravityWellTransferModel(modelScene, makeGravityWellFallbackItem, 0.48);
        spawnGravityWellTransferObject(
          object,
          event.victimId,
          event.casterId,
          `gravity-well-item-transfer:${event.timestamp}:${event.stolenItemId}`,
        );
      });
    }
  }

  function getPlayerNotificationAnchor(
    playerId: string,
  ): { screenX: number; screenY: number } | null {
    const player = latestGameState?.players.get(playerId) ?? null;
    const rigPosition = findPlayerRigPosition(playerId);
    let worldPosition: THREE.Vector3 | null = null;

    if (rigPosition != null) {
      worldPosition = rigPosition.clone();
      worldPosition.y += BOARD_FOCUS_Y_OFFSET;
    } else if (player?.nodeId != null) {
      worldPosition = focusForNode(player.nodeId);
    }

    if (worldPosition == null) {
      worldPosition = boardCentroidFocus();
    }
    if (worldPosition == null) return null;

    renderer.cameraRig.camera.updateMatrixWorld();
    const projected = worldPosition.clone().project(renderer.cameraRig.camera);
    if (
      !Number.isFinite(projected.x) ||
      !Number.isFinite(projected.y) ||
      !Number.isFinite(projected.z)
    ) {
      return null;
    }

    const rect = opts.canvas.getBoundingClientRect();
    const viewportWidth =
      rect.width > 0 ? rect.width : opts.canvas.clientWidth || window.innerWidth || 1;
    const viewportHeight =
      rect.height > 0 ? rect.height : opts.canvas.clientHeight || window.innerHeight || 1;

    return {
      screenX: rect.left + ((projected.x + 1) / 2) * viewportWidth,
      screenY: rect.top + ((1 - projected.y) / 2) * viewportHeight,
    };
  }

  function getRigForItemPreview(playerId: string): CharacterPreviewRig | null {
    return boardRigs.get(playerId) ?? rosterRigs.get(playerId) ?? null;
  }

  function removeHeldItemObject(playerId: string, mode?: 'preview' | 'use'): void {
    if (mode != null && heldItemObjectModes.get(playerId) !== mode) return;
    const object = heldItemObjects.get(playerId);
    if (object == null) return;
    object.parent?.remove(object);
    heldItemObjects.delete(playerId);
    heldItemObjectModes.delete(playerId);
  }

  function clearHeldItemPreview(): void {
    if (activeHeldItemPreview != null) {
      removeHeldItemObject(activeHeldItemPreview.playerId, 'preview');
    }
    activeHeldItemPreview = null;
    heldItemPreviewToken++;
  }

  function loadItemModelScene(itemId: string): Promise<THREE.Group | null> {
    if (gltfLoader == null) return Promise.resolve(null);
    const cached = itemModelSceneCache.get(itemId);
    if (cached != null) return cached;
    const promise = gltfLoader
      .load(runtimeAssetUrl(itemModelUrl(itemId)))
      .then((scene) => {
        resolvedItemModelScenes.set(itemId, scene);
        return scene;
      })
      .catch((err: unknown) => {
        console.warn(`[smashparty] failed to load item preview model "${itemId}":`, err);
        resolvedItemModelScenes.set(itemId, null);
        return null;
      });
    itemModelSceneCache.set(itemId, promise);
    return promise;
  }

  function getResolvedItemModelScene(itemId: string): THREE.Group | null {
    return resolvedItemModelScenes.get(itemId) ?? null;
  }

  function preloadWarmupInventoryItemModels(state: GameState): void {
    const itemIds = new Set<string>();
    for (const player of state.players.values()) {
      for (const itemId of player.items) itemIds.add(itemId);
    }
    if (itemIds.size === 0) return;
    const ordered = [...itemIds].sort((a, b) => {
      if (a === 'thunder_fist') return -1;
      if (b === 'thunder_fist') return 1;
      return a.localeCompare(b);
    });
    void (customizationBackgroundPromise ?? Promise.resolve()).then(() =>
      runLimitedConcurrency(ordered, BACKGROUND_PRELOAD_CONCURRENCY, async (itemId) => {
        await loadItemModelScene(itemId);
      }),
    );
  }

  async function attachHeldItemObject(
    playerId: string,
    itemId: string,
    token: number,
    mode: 'preview' | 'use' = 'preview',
  ): Promise<void> {
    const rig = getRigForItemPreview(playerId);
    if (rig == null) return;
    const scene = await loadItemModelScene(itemId);
    if (scene == null) return;

    const validPreview =
      mode === 'preview' &&
      activeHeldItemPreview != null &&
      activeHeldItemPreview.playerId === playerId &&
      activeHeldItemPreview.itemId === itemId &&
      activeHeldItemPreview.token === token;
    const validUse = mode === 'use' && heldItemUseTokens.get(playerId) === token;
    if (!validPreview && !validUse) {
      return;
    }

    removeHeldItemObject(playerId);
    const object = scene.clone(true);
    object.name = `held-item-preview:${itemId}`;
    const socket = rig.resolveSocket?.('rightHand') ?? rig.root;
    socket.updateWorldMatrix(true, false);
    rig.root.updateWorldMatrix(true, false);
    const rootScale = rig.root.getWorldScale(new THREE.Vector3());
    const socketScale = socket.getWorldScale(new THREE.Vector3());
    const scaleCompensation =
      Math.max(rootScale.x, 0.0001) / Math.max(Math.abs(socketScale.x), 0.0001);
    object.scale.setScalar(HELD_ITEM_PREVIEW_SCALE * scaleCompensation);
    object.position.set(
      0.02 * scaleCompensation,
      0.02 * scaleCompensation,
      0.04 * scaleCompensation,
    );
    object.rotation.set(0.25, -0.35, 0.45);
    socket.add(object);
    heldItemObjects.set(playerId, object);
    heldItemObjectModes.set(playerId, mode);
  }

  function previewItemInHand(itemId: string): void {
    if (localPlayerId == null) return;
    const token = ++heldItemPreviewToken;
    activeHeldItemPreview = { playerId: localPlayerId, itemId, token };
    void attachHeldItemObject(localPlayerId, itemId, token, 'preview');
  }

  function handleItemUsePresentationEvent(event: GameEvent): void {
    if (event.type === 'item_cast') {
      playItemUsePresentation(event.casterId, event.itemId, event.targetId, event.direction);
      return;
    }

    if (event.type === 'item_hit') {
      if (event.itemId === 'thunder_fist') {
        void runThunderFistHitCinematic(event);
        return;
      }
      playItemImpactReaction(event.targetId, event.casterId);
      return;
    }

    if (event.type === 'item_dash_hit') {
      playItemImpactReaction(event.victimId, event.casterId);
      return;
    }

    if (event.type === 'item_blocked_stoneskin') {
      playItemImpactReaction(event.blockerId, event.casterId, 0.55);
    }
  }

  function playItemUsePresentation(
    casterId: string,
    itemId: string,
    targetId?: string,
    direction?: { dx: number; dy: number },
  ): void {
    const rig = boardRigs.get(casterId);
    if (rig == null) return;

    itemUsePresentationHandles.get(casterId)?.dispose();
    cleanupItemUsePresentation(casterId);

    const token = (heldItemUseTokens.get(casterId) ?? 0) + 1;
    heldItemUseTokens.set(casterId, token);
    void attachHeldItemObject(casterId, itemId, token, 'use');

    const from = findPlayerRigPosition(casterId);
    const to = targetId != null ? findPlayerRigPosition(targetId) : null;
    const directionYaw =
      direction != null && (direction.dx !== 0 || direction.dy !== 0)
        ? Math.atan2(direction.dx, direction.dy)
        : null;
    const facingYaw =
      from != null && to != null ? yawFromTo(from, to) : (directionYaw ?? rig.root.rotation.y);
    const archetype = itemUseArchetype(itemId);
    const proxy = {
      yaw: facingYaw,
      pitch: 0,
      roll: 0,
      scale: 1,
    };
    const apply = (): void => {
      boardRigActionYawOffsets.set(casterId, proxy.yaw);
      boardRigActionPitchOffsets.set(casterId, proxy.pitch);
      boardRigActionRollOffsets.set(casterId, proxy.roll);
      boardRigActionScale.set(casterId, proxy.scale);
    };
    apply();

    const handle = gsapRegistry.create(`item-use:${casterId}:${itemId}`);
    itemUsePresentationHandles.set(casterId, handle);
    const tl = handle.timeline;
    const semanticAlias =
      archetype === 'punch' ? 'punch' : itemId === 'phoenix_potion' ? 'drink' : null;
    if (semanticAlias !== null) {
      boardRigAnimationOverrides.set(casterId, {
        alias: semanticAlias,
        untilMs: performance.now() + ITEM_USE_PRESENTATION_SECONDS * 1000,
      });
      rig.playAnimation?.(semanticAlias);
    }

    if (archetype === 'punch') {
      tl.to(proxy, { pitch: -0.24, roll: -0.08, scale: 1.05, duration: 0.16, onUpdate: apply }, 0);
      tl.to(proxy, { pitch: 0.34, roll: 0.12, scale: 1.12, duration: 0.12, onUpdate: apply }, 0.16);
      tl.to(
        proxy,
        { pitch: 0, roll: 0, scale: 1, duration: 0.34, ease: 'power2.out', onUpdate: apply },
        0.28,
      );
    } else if (archetype === 'self') {
      tl.to(proxy, { pitch: -0.1, roll: 0.16, scale: 1.08, duration: 0.22, onUpdate: apply }, 0);
      tl.to(
        proxy,
        { pitch: 0, roll: 0, scale: 1, duration: 0.34, ease: 'power2.out', onUpdate: apply },
        0.28,
      );
    } else {
      tl.to(proxy, { pitch: -0.16, roll: -0.14, scale: 1.04, duration: 0.18, onUpdate: apply }, 0);
      tl.to(proxy, { pitch: 0.12, roll: 0.14, scale: 1.08, duration: 0.18, onUpdate: apply }, 0.18);
      tl.to(
        proxy,
        { pitch: 0, roll: 0, scale: 1, duration: 0.32, ease: 'power2.out', onUpdate: apply },
        0.36,
      );
    }

    tl.call(
      () => {
        cleanupItemUsePresentation(casterId);
        handle.dispose();
      },
      undefined,
      ITEM_USE_PRESENTATION_SECONDS,
    );
  }

  function playItemImpactReaction(playerId: string, casterId: string, scaleBoost = 0.42): void {
    const rig = boardRigs.get(playerId);
    if (rig == null) return;

    itemImpactPresentationHandles.get(playerId)?.dispose();
    itemImpactPresentationHandles.delete(playerId);

    const playerPosition = findPlayerRigPosition(playerId);
    const casterPosition = findPlayerRigPosition(casterId);
    const yaw =
      playerPosition != null && casterPosition != null
        ? yawFromTo(casterPosition, playerPosition)
        : rig.root.rotation.y;
    const proxy = { yaw, pitch: -0.2, roll: 0, scale: 1 + scaleBoost * 0.12 };
    const apply = (): void => {
      boardRigActionYawOffsets.set(playerId, proxy.yaw);
      boardRigActionPitchOffsets.set(playerId, proxy.pitch);
      boardRigActionRollOffsets.set(playerId, proxy.roll);
      boardRigActionScale.set(playerId, proxy.scale);
    };
    apply();

    const handle = gsapRegistry.create(`item-impact:${playerId}`);
    itemImpactPresentationHandles.set(playerId, handle);
    handle.timeline.to(
      proxy,
      {
        pitch: 0,
        roll: 0,
        scale: 1,
        duration: ITEM_USE_HIT_REACTION_SECONDS,
        ease: 'power2.out',
        onUpdate: apply,
      },
      0,
    );
    handle.timeline.call(
      () => {
        if (itemImpactPresentationHandles.get(playerId) !== handle) return;
        boardRigActionYawOffsets.delete(playerId);
        boardRigActionPitchOffsets.delete(playerId);
        boardRigActionRollOffsets.delete(playerId);
        boardRigActionScale.delete(playerId);
        itemImpactPresentationHandles.delete(playerId);
        handle.dispose();
      },
      undefined,
      ITEM_USE_HIT_REACTION_SECONDS,
    );
  }

  function cleanupItemUsePresentation(playerId: string): void {
    removeHeldItemObject(playerId, 'use');
    boardRigAnimationOverrides.delete(playerId);
    boardRigActionYawOffsets.delete(playerId);
    boardRigActionPitchOffsets.delete(playerId);
    boardRigActionRollOffsets.delete(playerId);
    boardRigActionScale.delete(playerId);
    heldItemUseTokens.delete(playerId);
    itemUsePresentationHandles.delete(playerId);
  }

  function yawFromTo(from: THREE.Vector3, to: THREE.Vector3): number {
    return Math.atan2(to.x - from.x, to.z - from.z);
  }

  function shortestAngleDelta(from: number, to: number): number {
    return Math.atan2(Math.sin(to - from), Math.cos(to - from));
  }

  function itemUseArchetype(itemId: string): 'punch' | 'self' | 'cast' {
    if (itemId === 'thunder_fist') return 'punch';
    if (
      itemId === 'stone_skin' ||
      itemId === 'phoenix_potion' ||
      itemId === 'mystery_box' ||
      itemId === 'shadow_stalker'
    ) {
      return 'self';
    }
    return 'cast';
  }

  function itemVfxPositionForPlayer(
    playerId: string,
  ): { x: number; y: number; z: number } | undefined {
    const rigPosition = findPlayerRigPosition(playerId);
    if (rigPosition != null) {
      return { x: rigPosition.x, y: rigPosition.y + 0.45, z: rigPosition.z };
    }
    const nodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    const nodeFocus = nodeId != null ? focusForNode(nodeId) : null;
    if (nodeFocus == null) return undefined;
    return { x: nodeFocus.x, y: nodeFocus.y, z: nodeFocus.z };
  }

  function itemVfxPositionForNode(nodeId: string): { x: number; y: number; z: number } | undefined {
    const nodeFocus = focusForNode(nodeId);
    if (nodeFocus == null) return undefined;
    return { x: nodeFocus.x, y: nodeFocus.y, z: nodeFocus.z };
  }

  async function playDiceRollVisual(
    playerId: string,
    result: number,
  ): Promise<DiceRollVisualHandle | void> {
    const rig = boardRigs.get(playerId);
    const fallbackNodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    const fallbackFocus = fallbackNodeId != null ? focusForNode(fallbackNodeId) : null;
    const anchor = findPlayerRigPosition(playerId) ?? fallbackFocus ?? boardCentroidFocus();
    if (anchor == null) {
      console.warn(
        `[smashparty] skipping procedural dice roll for ${playerId}: no rig, node focus, or board centroid is available yet.`,
      );
      return;
    }
    playSfx('sfx-dice-roll', {
      spatialPosition: { x: anchor.x, y: anchor.y, z: anchor.z },
    });
    const diceLayout = computeDiceCatchLayout({
      anchorPosition: anchor,
      tuning: DICE_CATCH_TUNING,
    });
    const handle = spawnProceduralD20Roll({
      scene: renderer.scene.scene,
      gsapRegistry,
      catchCenterPosition: diceLayout.catchDiceCenter,
      result,
      durationSeconds: DICE_ROLL_VISUAL_DURATION_SECONDS,
      catchScale: DICE_CATCH_D20_SCALE,
      catchScaleAtSeconds: DICE_ROLL_VISUAL_DURATION_SECONDS - 0.18,
      onImpact: () => {
        // Dice-land SFX is fired when the result overlay mounts the confirmed value.
      },
    });
    const catchVisual = rig != null ? startDiceCatchVisual(playerId, rig, diceLayout) : null;
    const visualSettled = catchVisual?.grounded ?? Promise.resolve();
    await Promise.all([handle.finished, catchVisual?.caught ?? Promise.resolve()]);
    const resultAnchor =
      screenAnchorForWorldPoint(
        diceLayout.catchDiceCenter
          .clone()
          .add(new THREE.Vector3(0, DICE_CATCH_RESULT_ANCHOR_Y_OFFSET, 0)),
      ) ?? screenAnchorForObject(handle.root);

    let fadeHandle: ReturnType<typeof gsapRegistry.create> | null = null;
    let diceDisposed = false;

    return {
      resultAnchor,
      settled: visualSettled,
      fadeOut(seconds: number): void {
        if (diceDisposed) return;
        fadeHandle?.dispose();
        const proxy = { opacity: 1, scale: handle.root.scale.x };
        fadeHandle = gsapRegistry.create(`procedural-d20:${playerId}:fade-out`);
        fadeHandle.timeline.to(
          proxy,
          {
            opacity: 0,
            scale: Math.max(0.01, handle.root.scale.x * 0.78),
            duration: Math.max(0, seconds),
            ease: 'sine.in',
            onUpdate: () => {
              setObjectOpacity(handle.root, proxy.opacity);
              handle.root.scale.setScalar(proxy.scale);
            },
          },
          0,
        );
      },
      dispose(): void {
        if (diceDisposed) return;
        diceDisposed = true;
        fadeHandle?.dispose();
        fadeHandle = null;
        handle.dispose();
        catchVisual?.dispose();
        boardRigAnimationOverrides.delete(playerId);
      },
    };
  }

  function startDiceCatchVisual(
    playerId: string,
    rig: CharacterPreviewRig,
    layout: ReturnType<typeof computeDiceCatchLayout>,
  ): {
    readonly caught: Promise<void>;
    readonly grounded: Promise<void>;
    dispose(): void;
  } {
    let disposed = false;
    let jumpHandle: ReturnType<typeof gsapRegistry.create> | null = null;
    let resolveCaught: () => void = () => {};
    let resolveGrounded: () => void = () => {};

    const catchDuration =
      animationDurationSecondsForAlias(rig, 'diceCatch') || DICE_CATCH_FALLBACK_DURATION_SECONDS;
    const catchStartSeconds = Math.max(
      DICE_CATCH_MIN_START_SECONDS,
      DICE_ROLL_VISUAL_DURATION_SECONDS - catchDuration * DICE_CATCH_GRAB_AT_RATIO,
    );
    const secondsToImpact = Math.max(0.12, DICE_ROLL_VISUAL_DURATION_SECONDS - catchStartSeconds);

    if (rig.playAnimation?.('diceStretch') === true) {
      boardRigAnimationOverrides.set(playerId, {
        alias: 'diceStretch',
        untilMs: performance.now() + Math.min(DICE_STRETCH_OVERRIDE_MS, catchStartSeconds * 1000),
      });
    }

    const caught = new Promise<void>((resolve) => {
      resolveCaught = resolve;
    });
    const grounded = new Promise<void>((resolve) => {
      resolveGrounded = resolve;
    });

    const cleanupJump = (): void => {
      jumpHandle?.dispose();
      jumpHandle = null;
      boardRigHopOffsets.delete(playerId);
      boardRigActionScale.delete(playerId);
      boardRigActionYawOffsets.delete(playerId);
      clearBoardRigActionPositionOffset(playerId);
      boardRigAnimationOverrides.delete(playerId);
      resolveGrounded();
    };

    void waitWithGsap(catchStartSeconds, `dice-catch:${playerId}:start`).then(() => {
      if (disposed) return;
      if (rig.playAnimation?.('diceCatch') !== true) {
        resolveCaught();
        resolveGrounded();
        return;
      }

      boardRigAnimationOverrides.set(playerId, {
        alias: 'diceCatch',
        untilMs:
          performance.now() +
          (catchDuration + DICE_CATCH_HOLD_AFTER_IMPACT_SECONDS + DICE_CATCH_DESCEND_SECONDS) *
            1000,
      });

      const jumpUpSeconds = Math.min(0.34, Math.max(0.24, secondsToImpact * 0.56));
      const hoverSeconds = Math.max(0, secondsToImpact - jumpUpSeconds);
      const catchHoldHop = hoverSeconds > 0.02 ? layout.targetHop * 0.94 : layout.targetHop;
      const catchHoldScale = hoverSeconds > 0.02 ? layout.holdScale : layout.peakScale;
      const baseYaw = boardRigFacingYawOffsets.get(playerId) ?? rig.root.rotation.y;
      const targetActionYaw = shortestAngleDelta(baseYaw, layout.characterYaw);
      const proxy = { hop: 0, scale: 1, offsetRatio: 0, actionYaw: 0 };
      const apply = (): void => {
        boardRigHopOffsets.set(playerId, proxy.hop);
        boardRigActionScale.set(playerId, proxy.scale);
        boardRigActionYawOffsets.set(playerId, proxy.actionYaw);
        setBoardRigActionPositionOffset(
          playerId,
          layout.characterOffset.x * proxy.offsetRatio,
          layout.characterOffset.z * proxy.offsetRatio,
        );
      };

      jumpHandle = gsapRegistry.create(`dice-catch:${playerId}:jump`);
      jumpHandle.timeline.to(
        proxy,
        {
          hop: layout.targetHop,
          scale: layout.peakScale,
          offsetRatio: 1,
          actionYaw: targetActionYaw,
          duration: jumpUpSeconds,
          ease: 'power3.out',
          onUpdate: apply,
        },
        0,
      );
      if (hoverSeconds > 0.02) {
        jumpHandle.timeline.to(
          proxy,
          {
            hop: layout.targetHop * 0.94,
            scale: layout.holdScale,
            offsetRatio: 1,
            actionYaw: targetActionYaw,
            duration: hoverSeconds,
            ease: 'sine.inOut',
            onUpdate: apply,
          },
          jumpUpSeconds,
        );
      }
      jumpHandle.timeline.call(
        () => {
          resolveCaught();
        },
        undefined,
        secondsToImpact,
      );
      if (DICE_CATCH_HOLD_AFTER_IMPACT_SECONDS > 0) {
        jumpHandle.timeline.to(
          proxy,
          {
            hop: catchHoldHop,
            scale: catchHoldScale,
            offsetRatio: 1,
            actionYaw: targetActionYaw,
            duration: DICE_CATCH_HOLD_AFTER_IMPACT_SECONDS,
            ease: 'sine.inOut',
            onUpdate: apply,
          },
          secondsToImpact,
        );
      }
      jumpHandle.timeline.to(
        proxy,
        {
          hop: 0,
          scale: 1,
          offsetRatio: 0,
          actionYaw: 0,
          duration: DICE_CATCH_DESCEND_SECONDS,
          ease: 'power2.inOut',
          onUpdate: apply,
        },
        secondsToImpact + DICE_CATCH_HOLD_AFTER_IMPACT_SECONDS,
      );
      jumpHandle.timeline.call(
        cleanupJump,
        undefined,
        secondsToImpact + DICE_CATCH_HOLD_AFTER_IMPACT_SECONDS + DICE_CATCH_DESCEND_SECONDS,
      );
      apply();
    });

    return {
      caught,
      grounded,
      dispose(): void {
        if (disposed) return;
        disposed = true;
        resolveCaught();
        cleanupJump();
      },
    };
  }

  function screenAnchorForObject(
    object: THREE.Object3D,
    anchorOpts: { yOffset?: number } = {},
  ): { screenX: number; screenY: number } | undefined {
    if (!(object instanceof THREE.Object3D)) return undefined;
    renderer.cameraRig.camera.updateMatrixWorld();
    object.updateMatrixWorld();
    const worldPosition = object.getWorldPosition(new THREE.Vector3());
    const projected = screenPositionForWorldPoint(worldPosition);
    if (projected == null) return undefined;

    return {
      screenX: projected.screenX,
      screenY: projected.screenY + (anchorOpts.yOffset ?? 0),
    };
  }

  function screenAnchorForWorldPoint(
    worldPosition: THREE.Vector3,
  ): { screenX: number; screenY: number } | undefined {
    const projected = screenPositionForWorldPoint(worldPosition);
    if (projected == null) return undefined;
    return projected;
  }

  function screenPositionForWorldPoint(
    worldPosition: THREE.Vector3,
  ): { screenX: number; screenY: number } | undefined {
    renderer.cameraRig.camera.updateMatrixWorld();
    const projected = worldPosition.clone().project(renderer.cameraRig.camera);
    if (
      !Number.isFinite(projected.x) ||
      !Number.isFinite(projected.y) ||
      !Number.isFinite(projected.z)
    ) {
      return undefined;
    }

    const rect = opts.canvas.getBoundingClientRect();
    const viewportWidth =
      rect.width > 0 ? rect.width : opts.canvas.clientWidth || window.innerWidth || 1;
    const viewportHeight =
      rect.height > 0 ? rect.height : opts.canvas.clientHeight || window.innerHeight || 1;

    return {
      screenX: rect.left + ((projected.x + 1) / 2) * viewportWidth,
      screenY: rect.top + ((1 - projected.y) / 2) * viewportHeight,
    };
  }

  function setObjectOpacity(object: THREE.Object3D, opacity: number): void {
    object.traverse((child) => {
      const material = (child as THREE.Object3D & { material?: THREE.Material | THREE.Material[] })
        .material;
      if (material == null) return;
      const materials = Array.isArray(material) ? material : [material];
      for (const entry of materials) {
        entry.transparent = true;
        entry.opacity = opacity;
        entry.needsUpdate = true;
      }
    });
  }

  function animationDurationSecondsForAlias(rig: CharacterPreviewRig, alias: string): number {
    const action = rig.actions?.get(alias) ?? rig.actions?.get(alias.toLowerCase());
    const duration = action?.getClip().duration ?? 0;
    return Number.isFinite(duration) && duration > 0 ? duration : 0;
  }

  function enqueueLightningDashPresentation(event: ItemDashEvent): void {
    pendingLightningDashPresentations.push(event);
    if (!lightningDashPresentationRunning) {
      void processLightningDashPresentationQueue();
    }
  }

  async function processLightningDashPresentationQueue(): Promise<void> {
    if (lightningDashPresentationRunning) return;
    const event = pendingLightningDashPresentations.shift();
    if (event == null) return;

    lightningDashPresentationRunning = true;
    setActionInputLock(ACTION_LOCK_LIGHTNING_DASH, true);
    clearPathArrowsAndFacing();
    try {
      await runLightningDashPresentation(event);
    } finally {
      lightningDashPresentationRunning = false;
      if (pendingLightningDashPresentations.length > 0) {
        void processLightningDashPresentationQueue();
      } else {
        setActionInputLock(ACTION_LOCK_LIGHTNING_DASH, false);
        flushBoardPresentationMessages();
        maybeRouteDeferredBoardPhase();
        flushPendingTurnBanner();
        flushPendingBoardPromptReady();
        if (latestGameState != null) {
          syncPathArrowsForState(latestGameState);
        }
      }
    }
  }

  async function runLightningDashPresentation(event: ItemDashEvent): Promise<void> {
    const rig = boardRigs.get(event.casterId);
    if (rig == null || boardHandle == null) {
      await waitWithGsap(0.18, `lightning-dash:${event.casterId}:missing-rig`);
      return;
    }

    const nodePath = resolveLightningDashPresentationPath(event);
    const previousNodeId = boardRigNodeIds.get(event.casterId);
    const targets = nodePath
      .map((nodeId) => placementForBoardRigNode(event.casterId, rig, nodeId))
      .filter((placement): placement is THREE.Vector3 => placement != null);
    settleBoardRigStackForNode(previousNodeId, event.casterId);
    settleBoardRigStackForNode(nodePath.at(-1), event.casterId);
    await tweenBoardRigAlongDashPath(event.casterId, rig, targets);
  }

  function resolveLightningDashPresentationPath(event: ItemDashEvent): string[] {
    const path = Array.isArray(event.path) ? event.path.filter((nodeId) => nodeId !== '') : [];
    if (path.length > 0) return path;
    if (typeof event.toNodeId === 'string' && event.toNodeId !== '') return [event.toNodeId];
    const currentNodeId = latestGameState?.players.get(event.casterId)?.nodeId;
    return currentNodeId == null ? [] : [currentNodeId];
  }

  function enqueueMovementStep(event: MovementStepEvent): void {
    const queue = queuedMovementSteps.get(event.playerId) ?? [];
    queue.push(event);
    queuedMovementSteps.set(event.playerId, queue);
    if (!heldMovementPlayers.has(event.playerId)) {
      void processMovementQueue(event.playerId);
    }
  }

  async function processMovementQueue(playerId: string): Promise<void> {
    if (orbitalCinematicRunning) return;
    if (trapCinematicRunning) return;
    if (thunderFistCinematicRunning) return;
    if (specialRelocationRevealRunning || pendingSpecialRelocationReveals.length > 0) return;
    if (heldMovementPlayers.has(playerId) || animatingMovementPlayers.has(playerId)) return;
    const queue = queuedMovementSteps.get(playerId);
    if (queue == null || queue.length === 0) {
      queuedMovementSteps.delete(playerId);
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      tryShowNextGameplayLesson();
      return;
    }

    const rig = boardRigs.get(playerId);
    if (rig == null || mapHandle == null) {
      queuedMovementSteps.delete(playerId);
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      tryShowNextGameplayLesson();
      return;
    }

    animatingMovementPlayers.add(playerId);
    try {
      while (queue.length > 0) {
        const step = queue.shift()!;
        let node = findMapNode(step.toNodeId);
        if (node == null) {
          // Server/client board drift fallback: build minimal node from boardHandle.
          // Without this, a step silently drops and the rig stops short of its true position.
          const fallbackPos = boardHandle?.getWorldPositionForNode(step.toNodeId);
          if (fallbackPos != null) {
            node = {
              id: step.toNodeId,
              type: 'gold',
              position: { x: fallbackPos.x, y: fallbackPos.y, z: fallbackPos.z },
              connections: [],
              supportsGoal: false,
            } as BoardNode;
          } else {
            console.warn(
              `[smashparty] movement step ${step.stepIndex + 1}/${step.totalSteps} dropped: node '${step.toNodeId}' missing from client schema AND boardHandle. WS server may have a newer board than this client; refresh.`,
            );
            continue;
          }
        }
        const previousNodeId = boardRigNodeIds.get(playerId);
        const nodeIdsByPlayer = new Map(boardRigNodeIds);
        nodeIdsByPlayer.set(playerId, node.id);
        const placement = computeBoardRigPlacement(playerId, rig, node, nodeIdsByPlayer);
        boardRigNodeIds.set(playerId, node.id);
        settleBoardRigStackForNode(previousNodeId, playerId);
        settleBoardRigStackForNode(node.id, playerId);
        const stepStartedAt = performance.now();
        tracePresentation({
          stage: 'movement_step_start',
          activePlayerId: playerId,
          nodeId: step.toNodeId,
          serverEventType: 'movement_step',
          eventTimestamp: step.timestamp,
          counts: {
            stepIndex: step.stepIndex,
            totalSteps: step.totalSteps,
          },
        });
        characterEyeExpressions.setSticky(playerId, 'movement', 'excited');
        await tweenBoardRigTo(playerId, rig, placement);
        tracePresentation({
          stage: 'movement_step_done',
          activePlayerId: playerId,
          nodeId: step.toNodeId,
          serverEventType: 'movement_step',
          eventTimestamp: step.timestamp,
          durationMs: performance.now() - stepStartedAt,
          counts: {
            stepIndex: step.stepIndex,
            totalSteps: step.totalSteps,
          },
        });
        flushPassThroughCollectionMessages(playerId, step.toNodeId);
        flushDroppedKeyPickupMessages(playerId);
      }
    } finally {
      animatingMovementPlayers.delete(playerId);
      characterEyeExpressions.clearLayer(playerId, 'movement');
      queuedMovementSteps.delete(playerId);
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      tryShowNextGameplayLesson();
    }
  }

  function releaseHeldMovementForPlayer(playerId: string): Promise<void> {
    heldMovementPlayers.delete(playerId);
    return processMovementQueue(playerId);
  }

  function enqueueDiceRollPresentation(event: DiceRollEvent, leadIn?: Promise<void>): void {
    heldMovementPlayers.add(event.playerId);
    movementProgressByPlayer.set(event.playerId, { expected: event.result, observed: 0 });
    dicePresentationChain = dicePresentationChain
      .catch((err) => {
        console.error('[smashparty] dice presentation failed:', err);
      })
      .then(() => {
        if (leadIn == null) {
          return presentDiceRollEvent(event);
        }
        return leadIn.then(() => presentDiceRollEvent(event));
      });
  }

  async function presentDiceRollEvent(event: DiceRollEvent): Promise<void> {
    const startedAt = performance.now();
    const state = latestGameState;
    const payloadBase = state == null ? null : makeBoardPayload(state);
    if (disposed || state == null || payloadBase == null) {
      tracePresentation({
        stage: 'dice_skipped',
        activePlayerId: event.playerId,
        serverEventType: 'dice_roll',
        eventTimestamp: event.timestamp,
        reason: disposed ? 'disposed' : 'missing_state_or_payload',
      });
      await releaseHeldMovementForPlayer(event.playerId);
      return;
    }
    tracePresentation({
      stage: 'dice_start',
      activePlayerId: event.playerId,
      serverEventType: 'dice_roll',
      eventTimestamp: event.timestamp,
      counts: { diceResult: event.result },
    });
    characterEyeExpressions.showTimed(event.playerId, 'action', 'excited', 1400);

    const latestPayloadBase =
      latestGameState == null ? null : (makeBoardPayload(latestGameState) ?? payloadBase);
    const dicePayloadBase = latestPayloadBase ?? payloadBase;

    await new Promise<void>((resolve) => {
      screenRouter.navigate('dice-roll', {
        ...dicePayloadBase,
        rollerPlayerId: event.playerId,
        initialResult: event.result,
        playDiceRoll: playDiceRollVisual,
        shouldDeferBoardHudReturn: isSystemOverlayOpen,
        onComplete: (rollerPlayerId: string) => {
          tracePresentation({
            stage: 'dice_done',
            activePlayerId: rollerPlayerId,
            serverEventType: 'dice_roll',
            eventTimestamp: event.timestamp,
            durationMs: performance.now() - startedAt,
            counts: { diceResult: event.result },
          });
          characterEyeExpressions.clearLayer(rollerPlayerId, 'action');
          void releaseHeldMovementForPlayer(rollerPlayerId).then(resolve, resolve);
        },
      });
    });
  }

  function maybeRouteDeferredBoardPhase(): void {
    if (deferredBoardRouteState == null || isPresentationBlocked()) return;
    const pending = deferredBoardRouteState;
    deferredBoardRouteState = null;
    routeBoardPhase(pending.state, { forceRemount: pending.forceBoardRemount });
  }

  function handleNotificationActivityChange(active: boolean): void {
    notificationsActive = active;
    tracePresentation({
      stage: 'notification_active_changed',
      reason: active ? 'active' : 'idle',
      counts: { notificationsActive: active ? 1 : 0 },
    });
    if (!active) {
      boardPresentationQueue.flush();
      drainBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      flushPendingTurnBanner();
      flushPendingBoardPromptReady();
      maybeMountPendingMinigameStart();
      tryShowNextGameplayLesson();
    }
  }

  function applyPendingBoardEffectMinigameStateSync(): void {
    const pending = pendingBoardEffectMinigameStateSync;
    if (pending == null || boardEffectActivityCount > 0 || isPresentationBlocked()) return;
    pendingBoardEffectMinigameStateSync = null;
    handleStateSyncState(pending.state);
    boardPresentationQueue.push(pending.msg);
    flushForcedRelocationMessages();
  }

  function handleBoardEffectActivityChange(active: boolean): void {
    boardEffectActivityCount = Math.max(0, boardEffectActivityCount + (active ? 1 : -1));
    tracePresentation({
      stage: 'board_effect_active_changed',
      reason: active ? 'active' : 'idle',
      counts: { boardEffectActivityCount },
    });
    if (boardEffectActivityCount === 0) {
      applyPendingBoardEffectMinigameStateSync();
      maybeShowPendingMinigameSelection();
      maybeMountPendingMinigameStart();
      tryShowNextGameplayLesson();
    }
  }

  function buildBoardPromptReadyForState(state: GameState): PendingBoardPromptReady | null {
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    if (!isOwnedPlayerId(activeId, state)) return null;
    const player = activeId == null ? null : state.players.get(activeId);
    if (player == null) return null;

    const base = {
      playerId: activeId,
      round: state.round,
      currentPlayerIndex: state.currentPlayerIndex,
    };

    if (state.phase === 'fork_choice' && state.forkChoiceDeadlineAtMs == null) {
      const forkScopeId =
        state.pendingForkChoice?.id ??
        `${state.round}:${state.currentPlayerIndex}:${activeId}:${player.nodeId}`;
      const key = `fork_choice:${state.round}:${state.currentPlayerIndex}:${activeId}:${forkScopeId}`;
      return {
        key,
        msg: {
          type: 'board_prompt_ready',
          promptKind: 'fork_choice',
          nodeId: player.nodeId,
          ...base,
        },
      };
    }

    const pendingClaim = state.pendingSoulAnchorClaim;
    if (
      pendingClaim != null &&
      isOwnedPlayerId(pendingClaim.playerId, state) &&
      pendingClaim.deadlineAtMs == null
    ) {
      const key = `soul_anchor_claim:${state.round}:${state.currentPlayerIndex}:${pendingClaim.playerId}:${pendingClaim.nodeId}`;
      return {
        key,
        msg: {
          type: 'board_prompt_ready',
          promptKind: 'soul_anchor_claim',
          nodeId: pendingClaim.nodeId,
          ...base,
          playerId: pendingClaim.playerId,
        },
      };
    }

    const pendingEncounter = state.pendingBoardEncounter;
    if (
      pendingEncounter != null &&
      isOwnedPlayerId(pendingEncounter.playerId, state) &&
      pendingEncounter.deadlineAtMs == null
    ) {
      const key = `board_encounter:${pendingEncounter.kind}:${state.round}:${state.currentPlayerIndex}:${pendingEncounter.playerId}:${pendingEncounter.nodeId}`;
      return {
        key,
        msg: {
          type: 'board_prompt_ready',
          promptKind: 'board_encounter',
          encounterKind: pendingEncounter.kind,
          nodeId: pendingEncounter.nodeId,
          ...base,
          playerId: pendingEncounter.playerId,
        },
      };
    }

    return null;
  }

  function requestBoardPromptReadyForState(state: GameState): void {
    const ready = buildBoardPromptReadyForState(state);
    if (ready == null || ready.key === lastSentBoardPromptReadyKey) return;
    pendingBoardPromptReady = ready;
    tracePresentation({
      stage: 'prompt_ready_pending',
      turnKey: turnKeyForState(state, state.turnOrder[state.currentPlayerIndex] ?? null),
      nodeId: ready.msg.nodeId,
      encounterKind: ready.msg.encounterKind,
      reason: ready.msg.promptKind,
    });
    flushPendingBoardPromptReady();
  }

  function flushPendingBoardPromptReady(): void {
    if (
      pendingBoardPromptReady == null ||
      boardPresentationPaused ||
      notificationsActive ||
      isMovementCinematicBusy()
    ) {
      return;
    }
    const currentReady =
      latestGameState == null ? null : buildBoardPromptReadyForState(latestGameState);
    if (currentReady == null || currentReady.key !== pendingBoardPromptReady.key) {
      tracePresentation({
        stage: 'prompt_ready_stale_dropped',
        nodeId: pendingBoardPromptReady.msg.nodeId,
        encounterKind: pendingBoardPromptReady.msg.encounterKind,
        reason: pendingBoardPromptReady.msg.promptKind,
      });
      pendingBoardPromptReady = null;
      return;
    }
    pendingBoardPromptReady = null;
    lastSentBoardPromptReadyKey = currentReady.key;
    tracePresentation({
      stage: 'prompt_ready_sent',
      nodeId: currentReady.msg.nodeId,
      encounterKind: currentReady.msg.encounterKind,
      reason: currentReady.msg.promptKind,
    });
    wsClient.send(currentReady.msg);
  }

  function requestTurnBannerForPlayer(
    playerId: string,
    turnKey: string,
    opts: { requireCurrent?: boolean; sendIntroComplete?: boolean } = {},
  ): Promise<void> {
    const existingLeadIn = turnBannerLeadIns.get(turnKey);
    if (lastAnnouncedTurnKey === turnKey && existingLeadIn != null) return existingLeadIn;
    if (lastAnnouncedTurnKey === turnKey) return Promise.resolve();
    const player = latestGameState?.players.get(playerId) ?? null;
    if (player == null) return Promise.resolve();
    lastAnnouncedTurnKey = turnKey;

    let resolveLeadIn!: () => void;
    const leadIn = new Promise<void>((resolve) => {
      resolveLeadIn = resolve;
    });
    turnBannerLeadIns.set(turnKey, leadIn);
    const request: PendingTurnBannerRequest = {
      playerId,
      turnKey,
      requireCurrent: opts.requireCurrent ?? true,
      sendIntroComplete: opts.sendIntroComplete ?? false,
      resolveLeadIn,
    };
    tracePresentation({
      stage: 'turn_banner_requested',
      activePlayerId: playerId,
      turnKey,
      reason:
        notificationsActive || isTurnBannerBlockedForPlayer(playerId) ? 'blocked' : 'immediate',
    });

    if (boardPresentationPaused || notificationsActive || isTurnBannerBlockedForPlayer(playerId)) {
      pendingTurnBanner = request;
      return leadIn;
    }
    showTurnBannerNow(request);
    return leadIn;
  }

  function flushPendingTurnBanner(): void {
    if (boardPresentationPaused || notificationsActive || pendingTurnBanner == null) return;
    if (isTurnBannerBlockedForPlayer(pendingTurnBanner.playerId)) return;
    const pending = pendingTurnBanner;
    pendingTurnBanner = null;
    if (pending.requireCurrent && !isCurrentTurnBannerRequest(pending)) {
      tracePresentation({
        stage: 'turn_banner_stale_dropped',
        activePlayerId: pending.playerId,
        turnKey: pending.turnKey,
      });
      pending.resolveLeadIn();
      turnBannerLeadIns.delete(pending.turnKey);
      return;
    }
    showTurnBannerNow(pending);
  }

  function isCurrentTurnBannerRequest(pending: { playerId: string; turnKey: string }): boolean {
    const state = latestGameState;
    if (state == null) return false;
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    if (activeId !== pending.playerId) return false;
    return turnKeyForState(state, activeId) === pending.turnKey;
  }

  function showTurnBannerNow(request: PendingTurnBannerRequest): void {
    const player = latestGameState?.players.get(request.playerId) ?? null;
    if (player == null) {
      request.resolveLeadIn();
      turnBannerLeadIns.delete(request.turnKey);
      return;
    }
    tracePresentation({
      stage: 'turn_banner_shown',
      activePlayerId: request.playerId,
      turnKey: request.turnKey,
    });
    turnBannerHandle.show({
      playerName: player.name,
      playerColor: `#${player.color.toString(16).padStart(6, '0')}`,
      iconText: player.name.trim().charAt(0).toUpperCase() || 'P',
    });
    void waitWithGsap(TURN_BANNER_TOTAL_SECONDS, `turn-banner:complete:${request.turnKey}`).then(
      () => {
        if (disposed) {
          request.resolveLeadIn();
          turnBannerLeadIns.delete(request.turnKey);
          return;
        }
        if (request.sendIntroComplete) {
          sendTurnIntroComplete(request.playerId, request.turnKey);
        }
        tracePresentation({
          stage: 'turn_banner_done',
          activePlayerId: request.playerId,
          turnKey: request.turnKey,
        });
        request.resolveLeadIn();
        turnBannerLeadIns.delete(request.turnKey);
      },
    );
  }

  function turnKeyForState(state: GameState, activeId: string | null): string {
    return `${state.round}:${state.currentPlayerIndex}:${activeId ?? ''}`;
  }

  function awardStatSuffix(categoryId: string): string {
    switch (categoryId) {
      case 'most_keys_gained':
        return 'keys';
      case 'most_damage_dealt':
        return 'damage';
      case 'minigame_champion':
      case 'most_minigames_won':
        return 'wins';
      case 'most_tiles_traveled':
        return 'tiles';
      case 'most_items_used':
        return 'items';
      default:
        return '';
    }
  }

  function dropBoardPresentationStateForWarmupLeave(): void {
    suppressRoomMessagesUntilEntry = true;
    activeRoomId = null;
    latestRevisionByRoomId.clear();
    resetEyeExpressionEventCursor(true);
    resetBoardPresentationAfterReconnect();
    pendingForkOptionsByPlayer.clear();
    pendingRollSubmissionKeysByPlayer.clear();
    suppressedPrompt = null;
    latestGameState = null;
    canonicalGameState = null;
    canonicalGameStateRoomId = null;
    canonicalGameStateRevision = null;
    resetGameplayTutorialRuntime();
    boardPresentationPaused = false;
    localPresentationPauseActive = false;
    localPauseRequestCoversPresentation = false;
    serverPauseActive = false;
    suppressedPauseNotificationText = null;
    setBoardPresentationGsapPaused(false);
    notificationsActive = false;
    currentPhase = 'game_start';
    inWarmupArena = true;
    warmupCameraView = 'arena';
    warmupArenaCameraFocused = false;
    pendingRestoredBoardRoute = false;
    disposeActiveMinigame();
    disposeTileEffectOverlay();
    disposeNavigationOverlays();
    turnBannerHandle.hide();
    trapCinematicOverlay?.remove();
    trapCinematicOverlay = null;
    orbitalCinematicOverlay?.remove();
    orbitalCinematicOverlay = null;
  }

  function sendLeaveThenWarmupSubscribe(): void {
    sendWithActionLock({ type: 'leave' });
    sendWithActionLock(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
  }

  function leaveCurrentGameToWarmup(): void {
    dropBoardPresentationStateForWarmupLeave();
    sendLeaveThenWarmupSubscribe();
    window.dispatchEvent(new CustomEvent('smashparty:warmup-restore-hud'));
    screenRouter.navigate('warmup-arena-overlay', { portal: null });
  }

  function buildGameOverPayload(state: GameState, payloadBase: BoardMountPayload): unknown {
    return {
      ...payloadBase,
      winnerPlayerId: state.endGame?.winnerPlayerId ?? null,
      onPlayAgain: leaveCurrentGameToWarmup,
      onBackToMenu: leaveCurrentGameToWarmup,
    };
  }

  function routeGameOverFlow(state: GameState, payloadBase: BoardMountPayload): void {
    if (screenRouter.current === 'pummel-awards' || screenRouter.current === 'game-over') {
      return;
    }

    const gameOverPayload = buildGameOverPayload(state, payloadBase);
    const awards =
      state.endGame?.awards.map((award) => ({
        categoryId: award.categoryId,
        categoryLabel: award.categoryLabel,
        winnerPlayerId: award.playerId,
        statValue: award.statValue,
        statSuffix: awardStatSuffix(award.categoryId),
      })) ?? [];

    if (awards.length === 0) {
      screenRouter.navigate('game-over', gameOverPayload);
      return;
    }

    screenRouter.navigate('pummel-awards', {
      ...payloadBase,
      awards,
      onAllRevealed: () => screenRouter.navigate('game-over', gameOverPayload),
    });
  }

  function sendTurnIntroComplete(playerId: string, turnKey: string): void {
    const state = latestGameState;
    if (state == null) return;
    if (boardPresentationPaused || state.pause != null) return;
    if (turnKeyForState(state, playerId) !== turnKey) return;
    if (state.phase !== 'item_phase') return;
    if (state.turnOrder[state.currentPlayerIndex] !== playerId) return;
    wsClient.send({
      type: 'turn_intro_complete',
      playerId,
      round: state.round,
      currentPlayerIndex: state.currentPlayerIndex,
    });
  }

  function gameplayTutorialTarget(name: string): HTMLElement | null {
    return document.querySelector<HTMLElement>(`[data-tutorial-target="${name}"]`);
  }

  function resetGameplayTutorialRuntime(): void {
    gameplayTutorialHandle?.dispose();
    gameplayTutorialHandle = null;
    gameplayTutorialVisibleLesson = null;
    gameplayTutorialMinimized = false;
    gameplayTutorialCoreCompletedThisMatch = false;
    gameplayTutorialCompletedCoreLessons.clear();
    gameplayTutorialSeenLessons.clear();
    gameplayTutorialLessonQueue.length = 0;
    gameplayTutorialLastLanding = null;
    gameplayTutorialLandingLessonQueued = false;
  }

  function isGameplayCoreLessonId(lessonId: GameplayLessonId): lessonId is GameplayCoreStepId {
    return GAMEPLAY_TUTORIAL_CORE_LESSON_IDS.includes(lessonId as GameplayCoreStepId);
  }

  function isGameplayTutorialAvailable(state: GameState | null): state is GameState {
    return (
      state != null &&
      localPlayerId != null &&
      !inWarmupArena &&
      !areTutorialsHidden() &&
      state.phase !== 'lobby' &&
      state.phase !== 'game_over' &&
      !ACTIVE_MINIGAME_PHASES.has(state.phase)
    );
  }

  function isLocalActivePlayer(state: GameState): boolean {
    return localPlayerId != null && state.turnOrder[state.currentPlayerIndex] === localPlayerId;
  }

  function isOpponentActionTurn(state: GameState): boolean {
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    return (
      localPlayerId != null &&
      activeId != null &&
      activeId !== localPlayerId &&
      (state.phase === 'item_phase' || state.phase === 'rolling')
    );
  }

  function isGameplayLessonDisplayAllowed(): boolean {
    if (gameplayTutorialHandle != null) return false;
    if (!isGameplayTutorialAvailable(latestGameState)) return false;
    if (screenRouter.current !== 'board-hud') return false;
    if (isSystemOverlayOpen()) return false;
    if (screenRouter.overlayCurrent != null) return false;
    if (isMovementCinematicBusy() || notificationsActive || boardPresentationPaused) return false;
    if (latestGameState.pendingBoardEncounter != null) return false;
    if (latestGameState.pendingSoulAnchorClaim != null) return false;
    return true;
  }

  function hasCompletedGameplayLesson(lessonId: GameplayLessonId): boolean {
    if (isGameplayCoreLessonId(lessonId)) {
      return hasSeenGameplayTutorial() || gameplayTutorialCompletedCoreLessons.has(lessonId);
    }
    return gameplayTutorialSeenLessons.has(lessonId) || hasSeenGameplayTutorialLesson(lessonId);
  }

  function markGameplayLessonCompleted(lesson: GameplayLesson): void {
    gameplayTutorialSeenLessons.add(lesson.id);
    if (isGameplayCoreLessonId(lesson.id)) {
      gameplayTutorialCompletedCoreLessons.add(lesson.id);
      if (
        !gameplayTutorialCoreCompletedThisMatch &&
        GAMEPLAY_TUTORIAL_CORE_LESSON_IDS.every((id) =>
          gameplayTutorialCompletedCoreLessons.has(id),
        )
      ) {
        gameplayTutorialCoreCompletedThisMatch = true;
        markGameplayTutorialSeen();
      }
      return;
    }
    markGameplayTutorialLessonSeen(lesson.id);
  }

  function requeueVisibleGameplayLesson(): void {
    const visible = gameplayTutorialVisibleLesson;
    if (visible == null || hasCompletedGameplayLesson(visible.id)) return;
    if (gameplayTutorialLessonQueue.some((queued) => queued.id === visible.id)) return;
    gameplayTutorialLessonQueue.unshift(visible);
  }

  function queueGameplayLesson(lesson: GameplayLesson): void {
    if (!isGameplayTutorialAvailable(latestGameState)) return;
    if (hasCompletedGameplayLesson(lesson.id)) return;
    if (gameplayTutorialVisibleLesson?.id === lesson.id) return;
    if (gameplayTutorialLessonQueue.some((queued) => queued.id === lesson.id)) return;
    if (
      lesson.critical === true &&
      gameplayTutorialVisibleLesson != null &&
      gameplayTutorialVisibleLesson.critical !== true
    ) {
      requeueVisibleGameplayLesson();
      gameplayTutorialHandle?.dispose();
      gameplayTutorialHandle = null;
      gameplayTutorialVisibleLesson = null;
    }
    if (lesson.critical === true) {
      const passiveIndex = gameplayTutorialLessonQueue.findIndex(
        (queued) => queued.critical !== true,
      );
      if (passiveIndex === -1) {
        gameplayTutorialLessonQueue.push(lesson);
      } else {
        gameplayTutorialLessonQueue.splice(passiveIndex, 0, lesson);
      }
    } else {
      gameplayTutorialLessonQueue.push(lesson);
    }
    tryShowNextGameplayLesson();
  }

  function tryShowNextGameplayLesson(): void {
    if (!isGameplayLessonDisplayAllowed()) return;
    let lesson = gameplayTutorialLessonQueue.shift();
    while (lesson != null && hasCompletedGameplayLesson(lesson.id)) {
      lesson = gameplayTutorialLessonQueue.shift();
    }
    if (lesson == null) return;
    gameplayTutorialVisibleLesson = lesson;
    const initialMinimized = gameplayTutorialMinimized && lesson.critical !== true;
    if (lesson.critical === true) {
      gameplayTutorialMinimized = false;
    }
    gameplayTutorialHandle = createLiveTutorialWidget({
      mount: opts.host,
      steps: [lesson],
      testId: 'gameplay-live-tutorial',
      zIndex: 5000,
      showProgress: false,
      enableKeyboardAdvance: false,
      showHideButton: true,
      minimizeOnHide: true,
      initialMinimized,
      minimizedText: `Tutorial: ${lesson.title}`,
      onMinimize: () => {
        gameplayTutorialMinimized = true;
      },
      onExpand: () => {
        gameplayTutorialMinimized = false;
      },
      onComplete: () => {
        markGameplayLessonCompleted(lesson);
        gameplayTutorialHandle = null;
        gameplayTutorialVisibleLesson = null;
        if (latestGameState != null) observeGameplayTutorialState(latestGameState);
        tryShowNextGameplayLesson();
      },
    });
  }

  function formatDelta(amount: number, noun: string): string {
    return `${amount >= 0 ? '+' : ''}${amount} ${noun}`;
  }

  function queueGameplayLandingLesson(lesson: GameplayLesson): void {
    if (gameplayTutorialLandingLessonQueued) return;
    gameplayTutorialLandingLessonQueued = true;
    queueGameplayLesson(lesson);
  }

  function observeGameplayTutorialState(state: GameState): void {
    if (!isGameplayTutorialAvailable(state)) return;
    const touchTutorialCopy = getInputModality() === 'touch';
    if (
      !hasCompletedGameplayLesson('roll') &&
      isLocalActivePlayer(state) &&
      (state.phase === 'item_phase' || state.phase === 'rolling')
    ) {
      const turnSeconds = state.settings.turnTimer > 0 ? state.settings.turnTimer : 30;
      queueGameplayLesson({
        id: 'roll',
        title: 'ROLL',
        prompt: 'Roll the dice to move',
        detail: touchTutorialCopy
          ? `You have about ${turnSeconds} seconds on your turn. Use items before rolling, then tap Roll Dice and tap to throw.`
          : `You have about ${turnSeconds} seconds on your turn. Use items before rolling, then click Roll Dice and throw with click, Space, or Enter.`,
        target: () => gameplayTutorialTarget('roll-dice') ?? gameplayTutorialTarget('action-bar'),
        placement: 'top',
        manualAdvance: true,
        waitingText: 'ROLL',
        critical: true,
      });
    }
    if (
      state.phase === 'fork_choice' &&
      state.pendingForkChoice != null &&
      state.pendingForkChoice.playerId === localPlayerId
    ) {
      queueGameplayLesson({
        id: 'fork',
        title: 'FORK',
        prompt: 'Choose your route',
        detail: touchTutorialCopy
          ? 'Fork choices have 5 seconds. Tap the route arrow you want.'
          : 'Fork choices have 5 seconds. Click an arrow, use A/D or arrow keys, or use the gamepad stick/D-pad and A/Cross.',
        target: () => gameplayTutorialTarget('round-timer') ?? gameplayTutorialTarget('round-pill'),
        placement: 'bottom',
        critical: true,
      });
    }
    if (
      isLocalActivePlayer(state) &&
      state.phase === 'item_phase' &&
      hasCompletedGameplayLesson('objective')
    ) {
      queueGameplayLesson({
        id: 'actions',
        title: 'ITEMS',
        prompt: 'Items come before the roll',
        detail: touchTutorialCopy
          ? 'Tap Items before rolling when you have gear. If you are ready to move, roll the dice.'
          : 'Open items with E, RB/R1, or the Items button. If you are ready to move, roll the dice.',
        target: () => gameplayTutorialTarget('action-bar'),
        placement: 'top',
        passive: true,
      });
    }
    if (isOpponentActionTurn(state)) {
      queueGameplayLesson({
        id: 'players',
        title: 'PLAYERS',
        prompt: 'Track everyone on the left',
        detail: 'Player cards show HP, keys, trophies, and item count. The glowing card is up now.',
        target: () => gameplayTutorialTarget('player-cards'),
        placement: 'right',
        passive: true,
      });
      queueGameplayLesson({
        id: 'round',
        title: 'ROUND',
        prompt: 'Watch the round and timer',
        detail: 'The top timer appears for turns and fork choices. Timers keep the game moving.',
        target: () => gameplayTutorialTarget('round-timer') ?? gameplayTutorialTarget('round-pill'),
        placement: 'bottom',
        passive: true,
      });
      queueGameplayLesson({
        id: 'emotes',
        title: 'EMOTES',
        prompt: 'React at any time',
        detail: touchTutorialCopy
          ? 'Tap the emote button, then tap the reaction you want.'
          : 'Tap the emote button, hold T and aim with the mouse, or hold LT/L2 and use the right stick.',
        target: () => gameplayTutorialTarget('emote-button'),
        placement: 'top',
        passive: true,
      });
      queueGameplayLesson({
        id: 'menu',
        title: 'MENU',
        prompt: 'Settings and reminders live here',
        detail: 'Open the menu for settings and the How To Play reminder.',
        target: () => gameplayTutorialTarget('settings'),
        placement: 'left',
        passive: true,
      });
    }
    tryShowNextGameplayLesson();
  }

  function observeGameplayTutorialEvent(event: GameEvent): void {
    if (event.playerId !== localPlayerId) return;
    if (!isGameplayTutorialAvailable(latestGameState)) return;

    if (event.type === 'dice_roll') {
      if (gameplayTutorialVisibleLesson?.id === 'roll') {
        gameplayTutorialHandle?.advance();
      } else if (!hasCompletedGameplayLesson('roll')) {
        markGameplayLessonCompleted({
          id: 'roll',
          title: 'ROLL',
          prompt: 'Roll the dice to move',
        });
      }
      return;
    }

    if (event.type === 'movement_step' && !hasCompletedGameplayLesson('movement')) {
      const touchTutorialCopy = getInputModality() === 'touch';
      queueGameplayLesson({
        id: 'movement',
        title: 'MOVEMENT',
        prompt: 'Move tile by tile',
        detail: touchTutorialCopy
          ? 'Tap route arrows or board targets to choose paths. Your character follows the rolled number of steps.'
          : 'Use WASD, arrows, mouse, touch, or left stick/D-pad to choose paths. Your character follows the rolled number of steps.',
        placement: 'top',
        critical: true,
      });
      return;
    }

    if (event.type === 'turn_end') {
      gameplayTutorialLandingLessonQueued = false;
      if (!hasCompletedGameplayLesson('objective')) {
        const state = latestGameState;
        const targetTrophies = state?.settings.targetTrophies ?? 3;
        const trophyCost = state?.settings.trophyCost ?? 40;
        queueGameplayLesson({
          id: 'objective',
          title: 'GOAL',
          prompt: `Collect ${targetTrophies} trophies to win`,
          detail: `Collect keys, then buy trophies at treasure chests for ${trophyCost} keys. The first player to the trophy target wins.`,
          placement: 'top',
          critical: true,
        });
      }
      return;
    }

    if (event.type === 'landing') {
      if (
        gameplayTutorialLastLanding?.playerId === event.playerId &&
        gameplayTutorialLastLanding.nodeId === event.nodeId &&
        gameplayTutorialLastLanding.timestamp === event.timestamp
      ) {
        return;
      }
      gameplayTutorialLastLanding = {
        playerId: event.playerId,
        nodeId: event.nodeId,
        tileType: event.tileType,
        timestamp: event.timestamp,
      };
      gameplayTutorialLandingLessonQueued = false;
      if (event.nodeId === latestGameState?.treasureChestNodeId) {
        queueGameplayLandingLesson({
          id: 'treasure-chest',
          title: 'TROPHY',
          prompt: 'Treasure chests sell trophies',
          detail: `If you have ${latestGameState.settings.trophyCost} keys, you can buy a trophy here.`,
          placement: 'top',
          critical: true,
        });
      } else if (event.nodeId === latestGameState?.weaponsCacheNodeId) {
        queueGameplayLandingLesson({
          id: 'weapons-cache',
          title: 'CACHE',
          prompt: 'Weapons caches grant stronger items',
          detail: 'Choose a cache item when prompted. The cache relocates after it is claimed.',
          placement: 'top',
          critical: true,
        });
      } else if (event.tileType === 'hazard') {
        queueGameplayLandingLesson({
          id: 'hazard',
          title: 'HAZARD',
          prompt: 'Hazards hurt',
          detail: 'Hazard tiles deal damage and can knock keys loose. Stone Skin can block them.',
          placement: 'top',
          critical: true,
        });
      } else if (event.tileType === 'soul_anchor') {
        queueGameplayLandingLesson({
          id: 'soul-anchor',
          title: 'SOUL ANCHOR',
          prompt: 'Claim anchors for pressure',
          detail:
            'Choose keys or HP when claiming. Your own anchors are safe, but other players trigger them.',
          placement: 'top',
          critical: true,
        });
      }
      return;
    }

    if (event.type === 'economy_change' && event.reason === 'gold_tile') {
      queueGameplayLandingLesson({
        id: 'gold',
        title: 'GOLD',
        prompt: 'Gold tiles give keys',
        detail: `${formatDelta(event.newValue - event.oldValue, 'keys')} from this landing. Keys buy trophies and fuel some effects.`,
        placement: 'top',
        critical: true,
      });
      return;
    }

    if (event.type === 'player_received_item') {
      if (event.source === 'item_tile') {
        const item = getItem(event.itemId);
        queueGameplayLandingLesson({
          id: 'item',
          title: 'ITEM TILE',
          prompt: item == null ? 'You found an item' : `You found ${item.name}`,
          detail:
            'Item tiles grant a random item. Use items at the start of your turn before rolling.',
          target: () => gameplayTutorialTarget('items') ?? gameplayTutorialTarget('items-fab'),
          placement: 'top',
          critical: true,
        });
      } else if (event.source === 'weapons_cache') {
        const item = getItem(event.itemId);
        queueGameplayLandingLesson({
          id: 'weapons-cache',
          title: 'CACHE',
          prompt: item == null ? 'Cache item claimed' : `${item.name} claimed`,
          detail: 'Weapons caches offer stronger items and move after a claim.',
          placement: 'top',
          critical: true,
        });
      }
      return;
    }

    if (event.type === 'heal' && event.source === 'heal_tile') {
      queueGameplayLandingLesson({
        id: 'heal',
        title: 'HEAL',
        prompt: 'Heal tiles restore HP',
        detail: `${formatDelta(event.amount, 'HP')} from this tile. Normal healing caps at 30 HP.`,
        placement: 'top',
        critical: true,
      });
      return;
    }

    if (event.type === 'board_object_collected' && event.objectKind === 'dropped_keys') {
      queueGameplayLandingLesson({
        id: 'dropped-keys',
        title: 'DROPPED KEYS',
        prompt: 'Loose keys can be picked up',
        detail: `${formatDelta(event.quantity, 'keys')} from this tile. Players can collect keys dropped by hazards or knockouts.`,
        placement: 'top',
        critical: true,
      });
      return;
    }

    if (event.type === 'soul_anchor_claim') {
      queueGameplayLandingLesson({
        id: 'soul-anchor',
        title: 'SOUL ANCHOR',
        prompt: 'This anchor is yours',
        detail:
          event.mode === 'hp'
            ? 'Other players who land here lose HP. You do not trigger your own anchor.'
            : 'Other players who land here lose keys. You do not trigger your own anchor.',
        placement: 'top',
        critical: true,
      });
      return;
    }

    if (event.type === 'soul_anchor_trigger') {
      queueGameplayLandingLesson({
        id: 'soul-anchor',
        title: 'SOUL ANCHOR',
        prompt: 'Anchors punish intruders',
        detail: `${event.effectType === 'hp' ? 'HP drain' : 'Key steal'} triggered for ${event.amount}. Owners do not trigger their own anchors.`,
        placement: 'top',
        critical: true,
      });
      return;
    }

    if (event.type === 'death' || event.type === 'respawn') {
      queueGameplayLesson({
        id: 'death-respawn',
        title: 'RESPAWN',
        prompt: 'Knocked out players return',
        detail:
          'When HP hits zero, dropped keys can remain on the board and the player respawns at a graveyard.',
        placement: 'top',
        critical: true,
      });
    }
  }

  function routeBoardPhase(
    state: GameState,
    opts?: { forceRemount?: boolean; allowSystemOverlay?: boolean },
  ): void {
    if (state.phase === 'lobby') return;
    if (ACTIVE_MINIGAME_PHASES.has(state.phase)) return;
    if (boardPresentationPaused) {
      deferredBoardRouteState = {
        state,
        forceBoardRemount: opts?.forceRemount === true,
      };
      return;
    }
    if (isMovementCinematicBusy()) {
      tracePresentation({
        stage: 'state_sync_deferred',
        phase: state.phase,
        activePlayerId: state.turnOrder[state.currentPlayerIndex] ?? undefined,
        serverMessageType: 'state_sync',
        reason: 'route_blocked',
      });
      deferredBoardRouteState = {
        state,
        forceBoardRemount: opts?.forceRemount === true,
      };
      return;
    }

    observeGameplayTutorialState(state);
    requestAnimationFrame(() => {
      gameplayTutorialHandle?.layout();
      tryShowNextGameplayLesson();
    });

    const payloadBase = makeBoardPayload(state);
    if (payloadBase == null) return;
    if (state.phase === 'game_over') {
      disposeTileEffectOverlay();
      routeGameOverFlow(state, payloadBase);
      return;
    }
    if (opts?.allowSystemOverlay !== true && isSystemOverlayOpen()) {
      deferredBoardRouteState = {
        state,
        forceBoardRemount: opts?.forceRemount === true,
      };
      return;
    }
    ensureTileEffectOverlay(payloadBase);
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    const activePlayer = activeId == null ? null : (state.players.get(activeId) ?? null);
    const turnKey = turnKeyForState(state, activeId);

    if (shouldSuppressLocalPromptRoute(state)) {
      if (
        screenRouter.current === 'treasure-chest' ||
        screenRouter.current === 'weapons-cache' ||
        screenRouter.current === 'soul-anchor'
      ) {
        screenRouter.navigate('board-hud', payloadBase);
      } else if (opts?.forceRemount === true || !isBoardPhaseScreen(screenRouter.current)) {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (state.phase === 'rolling') {
      if (activeId != null && activePlayer?.isBot !== true && lastAnnouncedTurnKey !== turnKey) {
        void requestTurnBannerForPlayer(activeId, turnKey);
      }
      if (
        opts?.forceRemount === true ||
        screenRouter.current === 'turn-banner' ||
        (screenRouter.current !== 'board-hud' && screenRouter.current !== 'dice-roll')
      ) {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (state.phase === 'item_phase') {
      if (activeId != null && activePlayer?.isBot !== true) {
        void requestTurnBannerForPlayer(activeId, turnKey, {
          sendIntroComplete: isOwnedPlayerId(activeId, state),
        });
      }
      const preservingItemChoice =
        isOwnedPlayerId(activeId, state) &&
        (screenRouter.current === 'inventory' || screenRouter.current === 'item-targeting');
      if (
        !preservingItemChoice &&
        (opts?.forceRemount === true || screenRouter.current !== 'board-hud')
      ) {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (state.phase === 'fork_choice') {
      if (opts?.forceRemount === true || screenRouter.current !== 'board-hud') {
        screenRouter.navigate('board-hud', payloadBase);
      }
      requestBoardPromptReadyForState(state);
      return;
    }

    if (state.pendingBoardEncounter != null) {
      if (isOwnedPlayerId(state.pendingBoardEncounter.playerId, state)) {
        const targetScreen =
          state.pendingBoardEncounter.kind === 'weapons_cache' ? 'weapons-cache' : 'treasure-chest';
        if (opts?.forceRemount === true || screenRouter.current !== targetScreen) {
          screenRouter.navigate(targetScreen, payloadBase);
        }
        requestBoardPromptReadyForState(state);
      } else if (screenRouter.current !== 'board-hud') {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (state.pendingSoulAnchorClaim != null) {
      if (isOwnedPlayerId(state.pendingSoulAnchorClaim.playerId, state)) {
        if (opts?.forceRemount === true || screenRouter.current !== 'soul-anchor') {
          screenRouter.navigate('soul-anchor', payloadBase);
        }
        requestBoardPromptReadyForState(state);
      } else if (screenRouter.current !== 'board-hud') {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (
      isEncounterModalScreen(screenRouter.current) &&
      !hasMatchingLocalBoardEncounterForScreen(state, screenRouter.current)
    ) {
      screenRouter.navigate('board-hud', payloadBase);
      void processSpecialRelocationRevealQueue();
      return;
    }

    if (state.phase === 'chest_phase') {
      if (
        isOwnedPlayerId(activeId, state) &&
        (opts?.forceRemount === true || screenRouter.current !== 'treasure-chest')
      ) {
        screenRouter.navigate('treasure-chest', payloadBase);
      } else if (opts?.forceRemount === true || !isBoardPhaseScreen(screenRouter.current)) {
        screenRouter.navigate('board-hud', payloadBase);
      }
      return;
    }

    if (opts?.forceRemount === true || !isBoardPhaseScreen(screenRouter.current)) {
      screenRouter.navigate('board-hud', payloadBase);
    }
  }

  function syncPathArrowsForState(state: GameState): void {
    if (mapHandle == null || state.phase === 'game_over') {
      clearPathArrowsAndFacing();
      return;
    }
    const activeId = state.turnOrder[state.currentPlayerIndex] ?? null;
    const activeNodeId = activeId == null ? null : state.players.get(activeId)?.nodeId;
    const pendingFork =
      activeId != null && activeNodeId != null ? pendingForkOptionsByPlayer.get(activeId) : null;
    const exactForkOptions =
      pendingFork != null && pendingFork.atNodeId === activeNodeId
        ? pendingFork.options
        : undefined;
    const activeIsLocal = isOwnedPlayerId(activeId, state);
    const showPreRollArrows =
      activeIsLocal &&
      isPreRollPathPhase(state.phase) &&
      !isFrozenForCurrentRound(state, activeId) &&
      !isRollSubmissionPendingForState(state);
    const showForkArrows =
      activeIsLocal && state.phase === 'fork_choice' && exactForkOptions != null;
    if (actionInputLocked || (!showPreRollArrows && !showForkArrows)) {
      clearPathArrowsAndFacing();
      return;
    }
    ensureNavigationOverlays();
    pathArrows?.update(state, mapHandle, actionInputLocked, exactForkOptions);
    faceActiveBoardRigTowardSelectedPathArrow(pathArrows?.getSelectedNextNodeId() ?? null);
  }

  function cameraPositionForFocus(focus: THREE.Vector3): THREE.Vector3 {
    return focus.clone().add(new THREE.Vector3(0, 4.5, 5.4));
  }

  function waitWithGsap(seconds: number, label: string): Promise<void> {
    return new Promise((resolve) => {
      const handle = gsapRegistry.create(label);
      handle.timeline.call(
        () => {
          handle.dispose();
          resolve();
        },
        undefined,
        seconds,
      );
    });
  }

  function tweenCameraTo(
    position: THREE.Vector3,
    lookAt: THREE.Vector3,
    fov: number,
    duration: number,
    label: string,
    startLookAtOverride?: THREE.Vector3,
  ): Promise<void> {
    const camera = renderer.cameraRig.camera;
    const startLookAt =
      startLookAtOverride ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const proxy = {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z,
      lx: startLookAt.x,
      ly: startLookAt.y,
      lz: startLookAt.z,
      fov: camera.fov,
    };
    return new Promise((resolve) => {
      const handle = gsapRegistry.create(label);
      handle.timeline.to(
        proxy,
        {
          x: position.x,
          y: position.y,
          z: position.z,
          lx: lookAt.x,
          ly: lookAt.y,
          lz: lookAt.z,
          fov,
          duration,
          ease: 'power2.inOut',
          onUpdate: () => {
            camera.position.set(proxy.x, proxy.y, proxy.z);
            camera.lookAt(proxy.lx, proxy.ly, proxy.lz);
            camera.fov = proxy.fov;
            camera.updateProjectionMatrix();
          },
        },
        0,
      );
      handle.timeline.call(
        () => {
          handle.dispose();
          camera.position.copy(position);
          camera.lookAt(lookAt);
          camera.fov = fov;
          camera.updateProjectionMatrix();
          resolve();
        },
        undefined,
        duration,
      );
    });
  }

  function createTrapCinematicOverlay(): HTMLElement {
    trapCinematicOverlay?.remove();
    const root = document.createElement('div');
    root.setAttribute('data-trap-cinematic', '');
    root.style.cssText = [
      'position: fixed',
      'inset: 0',
      'z-index: 460',
      'pointer-events: none',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'background: radial-gradient(circle at center, rgba(89, 38, 155, 0.38) 0%, rgba(18, 9, 34, 0.92) 48%, rgba(0, 0, 0, 0.99) 100%)',
      'opacity: 0',
      'color: #f7f2ff',
      "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
      'font-size: 52px',
      'font-weight: 700',
      'letter-spacing: 0',
      'line-height: 1',
      'text-align: center',
      'text-shadow: 0 0 24px rgba(154, 80, 255, 0.95), 0 0 72px rgba(54, 20, 122, 0.9)',
    ].join('; ');

    const label = document.createElement('div');
    label.textContent = 'TRAPPED';
    label.style.cssText = [
      'max-width: 92%',
      'overflow-wrap: anywhere',
      'opacity: 0',
      'transform: scale(0.84)',
    ].join('; ');
    root.appendChild(label);
    opts.host.appendChild(root);
    trapCinematicOverlay = root;
    return root;
  }

  function tweenTrapElement(
    element: HTMLElement,
    vars: Record<string, unknown>,
    duration: number,
    label: string,
  ): Promise<void> {
    return new Promise((resolve) => {
      const handle = gsapRegistry.create(label);
      handle.timeline.to(element, { ...vars, duration }, 0);
      handle.timeline.call(
        () => {
          handle.dispose();
          resolve();
        },
        undefined,
        duration,
      );
    });
  }

  function trapFocusForPlayer(playerId: string): THREE.Vector3 | null {
    const rigPosition = findPlayerRigPosition(playerId);
    if (rigPosition != null) {
      rigPosition.y += BOARD_FOCUS_Y_OFFSET;
      return rigPosition;
    }
    const nodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    return nodeId == null ? null : focusForNode(nodeId);
  }

  async function runMysteryBoxTrapCinematic(playerId: string): Promise<void> {
    if (trapCinematicRunning || thunderFistCinematicRunning || disposed) return;
    trapCinematicRunning = true;
    setActionInputLock(ACTION_LOCK_TRAP_CINEMATIC, true);
    clearPathArrowsAndFacing();

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;
    const overlay = createTrapCinematicOverlay();
    const label = overlay.firstElementChild as HTMLElement | null;

    try {
      if (orbitControls != null) {
        orbitControls.enabled = false;
      }
      renderer.cameraRig.setMode('spectator');

      const focus = trapFocusForPlayer(playerId);
      const cameraIn =
        focus == null
          ? waitWithGsap(0.42, 'mystery-box-trap:camera-skip')
          : (() => {
              const direction = camera.position.clone().sub(focus);
              if (direction.lengthSq() < 0.001) {
                direction.set(0, 2.2, 3.2);
              }
              direction.normalize();
              const position = focus.clone().addScaledVector(direction, 3.2);
              position.y = Math.max(position.y, focus.y + 1.05);
              return tweenCameraTo(
                position,
                focus,
                28,
                0.42,
                'mystery-box-trap:camera-in',
                originalTarget,
              );
            })();

      await Promise.all([
        cameraIn,
        tweenTrapElement(
          overlay,
          { opacity: 1, ease: 'power2.inOut' },
          0.42,
          'mystery-box-trap:fade-in',
        ),
      ]);

      if (label != null) {
        await tweenTrapElement(
          label,
          { opacity: 1, scale: 1, ease: 'back.out(1.55)' },
          0.28,
          'mystery-box-trap:label-in',
        );
        await tweenTrapElement(
          label,
          { scale: 1.04, ease: 'sine.inOut' },
          0.17,
          'mystery-box-trap:label-pulse-out',
        );
        await tweenTrapElement(
          label,
          { scale: 1, ease: 'sine.inOut' },
          0.17,
          'mystery-box-trap:label-pulse-in',
        );
      } else {
        await waitWithGsap(0.62, 'mystery-box-trap:label-skip');
      }

      await waitWithGsap(0.18, 'mystery-box-trap:hold');
      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        0.32,
        'mystery-box-trap:camera-return',
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
      await tweenTrapElement(
        overlay,
        { opacity: 0, ease: 'power2.out' },
        0.24,
        'mystery-box-trap:fade-out',
      );
    } finally {
      trapCinematicOverlay?.remove();
      trapCinematicOverlay = null;
      camera.position.copy(originalPosition);
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
      trapCinematicRunning = false;
      setActionInputLock(ACTION_LOCK_TRAP_CINEMATIC, false);
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      for (const [queuedPlayerId, queue] of queuedMovementSteps.entries()) {
        if (queue.length > 0) void processMovementQueue(queuedPlayerId);
      }
      void processShadowStalkerSpawnRevealQueue();
      void processSpecialRelocationRevealQueue();
    }
  }

  function createOrbitalCinematicOverlay(): HTMLElement {
    orbitalCinematicOverlay?.remove();
    const root = document.createElement('div');
    root.setAttribute('data-orbital-cinematic', '');
    root.style.cssText = [
      'position: fixed',
      'inset: 0',
      'z-index: 455',
      'pointer-events: none',
      'overflow: hidden',
      'background: radial-gradient(circle at center, rgba(19, 34, 73, 0.38) 0%, rgba(4, 7, 18, 0.92) 52%, rgba(0, 0, 0, 0.98) 100%)',
      'opacity: 0',
    ].join('; ');

    const stars = document.createElement('div');
    stars.setAttribute('data-orbital-stars', '');
    stars.style.cssText = [
      'position: absolute',
      'inset: 0',
      'background-image: radial-gradient(circle, rgba(255,255,255,0.9) 0 1px, transparent 1px), radial-gradient(circle, rgba(130,190,255,0.75) 0 1px, transparent 1px)',
      'background-size: 72px 72px, 113px 113px',
      'background-position: 8px 14px, 44px 55px',
      'opacity: 0.72',
    ].join('; ');

    const reticle = document.createElement('div');
    reticle.setAttribute('data-orbital-reticle', '');
    reticle.style.cssText = [
      'position: absolute',
      'left: 50%',
      'top: 50%',
      'width: 168px',
      'height: 168px',
      'margin-left: -84px',
      'margin-top: -84px',
      'border: 2px solid rgba(113, 202, 255, 0.95)',
      'border-radius: 50%',
      'box-shadow: 0 0 30px rgba(80, 190, 255, 0.88), inset 0 0 36px rgba(80, 190, 255, 0.35)',
      'opacity: 0',
      'transform: scale(0.38)',
    ].join('; ');

    const label = document.createElement('div');
    label.textContent = 'ORBITAL LOCK';
    label.style.cssText = [
      'position: absolute',
      'left: 0',
      'right: 0',
      'bottom: 34px',
      'color: #eaf8ff',
      "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
      'font-size: 34px',
      'font-weight: 700',
      'letter-spacing: 0',
      'line-height: 1',
      'text-align: center',
      'text-shadow: 0 0 18px rgba(88, 192, 255, 0.95), 0 0 52px rgba(20, 80, 180, 0.9)',
      'opacity: 0',
      'transform: scale(0.9)',
    ].join('; ');

    root.append(stars, reticle, label);
    opts.host.appendChild(root);
    orbitalCinematicOverlay = root;
    return root;
  }

  function getOrbitalOverlayPart(root: HTMLElement, selector: string): HTMLElement | null {
    return root.querySelector(selector);
  }

  function playOrbitalStrikeEffect(target: THREE.Vector3): Promise<void> {
    const group = new THREE.Group();
    group.name = 'orbital-cannon-cinematic-strike';

    const beamHeight = 22;
    const beamGeometry = new THREE.CylinderGeometry(0.22, 0.52, beamHeight, 32, 1, true);
    const beamMaterial = new THREE.MeshBasicMaterial({
      color: 0x9fe8ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const beam = new THREE.Mesh(beamGeometry, beamMaterial);
    beam.position.set(target.x, target.y + beamHeight * 0.5, target.z);
    beam.scale.set(0.12, 0.02, 0.12);
    group.add(beam);

    const glowGeometry = new THREE.CylinderGeometry(0.8, 1.55, beamHeight, 32, 1, true);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0x4fc6ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.position.copy(beam.position);
    glow.scale.set(0.18, 0.02, 0.18);
    group.add(glow);

    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.42, 18, 12), coreMaterial);
    core.position.set(target.x, target.y + beamHeight - 0.8, target.z);
    core.scale.set(0.75, 1.45, 0.75);
    group.add(core);

    const ringGeometry = new THREE.TorusGeometry(0.95, 0.045, 10, 36);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x9fe8ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.copy(target);
    ring.position.y += 0.08;
    ring.rotation.x = Math.PI / 2;
    ring.scale.setScalar(0.2);
    group.add(ring);

    const shockwaveGeometry = new THREE.TorusGeometry(1.32, 0.035, 10, 48);
    const shockwaveMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const shockwave = new THREE.Mesh(shockwaveGeometry, shockwaveMaterial);
    shockwave.position.copy(target);
    shockwave.position.y += 0.1;
    shockwave.rotation.x = Math.PI / 2;
    shockwave.scale.setScalar(0.16);
    group.add(shockwave);

    renderer.scene.groups.effects.add(group);

    return new Promise((resolve) => {
      const handle = gsapRegistry.create('orbital-cannon:world-strike');
      handle.timeline.to(beamMaterial, { opacity: 1, duration: 0.1, ease: 'power2.out' }, 0);
      handle.timeline.to(
        beam.scale,
        { x: 1.18, y: 1, z: 1.18, duration: 0.24, ease: 'power4.out' },
        0,
      );
      handle.timeline.to(glowMaterial, { opacity: 0.58, duration: 0.14, ease: 'power2.out' }, 0.02);
      handle.timeline.to(
        glow.scale,
        { x: 1.12, y: 1, z: 1.12, duration: 0.26, ease: 'power4.out' },
        0,
      );
      handle.timeline.to(coreMaterial, { opacity: 1, duration: 0.08, ease: 'power2.out' }, 0.04);
      handle.timeline.to(
        core.position,
        { y: target.y + 0.36, duration: 0.3, ease: 'power3.in' },
        0.04,
      );
      handle.timeline.to(
        core.scale,
        { x: 1.45, y: 0.55, z: 1.45, duration: 0.18, ease: 'power2.out' },
        0.26,
      );
      handle.timeline.to(ringMaterial, { opacity: 0.95, duration: 0.08, ease: 'power2.out' }, 0.06);
      handle.timeline.to(
        ring.scale,
        { x: 2.8, y: 2.8, z: 2.8, duration: 0.34, ease: 'power3.out' },
        0.06,
      );
      handle.timeline.to(
        shockwaveMaterial,
        { opacity: 0.85, duration: 0.08, ease: 'power2.out' },
        0.12,
      );
      handle.timeline.to(
        shockwave.scale,
        { x: 3.8, y: 3.8, z: 3.8, duration: 0.42, ease: 'power3.out' },
        0.12,
      );
      handle.timeline.to(beamMaterial, { opacity: 0, duration: 0.42, ease: 'power2.in' }, 0.42);
      handle.timeline.to(glowMaterial, { opacity: 0, duration: 0.42, ease: 'power2.in' }, 0.38);
      handle.timeline.to(coreMaterial, { opacity: 0, duration: 0.24, ease: 'power2.in' }, 0.36);
      handle.timeline.to(ringMaterial, { opacity: 0, duration: 0.3, ease: 'power2.in' }, 0.2);
      handle.timeline.to(
        shockwaveMaterial,
        { opacity: 0, duration: 0.32, ease: 'power2.in' },
        0.28,
      );
      handle.timeline.call(
        () => {
          handle.dispose();
          renderer.scene.groups.effects.remove(group);
          disposeSkillshotGroupLike(group);
          resolve();
        },
        undefined,
        0.86,
      );
    });
  }

  function thunderFistFocusForPlayer(playerId: string, yOffset: number): THREE.Vector3 | null {
    const rigPosition = findPlayerRigPosition(playerId);
    if (rigPosition != null) {
      rigPosition.y += yOffset;
      return rigPosition;
    }
    const nodeId = latestGameState?.players.get(playerId)?.nodeId ?? null;
    return nodeId == null ? null : focusForNode(nodeId);
  }

  function thunderFistDirectionForEvent(
    event: ItemHitEvent,
    casterFocus: THREE.Vector3,
    targetFocus: THREE.Vector3,
  ): THREE.Vector3 {
    const direction = targetFocus.clone().sub(casterFocus);
    direction.y = 0;
    if (direction.lengthSq() >= 1e-6) return direction.normalize();

    const eventDirection = event.direction;
    if (eventDirection != null) {
      const magnitude = Math.hypot(eventDirection.dx, eventDirection.dy);
      if (Number.isFinite(magnitude) && magnitude >= 0.001) {
        return new THREE.Vector3(eventDirection.dx / magnitude, 0, eventDirection.dy / magnitude);
      }
    }

    const cameraDirection = renderer.cameraRig.camera.getWorldDirection(new THREE.Vector3());
    cameraDirection.y = 0;
    if (cameraDirection.lengthSq() >= 1e-6) return cameraDirection.normalize();
    return new THREE.Vector3(0, 0, 1);
  }

  function resolveThunderFistHitCinematic(event: ItemHitEvent): {
    start: THREE.Vector3;
    hit: THREE.Vector3;
    targetFocus: THREE.Vector3;
  } | null {
    const casterFocus = thunderFistFocusForPlayer(event.casterId, THUNDER_FIST_HAND_Y_OFFSET);
    const targetNodeId =
      event.targetNodeId ??
      event.nodeId ??
      event.toNodeId ??
      latestGameState?.players.get(event.targetId)?.nodeId ??
      null;
    const targetFocus =
      thunderFistFocusForPlayer(event.targetId, THUNDER_FIST_CHEST_Y_OFFSET) ??
      (targetNodeId == null ? null : focusForNode(targetNodeId));
    if (casterFocus == null || targetFocus == null) return null;

    const direction = thunderFistDirectionForEvent(event, casterFocus, targetFocus);
    const start = casterFocus.clone().addScaledVector(direction, 0.48);
    start.y = casterFocus.y;
    const hit = targetFocus.clone().addScaledVector(direction, -0.34);
    hit.y = targetFocus.y;

    if (start.distanceToSquared(hit) < 0.64) {
      start.copy(hit).addScaledVector(direction, -1.15);
      start.y = casterFocus.y;
    }

    return { start, hit, targetFocus };
  }

  function thunderFistCameraPose(
    start: THREE.Vector3,
    hit: THREE.Vector3,
    targetFocus: THREE.Vector3,
    originalPosition: THREE.Vector3,
  ): { position: THREE.Vector3; lookAt: THREE.Vector3 } {
    const direction = hit.clone().sub(start);
    direction.y = 0;
    if (direction.lengthSq() < 1e-6) direction.set(0, 0, 1);
    direction.normalize();

    const up = new THREE.Vector3(0, 1, 0);
    const side = new THREE.Vector3().crossVectors(direction, up);
    if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
    side.normalize();

    const base = hit.clone().addScaledVector(direction, 2.45);
    base.y = Math.max(hit.y + 1.1, targetFocus.y + 0.98);
    const left = base.clone().addScaledVector(side, 2.05);
    const right = base.clone().addScaledVector(side, -2.05);
    const position =
      left.distanceToSquared(originalPosition) <= right.distanceToSquared(originalPosition)
        ? left
        : right;
    const lookAt = hit.clone().addScaledVector(direction, -0.16);
    return { position, lookAt };
  }

  function createThunderFistTrailGeometry(
    start: THREE.Vector3,
    hit: THREE.Vector3,
  ): THREE.BufferGeometry {
    const direction = hit.clone().sub(start);
    direction.y = 0;
    if (direction.lengthSq() < 1e-6) direction.set(0, 0, 1);
    direction.normalize();

    const side = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0));
    if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
    side.normalize();

    const p0 = start.clone();
    const p1 = start.clone().lerp(hit, 0.35).addScaledVector(side, 0.08);
    const p2 = start
      .clone()
      .lerp(hit, 0.72)
      .addScaledVector(side, -0.07)
      .add(new THREE.Vector3(0, 0.05, 0));
    const p3 = hit.clone();
    return new THREE.BufferGeometry().setFromPoints([p0, p1, p1, p2, p2, p3]);
  }

  function playThunderFistHitEffect(
    start: THREE.Vector3,
    hit: THREE.Vector3,
    modelScene: THREE.Group | null,
    onImpact: () => void,
  ): Promise<void> {
    const group = new THREE.Group();
    group.name = 'thunder-fist-hit-cinematic';

    const trailMaterial = new THREE.LineBasicMaterial({
      color: 0xfff099,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const trail = new THREE.LineSegments(createThunderFistTrailGeometry(start, hit), trailMaterial);
    group.add(trail);

    const projectile = createThunderFistProjectileObject(modelScene);
    projectile.name = `${projectile.name}:cinematic`;
    projectile.position.copy(start);
    projectile.scale.multiplyScalar(1.08);
    orientThunderFistProjectile(projectile, start, hit);
    group.add(projectile);

    const sparkMaterial = new THREE.SpriteMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const spark = new THREE.Sprite(sparkMaterial);
    spark.position.copy(hit);
    spark.scale.set(0.05, 0.05, 1);
    group.add(spark);

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xfff099,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.26, 0.035, 10, 40), ringMaterial);
    ring.position.copy(hit);
    orientThunderFistProjectile(ring, start, hit);
    ring.scale.setScalar(0.2);
    group.add(ring);

    renderer.scene.groups.effects.add(group);

    return new Promise((resolve) => {
      let impacted = false;
      const handle = gsapRegistry.create('thunder-fist:hit-cinematic');
      handle.timeline.to(trailMaterial, { opacity: 0.62, duration: 0.06, ease: 'power2.out' }, 0);
      handle.timeline.to(
        projectile.position,
        {
          x: hit.x,
          y: hit.y,
          z: hit.z,
          duration: THUNDER_FIST_HIT_TRAVEL_SECONDS,
          ease: 'power3.in',
        },
        0,
      );
      handle.timeline.to(
        projectile.scale,
        {
          x: projectile.scale.x * 1.06,
          y: projectile.scale.y * 1.06,
          z: projectile.scale.z * 1.06,
          duration: 0.1,
          ease: 'back.out(1.7)',
        },
        THUNDER_FIST_HIT_TRAVEL_SECONDS - 0.02,
      );
      handle.timeline.call(
        () => {
          if (impacted) return;
          impacted = true;
          onImpact();
        },
        undefined,
        THUNDER_FIST_HIT_TRAVEL_SECONDS,
      );
      handle.timeline.to(
        sparkMaterial,
        { opacity: 1, duration: 0.04, ease: 'power2.out' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS - 0.02,
      );
      handle.timeline.to(
        spark.scale,
        { x: 1.65, y: 1.65, z: 1, duration: 0.18, ease: 'power2.out' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS - 0.02,
      );
      handle.timeline.to(
        ringMaterial,
        { opacity: 0.88, duration: 0.04, ease: 'power2.out' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS,
      );
      handle.timeline.to(
        ring.scale,
        { x: 2.1, y: 2.1, z: 2.1, duration: 0.22, ease: 'power3.out' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS,
      );
      handle.timeline.to(
        trailMaterial,
        { opacity: 0, duration: 0.18, ease: 'power2.in' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS,
      );
      handle.timeline.to(
        sparkMaterial,
        { opacity: 0, duration: 0.12, ease: 'power2.in' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS + 0.08,
      );
      handle.timeline.to(
        ringMaterial,
        { opacity: 0, duration: 0.12, ease: 'power2.in' },
        THUNDER_FIST_HIT_TRAVEL_SECONDS + 0.1,
      );
      handle.timeline.call(
        () => {
          handle.dispose();
          renderer.scene.groups.effects.remove(group);
          disposeSkillshotGroupLike(group);
          resolve();
        },
        undefined,
        THUNDER_FIST_HIT_LIFETIME_SECONDS,
      );
    });
  }

  async function runThunderFistHitCinematic(event: ItemHitEvent): Promise<void> {
    const resolved = resolveThunderFistHitCinematic(event);
    if (resolved == null) {
      playItemImpactReaction(event.targetId, event.casterId);
      return;
    }
    if (
      thunderFistCinematicRunning ||
      trapCinematicRunning ||
      orbitalCinematicRunning ||
      disposed
    ) {
      playItemImpactReaction(event.targetId, event.casterId);
      return;
    }

    thunderFistCinematicRunning = true;
    setActionInputLock(ACTION_LOCK_THUNDER_FIST_CINEMATIC, true);
    clearPathArrowsAndFacing();

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;
    const cameraPose = thunderFistCameraPose(
      resolved.start,
      resolved.hit,
      resolved.targetFocus,
      originalPosition,
    );
    const modelLoad = loadItemModelScene('thunder_fist');

    try {
      if (orbitControls != null) {
        orbitControls.enabled = false;
      }
      renderer.cameraRig.setMode('spectator');

      await tweenCameraTo(
        cameraPose.position,
        cameraPose.lookAt,
        33,
        THUNDER_FIST_HIT_CAMERA_IN_SECONDS,
        'thunder-fist:camera-in',
        originalTarget,
      );

      let modelScene = getResolvedItemModelScene('thunder_fist');
      if (modelScene == null) {
        const loadedModel = await Promise.race<THREE.Group | null>([
          modelLoad,
          waitWithGsap(0.08, 'thunder-fist:model-timeout').then(() => null),
        ]);
        modelScene = loadedModel ?? getResolvedItemModelScene('thunder_fist');
      }

      await playThunderFistHitEffect(resolved.start, resolved.hit, modelScene, () => {
        playItemImpactReaction(event.targetId, event.casterId, 0.5);
      });

      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        THUNDER_FIST_HIT_CAMERA_RETURN_SECONDS,
        'thunder-fist:camera-return',
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
    } finally {
      camera.position.copy(originalPosition);
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
      thunderFistCinematicRunning = false;
      setActionInputLock(ACTION_LOCK_THUNDER_FIST_CINEMATIC, false);
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      for (const [queuedPlayerId, queue] of queuedMovementSteps.entries()) {
        if (queue.length > 0) void processMovementQueue(queuedPlayerId);
      }
      void processShadowStalkerSpawnRevealQueue();
      void processSpecialRelocationRevealQueue();
    }
  }

  async function runOrbitalCannonCinematic(targetPlayerId: string): Promise<void> {
    if (orbitalCinematicRunning || thunderFistCinematicRunning || disposed) return;
    orbitalCinematicRunning = true;
    setActionInputLock(ACTION_LOCK_ORBITAL_CINEMATIC, true);
    clearPathArrowsAndFacing();

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;
    const overlay = createOrbitalCinematicOverlay();
    const reticle = getOrbitalOverlayPart(overlay, '[data-orbital-reticle]');
    const stars = getOrbitalOverlayPart(overlay, '[data-orbital-stars]');
    const label = overlay.lastElementChild as HTMLElement | null;

    try {
      if (orbitControls != null) {
        orbitControls.enabled = false;
      }
      renderer.cameraRig.setMode('spectator');

      const boardFocus = boardCentroidFocus() ?? new THREE.Vector3(0, 0, 0);
      const targetFocus = trapFocusForPlayer(targetPlayerId) ?? boardFocus;
      const spacePosition = boardFocus.clone().add(new THREE.Vector3(0, 26, 0.35));
      await Promise.all([
        tweenCameraTo(
          spacePosition,
          boardFocus,
          62,
          0.62,
          'orbital-cannon:camera-to-space',
          originalTarget,
        ),
        tweenTrapElement(
          overlay,
          { opacity: 1, ease: 'power2.inOut' },
          0.42,
          'orbital-cannon:overlay-in',
        ),
      ]);

      await Promise.all([
        reticle == null
          ? waitWithGsap(0.44, 'orbital-cannon:reticle-skip')
          : tweenTrapElement(
              reticle,
              { opacity: 1, scale: 1, ease: 'back.out(1.45)' },
              0.44,
              'orbital-cannon:reticle-lock',
            ),
        label == null
          ? waitWithGsap(0.44, 'orbital-cannon:label-skip')
          : tweenTrapElement(
              label,
              { opacity: 1, scale: 1, ease: 'power2.out' },
              0.28,
              'orbital-cannon:label-in',
            ),
      ]);

      await waitWithGsap(0.08, 'orbital-cannon:lock-settle');

      await Promise.all([
        tweenTrapElement(
          overlay,
          { opacity: 0.44, ease: 'power2.out' },
          0.18,
          'orbital-cannon:overlay-clear-for-impact',
        ),
        stars == null
          ? waitWithGsap(0.18, 'orbital-cannon:stars-clear-skip')
          : tweenTrapElement(
              stars,
              { opacity: 0.22, ease: 'power2.out' },
              0.18,
              'orbital-cannon:stars-clear',
            ),
        reticle == null
          ? waitWithGsap(0.18, 'orbital-cannon:reticle-impact-clear-skip')
          : tweenTrapElement(
              reticle,
              { opacity: 0.42, scale: 0.88, ease: 'power2.out' },
              0.18,
              'orbital-cannon:reticle-impact-clear',
            ),
        label == null
          ? waitWithGsap(0.18, 'orbital-cannon:label-impact-clear-skip')
          : tweenTrapElement(
              label,
              { opacity: 0, scale: 0.94, ease: 'power2.out' },
              0.18,
              'orbital-cannon:label-impact-clear',
            ),
      ]);

      const diveDirection = camera.position.clone().sub(targetFocus);
      if (diveDirection.lengthSq() < 0.001) {
        diveDirection.set(0, 3.2, 4.2);
      }
      diveDirection.normalize();
      const targetCameraPosition = targetFocus.clone().addScaledVector(diveDirection, 4.3);
      targetCameraPosition.y = Math.max(targetCameraPosition.y, targetFocus.y + 1.8);
      await tweenCameraTo(
        targetCameraPosition,
        targetFocus,
        30,
        0.44,
        'orbital-cannon:camera-to-target',
      );

      await Promise.all([
        playOrbitalStrikeEffect(targetFocus),
        tweenTrapElement(
          overlay,
          {
            background:
              'radial-gradient(circle at center, rgba(115, 213, 255, 0.34) 0%, rgba(4, 7, 18, 0.84) 48%, rgba(0, 0, 0, 0.98) 100%)',
            ease: 'power2.out',
          },
          0.1,
          'orbital-cannon:flash-in',
        ).then(() =>
          tweenTrapElement(
            overlay,
            {
              background:
                'radial-gradient(circle at center, rgba(19, 34, 73, 0.38) 0%, rgba(4, 7, 18, 0.92) 52%, rgba(0, 0, 0, 0.98) 100%)',
              ease: 'power2.in',
            },
            0.24,
            'orbital-cannon:flash-out',
          ),
        ),
      ]);

      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        0.34,
        'orbital-cannon:camera-return',
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
      await tweenTrapElement(
        overlay,
        { opacity: 0, ease: 'power2.out' },
        0.24,
        'orbital-cannon:overlay-out',
      );
    } finally {
      orbitalCinematicOverlay?.remove();
      orbitalCinematicOverlay = null;
      camera.position.copy(originalPosition);
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
      orbitalCinematicRunning = false;
      setActionInputLock(ACTION_LOCK_ORBITAL_CINEMATIC, false);
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      for (const [queuedPlayerId, queue] of queuedMovementSteps.entries()) {
        if (queue.length > 0) void processMovementQueue(queuedPlayerId);
      }
      void processShadowStalkerSpawnRevealQueue();
      void processSpecialRelocationRevealQueue();
    }
  }

  function syncSpecialTileVisibility(
    treasureChestNodeId: string | null,
    weaponsCacheNodeId: string | null,
  ): void {
    if (boardHandle == null) return;

    const nextHidden = new Set<string>();
    if (treasureChestNodeId != null && treasureChestNodeId !== '') {
      nextHidden.add(treasureChestNodeId);
    }
    if (weaponsCacheNodeId != null && weaponsCacheNodeId !== '') {
      nextHidden.add(weaponsCacheNodeId);
    }

    for (const nodeId of hiddenSpecialTileNodeIds) {
      if (!nextHidden.has(nodeId)) {
        boardHandle.tileMarkers.setVisible(nodeId, true);
      }
    }
    for (const nodeId of nextHidden) {
      if (!hiddenSpecialTileNodeIds.has(nodeId)) {
        boardHandle.tileMarkers.setVisible(nodeId, false);
      }
    }
    hiddenSpecialTileNodeIds = nextHidden;
  }

  function updateSpecialOverlays(): void {
    if (boardHandle == null || mapHandle == null) return;
    boardHandle.chestOverlay.update(latestTreasureChestNodeId, mapHandle);
    boardHandle.cacheOverlay.update(latestWeaponsCacheNodeId, mapHandle);
    syncSpecialTileVisibility(latestTreasureChestNodeId, latestWeaponsCacheNodeId);
  }

  function syncBoardStateVisuals(state: GameState): boolean {
    if (boardHandle == null) {
      replayEyeExpressionEventsFromState(state);
      return false;
    }
    const playerColors = playerColorsForState(state);
    boardHandle.syncDroppedKeys(state.droppedKeys, playerColors);

    const anchorTints = new Map<string, number>();
    for (const player of state.players.values()) {
      const color = COLOR_ID_TO_HEX[player.customization.color] ?? player.color;
      for (const nodeId of player.soulAnchorClaims) {
        anchorTints.set(nodeId, color);
      }
    }
    boardHandle.syncSoulAnchorClaims(anchorTints);
    boardHandle.syncActiveClouds(state.activeClouds);
    syncBoardNpcs(state);
    characterStatusEffects.syncFromState(state);
    characterEyeExpressions.syncFromState(state);
    replayEyeExpressionEventsFromState(state);
    return true;
  }

  function playerColorsForState(state: GameState): Map<string, number> {
    const playerColors = new Map<string, number>();
    for (const [playerId, player] of state.players.entries()) {
      playerColors.set(playerId, player.color);
    }
    return playerColors;
  }

  async function runChestCacheSpawnSequence(
    chestNodeId: string,
    cacheNodeId: string,
  ): Promise<void> {
    if (boardHandle == null || mapHandle == null || hasRunSpawnSequence || spawnSequenceRunning) {
      return;
    }

    const chestFocus = focusForNode(chestNodeId);
    const cacheFocus = focusForNode(cacheNodeId);
    if (chestFocus == null || cacheFocus == null) {
      updateSpecialOverlays();
      hasRunSpawnSequence = true;
      pendingSpawnSequence = false;
      setActionInputLock(ACTION_LOCK_CHEST_SPAWN, false);
      return;
    }

    hasRunSpawnSequence = true;
    spawnSequenceRunning = true;
    pendingSpawnSequence = false;
    setActionInputLock(ACTION_LOCK_CHEST_SPAWN, true);
    syncSpecialTileVisibility(chestNodeId, cacheNodeId);

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;

    try {
      if (orbitControls != null) {
        orbitControls.enabled = false;
      }
      renderer.cameraRig.setMode('spectator');

      await tweenCameraTo(
        cameraPositionForFocus(chestFocus),
        chestFocus,
        35,
        0.6,
        'chest-spawn:lobby-to-cinematic',
        originalTarget,
      );
      await boardHandle.chestOverlay.playFirstAppearance(chestNodeId, mapHandle);
      spawnLandingBurst(boardHandle.chestOverlay.group.position, 'gold');
      await waitWithGsap(0.4, 'chest-spawn:hold-chest');

      const cacheRise = boardHandle.cacheOverlay
        .playFirstAppearance(cacheNodeId, mapHandle)
        .then(() => {
          if (boardHandle != null) {
            spawnLandingBurst(boardHandle.cacheOverlay.group.position, 'item');
          }
        });
      await tweenCameraTo(
        cameraPositionForFocus(cacheFocus),
        cacheFocus,
        35,
        0.45,
        'chest-spawn:camera-to-cache',
      );
      await cacheRise;
      await waitWithGsap(0.35, 'chest-spawn:hold-cache');

      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        0.45,
        'chest-spawn:camera-return',
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
    } finally {
      setActionInputLock(ACTION_LOCK_CHEST_SPAWN, false);
      spawnSequenceRunning = false;
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
      updateSpecialOverlays();
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
      void processShadowStalkerSpawnRevealQueue();
      void processSpecialRelocationRevealQueue();
    }
  }

  async function runSpecialRelocationReveal(reveal: PendingSpecialRelocationReveal): Promise<void> {
    if (boardHandle == null || mapHandle == null) return;
    const focus = focusForNode(reveal.nodeId);
    if (focus == null) {
      updateSpecialOverlays();
      return;
    }

    specialRelocationRevealRunning = true;
    setActionInputLock(ACTION_LOCK_SPECIAL_REVEAL, true);
    syncSpecialTileVisibility(latestTreasureChestNodeId, latestWeaponsCacheNodeId);

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;
    const overlay =
      reveal.kind === 'treasure_chest' ? boardHandle.chestOverlay : boardHandle.cacheOverlay;
    const burstKind = reveal.kind === 'treasure_chest' ? 'gold' : 'item';
    const labelPrefix = reveal.kind === 'treasure_chest' ? 'chest-relocation' : 'cache-relocation';
    const startedAt = performance.now();
    tracePresentation({
      stage: 'relocation_reveal_start',
      nodeId: reveal.nodeId,
      encounterKind: reveal.kind,
    });

    try {
      if (orbitControls != null) {
        orbitControls.enabled = false;
      }
      renderer.cameraRig.setMode('spectator');

      await tweenCameraTo(
        cameraPositionForFocus(focus),
        focus,
        35,
        0.55,
        `${labelPrefix}:camera-to-spawn`,
        originalTarget,
      );
      await overlay.playRelocationAppearance(reveal.nodeId, mapHandle);
      spawnLandingBurst(overlay.group.position, burstKind);
      await waitWithGsap(0.35, `${labelPrefix}:hold-spawn`);

      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        0.45,
        `${labelPrefix}:camera-return`,
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
    } finally {
      setActionInputLock(ACTION_LOCK_SPECIAL_REVEAL, false);
      specialRelocationRevealRunning = false;
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
      updateSpecialOverlays();
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
      tracePresentation({
        stage: 'relocation_reveal_done',
        nodeId: reveal.nodeId,
        encounterKind: reveal.kind,
        durationMs: performance.now() - startedAt,
      });
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      for (const [playerId, queue] of queuedMovementSteps.entries()) {
        if (queue.length > 0) void processMovementQueue(playerId);
      }
      void processShadowStalkerSpawnRevealQueue();
      void processSpecialRelocationRevealQueue();
    }
  }

  function enqueueShadowStalkerMove(move: {
    npcId: string;
    fromNodeId: string;
    toNodeId: string;
    stepIndex: number;
    totalSteps: number;
  }): void {
    if (
      pendingShadowStalkerMoves.some(
        (pending) =>
          pending.npcId === move.npcId &&
          pending.fromNodeId === move.fromNodeId &&
          pending.toNodeId === move.toNodeId &&
          pending.stepIndex === move.stepIndex,
      )
    ) {
      return;
    }
    pendingShadowStalkerMoves.push(move);
    void processShadowStalkerMotionQueue();
  }

  function enqueueShadowStalkerKill(kill: {
    npcId: string;
    playerId: string;
    atNodeId: string;
  }): void {
    if (
      pendingShadowStalkerKills.some(
        (pending) =>
          pending.npcId === kill.npcId &&
          pending.playerId === kill.playerId &&
          pending.atNodeId === kill.atNodeId,
      )
    ) {
      return;
    }
    pendingShadowStalkerKills.push(kill);
    void processShadowStalkerMotionQueue();
  }

  async function processShadowStalkerMotionQueue(): Promise<void> {
    if (shadowStalkerMotionRunning || shadowStalkerKillRunning) return;
    if (shadowStalkerSpawnRevealRunning || pendingShadowStalkerSpawnReveals.length > 0) return;
    if (boardHandle == null || mapHandle == null) return;
    if (pendingShadowStalkerMoves.length === 0 && pendingShadowStalkerKills.length === 0) return;

    shadowStalkerMotionRunning = true;
    setActionInputLock(ACTION_LOCK_SHADOW_STALKER_MOTION, true);
    try {
      while (pendingShadowStalkerMoves.length > 0 || pendingShadowStalkerKills.length > 0) {
        const move = pendingShadowStalkerMoves.shift();
        if (move != null) {
          await playShadowStalkerMoveStep(move);
          continue;
        }
        const kill = pendingShadowStalkerKills.shift();
        if (kill != null) {
          await playShadowStalkerKillPresentation(kill);
        }
      }
    } finally {
      shadowStalkerMotionRunning = false;
      setActionInputLock(ACTION_LOCK_SHADOW_STALKER_MOTION, false);
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
      if (pendingShadowStalkerMoves.length > 0 || pendingShadowStalkerKills.length > 0) {
        void processShadowStalkerMotionQueue();
      }
    }
  }

  function enqueueShadowStalkerSpawnReveal(
    npcId: string,
    nodeId: string,
    summonerId?: string,
  ): void {
    if (
      pendingShadowStalkerSpawnReveals.some(
        (pending) => pending.npcId === npcId && pending.nodeId === nodeId,
      )
    ) {
      return;
    }
    pendingShadowStalkerSpawnReveals.push({ npcId, nodeId, summonerId });
    void processShadowStalkerSpawnRevealQueue();
  }

  async function processShadowStalkerSpawnRevealQueue(): Promise<void> {
    if (shadowStalkerSpawnRevealRunning) return;
    if (boardHandle == null || mapHandle == null) return;
    if (
      spawnSequenceRunning ||
      specialRelocationRevealRunning ||
      trapCinematicRunning ||
      orbitalCinematicRunning ||
      thunderFistCinematicRunning
    ) {
      return;
    }

    const reveal = pendingShadowStalkerSpawnReveals.shift();
    if (reveal == null) return;
    await runShadowStalkerSpawnReveal(reveal.npcId, reveal.nodeId, reveal.summonerId);
    if (pendingShadowStalkerSpawnReveals.length > 0) {
      void processShadowStalkerSpawnRevealQueue();
    } else {
      void processShadowStalkerMotionQueue();
    }
  }

  async function runShadowStalkerSpawnReveal(
    npcId: string,
    nodeId: string,
    summonerId?: string,
  ): Promise<void> {
    if (boardHandle == null || mapHandle == null) return;
    const focus = focusForNode(nodeId);
    if (focus == null) return;

    shadowStalkerSpawnRevealRunning = true;
    setActionInputLock(ACTION_LOCK_SHADOW_STALKER_SPAWN, true);

    const camera = renderer.cameraRig.camera;
    const originalMode = renderer.cameraRig.mode;
    const originalPosition = camera.position.clone();
    const originalQuaternion = camera.quaternion.clone();
    const originalFov = camera.fov;
    const originalTarget =
      orbitControls?.target.clone() ??
      camera.position.clone().add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(8));
    const controlsWereEnabled = orbitControls?.enabled ?? false;

    try {
      if (orbitControls != null) {
        orbitControls.enabled = false;
      }
      renderer.cameraRig.setMode('spectator');

      await tweenCameraTo(
        cameraPositionForFocus(focus),
        focus,
        35,
        0.55,
        'shadow-stalker-spawn:camera-to-spawn',
        originalTarget,
      );

      await ensureShadowStalkerModel(npcId, nodeId, false, summonerId);
      const record = boardNpcModels.get(npcId);
      const portalPosition = shadowStalkerPlacementForNode(nodeId) ?? focus;
      const cameraToPortal = portalPosition.clone().sub(camera.position);
      if (cameraToPortal.lengthSq() > 0.001) {
        portalPosition.add(cameraToPortal.normalize().multiplyScalar(0.1));
      }
      const spawnedNpc = latestGameState?.npcs.find((npc) => npc.id === npcId);
      const portalColor = colorForShadowStalkerSummoner(spawnedNpc?.summonerId ?? summonerId);
      await playShadowStalkerPortal(portalPosition, portalColor, () => {
        const current = boardNpcModels.get(npcId);
        if (current != null && !current.despawning) {
          animateShadowStalkerSpawn(current);
        } else if (record != null && !record.despawning) {
          animateShadowStalkerSpawn(record);
        }
      });
      await waitWithGsap(0.18, 'shadow-stalker-spawn:hold');

      await tweenCameraTo(
        originalPosition,
        originalTarget,
        originalFov,
        0.45,
        'shadow-stalker-spawn:camera-return',
      );
      camera.quaternion.copy(originalQuaternion);
      camera.fov = originalFov;
      camera.updateProjectionMatrix();
    } finally {
      setActionInputLock(ACTION_LOCK_SHADOW_STALKER_SPAWN, false);
      shadowStalkerSpawnRevealRunning = false;
      if (orbitControls != null) {
        orbitControls.enabled = controlsWereEnabled;
        orbitControls.update();
      }
      renderer.cameraRig.setMode(originalMode);
      if (latestGameState != null) {
        syncPathArrowsForState(latestGameState);
      }
      flushBoardPresentationMessages();
      maybeRouteDeferredBoardPhase();
    }
  }

  async function processSpecialRelocationRevealQueue(): Promise<void> {
    if (
      pendingSpecialRelocationReveals.length > 0 &&
      (specialRelocationRevealRunning ||
        orbitalCinematicRunning ||
        trapCinematicRunning ||
        thunderFistCinematicRunning ||
        spawnSequenceRunning ||
        boardHandle == null ||
        mapHandle == null ||
        isEncounterModalScreen(screenRouter.current))
    ) {
      tracePresentation({
        stage: 'relocation_reveal_blocked',
        nodeId: pendingSpecialRelocationReveals[0]?.nodeId,
        encounterKind: pendingSpecialRelocationReveals[0]?.kind,
        reason:
          screenRouter.current === 'treasure-chest' || screenRouter.current === 'weapons-cache'
            ? `screen:${screenRouter.current}`
            : 'presentation_busy',
      });
    }
    if (
      specialRelocationRevealRunning ||
      orbitalCinematicRunning ||
      trapCinematicRunning ||
      thunderFistCinematicRunning ||
      spawnSequenceRunning ||
      boardHandle == null ||
      mapHandle == null ||
      isEncounterModalScreen(screenRouter.current) ||
      pendingSpecialRelocationReveals.length === 0
    ) {
      return;
    }
    const reveal = pendingSpecialRelocationReveals.shift();
    if (reveal == null) return;
    await runSpecialRelocationReveal(reveal);
  }

  function enqueueSpecialRelocationReveal(kind: SpecialRelocationRevealKind, nodeId: string): void {
    if (!isRealNodeId(nodeId)) return;
    if (
      pendingSpecialRelocationReveals.some(
        (pending) => pending.kind === kind && pending.nodeId === nodeId,
      )
    ) {
      return;
    }
    pendingSpecialRelocationReveals.push({ kind, nodeId });
    tracePresentation({
      stage: 'relocation_reveal_queued',
      nodeId,
      encounterKind: kind,
    });
    void processSpecialRelocationRevealQueue();
    queueMicrotask(() => void processSpecialRelocationRevealQueue());
  }

  function maybeRunChestCacheSpawnSequence(phaseTransitionedToGameStart: boolean): void {
    if (phaseTransitionedToGameStart) {
      pendingSpawnSequence = true;
      setActionInputLock(ACTION_LOCK_CHEST_SPAWN, true);
    }
    if (!phaseTransitionedToGameStart || hasRunSpawnSequence || spawnSequenceRunning) {
      return;
    }

    if (!isRealNodeId(latestTreasureChestNodeId) || !isRealNodeId(latestWeaponsCacheNodeId)) {
      pendingSpawnSequence = false;
      setActionInputLock(ACTION_LOCK_CHEST_SPAWN, false);
      hasRunSpawnSequence = true;
      updateSpecialOverlays();
      return;
    }

    if (boardHandle == null || mapHandle == null) {
      return;
    }

    void runChestCacheSpawnSequence(latestTreasureChestNodeId, latestWeaponsCacheNodeId);
  }

  function triggerMapLoad(phase: string): void {
    if (mapHandle != null || mapLoading) return;
    // eslint-disable-next-line no-console
    console.log(`[smashparty] ${phase} — loading skyforge map shell`);
    mapLoading = true;
    void loadMap('skyforge', {
      scene: renderer.scene.scene,
      groups: renderer.scene.groups,
      shaderRegistry,
      loadDecorations: false,
    })
      .then(async (handle) => {
        await Promise.all([tileIconTexturesReady, ensureBoardShellAssetsLoaded()]);
        mapHandle = handle;
        applyLightingConfig(renderer.lighting, handle.theme.lightingRig);
        applyMapFx(renderer.scene.renderer, renderer.composer, handle.fx);
        if (boardHandle != null) {
          applyLiveAtmosphereTheme(handle.theme);
          updateSpecialOverlays();
          return;
        }
        const shouldDeferSpecialSpawn =
          pendingSpawnSequence &&
          !hasRunSpawnSequence &&
          isRealNodeId(latestTreasureChestNodeId) &&
          isRealNodeId(latestWeaponsCacheNodeId);
        // buildBoard creates islands, bridges, tile pads, chests, sky dome,
        // cloud layer, fog.
        boardHandle = buildBoard({
          nodes: handle.schema.nodes as readonly BoardNode[],
          groups: renderer.scene.groups,
          scene: renderer.scene.scene,
          shaderRegistry,
          treasureChestNodeId: shouldDeferSpecialSpawn
            ? null
            : (latestTreasureChestNodeId ?? handle.schema.startingTreasureChestNodeId ?? null),
          weaponsCacheNodeId: shouldDeferSpecialSpawn
            ? null
            : (latestWeaponsCacheNodeId ?? handle.schema.startingWeaponsCacheNodeId ?? null),
          bridges: handle.schema.bridges,
          islandOverrides: handle.schema.islandOverrides,
          theme: handle.theme,
          ambientEnvVfx: {
            getLanterns: () => mapHandle?.getLanterns() ?? [],
            getCrystals: () => mapHandle?.getCrystals() ?? [],
            getFountainAnchors: () => mapHandle?.getFountainAnchors() ?? [],
          },
          itemVfx: {
            eventLog: liveItemVfxEventLog,
            getPositionForPlayer: itemVfxPositionForPlayer,
            getPositionForNode: itemVfxPositionForNode,
            getItemModelScene: getResolvedItemModelScene,
            getNpcById: (npcId) => latestGameState?.npcs.find((npc) => npc.id === npcId),
          },
          audio: boardAudioHooks,
          bridgeGltfScene,
          chunkLargeGltfScene,
          chunkSmallGltfScene,
          droppedKeyModelScene: droppedKeyGltfScene,
          tileIconTextures,
          gltfLoader: gltfLoader ?? null,
          gsapRegistry,
        });
        applyLiveAtmosphereTheme(handle.theme);
        setOrbitTargetToBoardCentroid(handle.schema.nodes as readonly BoardNode[]);
        ensureNavigationOverlays();
        const stateAfterBoardBuild = latestGameState;
        if (stateAfterBoardBuild != null && inWarmupArena) {
          const boardPlayers = stateAfterBoardBuild.turnOrder
            .map((id) => stateAfterBoardBuild.players.get(id))
            .filter((p): p is NonNullable<typeof p> => p != null)
            .map((p) => ({ id: p.id, nodeId: p.nodeId, customization: p.customization }));
          rebuildBoardRoster(boardPlayers);
          clearPathArrowsAndFacing();
          updateArenaMainGameSnapshot(latestArenaSnapshotPlayers);
          if (warmupCameraView === 'mainland') {
            focusWarmupMainlandCamera();
          } else {
            focusWarmupArenaCamera();
          }
        } else if (stateAfterBoardBuild != null && stateAfterBoardBuild.phase !== 'lobby') {
          const boardPlayers = stateAfterBoardBuild.turnOrder
            .map((id) => stateAfterBoardBuild.players.get(id))
            .filter((p): p is NonNullable<typeof p> => p != null)
            .map((p) => ({ id: p.id, nodeId: p.nodeId, customization: p.customization }));
          rebuildBoardRoster(boardPlayers);
          syncOrbitTargetToActivePlayer(stateAfterBoardBuild);
          syncPathArrowsForState(stateAfterBoardBuild);
        } else if (stateAfterBoardBuild != null) {
          const lobbyPlayers = stateAfterBoardBuild.turnOrder
            .map((id) => stateAfterBoardBuild.players.get(id))
            .filter((p): p is NonNullable<typeof p> => p != null)
            .map((p) => ({ id: p.id, nodeId: p.nodeId, customization: p.customization }));
          rebuildLobbyRoster(lobbyPlayers);
        } else if (inWarmupArena) {
          focusWarmupArenaCamera();
        }
        if (shouldDeferSpecialSpawn) {
          syncSpecialTileVisibility(latestTreasureChestNodeId, latestWeaponsCacheNodeId);
          maybeRunChestCacheSpawnSequence(true);
        } else {
          updateSpecialOverlays();
        }
        if (stateAfterBoardBuild != null) {
          syncBoardStateVisuals(stateAfterBoardBuild);
        }
        void processSpecialRelocationRevealQueue();
        // eslint-disable-next-line no-console
        console.log(
          '[smashparty] board built — board children:',
          renderer.scene.groups.board.children.length,
          'env children:',
          renderer.scene.groups.environment.children.length,
        );
        // Board stays visible in lobby — Skyforge map is the Battle-Royale background.
        if (currentPhase === 'lobby') {
          setupLobbyCamera();
        }
        if (mapDecorationsRequested) {
          void startMapDecorationPreload('pending_after_shell');
        }
      })
      .catch((err) => {
        console.error('[smashparty] loadMap/buildBoard failed:', err);
      })
      .finally(() => {
        mapLoading = false;
      });
  }

  function startMapDecorationPreload(phase: string): Promise<void> {
    mapDecorationsRequested = true;
    if (mapDecorationsLoaded) return Promise.resolve();
    if (mapDecorationPreloadPromise != null) return mapDecorationPreloadPromise;
    if (gltfLoader == null || mapHandle == null || boardHandle == null) return Promise.resolve();
    // eslint-disable-next-line no-console
    console.log(`[smashparty] ${phase} — loading skyforge decorations`);
    mapDecorationPreloadPromise = loadMap('skyforge', {
      scene: renderer.scene.scene,
      groups: renderer.scene.groups,
      shaderRegistry,
      gltfLoader,
      loadDecorations: true,
    })
      .then((decoratedHandle) => {
        const previousHandle = mapHandle;
        mapHandle = decoratedHandle;
        mapDecorationsLoaded = true;
        if (previousHandle != null && previousHandle !== decoratedHandle) {
          previousHandle.dispose();
        }
        applyLiveAtmosphereTheme(decoratedHandle.theme);
        updateSpecialOverlays();
        void processSpecialRelocationRevealQueue();
      })
      .catch((err) => {
        console.warn('[smashparty] skyforge decoration preload failed:', err);
        mapDecorationPreloadPromise = null;
      })
      .then(() => undefined);
    return mapDecorationPreloadPromise;
  }

  function startPostCustomizationBackgroundLoad(phase: string): void {
    void startMapDecorationPreload(phase);
    if (ensureCharacterAnimationBundles != null) {
      void ensureCharacterAnimationBundles(['actions'], {
        policy: 'degraded',
        reason: 'background action preload',
      });
      void ensureCharacterAnimationBundles(['boardActions'], {
        policy: 'degraded',
        reason: 'background board action preload',
      });
      void ensureCharacterAnimationBundles(['emotes'], {
        policy: 'degraded',
        reason: 'background emote/lobby preload',
      });
    }
  }

  function isLocalHost(): boolean {
    return localPlayerId != null && currentHostId === localPlayerId;
  }

  function isServerPausableBoardPhase(phase: GameState['phase'] | 'game_start' | 'game_started') {
    return (
      phase !== 'lobby' &&
      phase !== 'game_over' &&
      phase !== 'awards' &&
      phase !== 'game_start' &&
      phase !== 'game_started' &&
      !ACTIVE_MINIGAME_PHASES.has(phase as GameState['phase'])
    );
  }

  function canServerPause(): boolean {
    return (
      isLocalHost() &&
      currentRoomType === 'create_game' &&
      isServerPausableBoardPhase(currentPhase) &&
      latestGameState?.pause == null &&
      canonicalGameState?.pause == null &&
      !boardPresentationPaused
    );
  }

  function isPauseMenuOpen(): boolean {
    return screenRouter.current === 'pause-menu' || screenRouter.overlayCurrent === 'pause-menu';
  }

  function isSystemOverlayOpen(): boolean {
    const current = screenRouter.current;
    const overlayCurrent = screenRouter.overlayCurrent;
    return (
      current === 'pause-menu' ||
      current === 'settings' ||
      current === 'how-to-play' ||
      current === 'confirm-dialog' ||
      overlayCurrent === 'pause-menu' ||
      overlayCurrent === 'settings' ||
      overlayCurrent === 'how-to-play' ||
      overlayCurrent === 'confirm-dialog'
    );
  }

  function hasVisibleOrQueuedBoardPresentation(): boolean {
    if (screenRouter.current === 'dice-roll' || screenRouter.current === 'turn-banner') return true;
    if (screenRouter.current === 'board-hud' && currentPhase !== 'lobby') return true;
    if (isMovementCinematicBusy()) return true;
    if (pendingBoardPresentationMessages.length > 0) return true;
    if (pendingTurnBanner != null) return true;
    if (pendingMinigameStartMessage != null) return true;
    return false;
  }

  function setBoardPresentationGsapPaused(paused: boolean): void {
    gsapRegistry.setPaused?.(
      paused,
      paused ? { allowLabelPrefixes: PAUSE_GSAP_ALLOW_LABEL_PREFIXES } : undefined,
    );
  }

  function closePauseMenu(): void {
    if (screenRouter.overlayCurrent === 'pause-menu') {
      screenRouter.closeOverlay?.('pause-menu');
      if (latestGameState != null && latestGameState.pause == null && !isSystemOverlayOpen()) {
        routeBoardPhase(latestGameState, { forceRemount: true, allowSystemOverlay: true });
      }
      return;
    }
    if (
      screenRouter.current === 'pause-menu' &&
      latestGameState != null &&
      latestGameState.pause == null
    ) {
      routeBoardPhase(latestGameState, { forceRemount: true, allowSystemOverlay: true });
    }
  }

  function showSystemOverlay(id: ScreenId, payload?: unknown): void {
    if (screenRouter.showOverlay != null) {
      screenRouter.showOverlay(id, payload);
      return;
    }
    screenRouter.navigate(id, payload);
  }

  function openSystemSettings(): void {
    showSystemOverlay('settings', {
      audioSettings,
      onBack: () => {
        if (screenRouter.closeOverlay != null) {
          screenRouter.closeOverlay('settings');
        } else {
          openSystemMenu();
        }
      },
    });
  }

  function openSystemHowToPlay(): void {
    showSystemOverlay('how-to-play', {
      onBack: () => {
        if (screenRouter.closeOverlay != null) {
          screenRouter.closeOverlay('how-to-play');
        } else {
          openSystemMenu();
        }
      },
    });
  }

  function openSystemLeaveConfirm(payload: unknown): void {
    showSystemOverlay('confirm-dialog', {
      variant: 'leave-game',
      onConfirm: leaveCurrentGameToWarmup,
      onCancel: () => {
        if (screenRouter.closeOverlay != null) {
          screenRouter.closeOverlay('confirm-dialog');
        } else {
          screenRouter.navigate('pause-menu', payload);
        }
      },
    });
  }

  function beginBoardPresentationPause(
    opts: {
      readonly localRequest?: boolean;
      readonly serverAck?: boolean;
    } = {},
  ): void {
    boardPresentationPaused = true;
    if (opts.serverAck === true) {
      serverPauseActive = true;
      localPauseRequestCoversPresentation = false;
    }
    if (opts.localRequest === true) {
      localPresentationPauseActive = true;
      localPauseRequestCoversPresentation = hasVisibleOrQueuedBoardPresentation();
    }
    setBoardPresentationGsapPaused(true);
    openPauseMenu();
    refreshGlobalSystemButtons();
  }

  function clearLocalPresentationPause(): void {
    localPresentationPauseActive = false;
    localPauseRequestCoversPresentation = false;
    if (serverPauseActive || latestGameState?.pause != null || canonicalGameState?.pause != null) {
      return;
    }
    resumeBoardPresentationQueue();
  }

  function handlePauseRequestNotification(text: string): boolean {
    if (text === 'not_host' || text === 'pause_disabled') {
      clearLocalPresentationPause();
      return false;
    }
    if (text !== 'pause_unavailable' && text !== 'game_finished') return false;
    if (!localPresentationPauseActive) return false;
    if (localPauseRequestCoversPresentation) {
      localPauseRequestCoversPresentation = false;
      suppressedPauseNotificationText = text;
      return true;
    }
    clearLocalPresentationPause();
    return false;
  }

  function requestPauseGame(): void {
    if (!canServerPause()) {
      openSystemMenu();
      return;
    }
    beginBoardPresentationPause({ localRequest: true });
    wsClient.send({ type: 'pause_game' });
  }

  function requestResumeGame(): void {
    if (!isLocalHost()) return;
    if (!serverPauseActive && latestGameState?.pause == null && canonicalGameState?.pause == null) {
      clearLocalPresentationPause();
      return;
    }
    wsClient.send({ type: 'resume_game' });
  }

  function buildInGamePausePayload(): unknown {
    const payload: unknown = {
      isHost: isLocalHost(),
      roomType: 'create-game',
      menuState: isLocalHost() ? 'server-paused-host' : 'server-paused-readonly',
      onResume: requestResumeGame,
      onSettings: openSystemSettings,
      onHowToPlay: openSystemHowToPlay,
      onLeaveGame: () => openSystemLeaveConfirm(payload),
    };
    return payload;
  }

  function openPauseMenu(): void {
    if (isPauseMenuOpen()) return;
    const payload = buildInGamePausePayload();
    showSystemOverlay('pause-menu', payload);
    refreshGlobalSystemButtons();
  }

  function buildSystemMenuPayload(): unknown {
    const payload: unknown = {
      isHost: isLocalHost(),
      roomType: currentRoomType === 'quick_play' ? 'quick-play' : 'create-game',
      menuState: 'menu-only',
      title: 'MENU',
      subtitle: 'Game options',
      onContinue: closePauseMenu,
      onSettings: openSystemSettings,
      onHowToPlay: openSystemHowToPlay,
      onLeaveGame: () => openSystemLeaveConfirm(payload),
    };
    return payload;
  }

  function openSystemMenu(): void {
    if (isPauseMenuOpen()) return;
    playSfx('sfx-ui-menu-open');
    showSystemOverlay('pause-menu', buildSystemMenuPayload());
  }

  function applyBoardPresentationPause(): void {
    beginBoardPresentationPause({ serverAck: true });
  }

  function clearLatestPauseState(): void {
    if (latestGameState != null && latestGameState.pause != null) {
      latestGameState = { ...latestGameState, pause: null };
      publishLiveHudStateFromGameState(latestGameState);
    }
    if (canonicalGameState != null && canonicalGameState.pause != null) {
      canonicalGameState = { ...canonicalGameState, pause: null };
    }
  }

  function resumeBoardPresentationQueue(): void {
    boardPresentationPaused = false;
    localPresentationPauseActive = false;
    localPauseRequestCoversPresentation = false;
    serverPauseActive = false;
    setBoardPresentationGsapPaused(false);
    closePauseMenu();
    refreshGlobalSystemButtons();
    boardPresentationQueue.flush();
    drainBoardPresentationMessages();
    maybeRouteDeferredBoardPhase();
    flushPendingTurnBanner();
    flushPendingBoardPromptReady();
    maybeMountPendingMinigameStart();
    tryShowNextGameplayLesson();
  }

  // Global ESC → pause-menu binding so settings (music/VFX) and Leave Game are
  // reachable from any in-game screen, not just the 4-tile main menu.
  // Runs in capture phase so boot-level lobby fallbacks do not steal Quick Play
  // or active-minigame Escape before the shared system menu can open.
  function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
  }

  function handleGlobalEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    if (isPauseMenuOpen()) return;
    const current = screenRouter.current;
    const overlayCurrent = screenRouter.overlayCurrent;
    if (
      current === 'settings' ||
      current === 'confirm-dialog' ||
      current === 'how-to-play' ||
      overlayCurrent === 'settings' ||
      overlayCurrent === 'confirm-dialog' ||
      overlayCurrent === 'how-to-play'
    ) {
      return;
    }
    if (isTypingTarget(event.target)) return;

    const shouldOpenMenuOnly =
      currentRoomType === 'quick_play' ||
      ACTIVE_MINIGAME_PHASES.has(currentPhase as GameState['phase']) ||
      (currentPhase !== 'lobby' && !canServerPause());
    if (!canServerPause() && !shouldOpenMenuOnly) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openSystemMenu();
  }
  document.addEventListener('keydown', handleGlobalEscape, { capture: true });

  function handleStateSyncState(
    state: GameState,
    opts: { forceBoardRemount?: boolean } = {},
  ): void {
    const previousStateSyncPhase = lastStateSyncPhase;
    const returnedFromMinigameToBoard =
      previousStateSyncPhase != null &&
      ACTIVE_MINIGAME_PHASES.has(previousStateSyncPhase) &&
      !ACTIVE_MINIGAME_PHASES.has(state.phase);
    const previousTreasureChestNodeId = latestTreasureChestNodeId;
    const previousWeaponsCacheNodeId = latestWeaponsCacheNodeId;
    latestGameState = state;
    if (returnedFromMinigameToBoard) {
      characterEyeExpressions.clearLayerForAll('result');
      if (audioDesiredMusic !== 'music-skyforge-board') {
        audioDesiredMusic = 'music-skyforge-board';
        playDesiredMusic(500);
      }
    }
    publishLiveHudStateFromGameState(state);
    if (localPlayerId != null && !state.players.has(localPlayerId)) {
      localPlayerId = null;
    }
    if (localPlayerId == null && state.players.size === 1) {
      localPlayerId = state.turnOrder[0] ?? null;
    }
    const localPlayer = localPlayerId == null ? null : state.players.get(localPlayerId);
    if (
      localPlayer != null &&
      (!inWarmupArena || deferredWarmupCustomizationApplied || deferredWarmupCustomization == null)
    ) {
      savePreferredCustomization(localPlayer.customization);
    }
    syncLocalEventCursorFromState(state);
    resolvePendingLocalActionFromStateSync(state);
    reconcileSuppressedPromptWithState(state);
    if (
      !ACTIVE_MINIGAME_PHASES.has(state.phase) &&
      pendingMinigameStartMessage == null &&
      pendingMinigameSelectionMessage == null
    ) {
      disposeActiveMinigame();
    }
    const phaseTransitionedToGameStart =
      state.phase === 'game_start' &&
      sawGameStartedEvent &&
      (lastStateSyncPhase === 'lobby' || lastStateSyncPhase == null);
    lastStateSyncPhase = state.phase;
    currentPhase = state.phase;
    refreshGlobalSystemButtons();
    if (ACTIVE_MINIGAME_PHASES.has(state.phase)) {
      dismissReconnectingScreenAfterRestore('active_minigame_state_sync');
      queueMicrotask(() => {
        maybeShowPendingMinigameSelection();
        maybeMountPendingMinigameStart();
      });
    }
    if (state.phase !== 'fork_choice') {
      submittedForkChoiceKey = null;
      pendingForkOptionsByPlayer.clear();
    } else if (state.pendingForkChoice != null) {
      pendingForkOptionsByPlayer.set(state.pendingForkChoice.playerId, {
        atNodeId: state.pendingForkChoice.atNodeId,
        options: [...state.pendingForkChoice.options],
      });
    }
    observeGameplayTutorialState(state);
    if (localPlayerId != null) {
      const pendingRollSubmissionKey = pendingRollSubmissionKeysByPlayer.get(localPlayerId);
      if (
        pendingRollSubmissionKey != null &&
        getPreRollPathArrowKey(state) !== pendingRollSubmissionKey
      ) {
        pendingRollSubmissionKeysByPlayer.delete(localPlayerId);
      }
    }
    latestTreasureChestNodeId = state.treasureChestNodeId;
    latestWeaponsCacheNodeId = state.weaponsCacheNodeId;
    const canQueueSpecialRelocationReveal =
      previousStateSyncPhase != null &&
      !ACTIVE_MINIGAME_PHASES.has(previousStateSyncPhase) &&
      previousStateSyncPhase !== 'lobby' &&
      previousStateSyncPhase !== 'game_over' &&
      state.phase !== 'lobby' &&
      state.phase !== 'game_over' &&
      !ACTIVE_MINIGAME_PHASES.has(state.phase) &&
      !phaseTransitionedToGameStart &&
      !pendingRestoredBoardRoute;
    const treasureChestRelocated =
      canQueueSpecialRelocationReveal &&
      isRealNodeId(previousTreasureChestNodeId) &&
      isRealNodeId(latestTreasureChestNodeId) &&
      previousTreasureChestNodeId !== latestTreasureChestNodeId;
    const weaponsCacheRelocated =
      canQueueSpecialRelocationReveal &&
      isRealNodeId(previousWeaponsCacheNodeId) &&
      isRealNodeId(latestWeaponsCacheNodeId) &&
      previousWeaponsCacheNodeId !== latestWeaponsCacheNodeId;
    if (treasureChestRelocated && isRealNodeId(latestTreasureChestNodeId)) {
      enqueueSpecialRelocationReveal('treasure_chest', latestTreasureChestNodeId);
    }
    if (weaponsCacheRelocated && isRealNodeId(latestWeaponsCacheNodeId)) {
      enqueueSpecialRelocationReveal('weapons_cache', latestWeaponsCacheNodeId);
    }
    const specialRelocationRevealQueued = treasureChestRelocated || weaponsCacheRelocated;
    if (
      warmupPendingMove != null &&
      localPlayerId != null &&
      state.players.get(localPlayerId)?.nodeId === warmupPendingMove.nodeId
    ) {
      warmupPendingMove = null;
    }

    if (state.phase === 'lobby') {
      if (audioDesiredMusic !== 'music-lobby') {
        audioDesiredMusic = 'music-lobby';
        playDesiredMusic(500);
      }
      disposeTileEffectOverlay();
      pendingRestoredBoardRoute = false;
      // Catch-all: trigger map load if not already done
      triggerMapLoad('state_sync(lobby)');
      void startCustomizationBackgroundPreload('state_sync(lobby)');
      // Make sure lobby roster is visible; hide board rigs
      warmupCameraView = 'arena';
      warmupArenaCameraFocused = false;
      arenaSnapshotParent.visible = false;
      lobbyRosterParent.visible = true;
      boardRosterParent.visible = false;
      boardNpcParent.visible = false;
      // Rebuild roster from turnOrder (preserves join order)
      const players = state.turnOrder
        .map((id) => state.players.get(id))
        .filter((p): p is NonNullable<typeof p> => p != null)
        .map((p) => ({ id: p.id, nodeId: p.nodeId, customization: p.customization }));
      rebuildLobbyRoster(players);
      characterEyeExpressions.syncFromState(state);
      setupLobbyCamera();
      clearPathArrowsAndFacing();
    } else if (inWarmupArena) {
      if (screenRouter.current === 'reconnecting' || restoreAttemptInFlight) {
        recoverStaleStoredSessionToWarmup('warmup_arena_state_sync');
      } else {
        clearReconnectStateAfterWarmupRecovery('warmup_arena_state_sync');
      }
      if (audioDesiredMusic !== 'music-lobby') {
        audioDesiredMusic = 'music-lobby';
        playDesiredMusic(500);
      }
      triggerMapLoad('state_sync(warmup-arena)');
      lobbyRosterParent.visible = false;
      boardRosterParent.visible = true;
      boardNpcParent.visible = true;
      arenaSnapshotParent.visible = latestArenaSnapshotPlayers.length > 0;
      if (hasSceneGroups) {
        renderer.scene.groups.board.visible = true;
      }
      updateSpecialOverlays();
      const boardPlayers = state.turnOrder
        .map((id) => state.players.get(id))
        .filter((p): p is NonNullable<typeof p> => p != null)
        .map((p) => ({ id: p.id, nodeId: p.nodeId, customization: p.customization }));
      rebuildBoardRoster(boardPlayers);
      clearPathArrowsAndFacing();
      focusWarmupArenaCamera();
      window.dispatchEvent(new CustomEvent('smashparty:warmup-restore-hud'));
      if (!isWarmupOverlayScreen(screenRouter.current)) {
        screenRouter.navigate('warmup-arena-overlay', { portal: null });
      }
      void startCustomizationBackgroundPreload('state_sync(warmup-arena)');
      preloadWarmupInventoryItemModels(state);
    } else {
      if (ACTIVE_MINIGAME_PHASES.has(state.phase) || state.phase === 'game_over') {
        disposeTileEffectOverlay();
      }
      // Game phase (playing, minigame, game_over, etc.)
      triggerMapLoad('state_sync(game)');
      void startCustomizationBackgroundPreload('state_sync(game)');
      // Hide lobby rigs, show board rigs with current player positions
      lobbyRosterParent.visible = false;
      boardRosterParent.visible = true;
      boardNpcParent.visible = true;
      if (hasSceneGroups) {
        renderer.scene.groups.board.visible = true;
      }
      const boardPlayers = state.turnOrder
        .map((id) => state.players.get(id))
        .filter((p): p is NonNullable<typeof p> => p != null)
        .map((p) => ({ id: p.id, nodeId: p.nodeId, customization: p.customization }));
      if (phaseTransitionedToGameStart) {
        maybeRunChestCacheSpawnSequence(true);
        if (!spawnSequenceRunning) {
          updateSpecialOverlays();
        }
      } else if (!spawnSequenceRunning && !specialRelocationRevealQueued) {
        updateSpecialOverlays();
      } else if (specialRelocationRevealQueued) {
        syncSpecialTileVisibility(latestTreasureChestNodeId, latestWeaponsCacheNodeId);
      }
      rebuildBoardRoster(boardPlayers);
      syncBoardStateVisuals(state);
      if (state.phase === 'game_over') {
        disposeNavigationOverlays();
      } else {
        syncPathArrowsForState(state);
      }
      syncOrbitTargetToActivePlayer(state);
      const forceRemount =
        pendingRestoredBoardRoute || returnedFromMinigameToBoard || opts.forceBoardRemount === true;
      pendingRestoredBoardRoute = false;
      if (state.pause != null) {
        applyBoardPresentationPause();
        return;
      }
      routeBoardPhase(state, { forceRemount });
      void processSpecialRelocationRevealQueue();
    }
  }

  const latestRevisionByRoomId = new Map<
    string,
    { serverEpoch: string | null; revision: number }
  >();
  let activeRoomId: string | null = null;

  function isRoomEntryMessage(type: string | undefined): boolean {
    return (
      type === 'room_created' ||
      type === 'room_joined' ||
      type === 'quick_play_joined' ||
      type === 'session_restored' ||
      type === 'warmup_arena_joined'
    );
  }

  const filteredServerMessageSubscribers = new Set<(message: ServerMessage) => void>();
  function subscribeFilteredServerMessages(handler: (message: ServerMessage) => void): () => void {
    filteredServerMessageSubscribers.add(handler);
    return () => {
      filteredServerMessageSubscribers.delete(handler);
    };
  }

  function isEmoteInputEligible(): boolean {
    const state = latestGameState;
    if (inWarmupArena || state == null) return false;
    if (state.phase === 'lobby') return false;
    if (state.phase === 'awards' || state.phase === 'game_over') return false;
    return !ACTIVE_MINIGAME_PHASES.has(state.phase);
  }

  function logEmoteRuntimeDiagnostic(event: EmoteRuntimeDiagnostic): void {
    const state = latestGameState;
    logClientWsEvent({
      direction: event.type === 'emote_send_attempt' ? 'out' : 'error',
      stage: event.type,
      payload: {
        type: event.type,
        playerId: event.playerId,
        emoteId: event.emoteId,
        source: event.source,
        roomId: activeRoomId,
        phase: state?.phase,
        currentPlayerIndex: state?.currentPlayerIndex,
        activePlayerId:
          state == null ? undefined : (state.turnOrder[state.currentPlayerIndex] ?? undefined),
        ownedPlayerIds: [...ownedPlayerIds],
        ...(event.type === 'emote_send_blocked_cooldown' ? { remainingMs: event.remainingMs } : {}),
      },
    });
  }

  emoteRuntime = createEmoteRuntime({
    gsapRegistry,
    mount: opts.host,
    keyboardTarget: window,
    pointerTarget: window,
    documentRef: document,
    gamepad: warmupGamepad,
    getOwnedPlayerIds: () => [...ownedPlayerIds],
    isEmoteInputEnabled: isEmoteInputEligible,
    send: (message) => wsClient.send(message),
    subscribeServerMessages: subscribeFilteredServerMessages,
    getPlayerRig: (playerId) => boardRigs.get(playerId) ?? rosterRigs.get(playerId) ?? null,
    getEmoteTexture: (emoteId) => emoteBubbleTextures.get(emoteId) ?? null,
    playSfxForPlayer,
    iconUrls: emoteIconUrls,
    isTextEntryTarget,
    onDiagnostic: logEmoteRuntimeDiagnostic,
  });

  const mapUnsub = wsClient.subscribe((rawMsg) => {
    const msg = rawMsg as { type?: string };
    const revisioned = rawMsg as {
      roomId?: unknown;
      serverEpoch?: unknown;
      roomRevision?: unknown;
    };
    const roomId = typeof revisioned.roomId === 'string' ? revisioned.roomId : null;
    const serverEpoch = typeof revisioned.serverEpoch === 'string' ? revisioned.serverEpoch : null;
    const roomRevision =
      typeof revisioned.roomRevision === 'number' ? revisioned.roomRevision : null;
    const isRoomEntry = isRoomEntryMessage(msg.type);
    const isEmotePlayed = msg.type === 'emote_played';
    const emotePlayedLogPayload = isEmotePlayed
      ? {
          type: 'emote_played',
          playerId: (rawMsg as { playerId?: unknown }).playerId,
          emoteId: (rawMsg as { emoteId?: unknown }).emoteId,
          roomId,
          serverEpoch,
          roomRevision,
          activeRoomId,
          phase: latestGameState?.phase,
          currentPlayerIndex: latestGameState?.currentPlayerIndex,
          activePlayerId:
            latestGameState == null
              ? undefined
              : (latestGameState.turnOrder[latestGameState.currentPlayerIndex] ?? undefined),
          ownedPlayerIds: [...ownedPlayerIds],
        }
      : null;
    if (emotePlayedLogPayload != null) {
      logClientWsEvent({
        direction: 'in',
        stage: 'emote_played_received',
        payload: emotePlayedLogPayload,
      });
    }
    if (suppressRoomMessagesUntilEntry && isRoomEntry) {
      suppressRoomMessagesUntilEntry = false;
    } else if (
      suppressRoomMessagesUntilEntry &&
      msg.type !== 'leave_ack' &&
      msg.type !== 'notification'
    ) {
      if (emotePlayedLogPayload != null) {
        logClientWsEvent({
          direction: 'error',
          stage: 'emote_played_filtered',
          detail: 'suppress_room_messages_until_entry',
          payload: emotePlayedLogPayload,
        });
      }
      return;
    }
    if (roomId != null && (isRoomEntry || activeRoomId == null) && roomId !== activeRoomId) {
      activeRoomId = roomId;
      latestRevisionByRoomId.set(roomId, { serverEpoch: null, revision: -1 });
      resetEyeExpressionEventCursor(true);
    }
    if (!isRoomEntry && roomId != null && activeRoomId != null && roomId !== activeRoomId) {
      if (emotePlayedLogPayload != null) {
        logClientWsEvent({
          direction: 'error',
          stage: 'emote_played_filtered',
          detail: `room_mismatch activeRoomId=${activeRoomId}`,
          payload: emotePlayedLogPayload,
        });
      }
      return;
    }
    if (roomId != null && serverEpoch != null && roomRevision != null) {
      const cursor = latestRevisionByRoomId.get(roomId) ?? {
        serverEpoch: null,
        revision: -1,
      };
      if (cursor.serverEpoch !== serverEpoch) {
        cursor.serverEpoch = serverEpoch;
        cursor.revision = -1;
        resetEyeExpressionEventCursor(true);
      }
      if (roomRevision < cursor.revision) {
        if (emotePlayedLogPayload != null) {
          logClientWsEvent({
            direction: 'error',
            stage: 'emote_played_filtered',
            detail: `stale_revision cursorRevision=${cursor.revision}`,
            payload: emotePlayedLogPayload,
          });
        }
        return;
      }
      cursor.revision = roomRevision;
      latestRevisionByRoomId.set(roomId, cursor);
    }

    if (emotePlayedLogPayload != null) {
      logClientWsEvent({
        direction: 'in',
        stage: 'emote_played_presenting',
        payload: emotePlayedLogPayload,
      });
    }
    for (const subscriber of [...filteredServerMessageSubscribers]) {
      subscriber(rawMsg as ServerMessage);
    }

    switch (msg.type) {
      case 'room_created':
        {
          const created = msg as Extract<ServerMessage, { type: 'room_created' }>;
          resetEyeExpressionEventCursor(true);
          inWarmupArena = false;
          resetGameplayTutorialRuntime();
          warmupCameraView = 'arena';
          cameraUserDirty = false;
          lobbyCameraApplied = false;
          arenaSnapshotParent.visible = false;
          if (typeof created.hostId === 'string') {
            localPlayerId = created.hostId;
            currentHostId = created.hostId;
          }
          setOwnedPlayerIds(created.ownedPlayerIds, created.hostId);
          currentRoomType = 'create_game';
          if (typeof created.sessionToken === 'string' && typeof created.code === 'string') {
            setActiveStoredSession(created.sessionToken, created.code, {
              mode: 'game',
              phase: currentPhase,
            });
            restoreAttemptInFlight = false;
          }
          refreshGlobalSystemButtons();
        }
        triggerMapLoad(msg.type);
        break;
      case 'room_joined':
        {
          const joined = msg as Extract<ServerMessage, { type: 'room_joined' }>;
          resetEyeExpressionEventCursor(true);
          inWarmupArena = joined.mode === 'warmup-arena';
          resetGameplayTutorialRuntime();
          warmupCameraView = 'arena';
          warmupArenaCameraFocused = false;
          cameraUserDirty = false;
          lobbyCameraApplied = false;
          arenaSnapshotParent.visible = false;
          localPlayerId = joined.playerId;
          currentHostId = joined.hostId;
          currentRoomType = joined.roomType;
          setOwnedPlayerIds(joined.ownedPlayerIds, joined.playerId);
          setActiveStoredSession(joined.sessionToken, joined.roomCode, {
            mode: joined.mode === 'warmup-arena' ? 'warmup-arena' : 'game',
            phase: currentPhase,
          });
          restoreAttemptInFlight = false;
          refreshGlobalSystemButtons();
        }
        triggerMapLoad(msg.type);
        break;
      case 'quick_play_joined':
        {
          const joined = msg as Extract<ServerMessage, { type: 'quick_play_joined' }>;
          resetEyeExpressionEventCursor(true);
          inWarmupArena = false;
          resetGameplayTutorialRuntime();
          warmupCameraView = 'arena';
          cameraUserDirty = false;
          lobbyCameraApplied = false;
          arenaSnapshotParent.visible = false;
          const players = joined.players;
          const ownPlayerId = joined.playerId ?? players?.[players.length - 1]?.id;
          if (typeof ownPlayerId === 'string') {
            localPlayerId = ownPlayerId;
          }
          setOwnedPlayerIds(joined.ownedPlayerIds, ownPlayerId);
          if (typeof joined.sessionToken === 'string' && typeof joined.roomCode === 'string') {
            setActiveStoredSession(joined.sessionToken, joined.roomCode, {
              mode: 'game',
              phase: currentPhase,
            });
            restoreAttemptInFlight = false;
          }
          currentRoomType = 'quick_play';
          currentHostId = null;
          refreshGlobalSystemButtons();
        }
        triggerMapLoad(msg.type);
        break;
      case 'warmup_arena_joined': {
        const joined = msg as Extract<ServerMessage, { type: 'warmup_arena_joined' }>;
        recoverStaleStoredSessionToWarmup('warmup_arena_joined');
        resetEyeExpressionEventCursor(true);
        inWarmupArena = true;
        resetGameplayTutorialRuntime();
        warmupCameraView = 'arena';
        cameraUserDirty = false;
        lobbyCameraApplied = false;
        warmupArenaCameraFocused = false;
        localPlayerId = joined.playerId;
        setOwnedPlayerIds(joined.ownedPlayerIds, joined.playerId);
        if (typeof joined.sessionToken === 'string' && typeof joined.roomCode === 'string') {
          setActiveStoredSession(joined.sessionToken, joined.roomCode, {
            mode: 'warmup-arena',
            phase: currentPhase,
          });
          restoreAttemptInFlight = false;
        }
        currentPhase = 'game_start';
        currentRoomType = 'create_game';
        refreshGlobalSystemButtons();
        audioDesiredMusic = 'music-lobby';
        playDesiredMusic(500);
        lobbyRosterParent.visible = false;
        boardRosterParent.visible = true;
        boardNpcParent.visible = true;
        arenaSnapshotParent.visible = latestArenaSnapshotPlayers.length > 0;
        if (hasSceneGroups) {
          renderer.scene.groups.board.visible = true;
        }
        triggerMapLoad('warmup_arena_joined');
        focusWarmupArenaCamera();
        window.dispatchEvent(new CustomEvent('smashparty:warmup-restore-hud'));
        if (!isWarmupOverlayScreen(screenRouter.current)) {
          screenRouter.navigate('warmup-arena-overlay', { portal: null });
        }
        void startCustomizationBackgroundPreload('warmup_arena_joined');
        break;
      }
      case 'player_joined': {
        // Lobby entry events — trigger map load so island is visible immediately
        triggerMapLoad(msg.type);
        break;
      }
      case 'session_restored': {
        const restored = msg as RestoredSessionMessage;
        resetEyeExpressionEventCursor(true);
        logClientWsEvent({
          direction: 'in',
          stage: 'session_restored',
          payload: {
            type: 'session_restored',
            token: restored.token,
            roomCode: restored.roomCode,
            phase: restored.phase,
            roomType: restored.roomType,
            mode: restored.mode,
          },
        });
        inWarmupArena = restored.mode === 'warmup-arena';
        resetGameplayTutorialRuntime();
        warmupCameraView = 'arena';
        warmupArenaCameraFocused = false;
        cameraUserDirty = false;
        lobbyCameraApplied = false;
        localPlayerId = restored.playerId;
        currentHostId = restored.hostId;
        currentRoomType = restored.roomType;
        refreshGlobalSystemButtons();
        setOwnedPlayerIds(restored.ownedPlayerIds, restored.playerId);
        restoreAttemptInFlight = false;
        setActiveStoredSession(restored.token, restored.roomCode, {
          mode: restored.mode === 'warmup-arena' ? 'warmup-arena' : 'game',
          phase: restored.phase,
        });
        if (restored.mode === 'warmup-arena') {
          currentPhase = 'game_start';
          refreshGlobalSystemButtons();
          audioDesiredMusic = 'music-lobby';
          playDesiredMusic(500);
          triggerMapLoad('session_restored(warmup-arena)');
          window.dispatchEvent(new CustomEvent('smashparty:warmup-restore-hud'));
          screenRouter.navigate('warmup-arena-overlay', { portal: null });
          void startCustomizationBackgroundPreload('session_restored(warmup-arena)');
          break;
        }
        if (restored.phase === 'lobby') {
          clearBoardPresentationSequencer();
          void startCustomizationBackgroundPreload('session_restored(lobby)');
          routeRestoredLobby(restored);
        } else {
          resetBoardPresentationAfterReconnect();
          pendingRestoredBoardRoute = true;
          currentPhase = restored.phase;
          refreshGlobalSystemButtons();
          audioDesiredMusic = 'music-skyforge-board';
          playDesiredMusic(500);
          lobbyRosterParent.visible = false;
          boardRosterParent.visible = true;
          boardNpcParent.visible = true;
          if (hasSceneGroups) {
            renderer.scene.groups.board.visible = true;
          }
          triggerMapLoad('session_restored(game)');
          if (ACTIVE_MINIGAME_PHASES.has(restored.phase)) {
            dismissReconnectingScreenAfterRestore('active_minigame_session_restored');
          }
          if (latestGameState != null && latestGameState.phase === restored.phase) {
            routeBoardPhase(latestGameState, { forceRemount: true });
          }
        }
        break;
      }
      case 'host_changed': {
        const hostChanged = msg as Extract<ServerMessage, { type: 'host_changed' }>;
        currentHostId = hostChanged.newHostId;
        refreshGlobalSystemButtons();
        break;
      }
      case 'owned_players_changed': {
        const changed = msg as Extract<ServerMessage, { type: 'owned_players_changed' }>;
        setOwnedPlayerIds(changed.ownedPlayerIds, localPlayerId);
        break;
      }
      case 'leave_ack': {
        clearActiveStoredSession();
        ownedPlayerIds.clear();
        activeRoomId = null;
        latestRevisionByRoomId.clear();
        resetEyeExpressionEventCursor(true);
        resetGameplayTutorialRuntime();
        currentHostId = null;
        currentPhase = 'game_start';
        refreshGlobalSystemButtons();
        break;
      }
      case 'game_paused': {
        applyBoardPresentationPause();
        if (audioDesiredMusic !== 'music-pause') {
          prePauseMusic = audioDesiredMusic;
          audioDesiredMusic = 'music-pause';
          playDesiredMusic(300);
        }
        break;
      }
      case 'game_resumed': {
        clearLatestPauseState();
        boardPresentationPaused = false;
        if (prePauseMusic != null) {
          audioDesiredMusic = prePauseMusic;
          prePauseMusic = null;
          playDesiredMusic(300);
        }
        if (latestGameState != null && latestGameState.pause == null) {
          routeBoardPhase(latestGameState, { forceRemount: true });
        }
        resumeBoardPresentationQueue();
        break;
      }
      case 'game_started': {
        clearBoardPresentationSequencer();
        // Transition out of lobby: hide lobby rigs, show board rigs
        inWarmupArena = false;
        resetGameplayTutorialRuntime();
        warmupCameraView = 'arena';
        arenaSnapshotParent.visible = false;
        currentPhase = 'game_started';
        refreshGlobalSystemButtons();
        sawGameStartedEvent = true;
        audioDesiredMusic = 'music-skyforge-board';
        playDesiredMusic(500);
        lobbyRosterParent.visible = false;
        boardRosterParent.visible = true;
        if (hasSceneGroups) {
          renderer.scene.groups.board.visible = true;
        }
        triggerMapLoad('game_started');
        break;
      }
      case 'event': {
        enqueueBoardPresentationMessage(msg as ServerMessage);
        break;
      }
      case 'emote_played': {
        const emote = msg as Extract<ServerMessage, { type: 'emote_played' }>;
        void ensureCharacterReactionAnimationsLoaded().then(() => {
          const alias = EMOTE_ID_TO_CHARACTER_ANIMATION[emote.emoteId];
          const nowMs = performance.now();
          if (boardRigs.has(emote.playerId)) {
            setCharacterAnimationOverride(boardRigAnimationOverrides, emote.playerId, alias, nowMs);
          }
          if (rosterRigs.has(emote.playerId)) {
            setCharacterAnimationOverride(
              rosterRigAnimationOverrides,
              emote.playerId,
              alias,
              nowMs,
            );
          }
          if (ghostRigs.has(emote.playerId)) {
            setCharacterAnimationOverride(ghostRigAnimationOverrides, emote.playerId, alias, nowMs);
          }
        });
        break;
      }
      case 'fork_options': {
        const forkMsg = msg as {
          type: 'fork_options';
          playerId: string;
          atNodeId: string;
          options: string[];
        };
        pendingForkOptionsByPlayer.set(forkMsg.playerId, {
          atNodeId: forkMsg.atNodeId,
          options: [...forkMsg.options],
        });
        submittedForkChoiceKey = null;
        if (latestGameState != null) {
          syncPathArrowsForState(latestGameState);
          routeBoardPhase(latestGameState);
        }
        break;
      }
      case 'minigame_start': {
        const startMsg = msg as Extract<ServerMessage, { type: 'minigame_start' }>;
        const minigameDef = minigameRegistry.get(startMsg.minigameId);
        if (minigameDef != null) {
          const nextMusic: MusicTrackId =
            minigameDef.kind === 'realtime' ? 'music-minigame-realtime' : 'music-minigame-solo';
          if (audioDesiredMusic !== nextMusic) {
            audioDesiredMusic = nextMusic;
            playDesiredMusic(500);
          }
        }
        queueMinigameStart(startMsg);
        break;
      }
      case 'minigame_selection': {
        queueMinigameSelection(msg as Extract<ServerMessage, { type: 'minigame_selection' }>);
        break;
      }
      case 'minigame_round_results': {
        bufferOrApplyMinigameMessage(
          msg as Extract<ServerMessage, { type: 'minigame_round_results' }>,
        );
        break;
      }
      case 'minigame_snapshot': {
        bufferOrApplyMinigameMessage(msg as Extract<ServerMessage, { type: 'minigame_snapshot' }>);
        break;
      }
      case 'minigame_event': {
        bufferOrApplyMinigameMessage(msg as Extract<ServerMessage, { type: 'minigame_event' }>);
        break;
      }
      case 'minigame_ready_state': {
        bufferOrApplyMinigameMessage(
          msg as Extract<ServerMessage, { type: 'minigame_ready_state' }>,
        );
        break;
      }
      case 'minigame_results': {
        playSfx('sfx-minigame-end-fanfare');
        for (const placement of (msg as Extract<ServerMessage, { type: 'minigame_results' }>)
          .placements) {
          characterEyeExpressions.setSticky(
            placement.playerId,
            'result',
            placement.rank === 1 ? 'excited' : 'sad',
          );
        }
        bufferOrApplyMinigameMessage(msg as Extract<ServerMessage, { type: 'minigame_results' }>);
        break;
      }
      case 'game_over': {
        const gameOver = msg as Extract<ServerMessage, { type: 'game_over' }>;
        playSfx(
          gameOver.winnerId == null || gameOver.winnerId === localPlayerId
            ? 'sfx-fanfare-victory'
            : 'sfx-sting-defeat',
        );
        break;
      }
      case 'notification': {
        const notification = msg as Extract<ServerMessage, { type: 'notification' }>;
        if (suppressedPauseNotificationText === notification.text) {
          suppressedPauseNotificationText = null;
          break;
        }
        if (notification.level === 'error') {
          playSfx('sfx-ui-error');
        } else if (notification.level === 'warn') {
          playSfx('sfx-misc-buzzer-negative');
        }
        break;
      }
      case 'arena_main_game_snapshot': {
        updateArenaMainGameSnapshot(
          (msg as Extract<ServerMessage, { type: 'arena_main_game_snapshot' }>).players,
        );
        break;
      }
      case 'state_sync': {
        const syncMsg = msg as Extract<ServerMessage, { type: 'state_sync' }>;
        const state = deserializeGameState(syncMsg.state);
        updateCanonicalGameState(state, { roomId, roomRevision });
        if (
          !ACTIVE_MINIGAME_PHASES.has(state.phase) &&
          pendingMinigameStartMessage == null &&
          pendingMinigameSelectionMessage == null
        ) {
          disposeActiveMinigame();
        }
        if (inWarmupArena || state.phase === 'lobby') {
          boardPresentationPaused = false;
          clearBoardPresentationSequencer();
          handleStateSyncState(state);
          boardPresentationQueue.push(syncMsg);
          break;
        }

        if (state.pause != null) {
          serverPauseActive = true;
          handleStateSyncState(state);
          break;
        }

        if (boardPresentationPaused) {
          if (localPresentationPauseActive || serverPauseActive) {
            enqueueDeferredBoardStateSync(syncMsg, state, { forceBoardRemount: true });
            break;
          }
          boardPresentationPaused = false;
          setBoardPresentationGsapPaused(false);
          enqueueDeferredBoardStateSync(syncMsg, state, { forceBoardRemount: true });
          break;
        }

        if (ACTIVE_MINIGAME_PHASES.has(state.phase) && boardEffectActivityCount > 0) {
          tracePresentation({
            stage: 'state_sync_deferred',
            phase: state.phase,
            activePlayerId: state.turnOrder[state.currentPlayerIndex] ?? undefined,
            serverMessageType: 'state_sync',
            reason: 'board_effect_active',
          });
          pendingBoardEffectMinigameStateSync = { msg: syncMsg, state };
          break;
        }

        const deferralReason = getBoardStateSyncDeferralReason();
        if (deferralReason != null) {
          tracePresentation({
            stage: 'state_sync_deferred',
            phase: state.phase,
            activePlayerId: state.turnOrder[state.currentPlayerIndex] ?? undefined,
            serverMessageType: 'state_sync',
            reason: deferralReason,
          });
          enqueueDeferredBoardStateSync(syncMsg, state, {
            forceBoardRemount: isPresentationBlocked(),
            relocationGate:
              awaitingForcedRelocationStateSync || hasPendingUnresolvedForcedRelocationEvent(),
          });
          break;
        }

        handleStateSyncState(state);
        boardPresentationQueue.push(syncMsg);
        flushForcedRelocationMessages();
        break;
      }
      default:
        break;
    }
  });

  return {
    renderer,
    wsClient,
    screenRouter,
    errorReporter,
    atmosphere: atmosphereHandle,
    devTools: devToolsHandle,
    characterAdapter,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      audioDisposed = true;
      disposeActiveMinigame();
      removeAudioUnlockListeners();
      setButtonClickSfxHandler(null);
      setButtonHoverSfxHandler(null);
      closeSystemOverlays();
      resetGameplayTutorialRuntime();
      document.removeEventListener('keydown', handleGlobalEscape, { capture: true });
      window.removeEventListener('pagehide', handlePageLifecycleHidden);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      pauseButton.removeEventListener('click', handlePauseButtonClick);
      discordButton.removeEventListener('click', handleDiscordButtonClick);
      settingsButton.removeEventListener('click', handleSettingsButtonClick);
      pauseButton.remove();
      discordButton.remove();
      settingsButton.remove();
      restoreGlobalUiHostLayer();
      window.removeEventListener('resize', applyViewport);
      window.visualViewport?.removeEventListener('resize', applyViewport);
      window.visualViewport?.removeEventListener('scroll', applyViewport);
      window.removeEventListener(WARMUP_ARENA_WATCH_EVENT, focusWarmupMainlandCamera);
      window.removeEventListener(WARMUP_ARENA_BACK_EVENT, handleWarmupArenaBackCamera);
      opts.canvas.removeEventListener('pointerdown', handleBoardPointerDown);
      opts.canvas.removeEventListener('pointerup', handleBoardPointerUp);
      selectionGuard.dispose();
      document.removeEventListener('keydown', handleMovementDirectionKeydown);
      activeItemPlayerTargeting = null;
      devBoardTileClickListeners.clear();
      warmupDirectionalInputUnsub();
      warmupKeyboard.dispose();
      lobbyCameraTunerOverlay?.dispose();
      lobbyCameraTunerOverlay = null;
      customizationCameraHandle?.dispose();
      customizationCameraHandle = null;
      orbitControlsDomGateCleanup?.();
      orbitControlsDomGateCleanup = null;
      orbitTargetFollower?.dispose();
      orbitTargetFollower = null;
      orbitControls?.removeEventListener('start', handleOrbitControlStart);
      orbitControls?.dispose();
      restoreStateUnsub();
      notificationUnsub();
      toastOverlay.dispose();
      wsDebugOverlay?.dispose();
      emoteRuntime?.dispose();
      emoteRuntime = null;
      mapUnsub();
      boardPresentationQueue.dispose();
      turnBannerHandle.dispose();
      bloodPoolTickUnsub();
      bloodPool.dispose();
      for (const playerId of [...heldItemObjects.keys()]) removeHeldItemObject(playerId);
      activeHeldItemPreview = null;
      itemModelSceneCache.clear();
      for (const texture of emoteBubbleTextures.values()) texture.dispose();
      emoteBubbleTextures.clear();
      // Dispose lobby roster rigs
      for (const rig of rosterRigs.values()) rig.dispose();
      rosterRigs.clear();
      rosterIdleBaseY.clear();
      rosterPhaseOffsets.clear();
      rosterRigAnimationOverrides.clear();
      lobbyAmbientAnimationScheduler.clear();
      for (const handle of ghostFadeHandles.values()) handle.dispose();
      ghostFadeHandles.clear();
      for (const rig of ghostRigs.values()) rig.dispose();
      ghostRigs.clear();
      ghostIdleBaseY.clear();
      ghostPhaseOffsets.clear();
      ghostRigAnimationOverrides.clear();
      if (hasSceneGroups) {
        renderer.scene.groups.environment.remove(lobbyRosterParent);
      }
      // Dispose board roster rigs
      characterStatusEffects.dispose();
      characterEyeExpressions.dispose();
      characterEyeTextureStore?.dispose();
      characterEyeTextureStore = null;
      for (const rig of boardRigs.values()) rig.dispose();
      boardRigs.clear();
      boardRigCustomizationSignatures.clear();
      for (const record of boardNpcModels.values()) {
        disposeShadowStalkerRecord(record);
      }
      boardNpcModels.clear();
      boardNpcParent.clear();
      boardRigBaseY.clear();
      boardRigPhases.clear();
      boardRigCapeNone.clear();
      boardRigNodeIds.clear();
      boardRigVisualOrder.clear();
      boardRigHopOffsets.clear();
      boardRigFacingYawOffsets.clear();
      boardRigAnimationOverrides.clear();
      heldMovementPlayers.clear();
      animatingMovementPlayers.clear();
      queuedMovementSteps.clear();
      pendingSpecialRelocationReveals.length = 0;
      specialRelocationRevealRunning = false;
      pendingLightningDashPresentations.length = 0;
      lightningDashPresentationRunning = false;
      pendingShadowStalkerSpawnReveals.length = 0;
      pendingShadowStalkerMoves.length = 0;
      pendingShadowStalkerKills.length = 0;
      pendingShadowStalkerKilledDespawns.clear();
      shadowStalkerSpawnRevealRunning = false;
      shadowStalkerMotionRunning = false;
      shadowStalkerKillRunning = false;
      movementProgressByPlayer.clear();
      pendingForkOptionsByPlayer.clear();
      pendingRollSubmissionKeysByPlayer.clear();
      clearBoardPresentationSequencer();
      pendingTurnBanner = null;
      pendingBoardPromptReady = null;
      lastSentBoardPromptReadyKey = null;
      notificationsActive = false;
      turnBannerLeadIns.clear();
      botTurnIntroKeyByPlayer.clear();
      disposeTileEffectOverlay();
      for (const handle of boardMoveHandles.values()) handle.dispose();
      boardMoveHandles.clear();
      clearBoardRigFacingPreview();
      for (const handle of itemUsePresentationHandles.values()) handle.dispose();
      itemUsePresentationHandles.clear();
      for (const handle of itemImpactPresentationHandles.values()) handle.dispose();
      itemImpactPresentationHandles.clear();
      liveItemVfxSubscribers.clear();
      actionControlLockSubscribers.clear();
      pendingLocalAction = null;
      suppressedPrompt = null;
      heldItemUseTokens.clear();
      for (const playerId of [...heldItemObjects.keys()]) removeHeldItemObject(playerId);
      boardRigActionYawOffsets.clear();
      boardRigActionPitchOffsets.clear();
      boardRigActionRollOffsets.clear();
      boardRigActionScale.clear();
      clearBoardRigActionPositionOffsets();
      for (const mesh of arenaSnapshotMeshes.values()) disposeArenaSnapshotMesh(mesh);
      arenaSnapshotMeshes.clear();
      latestArenaSnapshotPlayers = [];
      if (hasSceneGroups) {
        renderer.scene.groups.board.remove(arenaSnapshotParent);
      }
      disposeNavigationOverlays();
      spawnLandingFxEmitters.clear();
      spawnLandingFxPool.dispose();
      if (hasSceneGroups) {
        renderer.scene.groups.board.remove(boardRosterParent);
      }
      boardHandle?.dispose();
      boardHandle = null;
      hiddenSpecialTileNodeIds.clear();
      mapHandle?.dispose();
      mapHandle = null;
      clearReconnectRefreshTimer();
      clearWarmupRestoreFallbackTimer();
      errorReporter.dispose();
      screenRouter.dispose();
      gsapRegistry.dispose();
      audioManager?.dispose();
      audioManager = null;
      renderer.dispose();
      wsClient.close();
    },
  };
}
